import { useState, useMemo, useEffect, useRef, useCallback, Fragment } from "react";
import useIsMobile from "../hooks/useIsMobile";
import { api } from "../lib/api";
import { FundBarChart, VehicleBarChart, DiversificationPies } from "./Charts/CommitmentCharts";
import {
  LineChart,
  AreaChart,
  BarChart,
  Bar,
  Line,
  Area,
  PieChart,
  Pie,
  ScatterChart,
  Scatter,
  ZAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import * as XLSX from "xlsx";

// Inline SVG icon components
const Icon = ({ children, className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);
const DollarSign = ({ className }) => <Icon className={className}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></Icon>;
const ArrowUpRight = ({ className }) => <Icon className={className}><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></Icon>;
const ArrowDownRight = ({ className }) => <Icon className={className}><line x1="7" y1="7" x2="17" y2="17"/><polyline points="17 7 17 17 7 17"/></Icon>;
const TrendingUp = ({ className }) => <Icon className={className}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></Icon>;
const BarChart3 = ({ className }) => <Icon className={className}><path d="M3 3v18h18"/><rect x="7" y="10" width="3" height="8" rx="1"/><rect x="14" y="5" width="3" height="13" rx="1"/></Icon>;
const Upload = ({ className }) => <Icon className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></Icon>;
const FileText = ({ className }) => <Icon className={className}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></Icon>;
const ChevronDown = ({ className }) => <Icon className={className}><polyline points="6 9 12 15 18 9"/></Icon>;
const ChevronUp = ({ className }) => <Icon className={className}><polyline points="18 15 12 9 6 15"/></Icon>;
const X = ({ className }) => <Icon className={className}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Icon>;
const Plus = ({ className }) => <Icon className={className}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Icon>;
const Filter = ({ className }) => <Icon className={className}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></Icon>;

const EMBEDDED_RAW_DATA = {"funds": [{"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "currency": "USD", "total_commitment": 7500.0, "investor_ref": "BPEAMMGLEVEMCOII024"}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "currency": "USD", "total_commitment": 22500.0, "investor_ref": "APACVIIILEVII055"}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "currency": "USD", "total_commitment": 7500.0, "investor_ref": "EXINDVILEVII006"}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "currency": "EUR", "total_commitment": 15000.0, "investor_ref": "GROEMP043"}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "currency": "EUR", "total_commitment": 15000.0, "investor_ref": "IXEMP047"}, {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "currency": "EUR", "total_commitment": 15000.0, "investor_ref": "INFRAVILEVII005"}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "currency": "EUR", "total_commitment": 10000.0, "investor_ref": "INFRAVEMPI012"}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "currency": "EUR", "total_commitment": 10000.0, "investor_ref": "REIICOLP089"}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "currency": "EUR", "total_commitment": 20000.0, "investor_ref": "VENTIIEMP025"}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "currency": "EUR", "total_commitment": 10000.0, "investor_ref": "VENIIIEMP082"}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "currency": "EUR", "total_commitment": 7500.0, "investor_ref": "VENIIILEVII013"}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "currency": "EUR", "total_commitment": 30000.0, "investor_ref": "EQTXEMPCOII022"}, {"fund": "EQT Healthcare Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "currency": "EUR", "total_commitment": 15000.0, "investor_ref": "EQTHGCLEVEMPII062"}], "cas_timeline": [{"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-06-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": 2331.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -148.0, "holding_pct": 0.0738, "portfolio": []}, {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-09-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": 2360.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -104.0, "holding_pct": 0.0738, "portfolio": []}, {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-12-31", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": 2246.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -164.0, "holding_pct": 0.0738, "portfolio": []}, {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-03-31", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": 2246.0, "nav_closing": 4259.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -424.0, "holding_pct": 0.0738, "portfolio": []}, {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-06-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": 2246.0, "nav_closing": 4117.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -501.0, "holding_pct": 0.0738, "portfolio": []}, {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-09-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": 2246.0, "nav_closing": 5144.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 589.0, "holding_pct": 0.0738, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2023-06-30", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -7500.0, "remaining_commitment": 15000.0, "nav_opening": null, "nav_closing": 11467.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 5505.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2023-09-30", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -7500.0, "remaining_commitment": 15000.0, "nav_opening": null, "nav_closing": 14178.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 8795.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2023-12-31", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -7500.0, "remaining_commitment": 15000.0, "nav_opening": null, "nav_closing": 12438.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 7577.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2024-03-31", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -7500.0, "remaining_commitment": 15000.0, "nav_opening": 12438.0, "nav_closing": 13862.0, "amounts_called": 0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 1577.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2024-06-30", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -15000.0, "remaining_commitment": 7500.0, "nav_opening": 12438.0, "nav_closing": 25031.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 5515.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2024-09-30", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -15000.0, "remaining_commitment": 7500.0, "nav_opening": 12438.0, "nav_closing": 27076.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 7955.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2024-12-31", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -15000.0, "remaining_commitment": 7500.0, "nav_opening": 12438.0, "nav_closing": 33996.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 15508.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2025-03-31", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -22500.0, "remaining_commitment": null, "nav_opening": 33996.0, "nav_closing": 38448.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -2148.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2025-06-30", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -22500.0, "remaining_commitment": null, "nav_opening": 33996.0, "nav_closing": 36807.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -2711.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2025-09-30", "currency": "USD", "total_commitment": 22500.0, "drawn_commitment": -22500.0, "remaining_commitment": null, "nav_opening": 33996.0, "nav_closing": 35349.0, "amounts_called": 7500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -3061.0, "holding_pct": 0.1016, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2023-09-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": null, "nav_closing": 1609.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -586.0, "holding_pct": 0.2674, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2023-12-31", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": 1133.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -675.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2024-03-31", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 1133.0, "nav_closing": 984.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -128.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2024-06-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": 1133.0, "nav_closing": 3510.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -78.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2024-09-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": 1133.0, "nav_closing": 3058.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -471.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2024-12-31", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": 1133.0, "nav_closing": 4872.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 1475.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2025-03-31", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -7500.0, "remaining_commitment": null, "nav_opening": 4872.0, "nav_closing": 6868.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -318.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2025-06-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -7500.0, "remaining_commitment": null, "nav_opening": 4872.0, "nav_closing": 6294.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -719.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2025-09-30", "currency": "USD", "total_commitment": 7500.0, "drawn_commitment": -7500.0, "remaining_commitment": null, "nav_opening": 4872.0, "nav_closing": 5960.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -883.0, "holding_pct": 0.0802, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -4518.0, "remaining_commitment": 11478.0, "nav_opening": null, "nav_closing": 3279.0, "amounts_called": 4518.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -220.0, "holding_pct": 0.0241, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -4805.0, "remaining_commitment": 11471.0, "nav_opening": null, "nav_closing": 2884.0, "amounts_called": 4805.0, "amounts_distributed": 0.0, "realized_gains": -14.0, "unrealized_gains": -580.0, "holding_pct": 0.0241, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -4805.0, "remaining_commitment": 11471.0, "nav_opening": null, "nav_closing": 2892.0, "amounts_called": 4805.0, "amounts_distributed": 0.0, "realized_gains": -19.0, "unrealized_gains": -330.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -5022.0, "remaining_commitment": 11449.0, "nav_opening": null, "nav_closing": 2910.0, "amounts_called": 5022.0, "amounts_distributed": 0.0, "realized_gains": -30.0, "unrealized_gains": -295.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -6168.0, "remaining_commitment": 10303.0, "nav_opening": 2910.0, "nav_closing": 3896.0, "amounts_called": 1148.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -102.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -6168.0, "remaining_commitment": 10303.0, "nav_opening": 2910.0, "nav_closing": 4009.0, "amounts_called": 1148.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 39.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -6173.0, "remaining_commitment": 10298.0, "nav_opening": 2910.0, "nav_closing": 4035.0, "amounts_called": 1153.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 96.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -6649.0, "remaining_commitment": 9822.0, "nav_opening": 2910.0, "nav_closing": 4533.0, "amounts_called": 1629.0, "amounts_distributed": 0.0, "realized_gains": 6.0, "unrealized_gains": 163.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -7324.0, "remaining_commitment": 9147.0, "nav_opening": 4533.0, "nav_closing": 5864.0, "amounts_called": 676.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 654.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -7882.0, "remaining_commitment": 8589.0, "nav_opening": 4533.0, "nav_closing": 6914.0, "amounts_called": 1234.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 1152.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -8751.0, "remaining_commitment": 7720.0, "nav_opening": 4533.0, "nav_closing": 7522.0, "amounts_called": 2103.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 889.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -8751.0, "remaining_commitment": 7720.0, "nav_opening": 4533.0, "nav_closing": 7882.0, "amounts_called": 2103.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 1269.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -8970.0, "remaining_commitment": 7501.0, "nav_opening": 7882.0, "nav_closing": 8604.0, "amounts_called": 217.0, "amounts_distributed": 25.0, "realized_gains": null, "unrealized_gains": 532.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -10090.0, "remaining_commitment": 6381.0, "nav_opening": 7882.0, "nav_closing": 10280.0, "amounts_called": 1337.0, "amounts_distributed": 25.0, "realized_gains": null, "unrealized_gains": 1092.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -10519.0, "remaining_commitment": 5952.0, "nav_opening": 7882.0, "nav_closing": 11452.0, "amounts_called": 1766.0, "amounts_distributed": 25.0, "realized_gains": null, "unrealized_gains": 1835.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -10519.0, "remaining_commitment": 5952.0, "nav_opening": 7882.0, "nav_closing": 12345.0, "amounts_called": 1766.0, "amounts_distributed": 25.0, "realized_gains": null, "unrealized_gains": 2744.0, "holding_pct": 0.023, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-09-30", "currency": "EUR", "total_commitment": null, "drawn_commitment": null, "remaining_commitment": 15000.0, "nav_opening": null, "nav_closing": null, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": null, "holding_pct": 0.0093, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-12-31", "currency": "EUR", "total_commitment": null, "drawn_commitment": null, "remaining_commitment": 15000.0, "nav_opening": null, "nav_closing": null, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 603.0, "holding_pct": 0.0093, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -2961.0, "remaining_commitment": 12039.0, "nav_opening": null, "nav_closing": null, "amounts_called": 2961.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -15.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -4701.0, "remaining_commitment": 10299.0, "nav_opening": null, "nav_closing": null, "amounts_called": 4702.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 491.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -5732.0, "remaining_commitment": 9711.0, "nav_opening": 584.0, "nav_closing": 7920.0, "amounts_called": 5732.0, "amounts_distributed": 443.0, "realized_gains": null, "unrealized_gains": 2095.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -10587.0, "remaining_commitment": 4856.0, "nav_opening": 584.0, "nav_closing": 13901.0, "amounts_called": 10587.0, "amounts_distributed": 443.0, "realized_gains": null, "unrealized_gains": 3292.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -12458.0, "remaining_commitment": 3518.0, "nav_opening": 13901.0, "nav_closing": 16405.0, "amounts_called": 1871.0, "amounts_distributed": 533.0, "realized_gains": null, "unrealized_gains": 1184.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -12652.0, "remaining_commitment": 3699.0, "nav_opening": 13901.0, "nav_closing": 16096.0, "amounts_called": 2065.0, "amounts_distributed": 908.0, "realized_gains": null, "unrealized_gains": 995.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -12711.0, "remaining_commitment": 3699.0, "nav_opening": 13901.0, "nav_closing": 16673.0, "amounts_called": 2124.0, "amounts_distributed": 967.0, "realized_gains": null, "unrealized_gains": 1653.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -12711.0, "remaining_commitment": 3699.0, "nav_opening": 13901.0, "nav_closing": 15775.0, "amounts_called": 2124.0, "amounts_distributed": 967.0, "realized_gains": -36.0, "unrealized_gains": 745.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -14083.0, "remaining_commitment": 2346.0, "nav_opening": 15775.0, "nav_closing": 17969.0, "amounts_called": 1370.0, "amounts_distributed": 108.0, "realized_gains": null, "unrealized_gains": 945.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -14083.0, "remaining_commitment": 2346.0, "nav_opening": 15775.0, "nav_closing": 17807.0, "amounts_called": 1370.0, "amounts_distributed": 108.0, "realized_gains": null, "unrealized_gains": 796.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -14984.0, "remaining_commitment": 1721.0, "nav_opening": 15775.0, "nav_closing": 17941.0, "amounts_called": 2271.0, "amounts_distributed": 601.0, "realized_gains": 225.0, "unrealized_gains": 190.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -14997.0, "remaining_commitment": 1708.0, "nav_opening": 15775.0, "nav_closing": 16918.0, "amounts_called": 2284.0, "amounts_distributed": 671.0, "realized_gains": 218.0, "unrealized_gains": -728.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -14997.0, "remaining_commitment": 1708.0, "nav_opening": 16918.0, "nav_closing": 17740.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 827.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -15212.0, "remaining_commitment": 1493.0, "nav_opening": 16918.0, "nav_closing": 17975.0, "amounts_called": 215.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 872.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -15428.0, "remaining_commitment": 1277.0, "nav_opening": 16918.0, "nav_closing": 18742.0, "amounts_called": 431.0, "amounts_distributed": 0.0, "realized_gains": -164.0, "unrealized_gains": 1261.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -15706.0, "remaining_commitment": 1367.0, "nav_opening": 16918.0, "nav_closing": 19888.0, "amounts_called": 709.0, "amounts_distributed": 1288.0, "realized_gains": 697.0, "unrealized_gains": 2886.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -15705.0, "remaining_commitment": 1368.0, "nav_opening": 19888.0, "nav_closing": 21127.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 1238.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -15705.0, "remaining_commitment": 1368.0, "nav_opening": 19888.0, "nav_closing": 20937.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": 1874.0, "unrealized_gains": -798.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -15915.0, "remaining_commitment": 1474.0, "nav_opening": 19888.0, "nav_closing": 19034.0, "amounts_called": 210.0, "amounts_distributed": 2760.0, "realized_gains": 2053.0, "unrealized_gains": -335.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -15942.0, "remaining_commitment": 1447.0, "nav_opening": 19888.0, "nav_closing": 19035.0, "amounts_called": 237.0, "amounts_distributed": 3006.0, "realized_gains": 2052.0, "unrealized_gains": -250.0, "holding_pct": 0.0089, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2759.0, "remaining_commitment": 7324.0, "nav_opening": 0, "nav_closing": 2579.0, "amounts_called": 2759.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -15.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -3742.0, "remaining_commitment": 6395.0, "nav_opening": 2579.0, "nav_closing": 3630.0, "amounts_called": 983.0, "amounts_distributed": 54.0, "realized_gains": null, "unrealized_gains": 159.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4744.0, "remaining_commitment": 5642.0, "nav_opening": 2579.0, "nav_closing": 4781.0, "amounts_called": 1985.0, "amounts_distributed": 304.0, "realized_gains": -15.0, "unrealized_gains": 571.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -5005.0, "remaining_commitment": 5760.0, "nav_opening": 2579.0, "nav_closing": 5025.0, "amounts_called": 2246.0, "amounts_distributed": 683.0, "realized_gains": -2.0, "unrealized_gains": 933.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -5565.0, "remaining_commitment": 5206.0, "nav_opening": 2579.0, "nav_closing": 5329.0, "amounts_called": 2806.0, "amounts_distributed": 689.0, "realized_gains": -6.0, "unrealized_gains": 704.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -7006.0, "remaining_commitment": 3806.0, "nav_opening": 5329.0, "nav_closing": 7185.0, "amounts_called": 1442.0, "amounts_distributed": 41.0, "realized_gains": 7.0, "unrealized_gains": 417.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -7413.0, "remaining_commitment": 3399.0, "nav_opening": 5329.0, "nav_closing": 7960.0, "amounts_called": 1849.0, "amounts_distributed": 41.0, "realized_gains": -7.0, "unrealized_gains": 843.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -8050.0, "remaining_commitment": 3042.0, "nav_opening": 5329.0, "nav_closing": 8937.0, "amounts_called": 2486.0, "amounts_distributed": 321.0, "realized_gains": 7.0, "unrealized_gains": 1496.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -8050.0, "remaining_commitment": 3042.0, "nav_opening": 5329.0, "nav_closing": 9016.0, "amounts_called": 2486.0, "amounts_distributed": 321.0, "realized_gains": null, "unrealized_gains": 1593.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -8526.0, "remaining_commitment": 2566.0, "nav_opening": 9016.0, "nav_closing": 10131.0, "amounts_called": 476.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 651.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -9231.0, "remaining_commitment": 1877.0, "nav_opening": 9016.0, "nav_closing": 11162.0, "amounts_called": 1181.0, "amounts_distributed": 14.0, "realized_gains": null, "unrealized_gains": 1001.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -9231.0, "remaining_commitment": 2026.0, "nav_opening": 9016.0, "nav_closing": 11129.0, "amounts_called": 1181.0, "amounts_distributed": 218.0, "realized_gains": null, "unrealized_gains": 1170.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -9568.0, "remaining_commitment": 2104.0, "nav_opening": 9016.0, "nav_closing": 11101.0, "amounts_called": 1518.0, "amounts_distributed": 633.0, "realized_gains": 104.0, "unrealized_gains": 1104.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -9637.0, "remaining_commitment": 2389.0, "nav_opening": 11101.0, "nav_closing": 10431.0, "amounts_called": 70.0, "amounts_distributed": 489.0, "realized_gains": 116.0, "unrealized_gains": -353.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -9704.0, "remaining_commitment": 2322.0, "nav_opening": 11101.0, "nav_closing": 10348.0, "amounts_called": 137.0, "amounts_distributed": 533.0, "realized_gains": 133.0, "unrealized_gains": -474.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -9773.0, "remaining_commitment": 2253.0, "nav_opening": 11101.0, "nav_closing": 11160.0, "amounts_called": 206.0, "amounts_distributed": 533.0, "realized_gains": 133.0, "unrealized_gains": 259.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -9773.0, "remaining_commitment": 2253.0, "nav_opening": 11101.0, "nav_closing": 11028.0, "amounts_called": 206.0, "amounts_distributed": 533.0, "realized_gains": 167.0, "unrealized_gains": 103.0, "holding_pct": 0.0116, "portfolio": []}, {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-03-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -5000.0, "remaining_commitment": 10000.0, "nav_opening": 4998.0, "nav_closing": 5177.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": -25.0, "unrealized_gains": 202.0, "holding_pct": 0.1418, "portfolio": []}, {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-06-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -5000.0, "remaining_commitment": 10000.0, "nav_opening": 4998.0, "nav_closing": 7250.0, "amounts_called": 0, "amounts_distributed": 0.0, "realized_gains": -28.0, "unrealized_gains": 2409.0, "holding_pct": 0.0213, "portfolio": []}, {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-09-30", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -10000.0, "remaining_commitment": 5000.0, "nav_opening": 4998.0, "nav_closing": 12673.0, "amounts_called": 5000.0, "amounts_distributed": 0.0, "realized_gains": -4.0, "unrealized_gains": 3099.0, "holding_pct": 0.0213, "portfolio": []}, {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-12-31", "currency": "EUR", "total_commitment": 15000.0, "drawn_commitment": -10000.0, "remaining_commitment": 5000.0, "nav_opening": 4998.0, "nav_closing": 12163.0, "amounts_called": 5000.0, "amounts_distributed": 0.0, "realized_gains": -59.0, "unrealized_gains": 3016.0, "holding_pct": 0.0213, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -1696.0, "remaining_commitment": 8304.0, "nav_opening": 0, "nav_closing": 1867.0, "amounts_called": 1697.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -600.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -1427.0, "remaining_commitment": 8573.0, "nav_opening": 0, "nav_closing": 1602.0, "amounts_called": 1428.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -594.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -1384.0, "remaining_commitment": 8871.0, "nav_opening": null, "nav_closing": null, "amounts_called": 1385.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -448.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -1538.0, "remaining_commitment": 8717.0, "nav_opening": null, "nav_closing": null, "amounts_called": 153.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 31.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -1698.0, "remaining_commitment": 8557.0, "nav_opening": null, "nav_closing": null, "amounts_called": 313.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 320.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -1753.0, "remaining_commitment": 8502.0, "nav_opening": 1428.0, "nav_closing": 2076.0, "amounts_called": 368.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 321.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2740.0, "remaining_commitment": 7515.0, "nav_opening": 1428.0, "nav_closing": 3365.0, "amounts_called": 1355.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 653.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2739.0, "remaining_commitment": 7516.0, "nav_opening": 3365.0, "nav_closing": 3395.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 40.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2739.0, "remaining_commitment": 7516.0, "nav_opening": 3365.0, "nav_closing": 3364.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": 6.0, "unrealized_gains": 27.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -3247.0, "remaining_commitment": 7008.0, "nav_opening": 3365.0, "nav_closing": 3893.0, "amounts_called": 508.0, "amounts_distributed": 0.0, "realized_gains": 11.0, "unrealized_gains": 51.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4213.0, "remaining_commitment": 6042.0, "nav_opening": 3365.0, "nav_closing": 5152.0, "amounts_called": 1474.0, "amounts_distributed": 0.0, "realized_gains": 11.0, "unrealized_gains": 379.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4521.0, "remaining_commitment": 5734.0, "nav_opening": 5152.0, "nav_closing": 5486.0, "amounts_called": 309.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 35.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4521.0, "remaining_commitment": 5734.0, "nav_opening": 5152.0, "nav_closing": 5394.0, "amounts_called": 309.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -62.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4621.0, "remaining_commitment": 5634.0, "nav_opening": 5152.0, "nav_closing": 5460.0, "amounts_called": 409.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -126.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4844.0, "remaining_commitment": 5411.0, "nav_opening": 5152.0, "nav_closing": 5989.0, "amounts_called": 632.0, "amounts_distributed": 74.0, "realized_gains": 41.0, "unrealized_gains": 271.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -5070.0, "remaining_commitment": 5185.0, "nav_opening": 5989.0, "nav_closing": 6103.0, "amounts_called": 227.0, "amounts_distributed": 8.0, "realized_gains": null, "unrealized_gains": -91.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -5335.0, "remaining_commitment": 4920.0, "nav_opening": 5989.0, "nav_closing": 6527.0, "amounts_called": 492.0, "amounts_distributed": 84.0, "realized_gains": null, "unrealized_gains": 128.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -5807.0, "remaining_commitment": 4448.0, "nav_opening": 5989.0, "nav_closing": 7033.0, "amounts_called": 964.0, "amounts_distributed": 200.0, "realized_gains": null, "unrealized_gains": 274.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -6776.0, "remaining_commitment": 3980.0, "nav_opening": 5989.0, "nav_closing": 6599.0, "amounts_called": 1933.0, "amounts_distributed": 1550.0, "realized_gains": 471.0, "unrealized_gains": -235.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -7002.0, "remaining_commitment": 3754.0, "nav_opening": 6599.0, "nav_closing": 6576.0, "amounts_called": 228.0, "amounts_distributed": 262.0, "realized_gains": null, "unrealized_gains": 13.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -7260.0, "remaining_commitment": 3602.0, "nav_opening": 6599.0, "nav_closing": 6915.0, "amounts_called": 486.0, "amounts_distributed": 262.0, "realized_gains": null, "unrealized_gains": 90.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -7429.0, "remaining_commitment": 3433.0, "nav_opening": 6599.0, "nav_closing": 7272.0, "amounts_called": 655.0, "amounts_distributed": 262.0, "realized_gains": null, "unrealized_gains": 274.0, "holding_pct": 0.0212, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2019-12-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": null, "remaining_commitment": 20000.0, "nav_opening": 0, "nav_closing": null, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -15.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-03-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -2144.0, "remaining_commitment": 17856.0, "nav_opening": null, "nav_closing": 2011.0, "amounts_called": 2144.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 26.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-06-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -4725.0, "remaining_commitment": 15275.0, "nav_opening": null, "nav_closing": 4564.0, "amounts_called": 4725.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 13.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-09-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -4725.0, "remaining_commitment": 15275.0, "nav_opening": null, "nav_closing": 4473.0, "amounts_called": 4725.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -70.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-12-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -4725.0, "remaining_commitment": 15275.0, "nav_opening": null, "nav_closing": 4435.0, "amounts_called": 4725.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -85.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-03-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -7342.0, "remaining_commitment": 12658.0, "nav_opening": 4435.0, "nav_closing": 8768.0, "amounts_called": 2617.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 1751.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-06-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -10078.0, "remaining_commitment": 9922.0, "nav_opening": null, "nav_closing": null, "amounts_called": 5353.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 4995.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-09-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -10078.0, "remaining_commitment": 9922.0, "nav_opening": 4435.0, "nav_closing": 14917.0, "amounts_called": 5353.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 5233.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-12-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -10078.0, "remaining_commitment": 9922.0, "nav_opening": 4435.0, "nav_closing": 24124.0, "amounts_called": 5353.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 14491.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-03-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -12734.0, "remaining_commitment": 7266.0, "nav_opening": 24125.0, "nav_closing": 30666.0, "amounts_called": 2656.0, "amounts_distributed": 0.0, "realized_gains": -264.0, "unrealized_gains": 4181.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -13366.0, "remaining_commitment": 6634.0, "nav_opening": 24125.0, "nav_closing": 26717.0, "amounts_called": 3288.0, "amounts_distributed": 0.0, "realized_gains": -264.0, "unrealized_gains": -349.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-09-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -15054.0, "remaining_commitment": 4946.0, "nav_opening": 24125.0, "nav_closing": 28868.0, "amounts_called": 4976.0, "amounts_distributed": 0.0, "realized_gains": -264.0, "unrealized_gains": 138.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -17450.0, "remaining_commitment": 2550.0, "nav_opening": 24125.0, "nav_closing": 24413.0, "amounts_called": 7372.0, "amounts_distributed": 0.0, "realized_gains": -264.0, "unrealized_gains": -6682.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -17452.0, "remaining_commitment": 2548.0, "nav_opening": 24413.0, "nav_closing": 24749.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 396.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -17452.0, "remaining_commitment": 2548.0, "nav_opening": 24413.0, "nav_closing": 24473.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": -152.0, "unrealized_gains": 317.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-09-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -17651.0, "remaining_commitment": 2349.0, "nav_opening": 24413.0, "nav_closing": 25564.0, "amounts_called": 199.0, "amounts_distributed": 0.0, "realized_gains": -152.0, "unrealized_gains": 1261.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -18408.0, "remaining_commitment": 1592.0, "nav_opening": 24413.0, "nav_closing": 23118.0, "amounts_called": 956.0, "amounts_distributed": 503.0, "realized_gains": -147.0, "unrealized_gains": -1260.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-03-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -18438.0, "remaining_commitment": 1562.0, "nav_opening": 23118.0, "nav_closing": 25026.0, "amounts_called": 30.0, "amounts_distributed": 0.0, "realized_gains": -322.0, "unrealized_gains": 2229.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -18438.0, "remaining_commitment": 1562.0, "nav_opening": 23118.0, "nav_closing": 26109.0, "amounts_called": 30.0, "amounts_distributed": 0.0, "realized_gains": -322.0, "unrealized_gains": 3270.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-09-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -19169.0, "remaining_commitment": 831.0, "nav_opening": 23118.0, "nav_closing": 23953.0, "amounts_called": 761.0, "amounts_distributed": 0.0, "realized_gains": -322.0, "unrealized_gains": 412.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -19169.0, "remaining_commitment": 831.0, "nav_opening": 23118.0, "nav_closing": 25133.0, "amounts_called": 761.0, "amounts_distributed": 0.0, "realized_gains": -322.0, "unrealized_gains": 1607.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-03-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -19170.0, "remaining_commitment": 830.0, "nav_opening": 25133.0, "nav_closing": 24401.0, "amounts_called": null, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -706.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -19203.0, "remaining_commitment": 797.0, "nav_opening": 25133.0, "nav_closing": 25551.0, "amounts_called": 33.0, "amounts_distributed": 33.0, "realized_gains": -271.0, "unrealized_gains": 719.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-09-30", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -19203.0, "remaining_commitment": 797.0, "nav_opening": 25133.0, "nav_closing": 24636.0, "amounts_called": 33.0, "amounts_distributed": 33.0, "realized_gains": -497.0, "unrealized_gains": 29.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "currency": "EUR", "total_commitment": 20000.0, "drawn_commitment": -19294.0, "remaining_commitment": 1606.0, "nav_opening": 25133.0, "nav_closing": 23429.0, "amounts_called": 1223.0, "amounts_distributed": 3971.0, "realized_gains": 2796.0, "unrealized_gains": -1702.0, "holding_pct": 0.0787, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2355.0, "remaining_commitment": 7645.0, "nav_opening": 0, "nav_closing": 2178.0, "amounts_called": 2355.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -71.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2355.0, "remaining_commitment": 7645.0, "nav_opening": 0, "nav_closing": 2002.0, "amounts_called": 2355.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -220.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2936.0, "remaining_commitment": 7064.0, "nav_opening": 0, "nav_closing": 2550.0, "amounts_called": 2936.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -221.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -2790.0, "remaining_commitment": 7210.0, "nav_opening": 0, "nav_closing": 2108.0, "amounts_called": 2790.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -496.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -3432.0, "remaining_commitment": 6568.0, "nav_opening": 2108.0, "nav_closing": 3093.0, "amounts_called": 642.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 361.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -3432.0, "remaining_commitment": 6568.0, "nav_opening": 2108.0, "nav_closing": 3499.0, "amounts_called": 642.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 727.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -3566.0, "remaining_commitment": 6434.0, "nav_opening": 2108.0, "nav_closing": 3778.0, "amounts_called": 776.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 893.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4131.0, "remaining_commitment": 5869.0, "nav_opening": 2108.0, "nav_closing": 4274.0, "amounts_called": 1341.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 818.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-03-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -4924.0, "remaining_commitment": 5076.0, "nav_opening": 4274.0, "nav_closing": 5878.0, "amounts_called": 795.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 808.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-06-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -6670.0, "remaining_commitment": 3330.0, "nav_opening": 4274.0, "nav_closing": 7651.0, "amounts_called": 2541.0, "amounts_distributed": 0.0, "realized_gains": -63.0, "unrealized_gains": 905.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-09-30", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -7193.0, "remaining_commitment": 2807.0, "nav_opening": 4274.0, "nav_closing": 9745.0, "amounts_called": 3064.0, "amounts_distributed": 0.0, "realized_gains": -63.0, "unrealized_gains": 2473.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-12-31", "currency": "EUR", "total_commitment": 10000.0, "drawn_commitment": -7463.0, "remaining_commitment": 2537.0, "nav_opening": 4274.0, "nav_closing": 11294.0, "amounts_called": 3334.0, "amounts_distributed": 0.0, "realized_gains": -133.0, "unrealized_gains": 3834.0, "holding_pct": 0.0172, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2023-03-31", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": 302.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -355.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2023-06-30", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": null, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -1100.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2023-09-30", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": null, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -1105.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2023-12-31", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -2500.0, "remaining_commitment": 5000.0, "nav_opening": 0, "nav_closing": null, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": -2.0, "unrealized_gains": -2479.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2024-03-31", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": null, "nav_closing": 902.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 1806.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2024-06-30", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": null, "nav_closing": 2824.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 3549.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2024-09-30", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": null, "nav_closing": 3030.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 3890.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2024-12-31", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -5000.0, "remaining_commitment": 2500.0, "nav_opening": null, "nav_closing": 2488.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 3487.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2025-03-31", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -7500.0, "remaining_commitment": null, "nav_opening": 2488.0, "nav_closing": 8587.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 3774.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2025-06-30", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -7500.0, "remaining_commitment": null, "nav_opening": 2488.0, "nav_closing": 8250.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": -314.0, "unrealized_gains": 3975.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2025-09-30", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -7500.0, "remaining_commitment": null, "nav_opening": 2488.0, "nav_closing": 15609.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": -314.0, "unrealized_gains": 11592.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2025-12-31", "currency": "EUR", "total_commitment": 7500.0, "drawn_commitment": -7500.0, "remaining_commitment": null, "nav_opening": 2488.0, "nav_closing": 21397.0, "amounts_called": 2500.0, "amounts_distributed": 0.0, "realized_gains": -665.0, "unrealized_gains": 18034.0, "holding_pct": 0.0765, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2023-12-31", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -10000.0, "remaining_commitment": 20000.0, "nav_opening": null, "nav_closing": 10693.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 2025.0, "holding_pct": 0.0836, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2024-03-31", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -10000.0, "remaining_commitment": 20000.0, "nav_opening": 10693.0, "nav_closing": 10213.0, "amounts_called": 0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -456.0, "holding_pct": 0.0836, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2024-06-30", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -20000.0, "remaining_commitment": 10000.0, "nav_opening": 10693.0, "nav_closing": 21396.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 792.0, "holding_pct": 0.0836, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2024-09-30", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -20000.0, "remaining_commitment": 10000.0, "nav_opening": 10693.0, "nav_closing": 20209.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": -317.0, "holding_pct": 0.0836, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2024-12-31", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -20000.0, "remaining_commitment": 10000.0, "nav_opening": 10693.0, "nav_closing": 25447.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 5021.0, "holding_pct": 0.0836, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2025-03-31", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -30000.0, "remaining_commitment": null, "nav_opening": 25447.0, "nav_closing": 38109.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": null, "unrealized_gains": 2755.0, "holding_pct": 0.0836, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2025-06-30", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -30000.0, "remaining_commitment": null, "nav_opening": 25447.0, "nav_closing": 34162.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": 9.0, "unrealized_gains": -779.0, "holding_pct": 0.0418, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2025-09-30", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -30000.0, "remaining_commitment": null, "nav_opening": 25447.0, "nav_closing": 34160.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": -124.0, "unrealized_gains": 33.0, "holding_pct": 0.0418, "portfolio": []}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2025-12-31", "currency": "EUR", "total_commitment": 30000.0, "drawn_commitment": -30000.0, "remaining_commitment": null, "nav_opening": 25447.0, "nav_closing": 36439.0, "amounts_called": 10000.0, "amounts_distributed": 0.0, "realized_gains": -125.0, "unrealized_gains": 3113.0, "holding_pct": 0.0418, "portfolio": []}], "drawdowns": [{"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-05-20", "value_date": "2020-06-05", "currency": "EUR", "amount": 1721.01, "total_commitment": 10000.0, "remaining_commitment": 8303.4}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-12-18", "value_date": null, "currency": "EUR", "amount": 345.66, "total_commitment": 20000.0, "remaining_commitment": 14929.7}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-01-05", "value_date": "2021-01-19", "currency": "EUR", "amount": 153.25, "total_commitment": 10000.0, "remaining_commitment": 8716.5}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-01-22", "value_date": null, "currency": "EUR", "amount": 1904.96, "total_commitment": 15000.0, "remaining_commitment": 13095.04}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-02-09", "value_date": null, "currency": "EUR", "amount": 2271.23, "total_commitment": 20000.0, "remaining_commitment": 12658.47}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-03-02", "value_date": null, "currency": "EUR", "amount": 1141.05, "total_commitment": 15000.0, "remaining_commitment": 12039.45}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-05-07", "value_date": null, "currency": "EUR", "amount": 2736.18, "total_commitment": 20000.0, "remaining_commitment": 9922.29}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-06-04", "value_date": null, "currency": "EUR", "amount": 1865.03, "total_commitment": 15000.0, "remaining_commitment": 10298.36}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-07-07", "value_date": null, "currency": "EUR", "amount": 48.57, "total_commitment": 15000.0, "remaining_commitment": 10302.26}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-07-29", "value_date": null, "currency": "EUR", "amount": 1033.7, "total_commitment": 15000.0, "remaining_commitment": 9711.61}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-07-30", "value_date": "2021-08-13", "currency": "EUR", "amount": 1881.54, "total_commitment": 10000.0, "remaining_commitment": 8118.46}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-10-12", "value_date": null, "currency": "EUR", "amount": 2529.85, "total_commitment": 15000.0, "remaining_commitment": 7772.41}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-10-20", "value_date": "2021-11-03", "currency": "EUR", "amount": 837.08, "total_commitment": 10000.0, "remaining_commitment": 7280.97}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-11-22", "value_date": "2021-12-06", "currency": "EUR", "amount": 3516.11, "total_commitment": 15000.0, "remaining_commitment": 11483.89}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-11-26", "value_date": "2021-12-10", "currency": "EUR", "amount": 789.98, "total_commitment": 10000.0, "remaining_commitment": 7515.2}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-12-01", "value_date": null, "currency": "EUR", "amount": 2916.82, "total_commitment": 15000.0, "remaining_commitment": 4855.59}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2021-12-10", "value_date": "2021-12-24", "currency": "EUR", "amount": 44.68, "total_commitment": 10000.0, "remaining_commitment": 7323.53}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-01-17", "value_date": null, "currency": "EUR", "amount": 2657.94, "total_commitment": 20000.0, "remaining_commitment": 7264.35}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-02-15", "value_date": "2022-03-01", "currency": "EUR", "amount": 928.8, "total_commitment": 10000.0, "remaining_commitment": 6394.73}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-02-28", "value_date": null, "currency": "EUR", "amount": 1848.43, "total_commitment": 15000.0, "remaining_commitment": 3462.41}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-04-07", "value_date": "2022-04-22", "currency": "EUR", "amount": 978.98, "total_commitment": 10000.0, "remaining_commitment": 5415.75}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-04-21", "value_date": null, "currency": "EUR", "amount": 631.35, "total_commitment": 20000.0, "remaining_commitment": 6633.0}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-05-30", "value_date": "2022-06-13", "currency": "EUR", "amount": 0.56, "total_commitment": 15000.0, "remaining_commitment": 11471.34}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-21", "value_date": null, "currency": "EUR", "amount": 1687.92, "total_commitment": 20000.0, "remaining_commitment": 4945.08}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-22", "value_date": null, "currency": "EUR", "amount": 59.05, "total_commitment": 15000.0, "remaining_commitment": 3697.3}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-10-14", "value_date": null, "currency": "EUR", "amount": 2397.38, "total_commitment": 20000.0, "remaining_commitment": 2547.7}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-11-03", "value_date": "2022-11-17", "currency": "EUR", "amount": 200.52, "total_commitment": 15000.0, "remaining_commitment": 11143.69}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-11-04", "value_date": "2022-11-21", "currency": "EUR", "amount": 965.68, "total_commitment": 10000.0, "remaining_commitment": 6042.45}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-11-07", "value_date": "2022-11-21", "currency": "EUR", "amount": 553.25, "total_commitment": 10000.0, "remaining_commitment": 5207.36}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-21", "value_date": "2023-01-06", "currency": "EUR", "amount": 650.67, "total_commitment": 10000.0, "remaining_commitment": 4556.69}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-23", "value_date": null, "currency": "EUR", "amount": 164.54, "total_commitment": 15000.0, "remaining_commitment": 3551.54}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2022-12-30", "value_date": "2023-01-13", "currency": "EUR", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 5000.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-01-06", "value_date": "2023-01-20", "currency": "EUR", "amount": 2381.3, "total_commitment": 10000.0, "remaining_commitment": 7645.31}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-02-13", "value_date": "2023-02-27", "currency": "EUR", "amount": 1146.75, "total_commitment": 15000.0, "remaining_commitment": 10304.28}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-01", "value_date": "2023-03-15", "currency": "EUR", "amount": 308.72, "total_commitment": 10000.0, "remaining_commitment": 5733.73}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-09", "value_date": null, "currency": "EUR", "amount": 1206.23, "total_commitment": 15000.0, "remaining_commitment": 2345.31}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-03-16", "value_date": "2023-03-30", "currency": "EUR", "amount": 759.76, "total_commitment": 10000.0, "remaining_commitment": 3806.74}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-04-18", "value_date": "2023-05-03", "currency": "EUR", "amount": 406.44, "total_commitment": 10000.0, "remaining_commitment": 3400.3}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2023-06-19", "value_date": "2023-07-03", "currency": "USD", "amount": 7500.0, "total_commitment": 22500.0, "remaining_commitment": 15000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-19", "value_date": "2023-07-03", "currency": "EUR", "amount": 486.61, "total_commitment": 15000.0, "remaining_commitment": 1858.7}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-06-20", "value_date": "2023-07-04", "currency": "EUR", "amount": 71.27, "total_commitment": 10000.0, "remaining_commitment": 7574.04}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-21", "value_date": "2023-07-05", "currency": "EUR", "amount": 199.41, "total_commitment": 20000.0, "remaining_commitment": 2348.29}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-22", "value_date": "2023-07-06", "currency": "EUR", "amount": 5.03, "total_commitment": 15000.0, "remaining_commitment": 10299.25}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-29", "value_date": "2023-07-13", "currency": "EUR", "amount": 100.03, "total_commitment": 10000.0, "remaining_commitment": 5633.7}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2023-07-27", "value_date": "2023-08-10", "currency": "USD", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 5000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-08-14", "value_date": "2023-08-29", "currency": "EUR", "amount": 358.21, "total_commitment": 10000.0, "remaining_commitment": 3042.09}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-09-01", "value_date": "2023-09-15", "currency": "EUR", "amount": 509.98, "total_commitment": 10000.0, "remaining_commitment": 7064.06}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-09-25", "value_date": "2023-10-09", "currency": "EUR", "amount": 411.16, "total_commitment": 20000.0, "remaining_commitment": 1937.13}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-10-13", "value_date": null, "currency": "EUR", "amount": 222.09, "total_commitment": 10000.0, "remaining_commitment": 5411.61}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-11-06", "value_date": "2023-11-20", "currency": "EUR", "amount": 480.61, "total_commitment": 15000.0, "remaining_commitment": 9823.67}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2023-11-23", "value_date": "2023-12-07", "currency": "EUR", "amount": 10000.0, "total_commitment": 30000.0, "remaining_commitment": 20000.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2023-12-15", "value_date": "2024-01-03", "currency": "EUR", "amount": 227.12, "total_commitment": 10000.0, "remaining_commitment": 6836.94}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-18", "value_date": null, "currency": "EUR", "amount": 476.15, "total_commitment": 10000.0, "remaining_commitment": 2565.94}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-20", "value_date": "2024-01-08", "currency": "EUR", "amount": 676.28, "total_commitment": 15000.0, "remaining_commitment": 9147.39}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-01-09", "value_date": "2024-01-23", "currency": "EUR", "amount": 30.55, "total_commitment": 20000.0, "remaining_commitment": 1561.14}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-01-11", "value_date": "2024-01-25", "currency": "EUR", "amount": 228.52, "total_commitment": 10000.0, "remaining_commitment": 5183.09}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2024-02-23", "value_date": "2024-03-08", "currency": "EUR", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 2500.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-02-29", "value_date": "2024-03-14", "currency": "EUR", "amount": 268.89, "total_commitment": 10000.0, "remaining_commitment": 6568.05}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-03-21", "value_date": "2024-04-08", "currency": "EUR", "amount": 557.58, "total_commitment": 15000.0, "remaining_commitment": 8589.81}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-03-25", "value_date": null, "currency": "EUR", "amount": 605.17, "total_commitment": 10000.0, "remaining_commitment": 1960.77}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2024-04-15", "value_date": "2024-04-29", "currency": "USD", "amount": 7500.0, "total_commitment": 22500.0, "remaining_commitment": 7500.0}, {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-04-19", "value_date": "2024-05-06", "currency": "USD", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 5000.0}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2024-05-03", "value_date": "2024-05-22", "currency": "EUR", "amount": 10000.0, "total_commitment": 30000.0, "remaining_commitment": 10000.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-05-13", "value_date": "2024-05-28", "currency": "EUR", "amount": 264.32, "total_commitment": 10000.0, "remaining_commitment": 4918.77}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-05-16", "value_date": null, "currency": "EUR", "amount": 83.03, "total_commitment": 10000.0, "remaining_commitment": 1877.74}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-05-24", "value_date": "2024-06-07", "currency": "EUR", "amount": 214.74, "total_commitment": 15000.0, "remaining_commitment": 1493.23}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2024-05-31", "value_date": "2024-06-14", "currency": "USD", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 2500.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-06-17", "value_date": "2024-07-01", "currency": "EUR", "amount": 133.51, "total_commitment": 10000.0, "remaining_commitment": 6434.54}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-21", "value_date": "2024-07-05", "currency": "EUR", "amount": 871.18, "total_commitment": 15000.0, "remaining_commitment": 7718.63}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-21", "value_date": "2024-07-05", "currency": "EUR", "amount": 215.43, "total_commitment": 15000.0, "remaining_commitment": 1277.8}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-28", "value_date": "2024-07-12", "currency": "EUR", "amount": 730.73, "total_commitment": 20000.0, "remaining_commitment": 830.41}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-07-04", "value_date": "2024-07-18", "currency": "EUR", "amount": 470.73, "total_commitment": 10000.0, "remaining_commitment": 4448.04}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-10-31", "value_date": null, "currency": "EUR", "amount": 734.73, "total_commitment": 10000.0, "remaining_commitment": 3713.31}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-11-07", "value_date": "2024-11-21", "currency": "EUR", "amount": 563.82, "total_commitment": 10000.0, "remaining_commitment": 5870.72}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-11-18", "value_date": "2024-12-02", "currency": "EUR", "amount": 75.35, "total_commitment": 10000.0, "remaining_commitment": 3637.96}, {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-12-03", "value_date": "2024-12-17", "currency": "EUR", "amount": 5000.0, "total_commitment": 15000.0, "remaining_commitment": 10000.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2024-12-18", "value_date": "2025-01-07", "currency": "EUR", "amount": 794.92, "total_commitment": 10000.0, "remaining_commitment": 5075.8}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-19", "value_date": null, "currency": "EUR", "amount": 216.77, "total_commitment": 15000.0, "remaining_commitment": 7501.86}, {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-01-14", "value_date": "2025-01-28", "currency": "USD", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 2500.0}, {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2025-01-17", "value_date": "2025-01-31", "currency": "EUR", "amount": 10000.0, "total_commitment": 30000.0, "remaining_commitment": 0.0}, {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2025-01-21", "value_date": null, "currency": "USD", "amount": 7500.0, "total_commitment": 22500.0, "remaining_commitment": 0.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-02-17", "value_date": null, "currency": "EUR", "amount": 236.87, "total_commitment": 10000.0, "remaining_commitment": 3753.88}, {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2025-03-10", "value_date": null, "currency": "USD", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 0.0}, {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2025-03-10", "value_date": null, "currency": "EUR", "amount": 2500.0, "total_commitment": 7500.0, "remaining_commitment": 0.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-03-28", "value_date": "2025-04-11", "currency": "EUR", "amount": 1743.75, "total_commitment": 10000.0, "remaining_commitment": 3332.05}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-05-08", "value_date": "2025-05-23", "currency": "EUR", "amount": 1119.9, "total_commitment": 15000.0, "remaining_commitment": 6381.96}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-05-12", "value_date": "2025-05-27", "currency": "EUR", "amount": 67.13, "total_commitment": 10000.0, "remaining_commitment": 2321.59}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-05-15", "value_date": "2025-05-30", "currency": "EUR", "amount": 258.46, "total_commitment": 10000.0, "remaining_commitment": 3601.59}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-05-21", "value_date": "2025-05-23", "currency": "EUR", "amount": 33.45, "total_commitment": 20000.0, "remaining_commitment": 796.96}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-17", "value_date": "2025-07-02", "currency": "EUR", "amount": 68.15, "total_commitment": 10000.0, "remaining_commitment": 2253.44}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-20", "value_date": "2025-07-07", "currency": "EUR", "amount": 428.98, "total_commitment": 15000.0, "remaining_commitment": 5952.98}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-06-20", "value_date": null, "currency": "EUR", "amount": 523.9, "total_commitment": 10000.0, "remaining_commitment": 2808.15}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-06-30", "value_date": null, "currency": "EUR", "amount": 168.37, "total_commitment": 10000.0, "remaining_commitment": 3433.22}, {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-08-27", "value_date": null, "currency": "EUR", "amount": 5000.0, "total_commitment": 15000.0, "remaining_commitment": 5000.0}, {"fund": "EQT Healthcare Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-09-10", "value_date": null, "currency": "EUR", "amount": 5000.0, "total_commitment": 15000.0, "remaining_commitment": 10000.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-09-26", "value_date": null, "currency": "EUR", "amount": 268.46, "total_commitment": 10000.0, "remaining_commitment": 2539.69}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-11-07", "value_date": "2025-11-21", "currency": "EUR", "amount": 0.0, "total_commitment": 10000.0, "remaining_commitment": 2253.44}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-23", "value_date": "2026-01-09", "currency": "EUR", "amount": 179.03, "total_commitment": 10000.0, "remaining_commitment": 2074.41}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-30", "value_date": "2026-01-14", "currency": "EUR", "amount": 2789.58, "total_commitment": 15000.0, "remaining_commitment": 3163.4}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2026-01-08", "value_date": "2026-01-22", "currency": "EUR", "amount": 157.2, "total_commitment": 20000.0, "remaining_commitment": 1763.07}], "distributions": [{"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-04-02", "value_date": null, "currency": "EUR", "total_distribution": 0, "net_distribution": null, "drawdown_in_distribution": 2580.91, "total_commitment": 20000.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-09-03", "value_date": null, "currency": "EUR", "total_distribution": 0, "net_distribution": null, "drawdown_in_distribution": 9.69, "total_commitment": 10000.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2020-11-13", "value_date": "2020-12-07", "currency": "EUR", "total_distribution": 298.0, "net_distribution": null, "drawdown_in_distribution": null, "total_commitment": 10000.0}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-02-24", "value_date": "2022-03-10", "currency": "EUR", "total_distribution": 600.83, "net_distribution": null, "drawdown_in_distribution": null, "total_commitment": 15000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-03-22", "value_date": null, "currency": "EUR", "total_distribution": 77.92, "net_distribution": null, "drawdown_in_distribution": 23.31, "total_commitment": 15000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-10", "value_date": null, "currency": "EUR", "total_distribution": 374.81, "net_distribution": null, "drawdown_in_distribution": 194.53, "total_commitment": 15000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-06-22", "value_date": "2022-06-27", "currency": "EUR", "total_distribution": 249.52, "net_distribution": null, "drawdown_in_distribution": null, "total_commitment": 10000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-08-16", "value_date": "2022-08-18", "currency": "EUR", "total_distribution": 378.82, "net_distribution": null, "drawdown_in_distribution": 258.15, "total_commitment": 10000.0}, {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2022-12-28", "value_date": "2022-12-30", "currency": "EUR", "total_distribution": 318.35, "net_distribution": null, "drawdown_in_distribution": null, "total_commitment": 15000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-06-30", "value_date": "2023-07-04", "currency": "EUR", "total_distribution": 0, "net_distribution": 5.69, "drawdown_in_distribution": null, "total_commitment": 10000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-08-11", "value_date": null, "currency": "EUR", "total_distribution": 492.97, "net_distribution": 79.2, "drawdown_in_distribution": 413.77, "total_commitment": 15000.0}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-11-10", "value_date": "2023-11-14", "currency": "EUR", "total_distribution": 502.64, "net_distribution": 157.2, "drawdown_in_distribution": 345.44, "total_commitment": 20000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2023-12-27", "value_date": null, "currency": "EUR", "total_distribution": 69.74, "net_distribution": null, "drawdown_in_distribution": 12.94, "total_commitment": 15000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-06-28", "value_date": "2024-07-02", "currency": "EUR", "total_distribution": 94.08, "net_distribution": null, "drawdown_in_distribution": null, "total_commitment": 10000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-08-14", "value_date": "2024-08-19", "currency": "EUR", "total_distribution": 109.87, "net_distribution": null, "drawdown_in_distribution": null, "total_commitment": 10000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-10-14", "value_date": "2024-10-16", "currency": "EUR", "total_distribution": 313.14, "net_distribution": 311.32, "drawdown_in_distribution": 1.82, "total_commitment": 15000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-10-29", "value_date": "2024-10-31", "currency": "EUR", "total_distribution": 0, "net_distribution": 219.43, "drawdown_in_distribution": null, "total_commitment": 10000.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-18", "value_date": null, "currency": "EUR", "total_distribution": 1223.43, "net_distribution": 1066.0, "drawdown_in_distribution": 157.43, "total_commitment": 10000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-20", "value_date": null, "currency": "EUR", "total_distribution": 215.25, "net_distribution": 62.41, "drawdown_in_distribution": 152.84, "total_commitment": 10000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2024-12-23", "value_date": "2024-12-27", "currency": "EUR", "total_distribution": 975.07, "net_distribution": 699.37, "drawdown_in_distribution": 275.7, "total_commitment": 15000.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-01-14", "value_date": null, "currency": "EUR", "total_distribution": 262.43, "net_distribution": null, "drawdown_in_distribution": null, "total_commitment": 10000.0}, {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-02-05", "value_date": "2025-02-07", "currency": "EUR", "total_distribution": 488.85, "net_distribution": 419.02, "drawdown_in_distribution": 69.83, "total_commitment": 10000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-07-04", "value_date": "2025-07-08", "currency": "EUR", "total_distribution": 2391.81, "net_distribution": 2295.12, "drawdown_in_distribution": 96.69, "total_commitment": 15000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-08-11", "value_date": "2025-08-13", "currency": "EUR", "total_distribution": 367.2, "net_distribution": 250.87, "drawdown_in_distribution": 116.33, "total_commitment": 15000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-10-13", "value_date": "2025-10-15", "currency": "EUR", "total_distribution": 246.58, "net_distribution": 219.99, "drawdown_in_distribution": 26.59, "total_commitment": 15000.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-10-22", "value_date": "2025-10-24", "currency": "EUR", "total_distribution": 332.09, "net_distribution": 256.59, "drawdown_in_distribution": 75.5, "total_commitment": 10000.0}, {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-11-26", "value_date": "2025-11-28", "currency": "EUR", "total_distribution": 4096.84, "net_distribution": 2904.53, "drawdown_in_distribution": 1192.31, "total_commitment": 20000.0}, {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-17", "value_date": "2025-12-19", "currency": "EUR", "total_distribution": 262.87, "net_distribution": 121.07, "drawdown_in_distribution": 141.8, "total_commitment": 10000.0}, {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2026-01-06", "value_date": "2026-01-08", "currency": "EUR", "total_distribution": 636.28, "net_distribution": 612.06, "drawdown_in_distribution": 24.22, "total_commitment": 15000.0}, {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2026-02-09", "value_date": "2026-02-11", "currency": "EUR", "total_distribution": 228.7, "net_distribution": 209.33, "drawdown_in_distribution": 19.37, "total_commitment": 10000.0}], "summary": {"total_committed_eur": 147500.0, "total_committed_usd": 37500.0, "total_drawn_eur": 117498.11999999998, "total_drawn_usd": 35000.0, "total_distributed_eur": 15608.090000000004, "total_distributed_usd": 0, "total_nav_eur": 154402.0, "total_nav_usd": 46453.0, "fund_count": 13, "latest_nav_by_fund": {"BPEA EQT Mid-Market Growth Leveraged|DLUGINSKI SMITH SE TRUST": {"fund": "BPEA EQT Mid-Market Growth Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-09-30", "nav": 5144.0, "currency": "USD"}, "EQT APAC VIII Leveraged|BDSCI LLC": {"fund": "EQT APAC VIII Leveraged", "investor": "BDSCI LLC", "date": "2025-09-30", "nav": 35349.0, "currency": "USD"}, "EQT Exeter Industrial VI Leveraged|BDSCI LLC": {"fund": "EQT Exeter Industrial VI Leveraged", "investor": "BDSCI LLC", "date": "2025-09-30", "nav": 5960.0, "currency": "USD"}, "EQT Growth|BRIAN SMITH VENTURES LLC": {"fund": "EQT Growth", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "nav": 12345.0, "currency": "EUR"}, "EQT IX|BRIAN SMITH VENTURES LLC": {"fund": "EQT IX", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "nav": 19035.0, "currency": "EUR"}, "EQT Infrastructure V|BRIAN SMITH VENTURES LLC": {"fund": "EQT Infrastructure V", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "nav": 11028.0, "currency": "EUR"}, "EQT Infrastructure VI Leveraged|DLUGINSKI SMITH SE TRUST": {"fund": "EQT Infrastructure VI Leveraged", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-12-31", "nav": 12163.0, "currency": "EUR"}, "EQT Real Estate II|BRIAN SMITH VENTURES LLC": {"fund": "EQT Real Estate II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-09-30", "nav": 7272.0, "currency": "EUR"}, "EQT Ventures II|BRIAN SMITH VENTURES LLC": {"fund": "EQT Ventures II", "investor": "BRIAN SMITH VENTURES LLC", "date": "2025-12-31", "nav": 23429.0, "currency": "EUR"}, "EQT Ventures III|DLUGINSKI SMITH SE TRUST": {"fund": "EQT Ventures III", "investor": "DLUGINSKI SMITH SE TRUST", "date": "2025-12-31", "nav": 11294.0, "currency": "EUR"}, "EQT Ventures III Leveraged|BDSCI LLC": {"fund": "EQT Ventures III Leveraged", "investor": "BDSCI LLC", "date": "2025-12-31", "nav": 21397.0, "currency": "EUR"}, "EQT X Leveraged|BDSCI LLC": {"fund": "EQT X Leveraged", "investor": "BDSCI LLC", "date": "2025-12-31", "nav": 36439.0, "currency": "EUR"}}}};

const EMBEDDED_FX_DATA = {"fx_timeline":[{"date":"2019-12-31","eurUsd":1.1234},{"date":"2020-03-31","eurUsd":1.0956},{"date":"2020-06-30","eurUsd":1.1198},{"date":"2020-09-30","eurUsd":1.1708},{"date":"2020-12-31","eurUsd":1.2271},{"date":"2021-03-31","eurUsd":1.1725},{"date":"2021-06-30","eurUsd":1.1884},{"date":"2021-09-30","eurUsd":1.1579},{"date":"2021-12-31","eurUsd":1.1326},{"date":"2022-03-31","eurUsd":1.1101},{"date":"2022-06-30","eurUsd":1.0387},{"date":"2022-09-30","eurUsd":0.9748},{"date":"2022-12-31","eurUsd":1.0666},{"date":"2023-03-31","eurUsd":1.0875},{"date":"2023-06-30","eurUsd":1.0866},{"date":"2023-09-30","eurUsd":1.0594},{"date":"2023-12-31","eurUsd":1.105},{"date":"2024-03-31","eurUsd":1.0811},{"date":"2024-06-30","eurUsd":1.0705},{"date":"2024-09-30","eurUsd":1.1196},{"date":"2024-12-31","eurUsd":1.0389},{"date":"2025-03-31","eurUsd":1.0815},{"date":"2025-06-30","eurUsd":1.172},{"date":"2025-09-30","eurUsd":1.1741},{"date":"2025-12-31","eurUsd":1.175},{"date":"2026-01-31","eurUsd":1.1919},{"date":"2026-02-28","eurUsd":1.1805},{"date":"2026-03-12","eurUsd":1.1547}],"fx_fund_impact":[{"fund":"EQT Real Estate II","investor":"BRIAN SMITH VENTURES LLC","currency":"EUR","total_drawn_eur":6698.11,"usd_cost_at_entry":7317.25,"usd_cost_at_current_rate":7734.31,"weighted_avg_entry_rate":1.0924,"current_rate":1.1547,"fx_on_cost_usd":417.06,"nav_eur":7272.0,"distributions_eur":2378.82,"nav_usd_at_current":8396.98,"nav_usd_at_entry_rate":7944.18,"fx_on_nav_usd":452.79,"fx_total_pl_usd":600.91,"rate_change_pct":5.7},{"fund":"EQT Ventures II","investor":"BRIAN SMITH VENTURES LLC","currency":"EUR","total_drawn_eur":14290.16,"usd_cost_at_entry":15896.6,"usd_cost_at_current_rate":16500.85,"weighted_avg_entry_rate":1.1124,"current_rate":1.1547,"fx_on_cost_usd":604.25,"nav_eur":23429.0,"distributions_eur":4599.48,"nav_usd_at_current":27053.47,"nav_usd_at_entry_rate":26062.79,"fx_on_nav_usd":990.67,"fx_total_pl_usd":1185.16,"rate_change_pct":3.8},{"fund":"EQT IX","investor":"BRIAN SMITH VENTURES LLC","currency":"EUR","total_drawn_eur":15635.01,"usd_cost_at_entry":18001.0,"usd_cost_at_current_rate":18053.75,"weighted_avg_entry_rate":1.1513,"current_rate":1.1547,"fx_on_cost_usd":52.75,"nav_eur":19035.0,"distributions_eur":5945.52,"nav_usd_at_current":21979.71,"nav_usd_at_entry_rate":21915.49,"fx_on_nav_usd":64.22,"fx_total_pl_usd":84.28,"rate_change_pct":0.29},{"fund":"EQT Infrastructure V","investor":"BRIAN SMITH VENTURES LLC","currency":"EUR","total_drawn_eur":8878.07,"usd_cost_at_entry":9885.62,"usd_cost_at_current_rate":10251.51,"weighted_avg_entry_rate":1.1135,"current_rate":1.1547,"fx_on_cost_usd":365.89,"nav_eur":11028.0,"distributions_eur":1761.51,"nav_usd_at_current":12734.03,"nav_usd_at_entry_rate":12279.54,"fx_on_nav_usd":454.49,"fx_total_pl_usd":527.09,"rate_change_pct":3.7},{"fund":"EQT Growth","investor":"BRIAN SMITH VENTURES LLC","currency":"EUR","total_drawn_eur":12009.85,"usd_cost_at_entry":13519.78,"usd_cost_at_current_rate":13867.77,"weighted_avg_entry_rate":1.1257,"current_rate":1.1547,"fx_on_cost_usd":347.99,"nav_eur":12345.0,"distributions_eur":919.18,"nav_usd_at_current":14254.77,"nav_usd_at_entry_rate":13897.07,"fx_on_nav_usd":357.7,"fx_total_pl_usd":384.33,"rate_change_pct":2.57},{"fund":"EQT Ventures II","investor":"BDSCI LLC","currency":"EUR","total_drawn_eur":5000.0,"usd_cost_at_entry":5391.05,"usd_cost_at_current_rate":5773.5,"weighted_avg_entry_rate":1.0782,"current_rate":1.1547,"fx_on_cost_usd":382.45,"nav_eur":21397.0,"distributions_eur":0,"nav_usd_at_current":24707.12,"nav_usd_at_entry_rate":23070.46,"fx_on_nav_usd":1636.66,"fx_total_pl_usd":1636.66,"rate_change_pct":7.09},{"fund":"EQT Ventures III","investor":"DLUGINSKI SMITH SE TRUST","currency":"EUR","total_drawn_eur":7486.92,"usd_cost_at_entry":8105.21,"usd_cost_at_current_rate":8645.15,"weighted_avg_entry_rate":1.0826,"current_rate":1.1547,"fx_on_cost_usd":539.93,"nav_eur":11294.0,"distributions_eur":228.7,"nav_usd_at_current":13041.18,"nav_usd_at_entry_rate":12226.7,"fx_on_nav_usd":814.49,"fx_total_pl_usd":830.98,"rate_change_pct":6.66},{"fund":"EQT X Leveraged","investor":"BDSCI LLC","currency":"EUR","total_drawn_eur":30000.0,"usd_cost_at_entry":32103.68,"usd_cost_at_current_rate":34641.0,"weighted_avg_entry_rate":1.0701,"current_rate":1.1547,"fx_on_cost_usd":2537.32,"nav_eur":36439.0,"distributions_eur":0,"nav_usd_at_current":42076.11,"nav_usd_at_entry_rate":38994.2,"fx_on_nav_usd":3081.91,"fx_total_pl_usd":3081.91,"rate_change_pct":7.9},{"fund":"EQT Infrastructure VI Leveraged","investor":"DLUGINSKI SMITH SE TRUST","currency":"EUR","total_drawn_eur":10000.0,"usd_cost_at_entry":11183.92,"usd_cost_at_current_rate":11547.0,"weighted_avg_entry_rate":1.1184,"current_rate":1.1547,"fx_on_cost_usd":363.08,"nav_eur":12163.0,"distributions_eur":0,"nav_usd_at_current":14044.62,"nav_usd_at_entry_rate":13603.01,"fx_on_nav_usd":441.61,"fx_total_pl_usd":441.61,"rate_change_pct":3.25},{"fund":"EQT Ventures III Leveraged","investor":"BDSCI LLC","currency":"EUR","total_drawn_eur":2500.0,"usd_cost_at_entry":2678.9,"usd_cost_at_current_rate":2886.75,"weighted_avg_entry_rate":1.0716,"current_rate":1.1547,"fx_on_cost_usd":207.85,"nav_eur":2500.0,"distributions_eur":0,"nav_usd_at_current":2886.75,"nav_usd_at_entry_rate":2678.9,"fx_on_nav_usd":207.85,"fx_total_pl_usd":207.85,"rate_change_pct":7.76},{"fund":"EQT Healthcare Growth Leveraged","investor":"DLUGINSKI SMITH SE TRUST","currency":"EUR","total_drawn_eur":5000.0,"usd_cost_at_entry":5868.22,"usd_cost_at_current_rate":5773.5,"weighted_avg_entry_rate":1.1736,"current_rate":1.1547,"fx_on_cost_usd":-94.72,"nav_eur":5000.0,"distributions_eur":0,"nav_usd_at_current":5773.5,"nav_usd_at_entry_rate":5868.22,"fx_on_nav_usd":-94.72,"fx_total_pl_usd":-94.72,"rate_change_pct":-1.61}],"eur_nav_in_usd":{"EQT Growth||BRIAN SMITH VENTURES LLC":[{"date":"2022-03-31","nav_eur":3279.0,"nav_usd":3640.02,"rate":1.1101,"nav_usd_at_entry_rate":3691.17},{"date":"2022-06-30","nav_eur":2884.0,"nav_usd":2995.61,"rate":1.0387,"nav_usd_at_entry_rate":3246.52},{"date":"2022-09-30","nav_eur":2892.0,"nav_usd":2819.12,"rate":0.9748,"nav_usd_at_entry_rate":3255.52},{"date":"2022-12-31","nav_eur":2910.0,"nav_usd":3103.81,"rate":1.0666,"nav_usd_at_entry_rate":3275.79},{"date":"2023-03-31","nav_eur":3896.0,"nav_usd":4236.9,"rate":1.0875,"nav_usd_at_entry_rate":4385.73},{"date":"2023-06-30","nav_eur":4009.0,"nav_usd":4356.18,"rate":1.0866,"nav_usd_at_entry_rate":4512.93},{"date":"2023-09-30","nav_eur":4035.0,"nav_usd":4274.68,"rate":1.0594,"nav_usd_at_entry_rate":4542.2},{"date":"2023-12-31","nav_eur":4533.0,"nav_usd":5008.97,"rate":1.105,"nav_usd_at_entry_rate":5102.8},{"date":"2024-03-31","nav_eur":5864.0,"nav_usd":6339.57,"rate":1.0811,"nav_usd_at_entry_rate":6601.1},{"date":"2024-06-30","nav_eur":6914.0,"nav_usd":7401.44,"rate":1.0705,"nav_usd_at_entry_rate":7783.09},{"date":"2024-09-30","nav_eur":7522.0,"nav_usd":8421.63,"rate":1.1196,"nav_usd_at_entry_rate":8467.52},{"date":"2024-12-31","nav_eur":7882.0,"nav_usd":8188.61,"rate":1.0389,"nav_usd_at_entry_rate":8872.77},{"date":"2025-03-31","nav_eur":8604.0,"nav_usd":9305.23,"rate":1.0815,"nav_usd_at_entry_rate":9685.52},{"date":"2025-06-30","nav_eur":10280.0,"nav_usd":12048.16,"rate":1.172,"nav_usd_at_entry_rate":11572.2},{"date":"2025-09-30","nav_eur":11452.0,"nav_usd":13445.79,"rate":1.1741,"nav_usd_at_entry_rate":12891.52},{"date":"2025-12-31","nav_eur":12345.0,"nav_usd":14505.38,"rate":1.175,"nav_usd_at_entry_rate":13896.77}],"EQT IX||BRIAN SMITH VENTURES LLC":[{"date":"2020-09-30","nav_eur":0.0,"nav_usd":0.0,"rate":1.1708,"nav_usd_at_entry_rate":0.0},{"date":"2020-12-31","nav_eur":0.0,"nav_usd":0.0,"rate":1.2271,"nav_usd_at_entry_rate":0.0},{"date":"2021-03-31","nav_eur":0.0,"nav_usd":0.0,"rate":1.1725,"nav_usd_at_entry_rate":0.0},{"date":"2021-06-30","nav_eur":0.0,"nav_usd":0.0,"rate":1.1884,"nav_usd_at_entry_rate":0.0},{"date":"2021-09-30","nav_eur":7920.0,"nav_usd":9170.57,"rate":1.1579,"nav_usd_at_entry_rate":9118.3},{"date":"2021-12-31","nav_eur":13901.0,"nav_usd":15744.27,"rate":1.1326,"nav_usd_at_entry_rate":16004.22},{"date":"2022-03-31","nav_eur":16405.0,"nav_usd":18211.19,"rate":1.1101,"nav_usd_at_entry_rate":18887.08},{"date":"2022-06-30","nav_eur":16096.0,"nav_usd":16718.92,"rate":1.0387,"nav_usd_at_entry_rate":18531.32},{"date":"2022-09-30","nav_eur":16673.0,"nav_usd":16252.84,"rate":0.9748,"nav_usd_at_entry_rate":19195.62},{"date":"2022-12-31","nav_eur":15775.0,"nav_usd":16825.62,"rate":1.0666,"nav_usd_at_entry_rate":18161.76},{"date":"2023-03-31","nav_eur":17969.0,"nav_usd":19541.29,"rate":1.0875,"nav_usd_at_entry_rate":20687.71},{"date":"2023-06-30","nav_eur":17807.0,"nav_usd":19349.09,"rate":1.0866,"nav_usd_at_entry_rate":20501.2},{"date":"2023-09-30","nav_eur":17941.0,"nav_usd":19006.7,"rate":1.0594,"nav_usd_at_entry_rate":20655.47},{"date":"2023-12-31","nav_eur":16918.0,"nav_usd":18694.39,"rate":1.105,"nav_usd_at_entry_rate":19477.69},{"date":"2024-03-31","nav_eur":17740.0,"nav_usd":19178.71,"rate":1.0811,"nav_usd_at_entry_rate":20424.06},{"date":"2024-06-30","nav_eur":17975.0,"nav_usd":19242.24,"rate":1.0705,"nav_usd_at_entry_rate":20694.62},{"date":"2024-09-30","nav_eur":18742.0,"nav_usd":20983.54,"rate":1.1196,"nav_usd_at_entry_rate":21577.66},{"date":"2024-12-31","nav_eur":19888.0,"nav_usd":20661.64,"rate":1.0389,"nav_usd_at_entry_rate":22897.05},{"date":"2025-03-31","nav_eur":21127.0,"nav_usd":22848.85,"rate":1.0815,"nav_usd_at_entry_rate":24323.52},{"date":"2025-06-30","nav_eur":20937.0,"nav_usd":24538.16,"rate":1.172,"nav_usd_at_entry_rate":24104.77},{"date":"2025-09-30","nav_eur":19034.0,"nav_usd":22347.82,"rate":1.1741,"nav_usd_at_entry_rate":21913.84},{"date":"2025-12-31","nav_eur":19035.0,"nav_usd":22366.12,"rate":1.175,"nav_usd_at_entry_rate":21915.0}],"EQT Infrastructure V||BRIAN SMITH VENTURES LLC":[{"date":"2021-12-31","nav_eur":2579.0,"nav_usd":2920.98,"rate":1.1326,"nav_usd_at_entry_rate":2871.72},{"date":"2022-03-31","nav_eur":3630.0,"nav_usd":4029.66,"rate":1.1101,"nav_usd_at_entry_rate":4042.0},{"date":"2022-06-30","nav_eur":4781.0,"nav_usd":4966.02,"rate":1.0387,"nav_usd_at_entry_rate":5323.64},{"date":"2022-09-30","nav_eur":5025.0,"nav_usd":4898.37,"rate":0.9748,"nav_usd_at_entry_rate":5595.34},{"date":"2022-12-31","nav_eur":5329.0,"nav_usd":5683.91,"rate":1.0666,"nav_usd_at_entry_rate":5933.84},{"date":"2023-03-31","nav_eur":7185.0,"nav_usd":7813.69,"rate":1.0875,"nav_usd_at_entry_rate":8000.5},{"date":"2023-06-30","nav_eur":7960.0,"nav_usd":8649.34,"rate":1.0866,"nav_usd_at_entry_rate":8863.46},{"date":"2023-09-30","nav_eur":8937.0,"nav_usd":9467.86,"rate":1.0594,"nav_usd_at_entry_rate":9951.35},{"date":"2023-12-31","nav_eur":9016.0,"nav_usd":9962.68,"rate":1.105,"nav_usd_at_entry_rate":10039.32},{"date":"2024-03-31","nav_eur":10131.0,"nav_usd":10952.62,"rate":1.0811,"nav_usd_at_entry_rate":11280.87},{"date":"2024-06-30","nav_eur":11162.0,"nav_usd":11948.92,"rate":1.0705,"nav_usd_at_entry_rate":12428.89},{"date":"2024-09-30","nav_eur":11129.0,"nav_usd":12460.03,"rate":1.1196,"nav_usd_at_entry_rate":12392.14},{"date":"2024-12-31","nav_eur":11101.0,"nav_usd":11532.83,"rate":1.0389,"nav_usd_at_entry_rate":12360.96},{"date":"2025-03-31","nav_eur":10431.0,"nav_usd":11281.13,"rate":1.0815,"nav_usd_at_entry_rate":11614.92},{"date":"2025-06-30","nav_eur":10348.0,"nav_usd":12127.86,"rate":1.172,"nav_usd_at_entry_rate":11522.5},{"date":"2025-09-30","nav_eur":11160.0,"nav_usd":13102.96,"rate":1.1741,"nav_usd_at_entry_rate":12426.66},{"date":"2025-12-31","nav_eur":11028.0,"nav_usd":12957.9,"rate":1.175,"nav_usd_at_entry_rate":12279.68}],"EQT Infrastructure VI Leveraged||DLUGINSKI SMITH SE TRUST":[{"date":"2025-03-31","nav_eur":5177.0,"nav_usd":5598.93,"rate":1.0815,"nav_usd_at_entry_rate":5789.96},{"date":"2025-06-30","nav_eur":7250.0,"nav_usd":8497.0,"rate":1.172,"nav_usd_at_entry_rate":8108.4},{"date":"2025-09-30","nav_eur":12673.0,"nav_usd":14879.37,"rate":1.1741,"nav_usd_at_entry_rate":14173.48},{"date":"2025-12-31","nav_eur":12163.0,"nav_usd":14291.52,"rate":1.175,"nav_usd_at_entry_rate":13603.1}],"EQT Real Estate II||BRIAN SMITH VENTURES LLC":[{"date":"2020-06-30","nav_eur":1867.0,"nav_usd":2090.67,"rate":1.1198,"nav_usd_at_entry_rate":2039.51},{"date":"2020-09-30","nav_eur":1602.0,"nav_usd":1875.62,"rate":1.1708,"nav_usd_at_entry_rate":1750.02},{"date":"2020-12-31","nav_eur":0.0,"nav_usd":0.0,"rate":1.2271,"nav_usd_at_entry_rate":0.0},{"date":"2021-03-31","nav_eur":0.0,"nav_usd":0.0,"rate":1.1725,"nav_usd_at_entry_rate":0.0},{"date":"2021-06-30","nav_eur":0.0,"nav_usd":0.0,"rate":1.1884,"nav_usd_at_entry_rate":0.0},{"date":"2021-09-30","nav_eur":2076.0,"nav_usd":2403.8,"rate":1.1579,"nav_usd_at_entry_rate":2267.82},{"date":"2021-12-31","nav_eur":3365.0,"nav_usd":3811.2,"rate":1.1326,"nav_usd_at_entry_rate":3675.93},{"date":"2022-03-31","nav_eur":3395.0,"nav_usd":3768.79,"rate":1.1101,"nav_usd_at_entry_rate":3708.7},{"date":"2022-06-30","nav_eur":3364.0,"nav_usd":3494.19,"rate":1.0387,"nav_usd_at_entry_rate":3674.83},{"date":"2022-09-30","nav_eur":3893.0,"nav_usd":3794.9,"rate":0.9748,"nav_usd_at_entry_rate":4252.71},{"date":"2022-12-31","nav_eur":5152.0,"nav_usd":5495.12,"rate":1.0666,"nav_usd_at_entry_rate":5628.04},{"date":"2023-03-31","nav_eur":5486.0,"nav_usd":5966.02,"rate":1.0875,"nav_usd_at_entry_rate":5992.91},{"date":"2023-06-30","nav_eur":5394.0,"nav_usd":5861.12,"rate":1.0866,"nav_usd_at_entry_rate":5892.41},{"date":"2023-09-30","nav_eur":5460.0,"nav_usd":5784.32,"rate":1.0594,"nav_usd_at_entry_rate":5964.5},{"date":"2023-12-31","nav_eur":5989.0,"nav_usd":6617.85,"rate":1.105,"nav_usd_at_entry_rate":6542.38},{"date":"2024-03-31","nav_eur":6103.0,"nav_usd":6597.95,"rate":1.0811,"nav_usd_at_entry_rate":6666.92},{"date":"2024-06-30","nav_eur":6527.0,"nav_usd":6987.15,"rate":1.0705,"nav_usd_at_entry_rate":7130.09},{"date":"2024-09-30","nav_eur":7033.0,"nav_usd":7874.15,"rate":1.1196,"nav_usd_at_entry_rate":7682.85},{"date":"2024-12-31","nav_eur":6599.0,"nav_usd":6855.7,"rate":1.0389,"nav_usd_at_entry_rate":7208.75},{"date":"2025-03-31","nav_eur":6576.0,"nav_usd":7111.94,"rate":1.0815,"nav_usd_at_entry_rate":7183.62},{"date":"2025-06-30","nav_eur":6915.0,"nav_usd":8104.38,"rate":1.172,"nav_usd_at_entry_rate":7553.95},{"date":"2025-09-30","nav_eur":7272.0,"nav_usd":8538.06,"rate":1.1741,"nav_usd_at_entry_rate":7943.93}],"EQT Ventures II||BRIAN SMITH VENTURES LLC":[{"date":"2019-12-31","nav_eur":0.0,"nav_usd":0.0,"rate":1.1234,"nav_usd_at_entry_rate":0.0},{"date":"2020-03-31","nav_eur":2011.0,"nav_usd":2203.25,"rate":1.0956,"nav_usd_at_entry_rate":2237.04},{"date":"2020-06-30","nav_eur":4564.0,"nav_usd":5110.77,"rate":1.1198,"nav_usd_at_entry_rate":5076.99},{"date":"2020-09-30","nav_eur":4473.0,"nav_usd":5236.99,"rate":1.1708,"nav_usd_at_entry_rate":4975.77},{"date":"2020-12-31","nav_eur":4435.0,"nav_usd":5442.19,"rate":1.2271,"nav_usd_at_entry_rate":4933.49},{"date":"2021-03-31","nav_eur":8768.0,"nav_usd":10280.48,"rate":1.1725,"nav_usd_at_entry_rate":9753.52},{"date":"2021-06-30","nav_eur":0.0,"nav_usd":0.0,"rate":1.1884,"nav_usd_at_entry_rate":0.0},{"date":"2021-09-30","nav_eur":14917.0,"nav_usd":17272.39,"rate":1.1579,"nav_usd_at_entry_rate":16593.67},{"date":"2021-12-31","nav_eur":24124.0,"nav_usd":27322.84,"rate":1.1326,"nav_usd_at_entry_rate":26835.54},{"date":"2022-03-31","nav_eur":30666.0,"nav_usd":34042.33,"rate":1.1101,"nav_usd_at_entry_rate":34112.86},{"date":"2022-06-30","nav_eur":26717.0,"nav_usd":27750.95,"rate":1.0387,"nav_usd_at_entry_rate":29719.99},{"date":"2022-09-30","nav_eur":28868.0,"nav_usd":28140.53,"rate":0.9748,"nav_usd_at_entry_rate":32112.76},{"date":"2022-12-31","nav_eur":24413.0,"nav_usd":26038.91,"rate":1.0666,"nav_usd_at_entry_rate":27157.02},{"date":"2023-03-31","nav_eur":24749.0,"nav_usd":26914.54,"rate":1.0875,"nav_usd_at_entry_rate":27530.79},{"date":"2023-06-30","nav_eur":24473.0,"nav_usd":26592.36,"rate":1.0866,"nav_usd_at_entry_rate":27223.77},{"date":"2023-09-30","nav_eur":25564.0,"nav_usd":27082.5,"rate":1.0594,"nav_usd_at_entry_rate":28437.39},{"date":"2023-12-31","nav_eur":23118.0,"nav_usd":25545.39,"rate":1.105,"nav_usd_at_entry_rate":25716.46},{"date":"2024-03-31","nav_eur":25026.0,"nav_usd":27055.61,"rate":1.0811,"nav_usd_at_entry_rate":27838.92},{"date":"2024-06-30","nav_eur":26109.0,"nav_usd":27949.68,"rate":1.0705,"nav_usd_at_entry_rate":29043.65},{"date":"2024-09-30","nav_eur":23953.0,"nav_usd":26817.78,"rate":1.1196,"nav_usd_at_entry_rate":26645.32},{"date":"2024-12-31","nav_eur":25133.0,"nav_usd":26110.67,"rate":1.0389,"nav_usd_at_entry_rate":27957.95},{"date":"2025-03-31","nav_eur":24401.0,"nav_usd":26389.68,"rate":1.0815,"nav_usd_at_entry_rate":27143.67},{"date":"2025-06-30","nav_eur":25551.0,"nav_usd":29945.77,"rate":1.172,"nav_usd_at_entry_rate":28422.93},{"date":"2025-09-30","nav_eur":24636.0,"nav_usd":28925.13,"rate":1.1741,"nav_usd_at_entry_rate":27405.09},{"date":"2025-12-31","nav_eur":23429.0,"nav_usd":27529.08,"rate":1.175,"nav_usd_at_entry_rate":26062.42}],"EQT Ventures II||BDSCI LLC":[{"date":"2023-03-31","nav_eur":302.0,"nav_usd":328.42,"rate":1.0875,"nav_usd_at_entry_rate":325.62},{"date":"2023-06-30","nav_eur":0.0,"nav_usd":0.0,"rate":1.0866,"nav_usd_at_entry_rate":0.0},{"date":"2023-09-30","nav_eur":0.0,"nav_usd":0.0,"rate":1.0594,"nav_usd_at_entry_rate":0.0},{"date":"2023-12-31","nav_eur":0.0,"nav_usd":0.0,"rate":1.105,"nav_usd_at_entry_rate":0.0},{"date":"2024-03-31","nav_eur":902.0,"nav_usd":975.15,"rate":1.0811,"nav_usd_at_entry_rate":972.54},{"date":"2024-06-30","nav_eur":2824.0,"nav_usd":3023.09,"rate":1.0705,"nav_usd_at_entry_rate":3044.84},{"date":"2024-09-30","nav_eur":3030.0,"nav_usd":3392.39,"rate":1.1196,"nav_usd_at_entry_rate":3266.95},{"date":"2024-12-31","nav_eur":2488.0,"nav_usd":2584.78,"rate":1.0389,"nav_usd_at_entry_rate":2682.56},{"date":"2025-03-31","nav_eur":8587.0,"nav_usd":9286.84,"rate":1.0815,"nav_usd_at_entry_rate":9258.5},{"date":"2025-06-30","nav_eur":8250.0,"nav_usd":9669.0,"rate":1.172,"nav_usd_at_entry_rate":8895.15},{"date":"2025-09-30","nav_eur":15609.0,"nav_usd":18326.53,"rate":1.1741,"nav_usd_at_entry_rate":16829.62},{"date":"2025-12-31","nav_eur":21397.0,"nav_usd":25141.48,"rate":1.175,"nav_usd_at_entry_rate":23070.25}],"EQT Ventures III||DLUGINSKI SMITH SE TRUST":[{"date":"2023-03-31","nav_eur":2178.0,"nav_usd":2368.57,"rate":1.0875,"nav_usd_at_entry_rate":2357.9},{"date":"2023-06-30","nav_eur":2002.0,"nav_usd":2175.37,"rate":1.0866,"nav_usd_at_entry_rate":2167.37},{"date":"2023-09-30","nav_eur":2550.0,"nav_usd":2701.47,"rate":1.0594,"nav_usd_at_entry_rate":2760.63},{"date":"2023-12-31","nav_eur":2108.0,"nav_usd":2329.34,"rate":1.105,"nav_usd_at_entry_rate":2282.12},{"date":"2024-03-31","nav_eur":3093.0,"nav_usd":3343.84,"rate":1.0811,"nav_usd_at_entry_rate":3348.48},{"date":"2024-06-30","nav_eur":3499.0,"nav_usd":3745.68,"rate":1.0705,"nav_usd_at_entry_rate":3788.02},{"date":"2024-09-30","nav_eur":3778.0,"nav_usd":4229.85,"rate":1.1196,"nav_usd_at_entry_rate":4090.06},{"date":"2024-12-31","nav_eur":4274.0,"nav_usd":4440.26,"rate":1.0389,"nav_usd_at_entry_rate":4627.03},{"date":"2025-03-31","nav_eur":5878.0,"nav_usd":6357.06,"rate":1.0815,"nav_usd_at_entry_rate":6363.52},{"date":"2025-06-30","nav_eur":7651.0,"nav_usd":8966.97,"rate":1.172,"nav_usd_at_entry_rate":8282.97},{"date":"2025-09-30","nav_eur":9745.0,"nav_usd":11441.6,"rate":1.1741,"nav_usd_at_entry_rate":10549.94},{"date":"2025-12-31","nav_eur":11294.0,"nav_usd":13270.45,"rate":1.175,"nav_usd_at_entry_rate":12226.88}],"EQT X Leveraged||BDSCI LLC":[{"date":"2023-12-31","nav_eur":10693.0,"nav_usd":11815.76,"rate":1.105,"nav_usd_at_entry_rate":11442.58},{"date":"2024-03-31","nav_eur":10213.0,"nav_usd":11041.27,"rate":1.0811,"nav_usd_at_entry_rate":10928.93},{"date":"2024-06-30","nav_eur":21396.0,"nav_usd":22904.42,"rate":1.0705,"nav_usd_at_entry_rate":22895.86},{"date":"2024-09-30","nav_eur":20209.0,"nav_usd":22626.0,"rate":1.1196,"nav_usd_at_entry_rate":21625.65},{"date":"2024-12-31","nav_eur":25447.0,"nav_usd":26436.89,"rate":1.0389,"nav_usd_at_entry_rate":27230.83},{"date":"2025-03-31","nav_eur":38109.0,"nav_usd":41214.88,"rate":1.0815,"nav_usd_at_entry_rate":40780.44},{"date":"2025-06-30","nav_eur":34162.0,"nav_usd":40037.86,"rate":1.172,"nav_usd_at_entry_rate":36556.76},{"date":"2025-09-30","nav_eur":34160.0,"nav_usd":40107.26,"rate":1.1741,"nav_usd_at_entry_rate":36554.62},{"date":"2025-12-31","nav_eur":36439.0,"nav_usd":42815.83,"rate":1.175,"nav_usd_at_entry_rate":38993.37}],"EQT Ventures III Leveraged||BDSCI LLC":[{"date":"2025-03-10","nav_eur":2500.0,"nav_usd":2678.9,"rate":1.0716,"nav_usd_at_entry_rate":2679.0},{"date":"2026-03-12","nav_eur":2500.0,"nav_usd":2886.75,"rate":1.1547,"nav_usd_at_entry_rate":2679.0}],"EQT Healthcare Growth Leveraged||DLUGINSKI SMITH SE TRUST":[{"date":"2025-09-10","nav_eur":5000.0,"nav_usd":5868.22,"rate":1.1736,"nav_usd_at_entry_rate":5868.0},{"date":"2026-03-12","nav_eur":5000.0,"nav_usd":5773.5,"rate":1.1547,"nav_usd_at_entry_rate":5868.0}]},"vehicle_fx_summary":[{"investor":"BRIAN SMITH VENTURES LLC","total_eur_exposure":57511.2,"usd_cost_at_entry":64620.25,"usd_cost_at_current_rate":66408.19,"fx_on_cost_usd":1787.94,"nav_usd_at_current":84418.96,"nav_usd_at_entry_rate":82099.07,"fx_total_pl_usd":2781.77},{"investor":"BDSCI LLC","total_eur_exposure":37500.0,"usd_cost_at_entry":40173.63,"usd_cost_at_current_rate":43301.25,"fx_on_cost_usd":3127.62,"nav_usd_at_current":69669.98,"nav_usd_at_entry_rate":64743.56,"fx_total_pl_usd":4926.42},{"investor":"DLUGINSKI SMITH SE TRUST","total_eur_exposure":22486.92,"usd_cost_at_entry":25157.35,"usd_cost_at_current_rate":25965.65,"fx_on_cost_usd":808.29,"nav_usd_at_current":32859.3,"nav_usd_at_entry_rate":31697.93,"fx_total_pl_usd":1177.87}],"eur_sensitivity":[{"scenario":"-15%","pct_change":-15,"new_rate":0.9815,"usd_value":174446.22,"delta_usd":-30784.63,"delta_pct":-15.0},{"scenario":"-10%","pct_change":-10,"new_rate":1.0392,"usd_value":184707.76,"delta_usd":-20523.08,"delta_pct":-10.0},{"scenario":"-5%","pct_change":-5,"new_rate":1.097,"usd_value":194969.3,"delta_usd":-10261.54,"delta_pct":-5.0},{"scenario":"+0%","pct_change":0,"new_rate":1.1547,"usd_value":205230.85,"delta_usd":0.0,"delta_pct":0.0},{"scenario":"+5%","pct_change":5,"new_rate":1.2124,"usd_value":215492.39,"delta_usd":10261.54,"delta_pct":5.0},{"scenario":"+10%","pct_change":10,"new_rate":1.2702,"usd_value":225753.93,"delta_usd":20523.08,"delta_pct":10.0},{"scenario":"+15%","pct_change":15,"new_rate":1.3279,"usd_value":236015.47,"delta_usd":30784.63,"delta_pct":15.0}],"current_rate":1.1547,"current_rate_date":"2026-03-12","home_currency":"USD"};

// Utility functions
const formatCurrency = (value, decimals = 2) => {
  if (value === null || value === undefined) return "0.00";
  return parseFloat(value).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return dateStr;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Vehicle display name — pass through the name from fund data as-is (no hardcoded abbreviations)
const vehicleDisplayName = (v) => v;

// ── Shared Constants (single source of truth) ──
// Capital structure: Participant 15%, EQT Perf 35%, SEB 50%
const CAPITAL_STACK = { participant: 0.15, eqtPerf: 0.35, seb: 0.50 };
// Waterfall splits from Excel "Other fund info" sheet
const WATERFALL = {
  bankRocShare: 0.85,    // Bank gets 85% of Return of Capital
  bankGainsShare: 0.50,  // Bank gets 50% of Capital Gains
  empRocShare: 0.15,     // Employee gets 15% of RoC
  eqtPrefRocShare: 0,    // EQT Pref gets 0% of RoC
  eqtPrefGainsShare: 0.50, // EQT Pref gets 50% of Capital Gains
};
// Fee rates from Excel "Other fund info"
const FEE_RATES = {
  opex: 0.0012,           // 0.12% of total exposure
  commitmentFee: 0.004,   // 0.40% on undrawn bank facility
  arrangementFee: 0.0025, // 0.25% one-time on bank facility
};
// Fund deployment schedule from Excel "Other fund info" — 6 years
const INVESTMENT_PACE = [0.25, 0.25, 0.20, 0.15, 0.05, 0];
const INVESTMENT_LEVEL = 0.9; // 90% of commitment deployed (sum of pace)
// Fund bridge facility
const BRIDGE_CAP_PCT = 0.35; // 35% of co-investment
// Default rates
const DEFAULT_EQT_PERF_RATE = 8.0;       // 8% annual on EQT preferred equity
const DEFAULT_LEVERAGE_RATIO = 6.67;      // 15k → ~100k exposure
const DEFAULT_BASE_RATE = 5.0;            // 5% SEB borrowing rate
const DEFAULT_PAYOUT_YEARS = 4;
const DEFAULT_FEDERAL_CAP_GAINS = 20;     // 20% federal
const DEFAULT_STATE_CAP_GAINS = 5;        // 5% state
const EMPLOYEE_DRAWDOWN_PAYMENTS = 3;     // 3 equal payments for leveraged
const REGULAR_DRAWDOWN_PAYMENTS = 2;      // 2 equal payments for unleveraged
// J-curve model parameters
const J_CURVE = {
  deployPct: 0.30,   // 30% of fund life = deployment phase
  depth: 0.15,       // 15% max TVPI dip
  recoveryFade: 0.3, // fade factor during recovery
  floor: 0.5,        // minimum TVPI floor
};
// Performance tracking thresholds
const PERF_THRESHOLDS = { ahead: 10, behind: -10 };
// Time constants
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

// ── Shared Utility Functions ──
/** Build composite key for fund+investor lookups */
const makeKey = (fund, investor) => `${fund}||${investor}`;

/** Get distribution amount with fallback chain */
const getDistAmount = (dist) => dist.total_distribution || dist.net_distribution || 0;

/** Compute latest CAS entry per fund||investor from a CAS timeline array */
const getLatestCasPerFund = (casTimeline) => {
  const latest = {};
  casTimeline.forEach((cas) => {
    const key = `${cas.fund}||${cas.investor}`;
    if (!latest[key] || cas.date > latest[key].date) {
      latest[key] = cas;
    }
  });
  return latest;
};

/** Get drawdowns for a specific fund+investor, sorted chronologically */
const getFundDrawdowns = (drawdowns, fund, investor) =>
  (drawdowns || [])
    .filter(d => d.fund === fund && d.investor === investor)
    .sort((a, b) => a.date.localeCompare(b.date));

/** Standard TVPI: (NAV + Distributed) / Drawn */
const calcTvpi = (drawn, nav, distributed) =>
  drawn > 0 ? (nav + distributed) / drawn : 0;

/** Standard DPI: Distributed / Drawn */
const calcDpi = (drawn, distributed) =>
  drawn > 0 ? distributed / drawn : 0;

/** Resolve federal capital gains rate for a year, with fallback */
const getFedRate = (rates, yr, fallbackYr) =>
  (rates[yr] || rates[fallbackYr] || DEFAULT_FEDERAL_CAP_GAINS) / 100;

/** Resolve state capital gains rate for a year, with fallback */
const getStateRate = (rates, yr, fallbackYr) =>
  (rates[yr] || rates[fallbackYr] || DEFAULT_STATE_CAP_GAINS) / 100;

/** Compute capital stack amounts for a leveraged fund */
const calcCapitalStack = (totalExposure, myCommitment, isLev) => ({
  participantAmt: isLev ? totalExposure * CAPITAL_STACK.participant : myCommitment,
  eqtPerfAmt: isLev ? totalExposure * CAPITAL_STACK.eqtPerf : 0,
  sebAmt: isLev ? totalExposure * CAPITAL_STACK.seb : 0,
});

/**
 * Year-by-year leveraged fund simulation.
 * Returns: { projectedSebInterest, projectedEqtPerfInterest, projectedTotalInterest,
 *            lpNetReturn, sebRepaid, eqtPerfRepaid, participantRepaid,
 *            sebGains, eqtPerfGains, participantGains, simInterestByYear }
 */
const simulateLeveragedFund = ({
  totalExposure, sebAmt, eqtPerfRate, bankInterestRate,
  firstInvestmentYear, vintageYear, avgHoldingPeriod,
  effectiveFundMoic, participantAmt,
}) => {
  const simYears = (firstInvestmentYear + INVESTMENT_PACE.length - 1 + avgHoldingPeriod) - firstInvestmentYear + 1;
  const investmentsByYear = [];
  let bankDrawn = 0, eqtPrefDrawn = 0, bridgeDrawn = 0;
  let totalEmpDistributions = 0;
  const bankFacility = totalExposure * CAPITAL_STACK.seb;
  const bridgeCap = totalExposure * BRIDGE_CAP_PCT;
  let cumulativeBankUtilized = 0;
  let arrangementFeePaid = false;
  const equityCommitment = participantAmt;
  const empDrawdownPerYear = equityCommitment / EMPLOYEE_DRAWDOWN_PAYMENTS;
  const empDrawdownStart = vintageYear;
  const empDrawdownEnd = empDrawdownStart + 2;

  let projectedSebInterest = 0, projectedEqtPerfInterest = 0;
  let sebRepaid = 0, eqtPerfRepaid = 0, participantRepaid = 0;
  let sebGains = 0, eqtPerfGains = 0, participantGains = 0;
  const simInterestByYear = {};
  const simDistributionsByYear = {};

  for (let i = 0; i < simYears; i++) {
    const yr = firstInvestmentYear + i;
    const pace = i < INVESTMENT_PACE.length ? INVESTMENT_PACE[i] : 0;
    const newInvestment = totalExposure * pace;
    investmentsByYear.push(newInvestment);

    let fundRoC = 0, fundCapGains = 0;
    if (i >= avgHoldingPeriod) {
      const returnIdx = i - avgHoldingPeriod;
      if (returnIdx < investmentsByYear.length && investmentsByYear[returnIdx] > 0) {
        const inv = investmentsByYear[returnIdx];
        fundRoC = inv * Math.min(effectiveFundMoic, 1);
        fundCapGains = inv * Math.max(0, effectiveFundMoic - 1);
      }
    }

    // Costs
    const yearBankInterest = bankDrawn * bankInterestRate;
    const undrawnFacility = Math.max(0, bankFacility - cumulativeBankUtilized);
    const yearCommitmentFee = (bankDrawn > 0 || cumulativeBankUtilized > 0) ? undrawnFacility * FEE_RATES.commitmentFee : 0;
    const yearArrangementFee = (!arrangementFeePaid && cumulativeBankUtilized > 0) ? bankFacility * FEE_RATES.arrangementFee : 0;
    if (yearArrangementFee > 0) arrangementFeePaid = true;
    const yearOpex = (bankDrawn > 0 || newInvestment > 0 || eqtPrefDrawn > 0 || bridgeDrawn > 0)
      ? totalExposure * FEE_RATES.opex : 0;
    const yearTotalCosts = yearBankInterest + yearCommitmentFee + yearArrangementFee + yearOpex;
    projectedSebInterest += yearBankInterest;
    const totalUses = newInvestment + yearTotalCosts;

    // Sources: bridge pre-drawdown, then bank+employee+EQT pref
    let yearBankDraw = 0, yearEmpDraw = 0, yearEqtPrefDraw = 0;
    let yearBridgeDraw = 0, yearBridgeRepay = 0;
    if (yr < empDrawdownStart) {
      yearBridgeDraw = Math.min(newInvestment, bridgeCap - bridgeDrawn);
      yearEqtPrefDraw = yearTotalCosts;
    } else {
      yearBankDraw = newInvestment * CAPITAL_STACK.seb + yearTotalCosts * CAPITAL_STACK.seb / (CAPITAL_STACK.seb + CAPITAL_STACK.eqtPerf);
      yearEmpDraw = (yr >= empDrawdownStart && yr <= empDrawdownEnd) ? empDrawdownPerYear : 0;
      yearBridgeRepay = Math.min(bridgeDrawn, yearBankDraw + yearEmpDraw);
      yearEqtPrefDraw = Math.max(0, totalUses - yearBankDraw - yearEmpDraw + yearBridgeRepay);
      cumulativeBankUtilized += yearBankDraw;
    }
    bridgeDrawn = Math.max(0, bridgeDrawn + yearBridgeDraw - yearBridgeRepay);

    bankDrawn += yearBankDraw;
    eqtPrefDrawn += yearEqtPrefDraw;

    // Bank repayment from distributions
    const bankRocRepay = Math.min(bankDrawn, fundRoC * WATERFALL.bankRocShare);
    const bankGainsRepay = Math.min(Math.max(0, bankDrawn - bankRocRepay), fundCapGains * WATERFALL.bankGainsShare);
    bankDrawn = Math.max(0, bankDrawn - bankRocRepay - bankGainsRepay);
    sebRepaid += bankRocRepay + bankGainsRepay;

    // EQT Pref repayment
    const eqtPrefRocRepay = 0; // 0% RoC per Excel
    const eqtPrefGainsRepay = Math.min(eqtPrefDrawn, fundCapGains * WATERFALL.eqtPrefGainsShare);
    eqtPrefDrawn = Math.max(0, eqtPrefDrawn - eqtPrefGainsRepay);
    eqtPerfRepaid += eqtPrefGainsRepay;

    // EQT Pref interest compounds
    const yearEqtPrefInterest = eqtPrefDrawn > 0 ? eqtPrefDrawn * eqtPerfRate / 100 : 0;
    projectedEqtPerfInterest += yearEqtPrefInterest;
    eqtPrefDrawn += yearEqtPrefInterest;

    simInterestByYear[yr] = {
      sebInterest: yearBankInterest,
      eqtPerfInterest: yearEqtPrefInterest,
      total: yearBankInterest + yearEqtPrefInterest,
    };

    // Employee: empRocShare of RoC + all residual after senior claims
    const empRoc = fundRoC * WATERFALL.empRocShare;
    const totalAllocated = empRoc + bankRocRepay + eqtPrefRocRepay + bankGainsRepay + eqtPrefGainsRepay;
    const residual = Math.max(0, (fundRoC + fundCapGains) - totalAllocated);
    const yearEmpDist = empRoc + residual;
    totalEmpDistributions += yearEmpDist;
    if (yearEmpDist > 0) {
      simDistributionsByYear[yr] = yearEmpDist;
    }
    participantRepaid += empRoc;
    participantGains += residual;
    sebGains += bankGainsRepay;
    eqtPerfGains += eqtPrefGainsRepay;
  }

  return {
    projectedSebInterest, projectedEqtPerfInterest,
    projectedTotalInterest: projectedSebInterest + projectedEqtPerfInterest,
    lpNetReturn: Math.max(0, totalEmpDistributions),
    sebRepaid, eqtPerfRepaid, participantRepaid,
    sebGains, eqtPerfGains, participantGains,
    simInterestByYear,
    simDistributionsByYear,
  };
};

const numOrZero = (x) => {
  const n = typeof x === "number" ? x : Number(x);
  return Number.isFinite(n) ? n : 0;
};

/** Coerce API / embedded fx_fund_impact rows so .toFixed / .toLocaleString never throw. */
const normalizeFxFundImpact = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((f) => ({
    ...f,
    total_drawn_eur: numOrZero(f.total_drawn_eur),
    weighted_avg_entry_rate: numOrZero(f.weighted_avg_entry_rate),
    current_rate: numOrZero(f.current_rate),
    rate_change_pct: numOrZero(f.rate_change_pct),
    usd_cost_at_entry: numOrZero(f.usd_cost_at_entry),
    usd_cost_at_current_rate: numOrZero(f.usd_cost_at_current_rate ?? f.usd_cost_at_current),
    distributions_eur: numOrZero(f.distributions_eur),
    fx_on_cost_usd: numOrZero(f.fx_on_cost_usd),
    fx_on_nav_usd: numOrZero(f.fx_on_nav_usd),
    fx_total_pl_usd: numOrZero(f.fx_total_pl_usd),
  }));
};

// Main Dashboard Component
export default function PEDashboard({ rawData: propRawData, fundVariablesData: propFundVars, fxData: propFxData, simulationSettings: propSimSettings, onSimulationChange, orgId } = {}) {
  // Fallback logic: use props if provided, otherwise use embedded data
  const RAW_DATA = useMemo(() => {
    const raw = propRawData || EMBEDDED_RAW_DATA;
    // Deduplicate records — DB may contain duplicates from re-uploading documents.
    const dedup = (arr, keyFn) => {
      const seen = new Set();
      return arr.filter((item) => {
        const key = keyFn(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };
    const drawdowns = dedup(raw.drawdowns || [], (dd) =>
      `${dd.fund}|${dd.investor}|${dd.date}|${dd.amount}`
    );
    const distributions = dedup(raw.distributions || [], (d) =>
      `${d.fund}|${d.investor}|${d.date}|${d.total_distribution || d.net_distribution || 0}`
    );
    const cas_timeline = dedup(raw.cas_timeline || [], (c) =>
      `${c.fund}|${c.investor}|${c.date}|${c.drawn_commitment}`
    );
    return { ...raw, drawdowns, distributions, cas_timeline };
  }, [propRawData]);
  const rawFx = propFxData && typeof propFxData === "object" ? propFxData : EMBEDDED_FX_DATA;
  const FX_DATA = {
    ...rawFx,
    current_rate: Number(rawFx?.current_rate) || 1.0,
    fx_timeline: Array.isArray(rawFx?.fx_timeline)
      ? rawFx.fx_timeline.map((p) => {
          const eurUsd = Number(p?.eurUsd ?? p?.rate);
          return { ...p, eurUsd: Number.isFinite(eurUsd) ? eurUsd : undefined };
        })
      : [],
    fx_fund_impact: normalizeFxFundImpact(rawFx?.fx_fund_impact),
    vehicle_fx_summary: Array.isArray(rawFx?.vehicle_fx_summary) ? rawFx.vehicle_fx_summary : [],
    eur_nav_in_usd:
      rawFx?.eur_nav_in_usd && typeof rawFx.eur_nav_in_usd === "object" ? rawFx.eur_nav_in_usd : {},
    eur_sensitivity: Array.isArray(rawFx?.eur_sensitivity) ? rawFx.eur_sensitivity : [],
  };

  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [selectedFund, setSelectedFund] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "fund",
    direction: "asc",
  });

  const [customVars, setCustomVars] = useState(null);
  const [customColumns, setCustomColumns] = useState([]);

  // Initialize customVars from propFundVars if provided
  useEffect(() => {
    if (propFundVars) {
      setCustomVars(propFundVars);
      setCustomColumns(Object.keys(Object.values(propFundVars)[0] || {}).filter(k => !['Fund','Vehicle'].includes(k)));
    }
  }, [propFundVars]);

  const isMobile = useIsMobile();       // true when viewport < 640px
  const chartH = isMobile ? 220 : 300;  // responsive chart height
  const chartHSm = isMobile ? 160 : 200; // smaller charts (bar segments)
  const chartHLg = isMobile ? 250 : 350; // larger charts (scatter)

  const [showVariables, setShowVariables] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [rateShift, setRateShift] = useState(0);  // bps shift from slider
  // Holding period: per-fund base from fund_variables (Excel), with global +/- adjustment slider
  // holdingPeriodAdjust is the +/- years offset applied to ALL funds' base holding periods
  const [holdingPeriodAdjust, setHoldingPeriodAdjust] = useState(0);  // +/- years
  // Per-fund holding period overrides (key: "fund||investor" -> absolute years)
  // When set, this REPLACES the base + adjust for that specific fund
  const [holdingPeriodOverrides, setHoldingPeriodOverrides] = useState({});
  // Whether the per-fund holding period breakdown is expanded
  const [showHoldingPeriodDetail, setShowHoldingPeriodDetail] = useState(false);
  // Default base holding period for funds without a per-fund value in fund_variables
  const DEFAULT_HOLDING_PERIOD = 5;
  // Per-fund EQT Performance Loan rate override (default 8%, can simulate waiver)
  const [eqtPerfRates, setEqtPerfRates] = useState({});  // key: "fund||investor" -> rate %
  // Per-fund MOIC override for simulation (key: "fund||investor" -> moic value)
  const [fundMoicOverrides, setFundMoicOverrides] = useState({});
  // Collapsible slider group states
  const [expandedSliders, setExpandedSliders] = useState({});
  // Per-year other net investment income (for investment interest deduction)
  const [otherInvestmentIncome, setOtherInvestmentIncome] = useState(() => {
    const inc = {};
    const cy = new Date().getFullYear();
    for (let y = cy; y < cy + 12; y++) inc[y] = 0;
    return inc;
  });
  const [cashNavTab, setCashNavTab] = useState("cf_fund");
  const [commitTab, setCommitTab] = useState("fund");
  const [commitValueMode, setCommitValueMode] = useState("commitment"); // "commitment" | "exposure" | "implied_return"
  const [fxExpTab, setFxExpTab] = useState("fund");
  const [pageTab, setPageTab] = useState("commitments");
  const [fxFundTab, setFxFundTab] = useState("all");
  const [forecastView, setForecastView] = useState("summary");
  const [perfTargetTab, setPerfTargetTab] = useState("all");
  const [perfTab, setPerfTab] = useState("summary");
  const [forecastDetailTab, setForecastDetailTab] = useState("lifecycle"); // "lifecycle" | "cashflows" | "taximpact"
  const [forecastChartTab, setForecastChartTab] = useState("unfunded"); // "unfunded" | "distribution"
  const [analyticsDetailTab, setAnalyticsDetailTab] = useState("heatmap"); // "heatmap" | "sensitivity" | "maturity"
  const [pcTab, setPcTab] = useState("overview"); // "overview" | "freshness" | "imports"
  const [pcData, setPcData] = useState({ companies: [], snapshots: [], freshness: [], imports: [] });
  const [pcLoading, setPcLoading] = useState(false);
  // Per-file upload tracking: [{ id, filename, status: "uploading"|"queued"|"running"|"completed"|"failed", msg, jobId }]
  const [pcUploadJobs, setPcUploadJobs] = useState([]);
  const [pcSelectedCompany, setPcSelectedCompany] = useState(null);
  const [exposureMode, setExposureMode] = useState("company"); // "fund" | "company"
  const [expandedFundRows, setExpandedFundRows] = useState({}); // { "fund||investor": true }
  const [pcAllSnapshots, setPcAllSnapshots] = useState([]); // all snapshots for exposure computation
  const [bubbleColorTab, setBubbleColorTab] = useState("all"); // "all" | "fund" | "area" | "strategy" | "type" | "geography" | "sector"

  // Fetch portfolio company data when PC tab is active
  const fetchPcData = useCallback(async () => {
    if (!orgId) return;
    setPcLoading(true);
    try {
      const [companies, freshness, imports, allSnaps] = await Promise.all([
        api.get(`/investor-data/${orgId}/companies`),
        api.get(`/investor-data/${orgId}/freshness`),
        api.get(`/investor-data/${orgId}/imports`),
        api.get(`/investor-data/${orgId}/snapshots`),
      ]);
      setPcData({ companies, freshness, imports, snapshots: [] });
      setPcAllSnapshots(allSnaps || []);
    } catch (e) {
      console.error("Failed to fetch PC data:", e);
    } finally {
      setPcLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (pageTab === "portfolio-companies" || pageTab === "analytics" || pageTab === "commitments" || pageTab === "performance") {
      fetchPcData();
    }
  }, [pageTab, fetchPcData]);

  // Upload multiple investor data files concurrently, track each independently
  const handlePcUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !orgId) return;
    // Reset input so same files can be re-selected
    e.target.value = "";

    // Create a tracking entry per file
    const newJobs = files.map((f, i) => ({
      id: `${Date.now()}-${i}`,
      filename: f.name,
      status: "uploading",
      msg: "Uploading...",
      jobId: null,
    }));
    setPcUploadJobs(prev => [...newJobs, ...prev]);

    // Kick off each upload concurrently
    files.forEach((file, i) => {
      const trackId = newJobs[i].id;
      const updateJob = (patch) =>
        setPcUploadJobs(prev => prev.map(j => j.id === trackId ? { ...j, ...patch } : j));

      (async () => {
        try {
          const result = await api.uploadInvestorData(orgId, file);
          updateJob({ status: "queued", msg: "Processing...", jobId: result.extraction_job_id });

          // Poll for completion
          const poll = setInterval(async () => {
            try {
              const job = await api.get(`/uploads/${orgId}/jobs/${result.extraction_job_id}`);
              if (job.status === "completed") {
                clearInterval(poll);
                updateJob({ status: "completed", msg: "Import complete" });
                fetchPcData();
              } else if (job.status === "failed") {
                clearInterval(poll);
                updateJob({ status: "failed", msg: job.error_message || "Import failed" });
              } else if (job.status === "running") {
                updateJob({ status: "running", msg: "Importing data..." });
              }
            } catch { /* ignore poll errors */ }
          }, 2500);
          // Safety: stop polling after 3 minutes
          setTimeout(() => { clearInterval(poll); }, 180000);
        } catch (err) {
          updateJob({ status: "failed", msg: err.message || "Upload failed" });
        }
      })();
    });
  };

  // On mount + whenever we navigate to this tab, check for any in-progress jobs
  // so uploads survive page refresh
  useEffect(() => {
    if (pageTab !== "portfolio-companies" || !orgId) return;
    let cancelled = false;

    (async () => {
      try {
        // Fetch recent extraction jobs of type investor_data_extraction
        const jobs = await api.get(`/uploads/${orgId}/jobs?job_type=investor_data_extraction&limit=10`);
        if (cancelled || !Array.isArray(jobs)) return;

        const active = jobs.filter(j => j.status === "queued" || j.status === "running");
        if (!active.length) return;

        // Add entries for any in-flight jobs not already tracked
        setPcUploadJobs(prev => {
          const existingJobIds = new Set(prev.map(p => p.jobId));
          const newEntries = active
            .filter(j => !existingJobIds.has(j.id))
            .map(j => ({
              id: `recover-${j.id}`,
              filename: j.result?.parsed_summary?.fund_name || "Previous upload",
              status: j.status,
              msg: j.status === "running" ? "Importing data..." : "Processing...",
              jobId: j.id,
            }));
          if (!newEntries.length) return prev;
          return [...newEntries, ...prev];
        });

        // Resume polling each
        active.forEach(j => {
          const trackId = `recover-${j.id}`;
          const updateJob = (patch) =>
            setPcUploadJobs(prev => prev.map(p => p.id === trackId ? { ...p, ...patch } : p));

          const poll = setInterval(async () => {
            try {
              const job = await api.get(`/uploads/${orgId}/jobs/${j.id}`);
              if (job.status === "completed") {
                clearInterval(poll);
                updateJob({ status: "completed", msg: "Import complete" });
                fetchPcData();
              } else if (job.status === "failed") {
                clearInterval(poll);
                updateJob({ status: "failed", msg: job.error_message || "Import failed" });
              } else if (job.status === "running") {
                updateJob({ status: "running", msg: "Importing data..." });
              }
            } catch { /* ignore */ }
          }, 2500);
          setTimeout(() => clearInterval(poll), 180000);
        });
      } catch { /* ignore recovery errors */ }
    })();

    return () => { cancelled = true; };
  }, [pageTab, orgId]);

  const fetchCompanySnapshots = async (companyId) => {
    if (!orgId) return;
    try {
      const snapshots = await api.get(`/investor-data/${orgId}/companies/${companyId}/snapshots`);
      setPcData(prev => ({ ...prev, snapshots }));
    } catch (e) {
      console.error("Failed to fetch snapshots:", e);
    }
  };

  // Helper: resolve effective holding period for a fund
  // Priority: 1) per-fund override from slider, 2) base from fund_variables + global adjust, 3) default + adjust
  const getEffectiveHoldingPeriod = (key, fundVars) => {
    if (holdingPeriodOverrides[key] !== undefined) return Math.max(1, holdingPeriodOverrides[key]);
    const basePeriod = (fundVars && Number(fundVars["Holding Period"]) > 0)
      ? Number(fundVars["Holding Period"])
      : DEFAULT_HOLDING_PERIOD;
    return Math.max(1, basePeriod + holdingPeriodAdjust);
  };

  // Helper: resolve first investment year (distinct from first drawdown / vintage)
  // Excel: First Investment is when fund starts deploying capital; First Drawdown is when employee pays in
  const getFirstInvestmentYear = (fundVars, vintageYear) => {
    if (fundVars && Number(fundVars["First Investment Year"]) > 0) {
      return Number(fundVars["First Investment Year"]);
    }
    // Default: 1 year before first drawdown (vintage), matching typical Excel pattern
    return vintageYear - 1;
  };

  // Helper: resolve bank interest rate from SEB base rate (consistent across dashboard)
  const DEFAULT_BANK_INTEREST_RATE = 5.57; // percent — fallback when no SEB rate is set
  const getBankInterestRate = (fundVars) => {
    if (fundVars && Number(fundVars["Avg Interest Rate (%)"]) > 0) {
      return Number(fundVars["Avg Interest Rate (%)"]) / 100; // seb_base_rate, convert to decimal
    }
    return DEFAULT_BANK_INTEREST_RATE / 100; // 0.0557
  };

  // Per-year interest rate assumptions for forecast (editable sliders)
  const currentYear = new Date().getFullYear();
  const forecastYears = Array.from({ length: 12 }, (_, i) => currentYear + i);
  const [simulatedFunds, setSimulatedFunds] = useState([]);

  const addSimulatedFund = () => {
    const nextIdx = simulatedFunds.length + 1;
    setSimulatedFunds((prev) => [
      ...prev,
      {
        id: `sim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        fundName: `Sim Fund ${nextIdx}`,
        firstDrawdownYear: currentYear,
        commitment: 1000000,
        participantPct: 15,
        eqtPerfPct: 35,
        sebPct: 50,
        targetMoic: 2.0,
      },
    ]);
  };

  const removeSimulatedFund = (id) => {
    setSimulatedFunds((prev) => prev.filter((f) => f.id !== id));
  };

  const updateSimulatedFund = (id, field, value) => {
    setSimulatedFunds((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        if (field === "fundName") return { ...f, fundName: value };
        if (field === "firstDrawdownYear") return { ...f, firstDrawdownYear: parseInt(value, 10) || currentYear };
        const parsed = parseFloat(value);
        return { ...f, [field]: Number.isFinite(parsed) ? parsed : 0 };
      })
    );
  };
  const [forecastRates, setForecastRates] = useState(() => {
    const rates = {};
    for (let y = currentYear; y < currentYear + 12; y++) rates[y] = 5.0;
    return rates;
  });

  // Per-year tax rate assumptions (federal + state capital gains)
  const [federalCapGainsRates, setFederalCapGainsRates] = useState(() => {
    const rates = {};
    for (let y = currentYear; y < currentYear + 12; y++) rates[y] = 20.0;
    return rates;
  });
  const [stateCapGainsRates, setStateCapGainsRates] = useState(() => {
    const rates = {};
    for (let y = currentYear; y < currentYear + 12; y++) rates[y] = 5.0;
    return rates;
  });

  // Initialize simulation settings from propSimSettings if provided
  const simSettingsLoaded = useRef(false);
  useEffect(() => {
    if (propSimSettings) {
      if (propSimSettings.eqt_perf_rate_overrides) setEqtPerfRates(propSimSettings.eqt_perf_rate_overrides);
      if (propSimSettings.fund_moic_overrides) setFundMoicOverrides(propSimSettings.fund_moic_overrides);
      if (propSimSettings.forecast_rates) setForecastRates(propSimSettings.forecast_rates);
      if (propSimSettings.federal_cap_gains_rates) setFederalCapGainsRates(propSimSettings.federal_cap_gains_rates);
      if (propSimSettings.state_cap_gains_rates) setStateCapGainsRates(propSimSettings.state_cap_gains_rates);
      if (propSimSettings.other_investment_income) setOtherInvestmentIncome(propSimSettings.other_investment_income);
      if (propSimSettings.holding_period != null) {
        const storedHp = propSimSettings.holding_period;
        if (storedHp > 4) {
          setHoldingPeriodAdjust(storedHp - DEFAULT_HOLDING_PERIOD);
        } else {
          setHoldingPeriodAdjust(storedHp);
        }
      }
      if (propSimSettings.holding_period_overrides) setHoldingPeriodOverrides(propSimSettings.holding_period_overrides);
      if (propSimSettings.rate_shift != null) setRateShift(propSimSettings.rate_shift);
      if (Array.isArray(propSimSettings.simulated_funds) && propSimSettings.simulated_funds.length > 0) {
        setSimulatedFunds(propSimSettings.simulated_funds);
      }
      // Mark loaded — the save effect below should skip until init is done
      setTimeout(() => { simSettingsLoaded.current = true; }, 100);
    } else {
      simSettingsLoaded.current = true;
    }
  }, [propSimSettings]);

  // Persist simulation settings to Supabase whenever any forecast state changes
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!onSimulationChange || !simSettingsLoaded.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSimulationChange({
        eqt_perf_rate_overrides: eqtPerfRates,
        fund_moic_overrides: fundMoicOverrides,
        forecast_rates: forecastRates,
        federal_cap_gains_rates: federalCapGainsRates,
        state_cap_gains_rates: stateCapGainsRates,
        other_investment_income: otherInvestmentIncome,
        holding_period: holdingPeriodAdjust,
        holding_period_overrides: holdingPeriodOverrides,
        rate_shift: rateShift,
        simulated_funds: simulatedFunds,
      });
    }, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [eqtPerfRates, fundMoicOverrides, forecastRates, federalCapGainsRates, stateCapGainsRates, otherInvestmentIncome, holdingPeriodAdjust, holdingPeriodOverrides, rateShift, simulatedFunds, onSimulationChange]);

  // SheetJS Excel file reader
  const handleExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        // Row 1 has merged category headers; row 2 has actual column names; row 3 is instructions
        // Use range starting at row 2 (0-indexed: row index 1) so row 2 becomes the header
        const range = XLSX.utils.decode_range(ws["!ref"]);
        range.s.r = 1; // skip row 1 (merged category headers)
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "", range });
        // Filter out instruction rows (row 3 has "← Auto-populated", "← Fill in..." etc.)
        const dataRows = rows.filter(row => {
          const fund = (row["Fund"] || "").trim();
          return fund && !fund.startsWith("←");
        });
        if (dataRows.length === 0) return;

        // Find the editable columns (everything after Currency)
        const allCols = Object.keys(dataRows[0]);
        const fixedCols = ["Fund", "Vehicle", "Currency"];
        const editableCols = allCols.filter((c) => !fixedCols.includes(c) && !c.startsWith("__"));

        // Build lookup: "fundName||investor" -> { col: value, ... }
        const varMap = {};
        dataRows.forEach((row) => {
          const fund = (row["Fund"] || "").trim();
          const vehicle = (row["Vehicle"] || "").trim();
          if (!fund) return;
          const key = makeKey(fund, vehicle);
          const vars = {};
          editableCols.forEach((col) => {
            const val = row[col];
            if (val !== "" && val !== null && val !== undefined) {
              vars[col] = val;
            }
          });
          if (Object.keys(vars).length > 0) {
            varMap[key] = vars;
          }
        });

        // Separate leverage columns from custom columns
        const leverageCols = ["Leveraged?", "My Commitment", "Total Exposure", "Leverage Ratio",
          "Co-Invest Amount", "Avg Interest Rate (%)", "Annual Interest Cost",
          "Target Fund MOIC", "Vintage Year", "Payout Years",
          "Holding Period", "First Investment Year",
          "Roth %", "Traditional IRA %", "Taxable %",
          "Fund Area", "Fund Strategy", "Product Type"];
        const displayCols = editableCols.filter((c) => !leverageCols.includes(c));

        setCustomVars(varMap);
        setCustomColumns(displayCols.filter((col) =>
          Object.values(varMap).some((v) => v[col] !== undefined)
        ));
      } catch (err) {
        console.error("Excel parse error:", err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleExcelFile(file);
  };

  const getCustomVar = (fundName, investor, col) => {
    if (!customVars) return null;
    const key = makeKey(fundName, investor);
    return customVars[key]?.[col] ?? null;
  };


  // Process and filter data
  const filteredData = useMemo(() => {
    let filteredFunds = RAW_DATA.funds;
    let filteredCasTimeline = RAW_DATA.cas_timeline || [];
    let filteredDrawdowns = RAW_DATA.drawdowns || [];
    let filteredDistributions = RAW_DATA.distributions || [];

    if (selectedInvestor) {
      filteredFunds = filteredFunds.filter(
        (f) => f.investor === selectedInvestor
      );
      filteredCasTimeline = filteredCasTimeline.filter(
        (c) => c.investor === selectedInvestor
      );
      filteredDrawdowns = filteredDrawdowns.filter(
        (d) => d.investor === selectedInvestor
      );
      filteredDistributions = filteredDistributions.filter(
        (d) => d.investor === selectedInvestor
      );
    }

    if (selectedFund) {
      filteredFunds = filteredFunds.filter((f) => f.fund === selectedFund);
      filteredCasTimeline = filteredCasTimeline.filter(
        (c) => c.fund === selectedFund
      );
      filteredDrawdowns = filteredDrawdowns.filter(
        (d) => d.fund === selectedFund
      );
      filteredDistributions = filteredDistributions.filter(
        (d) => d.fund === selectedFund
      );
    }

    if (selectedCurrency) {
      filteredFunds = filteredFunds.filter(
        (f) => f.currency === selectedCurrency
      );
      filteredCasTimeline = filteredCasTimeline.filter(
        (c) => c.currency === selectedCurrency
      );
      filteredDrawdowns = filteredDrawdowns.filter(
        (d) => d.currency === selectedCurrency
      );
      filteredDistributions = filteredDistributions.filter(
        (d) => d.currency === selectedCurrency
      );
    }

    return {
      funds: filteredFunds,
      casTimeline: filteredCasTimeline,
      drawdowns: filteredDrawdowns,
      distributions: filteredDistributions,
    };
  }, [selectedInvestor, selectedFund, selectedCurrency]);

  // Get unique values for filters
  const investors = useMemo(
    () => [...new Set(RAW_DATA.funds.map((f) => f.investor))],
    []
  );
  const funds = useMemo(
    () => [...new Set(RAW_DATA.funds.map((f) => f.fund))],
    []
  );
  const currencies = useMemo(
    () => [...new Set(RAW_DATA.funds.map((f) => f.currency))],
    []
  );

  // ── Shared derived data (computed once, used by many sections) ──
  const latestCas = useMemo(
    () => getLatestCasPerFund(filteredData.casTimeline),
    [filteredData.casTimeline]
  );

  const distributionsByFundKey = useMemo(() => {
    const byFund = {};
    filteredData.distributions.forEach((dist) => {
      const key = `${dist.fund}||${dist.investor}`;
      byFund[key] = (byFund[key] || 0) + getDistAmount(dist);
    });
    return byFund;
  }, [filteredData.distributions]);

  const firstDrawdownByFund = useMemo(() => {
    const first = {};
    (filteredData.drawdowns || []).forEach((dd) => {
      const key = `${dd.fund}||${dd.investor}`;
      if (!first[key] || dd.date < first[key]) {
        first[key] = dd.date;
      }
    });
    return first;
  }, [filteredData.drawdowns]);

  // Calculate summary metrics
  const summary = useMemo(() => {
    const CURRENT_RATE = FX_DATA.current_rate;

    let totalCommittedEur = 0;
    let totalCommittedUsd = 0;
    let totalDrawnEur = 0;
    let totalDrawnUsd = 0;
    let totalDistributedEur = 0;
    let totalDistributedUsd = 0;
    let totalNavEur = 0;
    let totalNavUsd = 0;

    filteredData.funds.forEach((fund) => {
      if (fund.currency === "EUR") {
        totalCommittedEur += fund.total_commitment || 0;
      } else {
        totalCommittedUsd += fund.total_commitment || 0;
      }
    });

    Object.values(latestCas).forEach((cas) => {
      const currency = cas.currency;
      const drawn = Math.abs(cas.drawn_commitment || 0);
      const nav = cas.nav_closing || 0;

      if (currency === "EUR") {
        totalDrawnEur += drawn;
        totalNavEur += nav;
      } else {
        totalDrawnUsd += drawn;
        totalNavUsd += nav;
      }
    });

    filteredData.distributions.forEach((dist) => {
      const currency = dist.currency;
      const amount = getDistAmount(dist);

      if (currency === "EUR") {
        totalDistributedEur += amount;
      } else {
        totalDistributedUsd += amount;
      }
    });

    // Per-currency TVPI and DPI
    const totalValueEur = totalNavEur + totalDistributedEur;
    const totalValueUsd = totalNavUsd + totalDistributedUsd;

    const tvpiEur = calcTvpi(totalDrawnEur, totalNavEur, totalDistributedEur).toFixed(2);
    const tvpiUsd = calcTvpi(totalDrawnUsd, totalNavUsd, totalDistributedUsd).toFixed(2);
    const dpiEur = calcDpi(totalDrawnEur, totalDistributedEur).toFixed(2);
    const dpiUsd = calcDpi(totalDrawnUsd, totalDistributedUsd).toFixed(2);

    // Overall TVPI and DPI — convert EUR to USD for meaningful cross-currency blending
    const totalDrawnAllUsd = totalDrawnEur * CURRENT_RATE + totalDrawnUsd;
    const totalNavAllUsd = totalNavEur * CURRENT_RATE + totalNavUsd;
    const totalDistAllUsd = totalDistributedEur * CURRENT_RATE + totalDistributedUsd;
    const overallTvpi = calcTvpi(totalDrawnAllUsd, totalNavAllUsd, totalDistAllUsd).toFixed(2);
    const overallDpi = calcDpi(totalDrawnAllUsd, totalDistAllUsd).toFixed(2);

    return {
      totalCommittedEur,
      totalCommittedUsd,
      totalDrawnEur,
      totalDrawnUsd,
      totalDistributedEur,
      totalDistributedUsd,
      totalNavEur,
      totalNavUsd,
      tvpiEur,
      tvpiUsd,
      dpiEur,
      dpiUsd,
      overallTvpi,
      overallDpi,
    };
  }, [filteredData.funds, filteredData.distributions, latestCas]);

  // Prepare cumulative cash flow data (nominal amounts, no FX conversion)
  const cumulativeCashFlowData = useMemo(() => {
    const allDates = new Set();
    const drawdownsByDate = {};
    const distributionsByDate = {};

    filteredData.drawdowns.forEach((dd) => {
      allDates.add(dd.date);
      if (!drawdownsByDate[dd.date]) drawdownsByDate[dd.date] = 0;
      drawdownsByDate[dd.date] += dd.amount || 0;
    });

    filteredData.distributions.forEach((dist) => {
      allDates.add(dist.date);
      if (!distributionsByDate[dist.date]) distributionsByDate[dist.date] = 0;
      distributionsByDate[dist.date] += getDistAmount(dist);
    });

    const sortedDates = Array.from(allDates).sort();
    let cumulativeDrawdowns = 0;
    let cumulativeDistributions = 0;

    return sortedDates.map((date) => {
      cumulativeDrawdowns += drawdownsByDate[date] || 0;
      cumulativeDistributions += distributionsByDate[date] || 0;

      return {
        date: formatDate(date),
        rawDate: date,
        "Paid In": cumulativeDrawdowns,
        "Distributed": cumulativeDistributions,
        "Net Position": cumulativeDistributions - cumulativeDrawdowns,
      };
    });
  }, [filteredData.drawdowns, filteredData.distributions]);

  // Prepare NAV over time data
  const navOverTimeData = useMemo(() => {
    const navByDateAndFund = {};
    const allFundKeys = new Set();

    filteredData.casTimeline.forEach((cas) => {
      if (!navByDateAndFund[cas.date]) {
        navByDateAndFund[cas.date] = {};
      }
      const fundKey = `${cas.fund} (${cas.investor.split(" ")[0]})`;
      allFundKeys.add(fundKey);
      navByDateAndFund[cas.date][fundKey] =
        (navByDateAndFund[cas.date][fundKey] || 0) + (cas.nav_closing || 0);
    });

    const sortedDates = Object.keys(navByDateAndFund).sort();

    // Forward-fill: carry each fund's last known NAV into dates where it's missing
    const lastKnown = {};
    return sortedDates.map((date) => {
      const navData = navByDateAndFund[date];
      const entry = { date: formatDate(date), rawDate: date };

      allFundKeys.forEach((fund) => {
        if (navData[fund] !== undefined) {
          lastKnown[fund] = navData[fund];
          entry[fund] = navData[fund];
        } else if (lastKnown[fund] !== undefined) {
          // Forward-fill from last known value
          entry[fund] = lastKnown[fund];
        }
        // If fund hasn't appeared yet, leave it undefined (fund didn't exist)
      });

      return entry;
    });
  }, [filteredData.casTimeline]);

  // Prepare fund-by-fund table data
  const fundTableData = useMemo(() => {
    const fundStats = {};

    filteredData.funds.forEach((fund) => {
      const key = `${fund.fund}||${fund.investor}`;
      fundStats[key] = {
        fund: fund.fund,
        investor: fund.investor,
        currency: fund.currency,
        commitment: fund.total_commitment,
      };
    });

    // Add CAS data from shared latestCas
    Object.keys(latestCas).forEach((key) => {
      const cas = latestCas[key];
      if (fundStats[key]) {
        fundStats[key].drawn = Math.abs(cas.drawn_commitment || 0);
        fundStats[key].nav = cas.nav_closing || 0;
        fundStats[key].holdingPct = cas.holding_pct || 0;
        fundStats[key].remaining = cas.remaining_commitment || 0;
      }
    });

    // Add distributions from shared distributionsByFundKey
    Object.keys(distributionsByFundKey).forEach((key) => {
      if (fundStats[key]) {
        fundStats[key].distributed = distributionsByFundKey[key];
      }
    });

    // Calculate TVPI and DPI, enrich with classification from fund variables
    const tableData = Object.values(fundStats).map((fs) => {
      const drawn = fs.drawn || 0;
      const nav = fs.nav || 0;
      const distributed = fs.distributed || 0;
      const key = makeKey(fs.fund, fs.investor);
      const v = customVars?.[key];
      const fdKey = `${fs.fund}||${fs.investor}`;
      const firstDrawdownDate = firstDrawdownByFund[fdKey] || null;

      return {
        ...fs,
        tvpi: calcTvpi(drawn, nav, distributed).toFixed(2),
        dpi: calcDpi(drawn, distributed).toFixed(2),
        remainingPct:
          fs.commitment > 0
            ? (((fs.commitment - drawn) / fs.commitment) * 100).toFixed(1)
            : 0,
        fundArea: v?.["Fund Area"] || "Unclassified",
        fundStrategy: v?.["Fund Strategy"] || "Unclassified",
        productType: v?.["Product Type"] || "Unclassified",
        isLeveraged: v?.["Leveraged?"] === "Yes",
        vintageYear: v?.["Vintage Year"] || null,
        firstDrawdownDate,
        totalExposure: Number(v?.["Total Exposure"]) || fs.commitment || 0,
      };
    });

    // Sort
    return tableData.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig, customVars]);

  // Prepare commitment utilization data
  const commitmentUtilizationData = useMemo(() => {
    const fundStats = {};

    filteredData.funds.forEach((fund) => {
      const key = `${fund.fund}||${fund.investor}`;
      fundStats[key] = {
        name: fund.fund,
        commitment: fund.total_commitment,
      };
    });

    Object.keys(latestCas).forEach((key) => {
      const cas = latestCas[key];
      if (fundStats[key]) {
        fundStats[key].drawn = Math.abs(cas.drawn_commitment || 0);
        fundStats[key].remaining = cas.remaining_commitment || 0;
      }
    });

    return Object.values(fundStats).map((fs) => ({
      name: fs.name.substring(0, 30),
      Committed: fs.commitment,
      Drawn: fs.drawn || 0,
      Remaining: fs.remaining || fs.commitment - (fs.drawn || 0),
    }));
  }, [filteredData.funds, latestCas]);

  // Commitment utilization aggregated by holding vehicle (USD converted to EUR at current rate)
  const vehicleCommitmentData = useMemo(() => {
    const CURRENT_RATE = FX_DATA.current_rate;
    const byVehicle = {};

    filteredData.funds.forEach((fund) => {
      const v = fund.investor;
      if (!byVehicle[v]) byVehicle[v] = { name: v, commitment: 0, drawn: 0, remaining: 0, nav: 0 };
      const toUsd = fund.currency === "EUR" ? CURRENT_RATE : 1;
      byVehicle[v].commitment += fund.total_commitment * toUsd;
    });

    Object.values(latestCas).forEach((cas) => {
      const v = cas.investor;
      if (!byVehicle[v]) return;
      const toUsd = cas.currency === "EUR" ? CURRENT_RATE : 1;
      byVehicle[v].drawn += Math.abs(cas.drawn_commitment || 0) * toUsd;
      byVehicle[v].nav += (cas.nav_closing || 0) * toUsd;
    });

    return Object.values(byVehicle).map((v) => ({
      name: vehicleDisplayName(v.name),
      fullName: v.name,
      Drawn: Math.round(v.drawn),
      Unfunded: Math.round(Math.max(0, v.commitment - v.drawn)),
      NAV: Math.round(v.nav),
    }));
  }, [filteredData.funds, latestCas]);

  // ── Total Exposure data (leveraged funds show full exposure, unleveraged = commitment) ──
  const exposureByFundData = useMemo(() => {
    return fundTableData.map((f) => {
      const drawn = f.drawn || 0;
      const te = f.totalExposure || f.commitment || 0;
      return {
        name: f.fund.substring(0, 30),
        Committed: te,
        Drawn: te > 0 && f.commitment > 0 ? Math.round(drawn * (te / f.commitment)) : drawn,
        Remaining: te > 0 && f.commitment > 0 ? Math.round((f.commitment - drawn) * (te / f.commitment)) : (f.commitment - drawn),
      };
    });
  }, [fundTableData]);

  const exposureByVehicleData = useMemo(() => {
    const CURRENT_RATE = FX_DATA.current_rate;
    const byVehicle = {};

    fundTableData.forEach((f) => {
      const v = f.investor;
      if (!byVehicle[v]) byVehicle[v] = { name: v, exposure: 0, drawn: 0, nav: 0 };
      const toUsd = f.currency === "EUR" ? CURRENT_RATE : 1;
      const te = f.totalExposure || f.commitment || 0;
      const ratio = f.commitment > 0 ? te / f.commitment : 1;
      byVehicle[v].exposure += te * toUsd;
      byVehicle[v].drawn += (f.drawn || 0) * ratio * toUsd;
      byVehicle[v].nav += (f.nav || 0) * ratio * toUsd;
    });

    return Object.values(byVehicle).map((v) => ({
      name: vehicleDisplayName(v.name),
      fullName: v.name,
      Drawn: Math.round(v.drawn),
      Unfunded: Math.round(Math.max(0, v.exposure - v.drawn)),
      NAV: Math.round(v.nav),
    }));
  }, [fundTableData]);

  // Prepare FX breakdown data
  const fxBreakdown = useMemo(() => {
    const byFund = {};

    filteredData.funds.forEach((fund) => {
      const key = makeKey(fund.fund, fund.investor);
      if (!byFund[key]) {
        byFund[key] = {
          fund: fund.fund,
          investor: fund.investor,
          USD: 0,
          EUR: 0,
        };
      }
      byFund[key][fund.currency] +=
        fund.total_commitment;
    });

    return Object.values(byFund);
  }, [filteredData.funds]);

  // Color palette
  const colors = [
    "var(--chart-series-1)",
    "var(--chart-series-2)",
    "var(--chart-series-3)",
    "var(--chart-series-4)",
    "var(--chart-series-5)",
    "var(--chart-series-6)",
    "var(--chart-series-7)",
    "var(--chart-series-8)",
  ];

  const fundColors = {};
  fundTableData.forEach((fund, idx) => {
    fundColors[makeKey(fund.fund, fund.investor)] = colors[idx % colors.length];
    // Also keep fund-only entry for backward compat with charts that use fund name as key
    if (!fundColors[fund.fund]) fundColors[fund.fund] = colors[idx % colors.length];
  });

  // FX: filter fund impact by selected investor
  const fxFundImpact = useMemo(() => {
    let items = FX_DATA.fx_fund_impact;
    if (selectedInvestor) items = items.filter(f => f.investor === selectedInvestor);
    return items;
  }, [selectedInvestor, propFxData]);

  const fxVehicleSummary = useMemo(() => {
    if (!selectedInvestor) return FX_DATA.vehicle_fx_summary;
    return FX_DATA.vehicle_fx_summary.filter(v => v.investor === selectedInvestor);
  }, [selectedInvestor, propFxData]);

  // USD NAV history in EUR — flatten to per-fund series for chart
  // Build a display name that distinguishes regular vs leveraged
  const fundDisplayName = (fund, investor) => {
    // Check if there's another entry with same base name but different leverage status
    const baseName = fund.replace(" Leveraged", "");
    const isLeveraged = fund.includes("Leveraged");
    const hasBoth = FX_DATA.fx_fund_impact.some(f =>
      f.fund.replace(" Leveraged", "") === baseName && f.fund.includes("Leveraged") !== isLeveraged
    ) || FX_DATA.fx_fund_impact.filter(f => f.fund === fund).length > 1;
    // Always show full name; add vehicle hint if same fund name appears with different vehicles
    const sameNameDiffVehicle = FX_DATA.fx_fund_impact.filter(f => f.fund === fund).length > 1;
    let label = fund;
    if (!isLeveraged && FX_DATA.fx_fund_impact.some(f => f.fund === fund + " Leveraged")) {
      label = fund + " (Regular)";
    }
    if (sameNameDiffVehicle) {
      label += " — " + vehicleDisplayName(investor);
    }
    return label;
  };

  const eurNavUsdSeries = useMemo(() => {
    const allDates = new Set();
    const byFund = {};
    Object.entries(FX_DATA.eur_nav_in_usd).forEach(([key, records]) => {
      if (!Array.isArray(records)) return;
      const [fundName, investor] = key.split("||");
      const label = fundDisplayName(fundName, investor);
      if (selectedInvestor && !key.includes(selectedInvestor)) return;
      byFund[label] = {};
      records.forEach(r => {
        allDates.add(r.date);
        byFund[label][r.date] = { nav_usd: r.nav_usd, nav_usd_at_entry_rate: r.nav_usd_at_entry_rate, rate: r.rate };
      });
    });
    return { byFund, allDates: [...allDates].sort() };
  }, [selectedInvestor, propFxData]);



  // ── Cash Flow by Vehicle ──
  const vehicleCashFlowData = useMemo(() => {
    const vehicles = {};
    filteredData.funds.forEach((f) => { vehicles[f.investor] = true; });

    const allDates = new Set();
    const ddByDateVehicle = {};
    const distByDateVehicle = {};

    filteredData.drawdowns.forEach((dd) => {
      allDates.add(dd.date);
      const k = makeKey(dd.date, dd.investor);
      ddByDateVehicle[k] = (ddByDateVehicle[k] || 0) + (dd.amount || 0);
    });
    filteredData.distributions.forEach((d) => {
      allDates.add(d.date);
      const k = makeKey(d.date, d.investor);
      distByDateVehicle[k] = (distByDateVehicle[k] || 0) + getDistAmount(d);
    });

    const sortedDates = Array.from(allDates).sort();
    const cumDD = {};
    const cumDist = {};
    Object.keys(vehicles).forEach((v) => { cumDD[v] = 0; cumDist[v] = 0; });

    return sortedDates.map((date) => {
      const entry = { date: formatDate(date), rawDate: date };
      Object.keys(vehicles).forEach((v) => {
        cumDD[v] += ddByDateVehicle[makeKey(date, v)] || 0;
        cumDist[v] += distByDateVehicle[makeKey(date, v)] || 0;
        const short = vehicleDisplayName(v);
        entry[short + " Paid In"] = cumDD[v];
        entry[short + " Distributed"] = cumDist[v];
        entry[short + " Net"] = cumDist[v] - cumDD[v];
      });
      return entry;
    });
  }, [filteredData.drawdowns, filteredData.distributions, filteredData.funds]);

  // ── NAV Over Time by Vehicle ──
  const vehicleNavOverTimeData = useMemo(() => {
    const navByDateVehicle = {};
    const allVehicles = new Set();

    filteredData.casTimeline.forEach((cas) => {
      if (!navByDateVehicle[cas.date]) navByDateVehicle[cas.date] = {};
      const v = cas.investor;
      allVehicles.add(v);
      navByDateVehicle[cas.date][v] = (navByDateVehicle[cas.date][v] || 0) + (cas.nav_closing || 0);
    });

    const sortedDates = Object.keys(navByDateVehicle).sort();
    const lastKnown = {};

    return sortedDates.map((date) => {
      const row = navByDateVehicle[date];
      const entry = { date: formatDate(date), rawDate: date };
      allVehicles.forEach((v) => {
        if (row[v] !== undefined) {
          lastKnown[v] = row[v];
          entry[vehicleDisplayName(v)] = row[v];
        } else if (lastKnown[v] !== undefined) {
          entry[vehicleDisplayName(v)] = lastKnown[v];
        }
      });
      return entry;
    });
  }, [filteredData.casTimeline]);

  // ── FX Exposure by Vehicle ──
  const fxByVehicle = useMemo(() => {
    const byVehicle = {};
    filteredData.funds.forEach((fund) => {
      const v = fund.investor;
      if (!byVehicle[v]) byVehicle[v] = { vehicle: vehicleDisplayName(v), EUR: 0, USD: 0 };
    });
    Object.values(latestCas).forEach((cas) => {
      const v = cas.investor;
      if (!byVehicle[v]) return;
      const nav = cas.nav_closing || 0;
      if (cas.currency === "EUR") byVehicle[v].EUR += nav;
      else byVehicle[v].USD += nav;
    });
    return Object.values(byVehicle);
  }, [filteredData.funds, latestCas]);

  // ── Missing CAS Statement Detection ──
  const missingCasData = useMemo(() => {
    // Build a map of which quarters each fund/investor has CAS data for
    const casQuarters = {};
    const allQuarters = new Set();

    filteredData.casTimeline.forEach((cas) => {
      const key = makeKey(cas.fund, cas.investor);
      if (!casQuarters[key]) casQuarters[key] = new Set();
      casQuarters[key].add(cas.date);
      allQuarters.add(cas.date);
    });

    // Determine the expected quarter-end dates from each fund's first CAS to today
    const quarterEnds = (startDate, endDate) => {
      const quarters = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Align to quarter end
      let year = start.getFullYear();
      let qMonth = Math.floor(start.getMonth() / 3) * 3 + 2; // 2=Mar, 5=Jun, 8=Sep, 11=Dec
      while (true) {
        const qEnd = new Date(year, qMonth + 1, 0); // last day of quarter month
        if (qEnd > end) break;
        if (qEnd >= start) {
          const dateStr = qEnd.toISOString().split("T")[0];
          quarters.push(dateStr);
        }
        qMonth += 3;
        if (qMonth > 11) { qMonth -= 12; year++; }
      }
      return quarters;
    };

    // Today's quarter (most recent completed quarter)
    const now = new Date();
    const curQ = Math.floor((now.getMonth()) / 3);
    const lastQMonth = curQ * 3 - 1; // last completed quarter's end month (0-indexed)
    const lastQYear = lastQMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const adjMonth = lastQMonth < 0 ? 11 : lastQMonth;
    const lastCompletedQ = new Date(lastQYear, adjMonth + 1, 0).toISOString().split("T")[0];

    const missingByFund = [];
    let totalMissing = 0;

    filteredData.funds.forEach((f) => {
      const key = makeKey(f.fund, f.investor);
      const fundQuarters = casQuarters[key];

      if (!fundQuarters || fundQuarters.size === 0) {
        // No CAS data at all
        missingByFund.push({
          fund: f.fund,
          investor: f.investor,
          firstCas: null,
          lastCas: null,
          totalExpected: 0,
          totalPresent: 0,
          missingQuarters: [],
          noCasData: true,
        });
        totalMissing++;
        return;
      }

      const sortedDates = [...fundQuarters].sort();
      const firstCas = sortedDates[0];
      const lastCas = sortedDates[sortedDates.length - 1];

      // Expected quarters from first CAS through last completed quarter
      const expected = quarterEnds(firstCas, lastCompletedQ);
      const missing = expected.filter(q => !fundQuarters.has(q));

      if (missing.length > 0) {
        missingByFund.push({
          fund: f.fund,
          investor: f.investor,
          firstCas,
          lastCas,
          totalExpected: expected.length,
          totalPresent: fundQuarters.size,
          missingQuarters: missing,
          noCasData: false,
        });
        totalMissing += missing.length;
      }
    });

    return { missingByFund, totalMissing, lastCompletedQ };
  }, [filteredData.casTimeline, filteredData.funds]);

    // ── Cash Forecast ──
  const cashForecastData = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const fundForecasts = [];

    fundTableData.forEach((f) => {
      const key = makeKey(f.fund, f.investor);
      const v = customVars ? customVars[key] : null;

      const isLev = v ? v["Leveraged?"] === "Yes" : f.fund.includes("Leveraged");
      const myCommitment = f.commitment || 0;
      const drawn = f.drawn || 0;
      const nav = f.nav || 0;
      const distributed = f.distributed || 0;
      const remaining = Math.max(0, myCommitment - drawn);

      // Leverage params — 3-source capital structure
      const totalExposure = (v && Number(v["Total Exposure"]))
        ? Number(v["Total Exposure"])
        : (isLev ? myCommitment * DEFAULT_LEVERAGE_RATIO : myCommitment);
      const coInvest = totalExposure - myCommitment;

      // Capital stack from shared constants
      const { participantAmt, eqtPerfAmt, sebAmt } = calcCapitalStack(totalExposure, myCommitment, isLev);
      // EQT Perf rate: per-fund slider override, default from constant
      const eqtPerfRate = (eqtPerfRates[key] !== undefined) ? eqtPerfRates[key] : DEFAULT_EQT_PERF_RATE;

      // Target MOIC from fund variables — NO DEFAULTS. User must set targets.
      const hasExcelMoic = v && Number(v["Target Fund MOIC"]) > 0;
      const targetFundMoic = hasExcelMoic ? Number(v["Target Fund MOIC"]) : null;
      const payoutYears = (v && Number(v["Payout Years"])) || DEFAULT_PAYOUT_YEARS;
      const hasTargets = hasExcelMoic;

      // Tax allocation from Excel (Roth = tax-free, Traditional IRA = tax-deferred, Taxable = taxed now)
      const rothPct = (v && Number(v["Roth %"]) >= 0) ? Number(v["Roth %"]) / 100 : 0;
      const tradIraPct = (v && Number(v["Traditional IRA %"]) >= 0) ? Number(v["Traditional IRA %"]) / 100 : 0;
      const taxablePct = (v && Number(v["Taxable %"]) >= 0) ? Number(v["Taxable %"]) / 100 : 1;

      // Determine vintage year (= First Drawdown in Excel) from first drawdown
      const fundDrawdowns = getFundDrawdowns(filteredData.drawdowns, f.fund, f.investor);
      const vintageYear = (v && Number(v["Vintage Year"]))
        || (fundDrawdowns.length > 0 ? parseInt(fundDrawdowns[0].date.substring(0, 4)) : currentYear);

      // First Investment Year: when fund starts deploying capital (distinct from First Drawdown)
      // Excel: simulation timeline starts at First Investment, employee drawdowns start at First Drawdown
      const firstInvestmentYear = getFirstInvestmentYear(v, vintageYear);

      // Fund lifecycle phases (from first investment, not first drawdown)
      const investmentEnd = firstInvestmentYear + INVESTMENT_PACE.length - 1;
      // Per-fund holding period (from fund_variables, with global +/- adjustment)
      const avgHoldingPeriod = getEffectiveHoldingPeriod(key, v);
      const holdingEnd = firstInvestmentYear + INVESTMENT_PACE.length - 1 + avgHoldingPeriod;
      const payoutStart = holdingEnd;
      const payoutEnd = payoutStart + payoutYears;
      const maturityYear = payoutEnd;

      // Calculate expected LP return from fund MOIC
      const baseFundMoic = targetFundMoic || null;
      const effectiveFundMoic = (fundMoicOverrides[key] !== undefined) ? fundMoicOverrides[key] : baseFundMoic;

      // ── Year-by-year fund simulation using shared function ──
      const bankInterestRate = getBankInterestRate(v);
      const grossFundReturn = effectiveFundMoic ? totalExposure * INVESTMENT_LEVEL * effectiveFundMoic : 0;

      let projectedSebInterest = 0, projectedEqtPerfInterest = 0, projectedTotalInterest = 0;
      let lpNetReturn = 0;
      let sebRepaid = 0, eqtPerfRepaid = 0, participantRepaid = 0;
      let sebGains = 0, eqtPerfGains = 0, participantGains = 0;
      let simInterestByYear = {};
      let simDistributionsByYear = {};

      if (effectiveFundMoic && isLev) {
        const simResult = simulateLeveragedFund({
          totalExposure, sebAmt, eqtPerfRate, bankInterestRate,
          firstInvestmentYear, vintageYear, avgHoldingPeriod,
          effectiveFundMoic, participantAmt,
        });
        ({ projectedSebInterest, projectedEqtPerfInterest, projectedTotalInterest,
           lpNetReturn, sebRepaid, eqtPerfRepaid, participantRepaid,
           sebGains, eqtPerfGains, participantGains, simInterestByYear, simDistributionsByYear } = simResult);
      } else if (effectiveFundMoic) {
        // Non-leveraged: vintage-year distribution model
        // Each tranche of INVESTMENT_PACE returns after avgHoldingPeriod years
        lpNetReturn = Math.max(0, grossFundReturn);
        participantRepaid = myCommitment;
        participantGains = Math.max(0, grossFundReturn - myCommitment);
        // Build per-year distributions matching Excel logic
        for (let i = 0; i < INVESTMENT_PACE.length; i++) {
          const returnYear = firstInvestmentYear + i + avgHoldingPeriod;
          const inv = myCommitment * INVESTMENT_PACE[i];
          if (inv > 0) {
            const vintageReturn = inv * effectiveFundMoic;
            simDistributionsByYear[returnYear] = (simDistributionsByYear[returnYear] || 0) + vintageReturn;
          }
        }
      }
      // Implied LP MOIC on their own commitment (participantAmt for leveraged, myCommitment for non-lev)
      const lpBasis = isLev ? participantAmt : myCommitment;
      const impliedLpMoic = (effectiveFundMoic && lpBasis > 0) ? lpNetReturn / lpBasis : 0;
      const currentTvpi = drawn > 0 ? (nav + distributed) / drawn : 0;

      // Total payout to LP = lpNetReturn (already accounts for leverage + interest deduction)
      const totalReturn = lpNetReturn;
      const remainingPayout = Math.max(0, totalReturn - distributed);

      // After-tax computation per payout year
      // Only the GAIN portion is taxable (payout - cost basis return)
      // Cost basis = participant contribution (LP's own capital); gain = totalReturn - cost basis
      const totalGain = Math.max(0, totalReturn - lpBasis);
      const gainFraction = totalReturn > 0 ? totalGain / totalReturn : 0;

      // Build year-by-year cash flows
      const yearlyFlows = {};

      // DRAWDOWNS: already drawn amounts by year (actual)
      const actualDrawnByYear = {};
      fundDrawdowns.forEach(dd => {
        const yr = parseInt(dd.date.substring(0, 4));
        actualDrawnByYear[yr] = (actualDrawnByYear[yr] || 0) + (dd.amount || dd.net_drawdown || 0);
      });

      // Project future drawdowns for remaining commitment
      if (remaining > 0) {
        if (isLev) {
          const numPayments = 3;
          const perPayment = remaining / numPayments;
          for (let i = 0; i < numPayments; i++) {
            const yr = currentYear + i;
            if (!yearlyFlows[yr]) yearlyFlows[yr] = { drawdown: 0, interest: 0, payout: 0 };
            yearlyFlows[yr].drawdown += perPayment;
          }
        } else {
          const numPayments = 2;
          const perPayment = remaining / numPayments;
          for (let i = 0; i < numPayments; i++) {
            const yr = currentYear + i;
            if (!yearlyFlows[yr]) yearlyFlows[yr] = { drawdown: 0, interest: 0, payout: 0 };
            yearlyFlows[yr].drawdown += perPayment;
          }
        }
      }

      // INTEREST COSTS (leveraged funds only) - from actual simulation results
      if (isLev && simInterestByYear) {
        Object.entries(simInterestByYear).forEach(([yr, intData]) => {
          const y = Number(yr);
          if (y < currentYear) return;
          if (!yearlyFlows[y]) yearlyFlows[y] = { drawdown: 0, interest: 0, sebInterest: 0, eqtPerfInterest: 0, payout: 0 };
          yearlyFlows[y].sebInterest = (yearlyFlows[y].sebInterest || 0) + intData.sebInterest;
          yearlyFlows[y].eqtPerfInterest = (yearlyFlows[y].eqtPerfInterest || 0) + intData.eqtPerfInterest;
          yearlyFlows[y].interest += intData.total;
        });
      }

      // PAYOUTS - vintage-year distribution model (matching Excel logic)
      // Each investment tranche returns after holdingPeriod years; distributions flow as exits occur
      // Subtract already-received actual distributions proportionally
      if (effectiveFundMoic && Object.keys(simDistributionsByYear).length > 0) {
        // Scale projected distributions to account for already-distributed amounts
        const totalSimDist = Object.values(simDistributionsByYear).reduce((s, v) => s + v, 0);
        const scaleFactor = totalSimDist > 0 ? remainingPayout / totalSimDist : 0;
        Object.entries(simDistributionsByYear).forEach(([yr, amt]) => {
          const y = Number(yr);
          if (y < currentYear) return; // skip past years — already captured as actuals
          const scaledAmt = amt * scaleFactor;
          if (scaledAmt <= 0) return;
          if (!yearlyFlows[y]) yearlyFlows[y] = { drawdown: 0, interest: 0, payout: 0, tax: 0, isProjected: true };
          yearlyFlows[y].payout += scaledAmt;
          yearlyFlows[y].isProjected = true;
          // Tax on the gain portion of the taxable allocation
          const taxableGain = scaledAmt * gainFraction * taxablePct;
          yearlyFlows[y].taxableGain = (yearlyFlows[y].taxableGain || 0) + taxableGain;
          const fedRate = getFedRate(federalCapGainsRates, y, currentYear + 11);
          const stateRate = getStateRate(stateCapGainsRates, y, currentYear + 11);
          const yearTax = taxableGain * (fedRate + stateRate);
          yearlyFlows[y].tax = (yearlyFlows[y].tax || 0) + yearTax;
        });
      }

      // Add actual historical drawdowns
      Object.entries(actualDrawnByYear).forEach(([yr, amt]) => {
        const y = parseInt(yr);
        if (!yearlyFlows[y]) yearlyFlows[y] = { drawdown: 0, interest: 0, payout: 0, isActual: true };
        yearlyFlows[y].drawdown = amt;
        yearlyFlows[y].isActual = true;
      });

      // Add actual historical distributions
      const fundDistributions = (filteredData.distributions || [])
        .filter(d => d.fund === f.fund && d.investor === f.investor);
      const actualDistByYear = {};
      fundDistributions.forEach(d => {
        const yr = parseInt(d.date.substring(0, 4));
        actualDistByYear[yr] = (actualDistByYear[yr] || 0) + getDistAmount(d);
      });
      Object.entries(actualDistByYear).forEach(([yr, amt]) => {
        const y = parseInt(yr);
        if (!yearlyFlows[y]) yearlyFlows[y] = { drawdown: 0, interest: 0, payout: 0, isActual: true };
        yearlyFlows[y].payout += amt;
        yearlyFlows[y].isActual = true;
      });

      // Compute total projected tax across all payout years
      const totalProjectedTax = Object.values(yearlyFlows).reduce((s, yf) => s + (yf.tax || 0), 0);
      const afterTaxReturn = Math.max(0, lpNetReturn - totalProjectedTax);
      const afterTaxMoic = (lpBasis > 0 && effectiveFundMoic) ? afterTaxReturn / lpBasis : 0;

      fundForecasts.push({
        fund: f.fund,
        investor: f.investor,
        currency: f.currency,
        isLeveraged: isLev,
        myCommitment,
        lpBasis,
        drawn,
        remaining,
        nav,
        distributed,
        totalExposure,
        coInvest,
        // Capital stack
        participantAmt, eqtPerfAmt, sebAmt,
        eqtPerfRate,
        projectedSebInterest, projectedEqtPerfInterest,
        // Waterfall results
        sebRepaid, eqtPerfRepaid, participantRepaid,
        sebGains, eqtPerfGains, participantGains,
        fundMoic: effectiveFundMoic,
        baseFundMoic,
        moicOverridden: fundMoicOverrides[key] !== undefined,
        grossFundReturn,
        projectedTotalInterest,
        lpNetReturn,
        impliedLpMoic,
        currentTvpi,
        hasTargets: hasTargets || fundMoicOverrides[key] !== undefined,
        moicSource: fundMoicOverrides[key] !== undefined ? "Simulation" : (hasExcelMoic ? "Set" : "Not set"),
        vintageYear,
        firstInvestmentYear,
        avgHoldingPeriod,
        investmentEnd,
        holdingEnd,
        payoutStart,
        payoutEnd,
        maturityYear,
        totalReturn,
        remainingPayout,
        payoutYears,
        yearlyFlows,
        simDistributionsByYear,
        // Tax fields
        rothPct, tradIraPct, taxablePct,
        totalGain, gainFraction,
        totalProjectedTax, afterTaxReturn, afterTaxMoic,
      });
    });

    // Local simulation funds (forecast tab only)
    if (pageTab === "forecast") simulatedFunds.forEach((sf) => {
      const simKey = `SIM::${sf.id}`;
      const fundName = (sf.fundName || "").trim() || "Simulated Fund";
      const investor = "Simulation";
      const firstDrawdownYear = Math.max(currentYear, Math.min(currentYear + 11, parseInt(sf.firstDrawdownYear, 10) || currentYear));
      const myCommitment = Math.max(0, Number(sf.commitment) || 0);
      const simParticipantPct = Number(sf.participantPct) || 15;
      const totalExposure = simParticipantPct > 0 && simParticipantPct < 100
        ? myCommitment / (simParticipantPct / 100)
        : myCommitment;
      const targetFundMoic = Math.max(0, Number(sf.targetMoic) || 0);
      const isLev = totalExposure > myCommitment;

      const drawn = 0;
      const nav = 0;
      const distributed = 0;
      const remaining = myCommitment;

      const { participantAmt, eqtPerfAmt, sebAmt } = calcCapitalStack(totalExposure, myCommitment, isLev);
      const eqtPerfRate = (eqtPerfRates[simKey] !== undefined) ? eqtPerfRates[simKey] : DEFAULT_EQT_PERF_RATE;

      const payoutYears = DEFAULT_PAYOUT_YEARS;
      const hasTargets = targetFundMoic > 0;
      const rothPct = 0;
      const tradIraPct = 0;
      const taxablePct = 1;

      const vintageYear = firstDrawdownYear;
      const firstInvestmentYear = vintageYear - 1;
      const investmentEnd = firstInvestmentYear + INVESTMENT_PACE.length - 1;
      const avgHoldingPeriod = Math.max(1, DEFAULT_HOLDING_PERIOD + holdingPeriodAdjust);
      const holdingEnd = firstInvestmentYear + INVESTMENT_PACE.length - 1 + avgHoldingPeriod;
      const payoutStart = holdingEnd;
      const payoutEnd = payoutStart + payoutYears;
      const maturityYear = payoutEnd;

      const baseFundMoic = hasTargets ? targetFundMoic : null;
      const effectiveFundMoic = (fundMoicOverrides[simKey] !== undefined) ? fundMoicOverrides[simKey] : baseFundMoic;

      const simBankInterestRate = DEFAULT_BANK_INTEREST_RATE / 100;
      const grossFundReturn = effectiveFundMoic ? totalExposure * INVESTMENT_LEVEL * effectiveFundMoic : 0;

      let projectedSebInterest = 0, projectedEqtPerfInterest = 0, projectedTotalInterest = 0;
      let lpNetReturn = 0;
      let sebRepaid = 0, eqtPerfRepaid = 0, participantRepaid = 0;
      let sebGains = 0, eqtPerfGains = 0, participantGains = 0;
      let simInterestByYear = {};
      let simDistributionsByYear = {};

      if (effectiveFundMoic && isLev) {
        const simResult = simulateLeveragedFund({
          totalExposure, sebAmt, eqtPerfRate,
          bankInterestRate: simBankInterestRate,
          firstInvestmentYear, vintageYear, avgHoldingPeriod,
          effectiveFundMoic, participantAmt,
        });
        ({ projectedSebInterest, projectedEqtPerfInterest, projectedTotalInterest,
           lpNetReturn, sebRepaid, eqtPerfRepaid, participantRepaid,
           sebGains, eqtPerfGains, participantGains, simInterestByYear, simDistributionsByYear } = simResult);
      } else if (effectiveFundMoic) {
        lpNetReturn = Math.max(0, grossFundReturn);
        participantRepaid = myCommitment;
        participantGains = Math.max(0, grossFundReturn - myCommitment);
        for (let i = 0; i < INVESTMENT_PACE.length; i++) {
          const returnYear = firstInvestmentYear + i + avgHoldingPeriod;
          const inv = myCommitment * INVESTMENT_PACE[i];
          if (inv > 0) {
            const vintageReturn = inv * effectiveFundMoic;
            simDistributionsByYear[returnYear] = (simDistributionsByYear[returnYear] || 0) + vintageReturn;
          }
        }
      }

      const lpBasis = isLev ? participantAmt : myCommitment;
      const impliedLpMoic = (effectiveFundMoic && lpBasis > 0) ? lpNetReturn / lpBasis : 0;
      const currentTvpi = 0;
      const totalReturn = lpNetReturn;
      const remainingPayout = Math.max(0, totalReturn - distributed);
      const totalGain = Math.max(0, totalReturn - lpBasis);
      const gainFraction = totalReturn > 0 ? totalGain / totalReturn : 0;

      const yearlyFlows = {};
      if (remaining > 0) {
        const numPayments = isLev ? EMPLOYEE_DRAWDOWN_PAYMENTS : REGULAR_DRAWDOWN_PAYMENTS;
        const perPayment = remaining / numPayments;
        for (let i = 0; i < numPayments; i++) {
          const yr = firstDrawdownYear + i;
          if (!yearlyFlows[yr]) yearlyFlows[yr] = { drawdown: 0, interest: 0, payout: 0 };
          yearlyFlows[yr].drawdown += perPayment;
        }
      }

      // Interest in yearly flows from actual simulation
      if (isLev && simInterestByYear) {
        Object.entries(simInterestByYear).forEach(([yr, intData]) => {
          const y = Number(yr);
          if (!yearlyFlows[y]) yearlyFlows[y] = { drawdown: 0, interest: 0, sebInterest: 0, eqtPerfInterest: 0, payout: 0 };
          yearlyFlows[y].sebInterest = (yearlyFlows[y].sebInterest || 0) + intData.sebInterest;
          yearlyFlows[y].eqtPerfInterest = (yearlyFlows[y].eqtPerfInterest || 0) + intData.eqtPerfInterest;
          yearlyFlows[y].interest += intData.total;
        });
      }

      // Vintage-year distributions (matching Excel logic)
      if (effectiveFundMoic && Object.keys(simDistributionsByYear).length > 0) {
        const totalSimDist = Object.values(simDistributionsByYear).reduce((s, v) => s + v, 0);
        const scaleFactor = totalSimDist > 0 ? remainingPayout / totalSimDist : 0;
        Object.entries(simDistributionsByYear).forEach(([yr, amt]) => {
          const y = Number(yr);
          const scaledAmt = amt * scaleFactor;
          if (scaledAmt <= 0) return;
          if (!yearlyFlows[y]) yearlyFlows[y] = { drawdown: 0, interest: 0, payout: 0, tax: 0, isProjected: true };
          yearlyFlows[y].payout += scaledAmt;
          yearlyFlows[y].isProjected = true;
          const taxableGain = scaledAmt * gainFraction * taxablePct;
          yearlyFlows[y].taxableGain = (yearlyFlows[y].taxableGain || 0) + taxableGain;
          const fedRate = getFedRate(federalCapGainsRates, y, currentYear + 11);
          const stateRate = getStateRate(stateCapGainsRates, y, currentYear + 11);
          const yearTax = taxableGain * (fedRate + stateRate);
          yearlyFlows[y].tax = (yearlyFlows[y].tax || 0) + yearTax;
        });
      }

      const totalProjectedTax = Object.values(yearlyFlows).reduce((s, yf) => s + (yf.tax || 0), 0);
      const afterTaxReturn = Math.max(0, lpNetReturn - totalProjectedTax);
      const afterTaxMoic = (lpBasis > 0 && effectiveFundMoic) ? afterTaxReturn / lpBasis : 0;

      fundForecasts.push({
        fund: `${fundName} (Sim)`,
        investor,
        currency: "USD",
        isLeveraged: isLev,
        myCommitment,
        lpBasis,
        drawn,
        remaining,
        nav,
        distributed,
        totalExposure,
        coInvest: totalExposure - myCommitment,
        participantAmt, eqtPerfAmt, sebAmt,
        eqtPerfRate,
        projectedSebInterest, projectedEqtPerfInterest,
        sebRepaid, eqtPerfRepaid, participantRepaid,
        sebGains, eqtPerfGains, participantGains,
        fundMoic: effectiveFundMoic,
        baseFundMoic,
        moicOverridden: fundMoicOverrides[simKey] !== undefined,
        grossFundReturn,
        projectedTotalInterest,
        lpNetReturn,
        impliedLpMoic,
        currentTvpi,
        hasTargets: hasTargets || fundMoicOverrides[simKey] !== undefined,
        moicSource: "Simulation",
        vintageYear,
        firstInvestmentYear,
        avgHoldingPeriod,
        investmentEnd,
        holdingEnd,
        payoutStart,
        payoutEnd,
        maturityYear,
        totalReturn,
        remainingPayout,
        payoutYears,
        yearlyFlows,
        simDistributionsByYear,
        rothPct, tradIraPct, taxablePct,
        totalGain, gainFraction,
        totalProjectedTax, afterTaxReturn, afterTaxMoic,
      });
    });

    // Determine the full year range (include historical)
    let minYear = currentYear;
    let maxYear = currentYear;
    fundForecasts.forEach(f => {
      Object.keys(f.yearlyFlows).forEach(yr => {
        const y = parseInt(yr);
        if (y < minYear) minYear = y;
        if (y > maxYear) maxYear = y;
      });
      if (f.maturityYear > maxYear) maxYear = f.maturityYear;
    });

    // Build aggregate timeline from first actual data through projections
    const timeline = [];
    for (let yr = minYear; yr <= maxYear; yr++) {
      const isHistorical = yr < currentYear;
      const entry = {
        year: yr,
        drawdowns: 0,
        interest: 0,
        payouts: 0,
        tax: 0,
        taxableGain: 0,  // raw taxable gain before interest deduction
        netCash: 0,
        netCashAfterTax: 0,
        isHistorical,
        // Split actual vs projected for stacked display
        actualDrawdowns: 0,
        actualPayouts: 0,
        projectedDrawdowns: 0,
        projectedInterest: 0,
        projectedPayouts: 0,
        projectedTax: 0,
      };
      const byVehicle = {};
      const byFund = {};

      fundForecasts.forEach(ff => {
        const flow = ff.yearlyFlows[yr] || { drawdown: 0, interest: 0, payout: 0, tax: 0 };
        entry.drawdowns += flow.drawdown;
        entry.interest += flow.interest;
        entry.payouts += flow.payout;
        entry.tax += (flow.tax || 0);
        entry.taxableGain += (flow.taxableGain || 0);

        if (flow.isActual || isHistorical) {
          entry.actualDrawdowns -= flow.drawdown;
          entry.actualPayouts += flow.payout;
        } else {
          entry.projectedDrawdowns -= flow.drawdown;
          entry.projectedInterest -= flow.interest;
          entry.projectedPayouts += flow.payout;
          entry.projectedTax -= (flow.tax || 0);
        }

        // Vehicle aggregation
        const vName = ff.investor;
        if (!byVehicle[vName]) byVehicle[vName] = { drawdowns: 0, interest: 0, payouts: 0, tax: 0 };
        byVehicle[vName].drawdowns += flow.drawdown;
        byVehicle[vName].interest += flow.interest;
        byVehicle[vName].payouts += flow.payout;
        byVehicle[vName].tax += (flow.tax || 0);

        // Fund aggregation
        const fKey = ff.fund;
        if (!byFund[fKey]) byFund[fKey] = { drawdowns: 0, interest: 0, payouts: 0, tax: 0 };
        byFund[fKey].drawdowns += flow.drawdown;
        byFund[fKey].interest += flow.interest;
        byFund[fKey].payouts += flow.payout;
        byFund[fKey].tax += (flow.tax || 0);
      });

      // Cashflow contract:
      // - `payouts` are modeled as net LP proceeds (waterfall already allocates financing costs).
      // - `interest` is tracked for visualization/tax deduction context, but should not be
      //   subtracted again from net cash to avoid double-counting financing drag.
      entry.netCash = entry.payouts - entry.drawdowns;
      entry.byVehicle = byVehicle;
      entry.byFund = byFund;
      timeline.push(entry);
    }

    // ── Investment Interest Deduction with Carryforward ──
    // IRS: interest on investment loans deductible against net investment income.
    // Limited to net investment income for the year; excess carries forward.
    let interestCarryforward = 0;
    timeline.forEach(t => {
      // Total investment interest paid this year (SEB + EQT Perf on leveraged funds)
      const yearInterest = t.interest;
      // Net investment income = taxable gains from PE distributions + other investment income
      const otherIncome = otherInvestmentIncome[t.year] || 0;
      // Raw taxable gain this year (aggregated from per-fund calculations)
      const rawTaxableGain = t.taxableGain || 0;
      // Total deductible interest = this year's interest + any carryforward from prior years
      const totalDeductibleInterest = yearInterest + interestCarryforward;
      // Net investment income pool = PE taxable gains + other income
      const netInvestmentIncome = rawTaxableGain + otherIncome;
      // Deduction is limited to net investment income (can't create a loss)
      const deductionUsed = Math.min(totalDeductibleInterest, netInvestmentIncome);
      // Carryforward = unused portion rolls to next year
      interestCarryforward = Math.max(0, totalDeductibleInterest - deductionUsed);
      // Recalculate tax: reduce taxable gain by the deduction (but not below zero)
      const adjustedTaxableGain = Math.max(0, rawTaxableGain - deductionUsed);
      const fedRate = getFedRate(federalCapGainsRates, t.year);
      const stateRate = getStateRate(stateCapGainsRates, t.year);
      const adjustedTax = adjustedTaxableGain * (fedRate + stateRate);
      // Track deduction details on the timeline entry
      t.rawTax = t.tax;
      t.interestDeduction = deductionUsed;
      t.taxSavings = Math.max(0, t.tax - adjustedTax);
      t.tax = adjustedTax;
      t.projectedTax = Math.min(0, t.projectedTax + t.taxSavings);
      t.interestCarryforward = interestCarryforward;
      t.netCashAfterTax = t.netCash - t.tax;
    });

    // Cumulative net cash (pre-tax and after-tax)
    let cumulative = 0;
    let cumulativeAfterTax = 0;
    timeline.forEach(t => {
      cumulative += t.netCash;
      cumulativeAfterTax += t.netCashAfterTax;
      t.cumulativeNet = cumulative;
      t.cumulativeNetAfterTax = cumulativeAfterTax;
    });

    // Total deduction impact
    const totalTaxSavings = timeline.reduce((s, t) => s + (t.taxSavings || 0), 0);
    const finalCarryforward = timeline.length > 0 ? timeline[timeline.length - 1].interestCarryforward : 0;
    const totalAfterTaxReturn = fundForecasts.reduce((s, f) => s + f.afterTaxReturn, 0);
    const totalCommitment = fundForecasts.reduce((s, f) => s + f.myCommitment, 0);
    const totalAfterTaxGain = totalAfterTaxReturn - totalCommitment;
    const finalCumulativeNet = timeline.length > 0 ? timeline[timeline.length - 1].cumulativeNet : 0;
    const finalCumulativeNetAfterTax = timeline.length > 0 ? timeline[timeline.length - 1].cumulativeNetAfterTax : 0;
    const reconciliationDeltaAfterTax = finalCumulativeNetAfterTax - totalAfterTaxGain;
    const reconciliationDeltaPreTax = finalCumulativeNet - fundForecasts.reduce((s, f) => s + f.lpNetReturn - f.myCommitment, 0);

    // Count how many funds have targets set
    const fundsWithTargets = fundForecasts.filter(f => f.hasTargets).length;
    const fundsWithoutTargets = fundForecasts.filter(f => !f.hasTargets).length;

    return {
      fundForecasts,
      timeline,
      minYear,
      maxYear,
      fundsWithTargets,
      fundsWithoutTargets,
      totalTaxSavings,
      finalCarryforward,
      totalAfterTaxReturn,
      totalCommitment,
      totalAfterTaxGain,
      finalCumulativeNet,
      finalCumulativeNetAfterTax,
      reconciliationDeltaAfterTax,
      reconciliationDeltaPreTax,
    };
  }, [fundTableData, customVars, filteredData, forecastRates, federalCapGainsRates, stateCapGainsRates, eqtPerfRates, otherInvestmentIncome, fundMoicOverrides, simulatedFunds, pageTab, holdingPeriodAdjust, holdingPeriodOverrides]);

  useEffect(() => {
    if (!import.meta.env.DEV || !cashForecastData) return;
    console.debug("[Forecast reconciliation]", {
      totalAfterTaxReturn: cashForecastData.totalAfterTaxReturn,
      totalCommitment: cashForecastData.totalCommitment,
      totalAfterTaxGain: cashForecastData.totalAfterTaxGain,
      finalCumulativeNet: cashForecastData.finalCumulativeNet,
      finalCumulativeNetAfterTax: cashForecastData.finalCumulativeNetAfterTax,
      reconciliationDeltaPreTax: cashForecastData.reconciliationDeltaPreTax,
      reconciliationDeltaAfterTax: cashForecastData.reconciliationDeltaAfterTax,
      timelineTaxTotal: cashForecastData.timeline.reduce((s, t) => s + (t.tax || 0), 0),
      fundTaxTotal: cashForecastData.fundForecasts.reduce((s, f) => s + (f.totalProjectedTax || 0), 0),
    });
  }, [cashForecastData]);

  // Implied Return by Fund — uses after-tax return from cash forecast
  const impliedReturnByFundData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    return cashForecastData.fundForecasts
      .filter(ff => ff.afterTaxReturn > 0 || ff.myCommitment > 0)
      .map((ff) => {
        const atr = ff.afterTaxReturn || 0;
        const cost = ff.myCommitment || 0;
        return {
          name: ff.fund.substring(0, 30),
          Committed: Math.round(atr),
          Drawn: Math.round(cost),
          Remaining: Math.round(Math.max(0, atr - cost)),
        };
      });
  }, [cashForecastData]);

  // Implied Return by Vehicle
  const impliedReturnByVehicleData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const CURRENT_RATE = FX_DATA.current_rate;
    const byVehicle = {};
    cashForecastData.fundForecasts.forEach((ff) => {
      const v = ff.investor;
      if (!byVehicle[v]) byVehicle[v] = { name: v, afterTaxReturn: 0, drawn: 0, nav: 0 };
      const toUsd = ff.currency === "EUR" ? CURRENT_RATE : 1;
      byVehicle[v].afterTaxReturn += (ff.afterTaxReturn || 0) * toUsd;
      byVehicle[v].drawn += (ff.drawn || 0) * toUsd;
      byVehicle[v].nav += (ff.nav || 0) * toUsd;
    });
    return Object.values(byVehicle).map((v) => ({
      name: vehicleDisplayName(v.name),
      fullName: v.name,
      Drawn: Math.round(v.drawn),
      Unfunded: Math.round(Math.max(0, v.afterTaxReturn - v.drawn)),
      NAV: Math.round(v.nav),
    }));
  }, [cashForecastData]);

  // fundTableData enriched with afterTaxReturn for DiversificationPies implied-return mode
  const fundTableDataWithReturns = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return fundTableData;
    const returnByKey = {};
    cashForecastData.fundForecasts.forEach(ff => {
      returnByKey[makeKey(ff.fund, ff.investor)] = ff.afterTaxReturn || 0;
    });
    return fundTableData.map(f => ({
      ...f,
      afterTaxReturn: returnByKey[makeKey(f.fund, f.investor)] || 0,
    }));
  }, [fundTableData, cashForecastData]);

  // ── Tax Allocation (Commitments tab) — by fund / vehicle / strategy ──
  // Helper: extract tax allocation amounts from a fund forecast entry
  const getTaxAmounts = (ff) => {
    const c = ff.myCommitment || 0;
    const roth = ff.rothPct ?? 0;
    const trad = ff.tradIraPct ?? 0;
    const taxable = ff.taxablePct ?? (1 - roth - trad);
    return {
      Roth: Math.round(c * roth),
      "Traditional IRA": Math.round(c * trad),
      Taxable: Math.round(c * taxable),
    };
  };

  const taxAllocationByFund = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    return cashForecastData.fundForecasts.map((ff) => ({
      name: ff.fund.substring(0, 30),
      ...getTaxAmounts(ff),
    })).filter(d => d.Roth > 0 || d["Traditional IRA"] > 0 || d.Taxable > 0);
  }, [cashForecastData]);

  const taxAllocationByVehicle = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const byVehicle = {};
    cashForecastData.fundForecasts.forEach((ff) => {
      const v = ff.investor;
      if (!byVehicle[v]) byVehicle[v] = { name: vehicleDisplayName(v), Roth: 0, "Traditional IRA": 0, Taxable: 0 };
      const tax = getTaxAmounts(ff);
      byVehicle[v].Roth += tax.Roth;
      byVehicle[v]["Traditional IRA"] += tax["Traditional IRA"];
      byVehicle[v].Taxable += tax.Taxable;
    });
    return Object.values(byVehicle).filter(d => d.Roth > 0 || d["Traditional IRA"] > 0 || d.Taxable > 0);
  }, [cashForecastData]);

  const taxAllocationByStrategy = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const byStrategy = {};
    cashForecastData.fundForecasts.forEach((ff) => {
      const ftd = fundTableData.find(f => f.fund === ff.fund && f.investor === ff.investor);
      const strat = ftd?.fundStrategy || "Unclassified";
      if (!byStrategy[strat]) byStrategy[strat] = { name: strat, Roth: 0, "Traditional IRA": 0, Taxable: 0 };
      const tax = getTaxAmounts(ff);
      byStrategy[strat].Roth += tax.Roth;
      byStrategy[strat]["Traditional IRA"] += tax["Traditional IRA"];
      byStrategy[strat].Taxable += tax.Taxable;
    });
    return Object.values(byStrategy).filter(d => d.Roth > 0 || d["Traditional IRA"] > 0 || d.Taxable > 0);
  }, [cashForecastData, fundTableData]);

  // ── Commitment Concentration Curve / Lorenz (Commitments tab) — by fund / vehicle / strategy ──
  const concentrationByFund = useMemo(() => {
    const sorted = [...fundTableData].sort((a, b) => (b.commitment || 0) - (a.commitment || 0));
    const total = sorted.reduce((s, f) => s + (f.commitment || 0), 0);
    if (total === 0) return [];
    let cumPct = 0;
    return sorted.map((f, i) => {
      cumPct += (f.commitment || 0) / total * 100;
      return { name: f.fund.substring(0, 25), rank: i + 1, cumPct: Math.round(cumPct * 10) / 10, equalLine: Math.round((i + 1) / sorted.length * 100 * 10) / 10, commitment: f.commitment || 0 };
    });
  }, [fundTableData]);

  const concentrationByVehicle = useMemo(() => {
    const byVehicle = {};
    fundTableData.forEach(f => {
      const v = f.investor;
      byVehicle[v] = (byVehicle[v] || 0) + (f.commitment || 0);
    });
    const sorted = Object.entries(byVehicle).sort((a, b) => b[1] - a[1]);
    const total = sorted.reduce((s, [, c]) => s + c, 0);
    if (total === 0) return [];
    let cumPct = 0;
    return sorted.map(([v, c], i) => {
      cumPct += c / total * 100;
      return { name: vehicleDisplayName(v), rank: i + 1, cumPct: Math.round(cumPct * 10) / 10, equalLine: Math.round((i + 1) / sorted.length * 100 * 10) / 10, commitment: c };
    });
  }, [fundTableData]);

  const concentrationByStrategy = useMemo(() => {
    const byStrategy = {};
    fundTableData.forEach(f => {
      const s = f.fundStrategy || "Unclassified";
      byStrategy[s] = (byStrategy[s] || 0) + (f.commitment || 0);
    });
    const sorted = Object.entries(byStrategy).sort((a, b) => b[1] - a[1]);
    const total = sorted.reduce((s, [, c]) => s + c, 0);
    if (total === 0) return [];
    let cumPct = 0;
    return sorted.map(([s, c], i) => {
      cumPct += c / total * 100;
      return { name: s, rank: i + 1, cumPct: Math.round(cumPct * 10) / 10, equalLine: Math.round((i + 1) / sorted.length * 100 * 10) / 10, commitment: c };
    });
  }, [fundTableData]);

  // ── After-Tax Return Waterfall (Performance tab) ──
  const afterTaxWaterfallData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    // Derive federal/state tax split from actual rate settings (not hardcoded)
    const sampleYear = Object.keys(federalCapGainsRates)[0] || currentYear;
    const fedRate = federalCapGainsRates[sampleYear] || 20;
    const stateRate = stateCapGainsRates[sampleYear] || 5;
    const totalRate = fedRate + stateRate;
    const fedShare = totalRate > 0 ? fedRate / totalRate : 0.8;

    const totals = cashForecastData.fundForecasts.reduce((acc, ff) => ({
      grossReturn: acc.grossReturn + (ff.grossFundReturn || 0),
      interest: acc.interest + (ff.projectedTotalInterest || 0),
      totalTax: acc.totalTax + (ff.totalProjectedTax || 0),
      afterTax: acc.afterTax + (ff.afterTaxReturn || 0),
      commitment: acc.commitment + (ff.myCommitment || 0),
    }), { grossReturn: 0, interest: 0, totalTax: 0, afterTax: 0, commitment: 0 });

    return [
      { name: "Gross Return", value: Math.round(totals.grossReturn), fill: "var(--chart-positive)" },
      { name: "Interest Cost", value: -Math.round(totals.interest), fill: "var(--chart-danger)" },
      { name: `Federal Tax (${fedRate}%)`, value: -Math.round(totals.totalTax * fedShare), fill: "var(--chart-warning)" },
      { name: `State Tax (${stateRate}%)`, value: -Math.round(totals.totalTax * (1 - fedShare)), fill: "#f97316" },
      { name: "After-Tax Return", value: Math.round(totals.afterTax), fill: "var(--chart-fill-primary)" },
    ];
  }, [cashForecastData, federalCapGainsRates, stateCapGainsRates]);

  // ── Cash Flow by Strategy (Forecast tab) ──
  const cashFlowByStrategyData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const byYearStrategy = {};
    cashForecastData.fundForecasts.forEach(ff => {
      const ftd = fundTableData.find(f => f.fund === ff.fund && f.investor === ff.investor);
      const strategy = ftd?.fundStrategy || "Unclassified";
      if (!ff.yearlyFlows) return;
      Object.entries(ff.yearlyFlows).forEach(([yr, flow]) => {
        const y = Number(yr);
        if (!byYearStrategy[y]) byYearStrategy[y] = { year: y };
        const net = (flow.payout || 0) - (flow.drawdown || 0);
        byYearStrategy[y][strategy] = (byYearStrategy[y][strategy] || 0) + net;
      });
    });
    return Object.values(byYearStrategy).sort((a, b) => a.year - b.year);
  }, [cashForecastData, fundTableData]);

  // ── Strategies list for colors (shared) ──
  const allStrategies = useMemo(() => {
    const s = new Set();
    fundTableData.forEach(f => s.add(f.fundStrategy || "Unclassified"));
    return [...s].sort();
  }, [fundTableData]);

  // ── Drawdown Pacing: Actual vs Projected (Forecast tab) ──
  // Uses yearlyFlows from cashForecastData as the single source of truth.
  // "Actual" = past drawdowns already made; "Projected" = future drawdowns from the forecast model.
  // Both are cumulative % of commitment, so the chart shows how the drawdown schedule is tracking.
  const drawdownPacingData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const currentYear = new Date().getFullYear();
    return cashForecastData.fundForecasts.map(ff => {
      if (!ff.myCommitment || ff.myCommitment <= 0 || !ff.yearlyFlows) return null;
      // Collect all drawdown years from yearlyFlows (the canonical cash flow source)
      const drawdownYears = Object.entries(ff.yearlyFlows)
        .filter(([, flow]) => (flow.drawdown || 0) > 0)
        .sort(([a], [b]) => Number(a) - Number(b));
      if (drawdownYears.length === 0) return null;
      const firstYear = Number(drawdownYears[0][0]);
      let cumDrawdown = 0;
      // Also add any actual drawn amount that predates yearlyFlows (already fully drawn)
      const preFlowDrawn = ff.drawn || 0;
      const flowDrawnTotal = drawdownYears.reduce((s, [, f]) => s + (f.drawdown || 0), 0);
      // If yearlyFlows doesn't capture all actual drawdowns (some may be pre-yearlyFlows), add them at year 0
      const missingDrawn = Math.max(0, preFlowDrawn - flowDrawnTotal);
      const points = [];
      if (missingDrawn > 0) {
        cumDrawdown += missingDrawn;
        points.push({
          year: firstYear,
          actual: Math.round((cumDrawdown / ff.myCommitment) * 100),
        });
      }
      drawdownYears.forEach(([yr, flow]) => {
        const y = Number(yr);
        cumDrawdown += flow.drawdown || 0;
        const pct = Math.min(100, Math.round((cumDrawdown / ff.myCommitment) * 100));
        const isProjected = flow.isProjected || y >= currentYear;
        points.push({
          year: y,
          ...(isProjected ? { projected: pct } : { actual: pct }),
        });
      });
      // Ensure both actual and projected lines connect: carry forward the last actual value
      // into projected points so the chart lines meet
      let lastActual = null;
      points.forEach(p => { if (p.actual !== undefined) lastActual = p.actual; });
      if (lastActual !== null) {
        const firstProjected = points.find(p => p.projected !== undefined);
        if (firstProjected && firstProjected.actual === undefined) {
          firstProjected.actual = lastActual;
        }
      }
      return { fund: ff.fund, data: points };
    }).filter(Boolean);
  }, [cashForecastData]);

  // ── Peak Unfunded Exposure Timeline (Forecast tab) ──
  const peakUnfundedData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const byYear = {};
    cashForecastData.fundForecasts.forEach(ff => {
      if (!ff.yearlyFlows || !ff.myCommitment || ff.myCommitment <= 0) return;
      const commitment = ff.myCommitment;
      // Sort years and compute cumulative drawdowns from yearlyFlows (single source of truth)
      const sortedYears = Object.entries(ff.yearlyFlows).sort(([a], [b]) => Number(a) - Number(b));
      // Account for any drawn amount not captured in yearlyFlows
      const flowDrawnTotal = sortedYears.reduce((s, [, f]) => s + (f.drawdown || 0), 0);
      const preFlowDrawn = Math.max(0, (ff.drawn || 0) - flowDrawnTotal);
      let cumDrawdown = preFlowDrawn;
      sortedYears.forEach(([yr, flow]) => {
        cumDrawdown += (flow.drawdown || 0);
        const remaining = Math.max(0, commitment - cumDrawdown);
        const y = Number(yr);
        byYear[y] = (byYear[y] || 0) + remaining;
      });
    });
    return Object.entries(byYear)
      .map(([yr, unfunded]) => ({ year: Number(yr), unfunded: Math.round(unfunded) }))
      .sort((a, b) => a.year - b.year);
  }, [cashForecastData]);

  // ── Distribution Source Breakdown Over Time (Forecast tab) ──
  const distSourceData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const byYear = {};
    cashForecastData.fundForecasts.forEach(ff => {
      const gf = ff.gainFraction || 0;
      if (!ff.yearlyFlows) return;
      Object.entries(ff.yearlyFlows).forEach(([yr, flow]) => {
        const payout = flow.payout || 0;
        if (payout <= 0) return;
        const y = Number(yr);
        if (!byYear[y]) byYear[y] = { year: y, returnOfCapital: 0, realizedGains: 0 };
        byYear[y].returnOfCapital += payout * (1 - gf);
        byYear[y].realizedGains += payout * gf;
      });
    });
    return Object.values(byYear)
      .map(d => ({ ...d, returnOfCapital: Math.round(d.returnOfCapital), realizedGains: Math.round(d.realizedGains) }))
      .sort((a, b) => a.year - b.year);
  }, [cashForecastData]);

  // ── Leveraged vs Non-Leveraged Comparison (Leverage tab) ──
  const levVsNonLevData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    return cashForecastData.fundForecasts
      .filter(ff => ff.myCommitment > 0)
      .map(ff => ({
        name: ff.fund.substring(0, 25),
        grossMoic: ff.fundMoic || 0,
        afterTaxMoic: ff.afterTaxMoic || 0,
        commitment: ff.myCommitment,
        isLeveraged: ff.isLeveraged,
        interestCost: (ff.projectedTotalInterest || 0),
        strategy: fundTableData.find(f => f.fund === ff.fund && f.investor === ff.investor)?.fundStrategy || "Unclassified",
      }));
  }, [cashForecastData, fundTableData]);

  // ── Currency-at-Risk Over Time (FX tab) ──
  const currencyAtRiskData = useMemo(() => {
    const byDate = {};
    // Use CAS snapshots for NAV by currency over time
    filteredData.casTimeline.forEach(cas => {
      const q = cas.date.substring(0, 7);
      if (!byDate[q]) byDate[q] = { date: q, EUR: 0, USD: 0 };
      const fund = filteredData.funds.find(f => f.fund === cas.fund && f.investor === cas.investor);
      const cur = fund?.currency || "USD";
      byDate[q][cur] += cas.nav_closing || 0;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredData]);

  // ── First Drawdown Year × Strategy Heatmap (Analytics tab) ──
  const drawdownYearStrategyHeatmap = useMemo(() => {
    const grid = {};
    const years = new Set();
    const strategies = new Set();
    fundTableData.forEach(f => {
      const yr = f.firstDrawdownDate ? new Date(f.firstDrawdownDate).getFullYear() : null;
      if (!yr) return;
      const strat = f.fundStrategy || "Unclassified";
      years.add(yr);
      strategies.add(strat);
      const key = `${yr}||${strat}`;
      grid[key] = (grid[key] || 0) + (f.commitment || 0);
    });
    const sortedYears = [...years].sort();
    const sortedStrategies = [...strategies].sort();
    const maxVal = Math.max(...Object.values(grid), 1);
    return { grid, years: sortedYears, strategies: sortedStrategies, maxVal };
  }, [fundTableData]);

  // ── MOIC Sensitivity Surface (Analytics tab) ──
  const moicSensitivityData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const deltas = [-0.5, -0.25, 0, 0.25, 0.5];
    return cashForecastData.fundForecasts
      .filter(ff => ff.fundMoic > 0 && ff.myCommitment > 0)
      .map(ff => {
        const row = { name: ff.fund.substring(0, 25), baseMoic: ff.fundMoic, isLeveraged: ff.isLeveraged };
        deltas.forEach(d => {
          const adjustedMoic = Math.max(0, ff.fundMoic + d);
          let adjustedReturn;
          if (ff.isLeveraged && ff.lpNetReturn != null && ff.totalExposure) {
            // For leveraged funds, LP return = totalExposure * INVESTMENT_LEVEL * fundMoic - fixedCosts.
            // fixedCosts (interest on SEB/EQT tranches) don't change with MOIC, so the sensitivity
            // slope is totalExposure * INVESTMENT_LEVEL per unit of fundMoic.
            adjustedReturn = ff.lpNetReturn + d * ff.totalExposure * INVESTMENT_LEVEL;
          } else {
            // Non-leveraged: LP return scales directly with commitment × MOIC
            adjustedReturn = ff.myCommitment * adjustedMoic;
          }
          row[`${d >= 0 ? "+" : ""}${d}x`] = Math.round(Math.max(0, adjustedReturn));
        });
        return row;
      });
  }, [cashForecastData]);

  // ── Maturity / Liquidity Profile (Analytics tab) ──
  const maturityProfileData = useMemo(() => {
    if (!cashForecastData?.fundForecasts) return [];
    const byYear = {};
    cashForecastData.fundForecasts.forEach(ff => {
      const ftd = fundTableData.find(f => f.fund === ff.fund && f.investor === ff.investor);
      const strategy = ftd?.fundStrategy || "Unclassified";
      const returnYear = ff.holdingEnd || (ff.vintageYear + (ff.avgHoldingPeriod || 5));
      if (!byYear[returnYear]) byYear[returnYear] = { year: returnYear };
      byYear[returnYear][strategy] = (byYear[returnYear][strategy] || 0) + (ff.myCommitment || 0);
    });
    return Object.values(byYear).sort((a, b) => a.year - b.year);
  }, [cashForecastData, fundTableData]);

        // ── Leverage Analysis ──
  // Defaults for leveraged funds (auto-detected from name) when no Excel loaded
  
  // ── IRR Calculation (Newton's method) ──
  const irrData = useMemo(() => {
    // Simple IRR via Newton-Raphson on cash flows
    const calcIRR = (cashflows) => {
      // cashflows: [{date, amount}] where negative = outflow, positive = inflow
      if (cashflows.length < 2) return null;
      const sorted = [...cashflows].sort((a, b) => new Date(a.date) - new Date(b.date));
      const t0 = new Date(sorted[0].date).getTime();
      const yearFrac = (d) => (new Date(d).getTime() - t0) / MS_PER_YEAR;

      const npv = (r) => sorted.reduce((s, cf) => s + cf.amount / Math.pow(1 + r, yearFrac(cf.date)), 0);
      const dnpv = (r) => sorted.reduce((s, cf) => {
        const t = yearFrac(cf.date);
        return s - t * cf.amount / Math.pow(1 + r, t + 1);
      }, 0);

      let r = 0.1;
      for (let i = 0; i < 100; i++) {
        const d = dnpv(r);
        if (Math.abs(d) < 1e-12) break;
        const step = npv(r) / d;
        r -= step;
        if (Math.abs(step) < 1e-8) break;
        if (r < -0.99) r = -0.5;
        if (r > 10) r = 2;
      }
      if (isNaN(r) || !isFinite(r) || r < -0.99 || r > 10) return null;
      return r;
    };

    const fundIRRs = [];
    const portfolioCFs = [];

    fundTableData.forEach((f) => {
      const cashflows = [];
      const casDrawn = f.drawn || 0;

      // Drawdowns as negative — scale to match CAS drawn_commitment so fees
      // and expenses don't inflate outflows beyond actual commitment drawn.
      const rawDDs = getFundDrawdowns(filteredData.drawdowns, f.fund, f.investor);
      const rawDDTotal = rawDDs.reduce((s, d) => s + (d.amount || d.net_drawdown || 0), 0);
      const ddScale = (rawDDTotal > 0 && casDrawn > 0) ? casDrawn / rawDDTotal : 1;
      rawDDs.forEach(d => {
        const rawAmt = d.amount || d.net_drawdown || 0;
        const amt = rawAmt * ddScale;
        if (amt > 0) {
          cashflows.push({ date: d.date, amount: -amt });
          portfolioCFs.push({ date: d.date, amount: -amt });
        }
      });

      // Distributions — use net cash received (excludes embedded capital calls)
      (filteredData.distributions || [])
        .filter(d => d.fund === f.fund && d.investor === f.investor)
        .forEach(d => {
          let net;
          if (d.drawdown_in_distribution > 0) {
            net = (d.total_distribution || 0) - d.drawdown_in_distribution;
          } else if (d.net_distribution != null && d.net_distribution > 0) {
            net = d.net_distribution;
          } else {
            net = d.total_distribution || 0;
          }
          if (net !== 0) {
            cashflows.push({ date: d.date, amount: net });
            portfolioCFs.push({ date: d.date, amount: net });
          }
        });

      // Current NAV as terminal value (today)
      const nav = f.nav || 0;
      if (nav > 0) {
        const today = new Date().toISOString().split("T")[0];
        cashflows.push({ date: today, amount: nav });
        portfolioCFs.push({ date: today, amount: nav });
      }

      const irr = cashflows.length >= 2 ? calcIRR(cashflows) : null;

      // Calculate years since first drawdown
      const fundDds = getFundDrawdowns(filteredData.drawdowns, f.fund, f.investor);
      const firstDrawdownDate = fundDds.length > 0 ? new Date(fundDds[0].date) : null;
      const years = firstDrawdownDate ? (new Date() - firstDrawdownDate) / MS_PER_YEAR : null;

      // Time-weighted annualized return: TVPI^(1/years) - 1
      const tvpiVal = parseFloat(f.tvpi) || 0;
      const twirr = (years && years > 0.25 && tvpiVal > 0) ? Math.pow(tvpiVal, 1 / years) - 1 : null;

      fundIRRs.push({
        fund: f.fund,
        investor: f.investor,
        currency: f.currency,
        irr,
        twirr,
        years,
        tvpi: tvpiVal,
        dpi: parseFloat(f.dpi) || 0,
        drawn: f.drawn || 0,
        nav: f.nav || 0,
        distributed: f.distributed || 0,
      });
    });

    const portfolioIRR = portfolioCFs.length >= 2 ? calcIRR(portfolioCFs) : null;

    // Portfolio-level annualized TVPI
    const totalDrawn = fundIRRs.reduce((s, f) => s + f.drawn, 0);
    const totalNav = fundIRRs.reduce((s, f) => s + f.nav, 0);
    const totalDist = fundIRRs.reduce((s, f) => s + f.distributed, 0);
    const portTvpi = calcTvpi(totalDrawn, totalNav, totalDist);
    // Use earliest first-drawdown across all funds
    const allYears = fundIRRs.map(f => f.years).filter(y => y && y > 0);
    const maxYears = allYears.length > 0 ? Math.max(...allYears) : null;
    const portfolioAnnTvpi = (maxYears && maxYears > 0.25 && portTvpi > 0) ? Math.pow(portTvpi, 1 / maxYears) - 1 : null;

    return { fundIRRs, portfolioIRR, portfolioAnnTvpi };
  }, [fundTableData, filteredData]);

  // ── J-Curve Data (cumulative net cash by fund age) ──
  const jCurveData = useMemo(() => {
    const fundCurves = {};
    fundTableData.forEach((f) => {
      const drawdowns = getFundDrawdowns(filteredData.drawdowns, f.fund, f.investor);
      if (drawdowns.length === 0) return;

      const firstDate = new Date(drawdowns[0].date);
      const flows = [];

      // Add drawdowns
      drawdowns.forEach(d => {
        const amt = d.amount || d.net_drawdown || 0;
        const monthsFromStart = (new Date(d.date) - firstDate) / (30.44 * 24 * 60 * 60 * 1000);
        flows.push({ quarter: Math.round(monthsFromStart / 3), amount: -amt });
      });
      // Add distributions
      (filteredData.distributions || [])
        .filter(d => d.fund === f.fund && d.investor === f.investor)
        .forEach(d => {
          const amt = getDistAmount(d);
          const monthsFromStart = (new Date(d.date) - firstDate) / (30.44 * 24 * 60 * 60 * 1000);
          flows.push({ quarter: Math.round(monthsFromStart / 3), amount: amt });
        });

      // Build cumulative by quarter
      const byQ = {};
      flows.forEach(fl => {
        byQ[fl.quarter] = (byQ[fl.quarter] || 0) + fl.amount;
      });

      let cumulative = 0;
      const maxQ = Math.max(...Object.keys(byQ).map(Number), 0);
      const curve = [];
      for (let q = 0; q <= maxQ; q++) {
        cumulative += (byQ[q] || 0);
        curve.push({ quarter: q, cumulative });
      }
      // Add current NAV as unrealized
      const nav = f.nav || 0;
      if (curve.length > 0) {
        curve[curve.length - 1].cumulativeWithNav = cumulative + nav;
      }

      const label = f.fund.replace(" Leveraged", " Lev").replace("EQT ", "");
      fundCurves[label] = curve;
    });

    // Normalize into chart-friendly format
    const maxQuarter = Math.max(...Object.values(fundCurves).flatMap(c => c.map(p => p.quarter)), 0);
    const chartData = [];
    for (let q = 0; q <= maxQuarter; q++) {
      const entry = { quarter: q, label: `Q${q}` };
      Object.entries(fundCurves).forEach(([fund, curve]) => {
        const point = curve.find(p => p.quarter === q);
        if (point) entry[fund] = point.cumulative;
      });
      chartData.push(entry);
    }
    return { chartData, fundNames: Object.keys(fundCurves) };
  }, [fundTableData, filteredData]);

  // ── Liquidity Runway (cash needed in next 6/12/18 months) ──
  const liquidityRunway = useMemo(() => {
    if (!cashForecastData) return null;
    const now = new Date();
    const windows = [6, 12, 18, 24];
    const results = windows.map(months => {
      const cutoff = new Date(now);
      cutoff.setMonth(cutoff.getMonth() + months);
      const cutoffYear = cutoff.getFullYear();

      let totalDrawdowns = 0;
      let totalInterest = 0;
      let totalPayouts = 0;
      let totalTax = 0;

      cashForecastData.timeline.forEach(t => {
        if (t.year >= now.getFullYear() && t.year <= cutoffYear) {
          totalDrawdowns += t.drawdowns;
          totalInterest += t.interest;
          totalPayouts += t.payouts;
          totalTax += t.tax;
        }
      });

      return {
        months,
        label: `${months}mo`,
        drawdowns: totalDrawdowns,
        interest: totalInterest,
        payouts: totalPayouts,
        tax: totalTax,
        netNeed: totalDrawdowns + totalInterest + totalTax - totalPayouts,
      };
    });
    return results;
  }, [cashForecastData]);

  // ── DPI vs TVPI Over Time ──
  const dpiTvpiHistory = useMemo(() => {
    // Build per-quarter DPI and TVPI for the whole portfolio
    const quarterData = {};
    filteredData.casTimeline.forEach(cas => {
      const q = cas.date;
      if (!quarterData[q]) quarterData[q] = { totalDrawn: 0, totalNav: 0, totalDist: 0 };
    });

    // Get latest CAS per fund per quarter for NAV
    const casByFundQuarter = {};
    filteredData.casTimeline.forEach(cas => {
      const key = `${cas.fund}||${cas.investor}||${cas.date}`;
      casByFundQuarter[key] = cas;
    });

    // For each quarter, compute portfolio-wide metrics
    const allDates = [...new Set(filteredData.casTimeline.map(c => c.date))].sort();
    const history = [];

    allDates.forEach(date => {
      let totalDrawn = 0;
      let totalNav = 0;
      let totalDist = 0;

      // For each fund, find the CAS on or before this date
      fundTableData.forEach(f => {
        const fundCas = filteredData.casTimeline
          .filter(c => c.fund === f.fund && c.investor === f.investor && c.date <= date)
          .sort((a, b) => b.date.localeCompare(a.date));
        if (fundCas.length > 0) {
          totalDrawn += Math.abs(fundCas[0].drawn_commitment || 0);
          totalNav += fundCas[0].nav_closing || 0;
        }
        // Distributions up to this date
        const fundDists = (filteredData.distributions || [])
          .filter(d => d.fund === f.fund && d.investor === f.investor && d.date <= date);
        totalDist += fundDists.reduce((s, d) => s + getDistAmount(d), 0);
      });

      if (totalDrawn > 0) {
        history.push({
          date,
          quarter: date.substring(0, 7),
          tvpi: parseFloat(((totalNav + totalDist) / totalDrawn).toFixed(3)),
          dpi: parseFloat((totalDist / totalDrawn).toFixed(3)),
          rvpi: parseFloat((totalNav / totalDrawn).toFixed(3)),
        });
      }
    });
    return history;
  }, [filteredData, fundTableData]);

  // ── Vintage Year Diversification ──
  const vintageData = useMemo(() => {
    const byVintage = {};
    cashForecastData && cashForecastData.fundForecasts.forEach(ff => {
      const v = ff.vintageYear || "Unknown";
      if (!byVintage[v]) byVintage[v] = { vintage: v, committed: 0, drawn: 0, nav: 0, funds: 0 };
      byVintage[v].committed += ff.myCommitment;
      byVintage[v].drawn += ff.drawn;
      byVintage[v].nav += ff.nav;
      byVintage[v].funds += 1;
    });
    return Object.values(byVintage).sort((a, b) => a.vintage - b.vintage);
  }, [cashForecastData]);

  // ── Unfunded Commitment Coverage Ratio ──
  const unfundedCoverage = useMemo(() => {
    const totalUnfunded = fundTableData.reduce((s, f) => s + Math.max(0, (f.commitment || 0) - (f.drawn || 0)), 0);
    const totalNav = fundTableData.reduce((s, f) => s + (f.nav || 0), 0);
    const totalDistributed = fundTableData.reduce((s, f) => s + (f.distributed || 0), 0);
    const ratio = totalUnfunded > 0 ? totalNav / totalUnfunded : 0;
    return { totalUnfunded, totalNav, totalDistributed, ratio };
  }, [fundTableData]);

  // ── Sector / Geography Concentration ──
  const concentrationData = useMemo(() => {
    const bySector = {};
    const byGeo = {};
    const byStrategy = {};

    fundTableData.forEach(f => {
      const key = makeKey(f.fund, f.investor);
      const v = customVars ? customVars[key] : null;
      const nav = f.nav || 0;
      const sector = (v && v["Sector / Strategy"]) || "Not Set";
      const geo = (v && v["Geography"]) || "Not Set";
      const strategy = (v && v["Sub-Strategy"]) || "Not Set";

      bySector[sector] = (bySector[sector] || 0) + nav;
      byGeo[geo] = (byGeo[geo] || 0) + nav;
      byStrategy[strategy] = (byStrategy[strategy] || 0) + nav;
    });

    const toArray = (obj) => Object.entries(obj)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .filter(x => x.value > 0)
      .sort((a, b) => b.value - a.value);

    return { bySector: toArray(bySector), byGeo: toArray(byGeo), byStrategy: toArray(byStrategy) };
  }, [fundTableData, customVars]);

  // ── Portfolio Company Exposure (from PC snapshots) ──
  const pcExposureData = useMemo(() => {
    if (!pcAllSnapshots || pcAllSnapshots.length === 0) {
      return { byIndustry: [], byGeography: [], topHoldings: [], totalValue: 0, companyCount: 0 };
    }

    // Get latest snapshot per company (dedupe by portfolio_company_id, keep most recent)
    const latestByCompany = {};
    pcAllSnapshots.forEach(s => {
      const key = s.portfolio_company_id;
      if (!latestByCompany[key] || s.snapshot_date > latestByCompany[key].snapshot_date) {
        latestByCompany[key] = s;
      }
    });

    const latest = Object.values(latestByCompany);
    const byIndustry = {};
    const byGeo = {};
    let totalVal = 0;

    latest.forEach(s => {
      const val = Math.abs(s.unrealized_value || s.total_value || 0);
      const industry = s.industry || (s.portfolio_companies && s.portfolio_companies.industry) || "Not Classified";
      const geo = s.geography || (s.portfolio_companies && s.portfolio_companies.geography) || "Not Classified";
      byIndustry[industry] = (byIndustry[industry] || 0) + val;
      byGeo[geo] = (byGeo[geo] || 0) + val;
      totalVal += val;
    });

    const toArr = (obj) => Object.entries(obj)
      .map(([name, value]) => ({ name, value: Math.round(value), pct: totalVal > 0 ? Math.round(value / totalVal * 100) : 0 }))
      .filter(x => x.value > 0)
      .sort((a, b) => b.value - a.value);

    // Full holdings list (all companies, no slice) — used by the table lookup
    const allHoldings = latest
      .map(s => ({
        id: s.portfolio_company_id,
        name: (s.portfolio_companies && s.portfolio_companies.company_name) || "Unknown",
        value: Math.abs(s.unrealized_value || s.total_value || 0),
        unrealizedValue: Math.abs(s.unrealized_value || 0),
        moic: s.gross_moic || null,
        industry: s.industry || (s.portfolio_companies && s.portfolio_companies.industry) || "—",
        geography: s.geography || (s.portfolio_companies && s.portfolio_companies.geography) || "—",
      }))
      .sort((a, b) => b.value - a.value);

    // ID-keyed map for O(1) lookup by portfolio_company_id in the table
    const byId = {};
    allHoldings.forEach(h => { byId[h.id] = h; });

    // Top 15 by value (for the bar chart in Analytics)
    const topHoldings = allHoldings.filter(h => h.value > 0).slice(0, 15);

    return {
      byIndustry: toArr(byIndustry),
      byGeography: toArr(byGeo),
      topHoldings,
      allHoldings,
      byId,
      totalValue: totalVal,
      companyCount: latest.length,
    };
  }, [pcAllSnapshots]);

  // ── PC data grouped by fund||investor key for Performance tab drill-down ──
  const pcByFundKey = useMemo(() => {
    if (!pcAllSnapshots || pcAllSnapshots.length === 0 || !pcData.imports) return {};

    // Build fund_investment_id -> "fund||investor" mapping from imports
    const fiToKey = {};
    (pcData.imports || []).forEach(imp => {
      if (imp.fund_investment_id) {
        // imports have fund_name and investor_name directly
        const fn = imp.fund_name || (imp.fund_investments?.funds?.fund_name) || "";
        const inv = imp.investor_name || (imp.fund_investments?.investors?.investor_name) || "";
        if (fn && inv) fiToKey[imp.fund_investment_id] = `${fn}||${inv}`;
      }
    });

    // Group latest snapshots by fund||investor key
    const grouped = {};
    const latestMap = {};
    pcAllSnapshots.forEach(s => {
      const snapKey = `${s.fund_investment_id}__${s.portfolio_company_id}`;
      if (!latestMap[snapKey] || s.snapshot_date > latestMap[snapKey].snapshot_date) {
        latestMap[snapKey] = s;
      }
    });
    Object.values(latestMap).forEach(s => {
      const fundKey = fiToKey[s.fund_investment_id];
      if (!fundKey) return;
      if (!grouped[fundKey]) grouped[fundKey] = [];
      grouped[fundKey].push({
        name: (s.portfolio_companies && s.portfolio_companies.company_name) || "Unknown",
        unrealized: s.unrealized_value || 0,
        realized: s.realized_proceeds || 0,
        total: s.total_value || 0,
        moic: s.gross_moic,
        geography: s.geography || (s.portfolio_companies && s.portfolio_companies.geography) || "—",
        industry: s.industry || (s.portfolio_companies && s.portfolio_companies.industry) || "—",
      });
    });
    // Sort each fund's companies by total value desc
    Object.keys(grouped).forEach(k => {
      grouped[k].sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    });
    return grouped;
  }, [pcAllSnapshots, pcData.imports]);

  // ── Bubble Chart Data for PC Overview ──
  // Groups snapshots and enriches with fund metadata (area, strategy, type, geo, sector)
  // Deduplication: "All" view merges same company across funds; "By Fund" separates them
  const bubbleChartData = useMemo(() => {
    if (!pcAllSnapshots || pcAllSnapshots.length === 0) return [];

    // Build fund_investment_id -> fund metadata mapping from imports + fundTableData
    const fiMeta = {};
    (pcData.imports || []).forEach(imp => {
      if (!imp.fund_investment_id) return;
      const fn = imp.fund_name || "";
      const inv = imp.investor_name || "";
      const ftdKey = `${fn}||${inv}`;
      const ftd = (fundTableData || []).find(f => `${f.fund}||${f.investor}` === ftdKey);
      fiMeta[imp.fund_investment_id] = {
        fund: fn,
        investor: inv,
        area: ftd?.fundArea || "Unclassified",
        strategy: ftd?.fundStrategy || "Unclassified",
        type: ftd?.productType || "Unclassified",
        firstDrawdownDate: ftd?.firstDrawdownDate || null,
      };
    });

    // Get latest snapshot per (company, fund_investment_id) combo
    const latestMap = {};
    pcAllSnapshots.forEach(s => {
      const snapKey = `${s.portfolio_company_id}__${s.fund_investment_id}`;
      if (!latestMap[snapKey] || s.snapshot_date > latestMap[snapKey].snapshot_date) {
        latestMap[snapKey] = s;
      }
    });

    const allEntries = Object.values(latestMap);
    const now = new Date();

    // Build per-fund entries (for "By Fund" view) with all metadata
    const perFundEntries = allEntries.map(s => {
      const companyName = (s.portfolio_companies && s.portfolio_companies.company_name) || "Unknown";
      const geography = s.geography || (s.portfolio_companies && s.portfolio_companies.geography) || "Not Classified";
      const industry = s.industry || (s.portfolio_companies && s.portfolio_companies.industry) || "Not Classified";
      const meta = fiMeta[s.fund_investment_id] || {};

      // Holding period: use the fund's first drawdown date as the start,
      // measured to the snapshot date. The Excel doesn't carry per-company
      // investment dates, so the fund's inception (first capital call) is
      // the best available proxy.
      const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
      let holdingYears = 0;
      const snapDate = s.snapshot_date ? new Date(s.snapshot_date) : now;
      if (meta.firstDrawdownDate) {
        const fundStart = new Date(meta.firstDrawdownDate);
        holdingYears = Math.max(0, (snapDate - fundStart) / MS_PER_YEAR);
      } else if (s.investment_date) {
        // Direct investment_date if it ever becomes available
        holdingYears = Math.max(0, (snapDate - new Date(s.investment_date)) / MS_PER_YEAR);
      }

      return {
        companyId: s.portfolio_company_id,
        company: companyName,
        holdingPeriod: Math.round(holdingYears * 10) / 10,
        moic: s.gross_moic || 0,
        totalValue: Math.abs(s.total_value || s.unrealized_value || 0),
        geography,
        industry,
        fund: meta.fund || "Unknown",
        area: meta.area || "Unclassified",
        strategy: meta.strategy || "Unclassified",
        type: meta.type || "Unclassified",
      };
    }).filter(e => e.totalValue > 0);

    return perFundEntries;
  }, [pcAllSnapshots, pcData.imports, fundTableData]);

  // Processed bubble data based on active color tab
  const processedBubbleData = useMemo(() => {
    if (!bubbleChartData.length) return { points: [], groups: [], colorMap: {} };

    const PALETTE = [
      "#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444",
      "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#6366f1",
      "#84cc16", "#a855f7", "#22d3ee", "#e11d48", "#0ea5e9",
    ];

    let points;
    if (bubbleColorTab === "fund") {
      // Separate: each company-fund combo is its own bubble
      points = bubbleChartData;
    } else {
      // Merged: aggregate same company across funds
      const merged = {};
      bubbleChartData.forEach(e => {
        if (!merged[e.companyId]) {
          merged[e.companyId] = { ...e, _totalValues: [e.totalValue], _moics: [e.moic], _holdings: [e.holdingPeriod] };
        } else {
          const m = merged[e.companyId];
          m._totalValues.push(e.totalValue);
          m._moics.push(e.moic);
          m._holdings.push(e.holdingPeriod);
          m.totalValue += e.totalValue;
        }
      });
      points = Object.values(merged).map(m => ({
        ...m,
        // Weighted-average MOIC by value
        moic: m._totalValues.reduce((sum, v, i) => sum + v * m._moics[i], 0) / Math.max(m.totalValue, 1),
        // Max holding period across funds
        holdingPeriod: Math.max(...m._holdings),
      }));
    }

    // Determine group key based on tab
    const groupKey = {
      all: () => "All Companies",
      fund: (p) => p.fund,
      area: (p) => p.area,
      strategy: (p) => p.strategy,
      type: (p) => p.type,
      geography: (p) => p.geography,
      sector: (p) => p.industry,
    }[bubbleColorTab] || (() => "All Companies");

    // Assign groups + colors
    const groupSet = new Set();
    points.forEach(p => groupSet.add(groupKey(p)));
    const groups = [...groupSet].sort();
    const colorMap = {};
    groups.forEach((g, i) => { colorMap[g] = PALETTE[i % PALETTE.length]; });

    // Add color and group to each point
    const coloredPoints = points.map(p => ({
      ...p,
      group: groupKey(p),
      color: colorMap[groupKey(p)],
    }));

    return { points: coloredPoints, groups, colorMap };
  }, [bubbleChartData, bubbleColorTab]);

  // ── Distribution Waterfall by Type ──
  const distWaterfall = useMemo(() => {
    const byFund = {};
    const ensure = (fund, investor) => {
      const key = makeKey(fund, investor);
      if (!byFund[key]) {
        byFund[key] = {
          fund,
          investor,
          returnOfCapital: 0,
          gains: 0,
          total: 0,
          rocFromDist: 0,
          gainsFromDist: 0,
        };
      }
      return key;
    };

    filteredData.casTimeline.forEach(cas => {
      ensure(cas.fund, cas.investor);
    });

    filteredData.distributions.forEach(d => {
      const key = ensure(d.fund, d.investor);
      const amt = Number(getDistAmount(d));
      byFund[key].total += amt;

      const roc = d.return_of_capital != null ? Number(d.return_of_capital) : NaN;
      const rg = d.realized_gains != null ? Number(d.realized_gains) : NaN;
      if (!Number.isNaN(roc)) byFund[key].rocFromDist += roc;
      if (!Number.isNaN(rg)) byFund[key].gainsFromDist += rg;
    });

    Object.values(byFund).forEach(f => {
      const hasPdfSplit = f.rocFromDist > 0 || f.gainsFromDist > 0;
      if (hasPdfSplit) {
        f.gains = f.gainsFromDist;
        f.returnOfCapital = Math.max(0, f.total - f.gainsFromDist);
        if (f.rocFromDist > 0) {
          f.returnOfCapital = f.rocFromDist;
          const unalloc = f.total - f.returnOfCapital - f.gains;
          if (unalloc > 0.01) f.returnOfCapital += unalloc;
        }
      } else {
        const cas = latestCas[makeKey(f.fund, f.investor)];
        const casGains = cas && cas.realized_gains != null ? Math.abs(Number(cas.realized_gains)) : 0;
        f.gains = casGains;
        f.returnOfCapital = Math.max(0, f.total - f.gains);
      }
    });

    return Object.values(byFund).filter(f => f.total > 0).sort((a, b) => b.total - a.total);
  }, [filteredData, latestCas]);

  // ── Currency-Adjusted Returns ──
  const currencyAdjustedReturns = useMemo(() => {
    const CURRENT_RATE = FX_DATA.current_rate;
    return fundTableData.map(f => {
      const drawn = f.drawn || 0;
      const nav = f.nav || 0;
      const distributed = f.distributed || 0;
      const tvpi = calcTvpi(drawn, nav, distributed);

      // Convert everything to USD for comparison
      const toUsd = f.currency === "EUR" ? CURRENT_RATE : 1;
      const drawnUsd = drawn * toUsd;
      const navUsd = nav * toUsd;
      const distUsd = distributed * toUsd;
      const tvpiUsd = calcTvpi(drawnUsd, navUsd, distUsd);

      // FX impact on return
      let entryRate = null;
      let fxImpact = 0;
      if (f.currency === "EUR") {
        const fundFxData = FX_DATA.fx_fund_impact.find(
          fx => fx.fund === f.fund && fx.investor === f.investor
        );
        if (fundFxData) {
          entryRate = fundFxData.weighted_avg_entry_rate;
          const navAtEntry = nav * entryRate;
          const navAtCurrent = nav * CURRENT_RATE;
          fxImpact = navAtCurrent - navAtEntry;
        }
      }

      return {
        fund: f.fund,
        investor: f.investor,
        currency: f.currency,
        drawn, nav, distributed,
        tvpiLocal: tvpi,
        drawnUsd, navUsd, distUsd,
        tvpiUsd,
        entryRate,
        fxImpact,
      };
    });
  }, [fundTableData]);


  // ── Performance vs Target Tracking ──
  const performanceTracking = useMemo(() => {
    if (!cashForecastData) return null;

    const funds = [];
    cashForecastData.fundForecasts.forEach(ff => {
      const f = fundTableData.find(ft => ft.fund === ff.fund && ft.investor === ff.investor);
      if (!f) return;

      const drawn = f.drawn || 0;
      const nav = f.nav || 0;
      const distributed = f.distributed || 0;
      const actualTvpi = drawn > 0 ? (nav + distributed) / drawn : 0;
      const targetMoic = ff.fundMoic;
      if (!targetMoic || targetMoic <= 0) {
        funds.push({ fund: ff.fund, investor: ff.investor, hasTarget: false, actualTvpi });
        return;
      }

      // Fund age in years
      const fundDrawdowns = getFundDrawdowns(filteredData.drawdowns, ff.fund, ff.investor);
      const firstDate = fundDrawdowns.length > 0 ? new Date(fundDrawdowns[0].date) : null;
      if (!firstDate) {
        funds.push({ fund: ff.fund, investor: ff.investor, hasTarget: true, targetMoic, actualTvpi, ageYears: 0 });
        return;
      }
      const now = new Date();
      const ageYears = (now - firstDate) / MS_PER_YEAR;
      const totalLife = ff.maturityYear - ff.vintageYear;
      const pctLife = totalLife > 0 ? Math.min(1, ageYears / totalLife) : 0;

      // Target IRR implied from MOIC and total life
      const targetIrr = totalLife > 0 ? Math.pow(targetMoic, 1 / totalLife) - 1 : 0;

      // Build expected TVPI trajectory: IRR curve with J-curve adjustment
      // J-curve: during deployment phase (first ~30% of life), discount expected TVPI
      // to model fee drag and unrealized value. Gradually fades to pure IRR curve.
      const deployPct = J_CURVE.deployPct;
      const jCurveDepth = J_CURVE.depth;

      const expectedTvpiAt = (t) => {
        if (t <= 0) return 1.0;
        const irrTvpi = Math.pow(1 + targetIrr, t);
        // J-curve adjustment: parabolic dip during deployment, fades out after
        const deployEnd = totalLife * deployPct;
        let jAdj = 0;
        if (t < deployEnd) {
          // Deepening J-curve: peaks at mid-deployment
          const deployProgress = t / deployEnd;
          jAdj = -jCurveDepth * 4 * deployProgress * (1 - deployProgress); // parabola, max at 0.5
        } else if (t < deployEnd * 2) {
          // Recovery phase: J-curve fading out
          const recoveryProgress = (t - deployEnd) / deployEnd;
          jAdj = -jCurveDepth * (1 - recoveryProgress) * J_CURVE.recoveryFade;
        }
        return Math.max(J_CURVE.floor, irrTvpi + jAdj);
      };

      const expectedNow = expectedTvpiAt(ageYears);
      const delta = actualTvpi - expectedNow;
      const deltaPct = expectedNow > 0 ? (delta / expectedNow) * 100 : 0;

      // Status
      let status = "on-track";
      if (deltaPct > PERF_THRESHOLDS.ahead) status = "ahead";
      else if (deltaPct < PERF_THRESHOLDS.behind) status = "behind";

      // Build trajectory curve points (for chart)
      const curvePoints = [];
      for (let t = 0; t <= totalLife; t += 0.25) {
        curvePoints.push({ year: t, expected: parseFloat(expectedTvpiAt(t).toFixed(3)) });
      }

      // Build actual TVPI history from CAS timeline
      const actualPoints = [];
      const fundCas = (filteredData.casTimeline || [])
        .filter(c => c.fund === ff.fund && c.investor === ff.investor)
        .sort((a, b) => a.date.localeCompare(b.date));
      let cumDist = 0;
      const distByDate = {};
      (filteredData.distributions || [])
        .filter(d => d.fund === ff.fund && d.investor === ff.investor)
        .forEach(d => { distByDate[d.date] = (distByDate[d.date] || 0) + getDistAmount(d); });
      // Accumulate distributions up to each CAS date
      const sortedDistDates = Object.keys(distByDate).sort();
      fundCas.forEach(cas => {
        // Sum distributions up through this CAS date
        let distUpTo = 0;
        sortedDistDates.forEach(dd => { if (dd <= cas.date) distUpTo += distByDate[dd]; });
        const casDrawn = Math.abs(cas.drawn_commitment || 0);
        const casNav = cas.nav_closing || 0;
        const casTvpi = casDrawn > 0 ? (casNav + distUpTo) / casDrawn : 0;
        const casDate = new Date(cas.date);
        const casAge = (casDate - firstDate) / MS_PER_YEAR;
        if (casAge >= 0) {
          actualPoints.push({ year: parseFloat(casAge.toFixed(2)), actual: parseFloat(casTvpi.toFixed(3)) });
        }
      });

      funds.push({
        fund: ff.fund,
        investor: ff.investor,
        currency: ff.currency || f.currency,
        hasTarget: true,
        targetMoic,
        actualTvpi,
        expectedTvpi: expectedNow,
        delta,
        deltaPct,
        status,
        ageYears,
        totalLife,
        pctLife,
        vintageYear: ff.vintageYear,
        maturityYear: ff.maturityYear,
        curvePoints,
        actualPoints,
      });
    });

    return { funds };
  }, [cashForecastData, fundTableData, filteredData]);

  // DEFAULT_LEVERAGE_RATIO and DEFAULT_BASE_RATE are defined as module-level constants above

  const leverageData = useMemo(() => {
    const funds = [];
    fundTableData.forEach((f) => {
      const key = makeKey(f.fund, f.investor);
      const v = customVars ? customVars[key] : null;

      // Auto-detect from fund name, Excel can override
      const isLev = v ? v["Leveraged?"] === "Yes" : f.fund.includes("Leveraged");
      const myCommitment = (v && Number(v["My Commitment"])) || f.commitment || 0;
      const totalExposure = (v && Number(v["Total Exposure"]))
        ? Number(v["Total Exposure"])
        : (isLev ? myCommitment * DEFAULT_LEVERAGE_RATIO : myCommitment);
      const coInvest = totalExposure - myCommitment;

      // Capital stack from shared constants
      const { participantAmt, eqtPerfAmt, sebAmt } = calcCapitalStack(totalExposure, myCommitment, isLev);

      // SEB rate: use per-fund avg bank rate if set, otherwise fall back to Excel "Avg Interest Rate (%)" or default
      const avgBankRate = getBankInterestRate(v) * 100; // back to percentage for display
      const sebBaseRate = (v && Number(v["Avg Interest Rate (%)"])) ? Number(v["Avg Interest Rate (%)"]) : (isLev ? avgBankRate : 0);
      const sebEffectiveRate = sebBaseRate + rateShift / 100;
      const sebAnnualInterest = sebAmt * Math.max(0, sebEffectiveRate) / 100;

      // EQT Perf rate (fixed, per-fund slider, default 8%)
      const eqtPerfRate = (eqtPerfRates[key] !== undefined) ? eqtPerfRates[key] : DEFAULT_EQT_PERF_RATE;
      const eqtPerfAnnualInterest = eqtPerfAmt * eqtPerfRate / 100;

      const annualInterest = sebAnnualInterest + eqtPerfAnnualInterest;
      // Per-fund holding period for leverage analysis
      const avgHp = getEffectiveHoldingPeriod(key, v);
      // Balance-weighted interest estimate: during investment phase (yrs 0-5) balance
      // ramps up, then declines during harvest. Average outstanding ≈ 55% of full amount.
      const invPace = INVESTMENT_PACE;
      let estTotalSebInterest = 0, estTotalEqtPrefInterest = 0;
      let estBank = 0, estPref = 0;
      const simLen = invPace.length + avgHp;
      for (let yi = 0; yi < simLen; yi++) {
        const pace = yi < invPace.length ? invPace[yi] : 0;
        estBank += totalExposure * pace * CAPITAL_STACK.seb;
        estPref += totalExposure * pace * CAPITAL_STACK.eqtPerf;
        if (yi >= avgHp && (yi - avgHp) < invPace.length) {
          estBank = Math.max(0, estBank - totalExposure * invPace[yi - avgHp] * WATERFALL.bankRocShare);
          estPref = Math.max(0, estPref - totalExposure * invPace[yi - avgHp] * WATERFALL.eqtPrefGainsShare);
        }
        estTotalSebInterest += estBank * Math.max(0, sebEffectiveRate) / 100;
        estTotalEqtPrefInterest += estPref * eqtPerfRate / 100;
      }
      const totalInterest = estTotalSebInterest + estTotalEqtPrefInterest;
      const leverageRatio = myCommitment > 0 ? totalExposure / myCommitment : 1;

      // Target Fund MOIC from Excel (for forward-looking return impact)
      const hasExcelMoic = v && Number(v["Target Fund MOIC"]) > 0;
      const targetFundMoic = hasExcelMoic ? Number(v["Target Fund MOIC"]) : null;
      const fundMoic = targetFundMoic;
      const moicSource = hasExcelMoic ? "Set" : "Not set";

      // Tax allocation
      const taxablePct = (v && Number(v["Taxable %"]) >= 0) ? Number(v["Taxable %"]) / 100 : 1;

      // Vintage year and first investment year for display
      const vintYr = (v && Number(v["Vintage Year"])) || (f.vintage_year ? Number(f.vintage_year) : null);
      const firstInvYr = vintYr ? getFirstInvestmentYear(v, vintYr) : null;

      funds.push({
        fund: f.fund,
        investor: f.investor,
        currency: f.currency,
        isLeveraged: isLev,
        myCommitment,
        totalExposure,
        coInvest,
        leverageRatio,
        holdingPeriod: avgHp,
        firstInvestmentYear: firstInvYr,
        vintageYear: vintYr,
        // Capital stack
        participantAmt, eqtPerfAmt, sebAmt,
        // SEB (floating)
        sebBaseRate,
        sebEffectiveRate,
        sebAnnualInterest,
        // EQT Perf (fixed)
        eqtPerfRate,
        eqtPerfAnnualInterest,
        // Combined
        annualInterest,
        totalInterest,
        nav: f.nav || 0,
        drawn: f.drawn || 0,
        tvpi: parseFloat(f.tvpi) || 0,
        fundMoic,
        moicSource,
        taxablePct,
      });
    });

    // Sensitivity: what happens at different SEB rate levels
    const leveragedFunds = funds.filter((f) => f.isLeveraged);
    const totalSebAmt = leveragedFunds.reduce((s, f) => s + f.sebAmt, 0);
    const weightedSebRate = totalSebAmt > 0
      ? leveragedFunds.reduce((s, f) => s + f.sebAmt * f.sebBaseRate, 0) / totalSebAmt
      : 0;
    const totalEqtPerfAmt = leveragedFunds.reduce((s, f) => s + f.eqtPerfAmt, 0);
    const weightedEqtPerfRate = totalEqtPerfAmt > 0
      ? leveragedFunds.reduce((s, f) => s + f.eqtPerfAmt * f.eqtPerfRate, 0) / totalEqtPerfAmt
      : 0;
    const totalCoInvest = leveragedFunds.reduce((s, f) => s + f.coInvest, 0);

    const scenarios = [];
    for (let shift = -300; shift <= 300; shift += 50) {
      const scenarioSebRate = weightedSebRate + shift / 100;
      if (scenarioSebRate < 0) continue;
      // Balance-weighted total cost across all leveraged funds for this scenario
      let totalPeriodCost = 0;
      const totalAnnualCost = leveragedFunds.reduce((s, f) => {
        const sebR = f.sebBaseRate + shift / 100;
        return s + f.sebAmt * Math.max(0, sebR) / 100 + f.eqtPerfAnnualInterest;
      }, 0);
      leveragedFunds.forEach(f => {
        const sebR = f.sebBaseRate + shift / 100;
        const ipace = INVESTMENT_PACE;
        const fHp = f.holdingPeriod; // per-fund holding period
        let eb = 0, ep = 0;
        for (let yi = 0; yi < ipace.length + fHp; yi++) {
          const p = yi < ipace.length ? ipace[yi] : 0;
          eb += f.totalExposure * p * CAPITAL_STACK.seb;
          ep += f.totalExposure * p * CAPITAL_STACK.eqtPerf;
          if (yi >= fHp && (yi - fHp) < ipace.length) {
            eb = Math.max(0, eb - f.totalExposure * ipace[yi - fHp] * WATERFALL.bankRocShare);
            ep = Math.max(0, ep - f.totalExposure * ipace[yi - fHp] * WATERFALL.eqtPrefGainsShare);
          }
          totalPeriodCost += eb * Math.max(0, sebR) / 100 + ep * f.eqtPerfRate / 100;
        }
      });
      scenarios.push({
        shift: shift > 0 ? "+" + shift + "bp" : shift === 0 ? "Base" : shift + "bp",
        shiftBps: shift,
        rate: scenarioSebRate.toFixed(2),
        annualCost: Math.round(totalAnnualCost),
        periodCost: Math.round(totalPeriodCost),
        label: scenarioSebRate.toFixed(1) + "%",
      });
    }

    return {
      funds,
      leveragedFunds,
      unleveragedFunds: funds.filter((f) => !f.isLeveraged),
      totalCoInvest,
      totalSebAmt,
      totalEqtPerfAmt,
      weightedSebRate,
      weightedEqtPerfRate,
      scenarios,
      totalAnnualInterest: leveragedFunds.reduce((s, f) => s + f.annualInterest, 0),
      totalSebAnnualInterest: leveragedFunds.reduce((s, f) => s + f.sebAnnualInterest, 0),
      totalEqtPerfAnnualInterest: leveragedFunds.reduce((s, f) => s + f.eqtPerfAnnualInterest, 0),
      totalPeriodInterest: leveragedFunds.reduce((s, f) => s + f.totalInterest, 0),
    };
  }, [customVars, fundTableData, rateShift, holdingPeriodAdjust, holdingPeriodOverrides, eqtPerfRates]);


    return (
    <div className="min-h-screen bg-surface text-ink py-3 sm:py-6">
      <div className="app-container px-0">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-ink mb-2">
            Private Equity Dashboard
          </h1>
          <p className="text-ink-muted">
            Portfolio performance and investment tracking
          </p>
          {/* Excel Variables Loader */}
          <div className="mt-4 flex items-center gap-4">
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              className={`flex items-center gap-3 px-4 py-2 rounded-lg border-2 border-dashed transition cursor-pointer ${
                dragOver ? "border-brand bg-brand/10" : customVars ? "border-success-border/50 bg-success/30" : "border-edge hover:border-edge"
              }`}
              onClick={() => document.getElementById("excel-upload").click()}
            >
              <input
                id="excel-upload"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => e.target.files[0] && handleExcelFile(e.target.files[0])}
              />
              {customVars ? (
                <>
                  <FileText className="w-4 h-4 text-success-fg" />
                  <span className="text-sm text-success-fg">
                    {Object.keys(customVars).length} funds loaded &middot; {customColumns.length} variables
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setCustomVars(null); setCustomColumns([]); }}
                    className="ml-2 p-1 rounded hover:bg-surface-tertiary"
                  >
                    <X className="w-3 h-3 text-ink-muted" />
                  </button>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 text-ink-muted" />
                  <span className="text-sm text-ink-muted">
                    Drop fund_variables.xlsx here or click to load
                  </span>
                </>
              )}
            </div>
          </div>
        </div>


        {/* Filters */}
        <div className="bg-surface-secondary rounded-lg p-6 mb-8 border border-edge-subtle">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-brand" />
            <h2 className="text-lg font-semibold">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Holding Vehicle
              </label>
              <select
                value={selectedInvestor || ""}
                onChange={(e) =>
                  setSelectedInvestor(e.target.value || null)
                }
                className="w-full bg-surface-tertiary border border-edge rounded px-3 py-2 text-ink hover:border-edge focus:border-brand focus:outline-none"
              >
                <option value="">All Vehicles</option>
                {investors.sort().map((inv) => (
                  <option key={inv} value={inv}>
                    {inv}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Fund
              </label>
              <select
                value={selectedFund || ""}
                onChange={(e) =>
                  setSelectedFund(e.target.value || null)
                }
                className="w-full bg-surface-tertiary border border-edge rounded px-3 py-2 text-ink hover:border-edge focus:border-brand focus:outline-none"
              >
                <option value="">All Funds</option>
                {funds.sort().map((fund) => (
                  <option key={fund} value={fund}>
                    {fund}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Currency
              </label>
              <select
                value={selectedCurrency || ""}
                onChange={(e) =>
                  setSelectedCurrency(e.target.value || null)
                }
                className="w-full bg-surface-tertiary border border-edge rounded px-3 py-2 text-ink hover:border-edge focus:border-brand focus:outline-none"
              >
                <option value="">All Currencies</option>
                {currencies.sort().map((curr) => (
                  <option key={curr} value={curr}>
                    {curr}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>


        {/* ===== PAGE TABS ===== */}
        <div className="flex items-center gap-1 mb-6 bg-surface-tertiary/80 rounded-xl p-1.5 border border-edge w-full scroll-x whitespace-nowrap">
          {[
            { key: "commitments", label: "Commitments", icon: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" },
            { key: "performance", label: "Fund Performance", icon: "M23 6l-9.5 9.5-5-5L1 18" },
            { key: "fx", label: "FX Exposure", icon: "M12 2v20M2 12h20" },
            { key: "leverage", label: "Leverage Analysis", icon: "M3 3v18h18" },
            { key: "forecast", label: "Cash Forecast", icon: "M2 12h4l3-9 4 18 3-9h4" },
            { key: "analytics", label: "Analytics", icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" },
            { key: "portfolio-companies", label: "Portfolio Companies", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setPageTab(t.key)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                pageTab === t.key
                  ? "bg-brand text-ink-onAccent shadow-lg ring-1 ring-brand/25"
                  : "text-ink-muted hover:text-ink hover:bg-surface-tertiary/50"
              }`}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d={t.icon} />
              </svg>
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== COMMITMENTS TAB ===== */}
        {pageTab === "commitments" && (
          <div>
            {/* Missing CAS Statement Alert */}
            {missingCasData.missingByFund.length > 0 && (
              <div className="bg-warning/40 border border-warning-border rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-warning-fg text-lg mt-0.5">⚠</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-warning-fg font-semibold text-sm">
                        Missing CAS Statements ({missingCasData.totalMissing} gap{missingCasData.totalMissing !== 1 ? "s" : ""} detected)
                      </h3>
                      <span className="text-xs text-ink-subtle">Through {missingCasData.lastCompletedQ}</span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {missingCasData.missingByFund.map((m, idx) => (
                        <div key={idx} className="bg-surface-secondary/50 rounded-md p-3 border border-edge-subtle">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-ink text-sm font-medium">{m.fund}</span>
                            <span className="text-xs text-ink-subtle">{vehicleDisplayName(m.investor)}</span>
                          </div>
                          {m.noCasData ? (
                            <span className="text-xs text-danger-fg">No CAS statements uploaded</span>
                          ) : (
                            <div>
                              <div className="text-xs text-ink-muted mb-1">
                                Coverage: {m.firstCas} to {m.lastCas} ({m.totalPresent} of {m.totalExpected} quarters)
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {m.missingQuarters.map((q) => (
                                  <span key={q} className="text-xs bg-danger/40 text-danger-fg px-2 py-0.5 rounded">
                                    {q.substring(0, 7)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Commitment Charts */}
        {/* Commitment Utilization (Tabbed) */}
        <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-1 bg-surface-tertiary rounded-lg p-1 whitespace-nowrap">
              {[
                { key: "fund", label: "By Fund" },
                { key: "vehicle", label: "By Vehicle" },
                { key: "strategy", label: "By Strategy" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setCommitTab(t.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    commitTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-surface-tertiary rounded-lg p-1 whitespace-nowrap">
              {[
                { key: "commitment", label: "Commitment" },
                { key: "exposure", label: "Total Exposure" },
                { key: "implied_return", label: "Implied Return" },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setCommitValueMode(m.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    commitValueMode === m.key ? "bg-surface-primary text-ink shadow-sm" : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {commitTab === "fund" && (
            <FundBarChart
              data={commitValueMode === "exposure" ? exposureByFundData : commitValueMode === "implied_return" ? impliedReturnByFundData : commitmentUtilizationData}
              title={commitValueMode === "exposure" ? "Total Exposure by Fund" : commitValueMode === "implied_return" ? "After-Tax Return by Fund" : "Commitment Utilization by Fund"}
              chartHeight={chartH}
              formatCurrency={formatCurrency}
            />
          )}

          {commitTab === "vehicle" && (
            <VehicleBarChart
              data={commitValueMode === "exposure" ? exposureByVehicleData : commitValueMode === "implied_return" ? impliedReturnByVehicleData : vehicleCommitmentData}
              title={commitValueMode === "exposure" ? "Total Exposure by Vehicle" : commitValueMode === "implied_return" ? "After-Tax Return by Vehicle" : "Commitment Utilization by Vehicle"}
              subtitle={commitValueMode === "implied_return"
                ? "Projected after-tax returns based on target MOIC and tax assumptions"
                : commitValueMode === "exposure"
                  ? `Total fund-level exposure (participant commitment ÷ ownership %). EUR converted at ${(Number(FX_DATA.current_rate) || 1).toFixed(4)}`
                  : `EUR converted to USD at current rate (${(Number(FX_DATA.current_rate) || 1).toFixed(4)})`}
              chartHeight={isMobile ? 180 : 250}
              isMobile={isMobile}
            />
          )}

          {commitTab === "strategy" && (
            <DiversificationPies
              fundData={commitValueMode === "implied_return" ? fundTableDataWithReturns : fundTableData}
              valueKey={commitValueMode === "exposure" ? "totalExposure" : commitValueMode === "implied_return" ? "afterTaxReturn" : "commitment"}
              valueLabel={commitValueMode === "exposure" ? "Total Exposure" : commitValueMode === "implied_return" ? "After-Tax Return" : "Commitment"}
              title={commitValueMode === "implied_return" ? "Implied Return Diversification" : commitValueMode === "exposure" ? "Total Exposure Diversification" : "Portfolio Diversification"}
              description={commitValueMode === "implied_return"
                ? "Projected after-tax return allocation across fund classifications. Set return targets in the Cash Flow Forecast tab."
                : commitValueMode === "exposure"
                  ? "Total fund-level exposure allocation across classifications. Shows full fund size, not just participant commitment."
                  : "Commitment allocation across fund classifications. Assign classifications in Fund Variables or the Admin page."}
              isMobile={isMobile}
            />
          )}
        </div>

        {/* ── Tax Allocation (respects commitTab) ── */}
        {(() => {
          const taxData = commitTab === "vehicle" ? taxAllocationByVehicle : commitTab === "strategy" ? taxAllocationByStrategy : taxAllocationByFund;
          const taxLabel = commitTab === "vehicle" ? "Vehicle" : commitTab === "strategy" ? "Strategy" : "Fund";
          if (!taxData || taxData.length === 0) return null;
          return (
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
              <h3 className="text-lg font-semibold mb-1 text-ink">Tax Allocation by {taxLabel}</h3>
              <p className="text-xs text-ink-muted mb-4">How commitments are split across Roth (tax-free), Traditional IRA (tax-deferred), and Taxable accounts</p>
              <ResponsiveContainer width="100%" height={chartH}>
                <BarChart data={taxData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                  <XAxis dataKey="name" stroke="var(--chart-axis)" style={{ fontSize: "0.65rem" }} angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }} formatter={(v) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                  <Bar dataKey="Roth" stackId="tax" fill="var(--chart-positive)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Traditional IRA" stackId="tax" fill="var(--chart-fill-primary)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Taxable" stackId="tax" fill="var(--chart-warning)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* ── Commitment Concentration (respects commitTab) ── */}
        {(() => {
          const concData = commitTab === "vehicle" ? concentrationByVehicle : commitTab === "strategy" ? concentrationByStrategy : concentrationByFund;
          const concLabel = commitTab === "vehicle" ? "vehicles" : commitTab === "strategy" ? "strategies" : "funds";
          if (!concData || concData.length === 0) return null;
          return (
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
              <h3 className="text-lg font-semibold mb-1 text-ink">Commitment Concentration</h3>
              <p className="text-xs text-ink-muted mb-4">Cumulative % of total commitment — shows how concentrated the portfolio is across {concLabel} (ranked largest to smallest)</p>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={concData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                  <XAxis dataKey="name" stroke="var(--chart-axis)" style={{ fontSize: "0.65rem" }} angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                    formatter={(v, name) => [`${v}%`, name]} />
                  <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                  <Area type="monotone" dataKey="cumPct" name="Actual Concentration" stroke="var(--chart-fill-primary)" fill="var(--chart-fill-primary)" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="equalLine" name="Equal Distribution" stroke="var(--chart-axis)" fill="none" strokeDasharray="5 5" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* ── Portfolio Exposure Summary (from PC data) ── */}
        {pcExposureData.companyCount > 0 && (
          <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-ink mb-1">Portfolio Exposure</h3>
                <p className="text-xs text-ink-muted">{pcExposureData.companyCount} portfolio companies across all funds</p>
              </div>
              <button onClick={() => setPageTab("portfolio-companies")}
                className="text-xs text-brand hover:underline">View all companies &rarr;</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Industry donut */}
              <div>
                <h4 className="text-sm font-medium text-ink-muted mb-3">By Industry</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pcExposureData.byIndustry.slice(0, 8)} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {pcExposureData.byIndustry.slice(0, 8).map((entry, i) => (
                        <Cell key={i} fill={["var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)", "var(--chart-warning)", "var(--chart-danger)", "#8b5cf6", "#ec4899", "#14b8a6"][i % 8]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                      formatter={(v, name) => [formatCurrency(v), name]} />
                    <Legend layout="vertical" align="right" verticalAlign="middle"
                      wrapperStyle={{ fontSize: "0.7rem" }}
                      formatter={(val, entry) => {
                        const item = pcExposureData.byIndustry.find(x => x.name === val);
                        return `${val} (${item ? item.pct : 0}%)`;
                      }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Geography donut */}
              <div>
                <h4 className="text-sm font-medium text-ink-muted mb-3">By Geography</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pcExposureData.byGeography.slice(0, 8)} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {pcExposureData.byGeography.slice(0, 8).map((entry, i) => (
                        <Cell key={i} fill={["var(--chart-positive)", "var(--chart-fill-primary)", "var(--chart-warning)", "var(--chart-indigo)", "#14b8a6", "#8b5cf6", "var(--chart-danger)", "#ec4899"][i % 8]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                      formatter={(v, name) => [formatCurrency(v), name]} />
                    <Legend layout="vertical" align="right" verticalAlign="middle"
                      wrapperStyle={{ fontSize: "0.7rem" }}
                      formatter={(val, entry) => {
                        const item = pcExposureData.byGeography.find(x => x.name === val);
                        return `${val} (${item ? item.pct : 0}%)`;
                      }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

                {/* ===== FUND VARIABLES PANEL ===== */}
        {customVars && customColumns.length > 0 && (
          <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-8 overflow-hidden">
            <button
              onClick={() => setShowVariables(!showVariables)}
              className="w-full flex items-center justify-between p-6 hover:bg-surface-secondary/50 transition"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-brand-secondary" />
                <h2 className="text-lg font-semibold text-ink">Fund Variables</h2>
                <span className="text-sm text-ink-muted">
                  ({customColumns.length} variables across {Object.keys(customVars).length} funds)
                </span>
              </div>
              {showVariables ? <ChevronUp className="w-5 h-5 text-ink-muted" /> : <ChevronDown className="w-5 h-5 text-ink-muted" />}
            </button>
            {showVariables && (
              <div className="px-3 sm:px-6 pb-4 sm:pb-6 scroll-x">
                <table className="w-full text-sm min-w-[600px]">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Fund</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Vehicle</th>
                      {customColumns.map((col) => (
                        <th key={col} className="text-center py-3 px-4 font-semibold text-brand-secondary">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fundTableData.map((fund) => {
                      const key = makeKey(fund.fund, fund.investor);
                      const hasData = customVars[key] && Object.keys(customVars[key]).length > 0;
                    
    return (
                        <tr key={key} className="border-b border-edge-subtle hover:bg-surface-secondary/50 transition">
                          <td className="py-3 px-4 font-medium text-ink">{fund.fund}</td>
                          <td className="py-3 px-4 text-ink-muted text-xs">{fund.investor}</td>
                          {customColumns.map((col) => {
                            const val = getCustomVar(fund.fund, fund.investor, col);
                          
    return (
                              <td key={col} className="text-center py-3 px-4">
                                {val !== null ? (
                                  <span className="text-brand-secondary">{typeof val === "number" ? (col.includes("%") || col.includes("IRR") ? val + "%" : col.includes("Multiple") || col.includes("Return") ? val + "x" : val) : val}</span>
                                ) : (
                                  <span className="text-ink-subtle">&mdash;</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

          </div>
        )}

        {/* ===== FUND PERFORMANCE TAB ===== */}
        {pageTab === "performance" && (
          <div>
        {/* Performance Sub-Tabs */}
        <div className="flex items-center gap-1 mb-6 bg-surface-tertiary rounded-lg p-1 w-full scroll-x whitespace-nowrap">
          {[
            { key: "summary", label: "Fund Summary" },
            { key: "segmentation", label: "By Segmentation" },
            { key: "leverage", label: "By Leverage" },
            { key: "vehicle", label: "By Vehicle" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setPerfTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                perfTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── By Segmentation Tab ── */}
        {perfTab === "segmentation" && (() => {
          const STRAT_COLORS = {
            "Equity": "var(--chart-fill-primary)", "Private Capital": "var(--chart-fill-primary)",
            "Growth": "var(--chart-positive)", "Real Assets": "var(--chart-positive)",
            "Real Estate": "var(--chart-indigo)", "Infrastructure": "var(--chart-warning)",
            "Ventures": "#8b5cf6", "Credit": "#ec4899",
          };
          const SEGMENT_COLORS = [
            "var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)",
            "var(--chart-warning)", "var(--chart-danger)", "#8b5cf6", "#ec4899", "#14b8a6",
          ];

          const fundsWithPerf = fundTableData
            .filter(f => (f.drawn || 0) > 0)
            .map(f => {
              const nav = f.nav || 0;
              const dist = f.distributed || 0;
              const drawn = f.drawn || 0;
              const now = new Date();
              const fdDate = f.firstDrawdownDate ? new Date(f.firstDrawdownDate) : null;
              const ageYears = fdDate ? (now - fdDate) / (365.25 * 24 * 60 * 60 * 1000) : null;
              const tvpi = parseFloat(f.tvpi) || 0;
              // Use fundMoic from fund variables if available, otherwise fall back to TVPI
              const moic = f.fundMoic || tvpi;
              return {
                name: f.fund,
                investor: f.investor,
                tvpi,
                moic,
                commitment: f.commitment || 0,
                drawn,
                nav,
                distributed: dist,
                strategy: f.fundStrategy || f.fundArea || "Other",
                age: ageYears !== null ? parseFloat(ageYears.toFixed(1)) : null,
              };
            })
            .filter(f => f.age !== null && f.age > 0);

          // Weighted TVPI by strategy for summary bar charts
          const avgByDimension = (dim) => {
            const groups = {};
            fundsWithPerf.forEach(f => {
              const key = f[dim];
              if (!groups[key]) groups[key] = { sum: 0, count: 0, totalDrawn: 0, totalValue: 0 };
              groups[key].sum += f.tvpi;
              groups[key].count += 1;
              groups[key].totalDrawn += f.drawn;
              groups[key].totalValue += (f.nav + f.distributed);
            });
            return Object.entries(groups).map(([name, g]) => ({
              name,
              avgTvpi: parseFloat((g.sum / g.count).toFixed(2)),
              weightedTvpi: g.totalDrawn > 0 ? parseFloat((g.totalValue / g.totalDrawn).toFixed(2)) : 0,
              fundCount: g.count,
            }));
          };
          const byStrategy = avgByDimension("strategy");

          return (
            <div>
              <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
                <h3 className="text-lg font-semibold mb-1 text-ink">Performance by Segmentation</h3>
                <p className="text-ink-muted text-xs mb-4">
                  Each bubble is a fund — size reflects weighted TVPI, position shows years since first drawdown vs. fund MOIC. Colored by strategy.
                </p>

                {/* Summary bar: weighted TVPI by strategy */}
                <div className="flex flex-wrap gap-3 mb-5">
                  {byStrategy.map((s) => (
                    <div key={s.name} className="flex items-center gap-2 bg-surface-tertiary/50 rounded-lg px-3 py-2 border border-edge-subtle">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STRAT_COLORS[s.name] || SEGMENT_COLORS[byStrategy.indexOf(s) % SEGMENT_COLORS.length] }} />
                      <span className="text-xs text-ink font-medium">{s.name}</span>
                      <span className="text-xs text-ink-muted">{s.fundCount} fund{s.fundCount !== 1 ? "s" : ""}</span>
                      <span className="text-xs font-semibold text-brand-secondary">{s.weightedTvpi}x</span>
                    </div>
                  ))}
                </div>

                {fundsWithPerf.length > 0 ? (
                  <ResponsiveContainer width="100%" height={450}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="age" name="Years" type="number" tick={{ fill: "var(--chart-axis)", fontSize: 11 }}
                        label={{ value: "Years Since First Drawdown", position: "insideBottom", offset: -15, fill: "var(--chart-axis)", fontSize: 11 }} />
                      <YAxis dataKey="moic" name="MOIC" type="number" tick={{ fill: "var(--chart-axis)", fontSize: 11 }}
                        tickFormatter={v => `${v}x`} domain={[0, "auto"]}
                        label={{ value: "MOIC", angle: -90, position: "insideLeft", fill: "var(--chart-axis)", fontSize: 11 }} />
                      <ZAxis dataKey="tvpi" range={[60, 500]} name="Weighted TVPI" />
                      <Tooltip
                        contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem", fontSize: "0.75rem" }}
                        formatter={(value, name) => {
                          if (name === "MOIC" || name === "Weighted TVPI") return [`${value}x`, name];
                          if (name === "Years") return [`${value} yrs`, name];
                          return [value, name];
                        }}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""}
                      />
                      <ReferenceLine y={1} stroke="var(--chart-axis)" strokeDasharray="3 3" label={{ value: "1.0x", position: "right", fill: "var(--chart-axis)", fontSize: 10 }} />
                      {[...new Set(fundsWithPerf.map(f => f.strategy))].map((strat, i) => (
                        <Scatter key={strat} name={strat} data={fundsWithPerf.filter(f => f.strategy === strat)} fill={STRAT_COLORS[strat] || SEGMENT_COLORS[i % SEGMENT_COLORS.length]} opacity={0.8} />
                      ))}
                      <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "0.5rem" }} />
                    </ScatterChart>
                  </ResponsiveContainer>
                ) : <div className="h-[450px] flex items-center justify-center text-ink-subtle text-sm">No funds with drawdown data</div>}
              </div>
            </div>
          );
        })()}

        {/* ── Fund Summary Tab ── */}
        {perfTab === "summary" && (
          <div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {/* Total Committed EUR */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Committed (EUR)
              </span>
              <DollarSign className="w-5 h-5 text-brand" />
            </div>
            <p className="text-2xl font-bold text-ink">
              €{formatCurrency(summary.totalCommittedEur)}
            </p>
          </div>

          {/* Total Committed USD */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Committed (USD)
              </span>
              <DollarSign className="w-5 h-5 text-success-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              ${formatCurrency(summary.totalCommittedUsd)}
            </p>
          </div>

          {/* Total Paid In EUR */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Paid In (EUR)
              </span>
              <ArrowDownRight className="w-5 h-5 text-warning-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              €{formatCurrency(summary.totalDrawnEur)}
            </p>
          </div>

          {/* Total Paid In USD */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Paid In (USD)
              </span>
              <ArrowDownRight className="w-5 h-5 text-warning-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              ${formatCurrency(summary.totalDrawnUsd)}
            </p>
          </div>

          {/* Total Received EUR */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Received (EUR)
              </span>
              <ArrowUpRight className="w-5 h-5 text-success-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              €{formatCurrency(summary.totalDistributedEur)}
            </p>
          </div>

          {/* Total Received USD */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Received (USD)
              </span>
              <ArrowUpRight className="w-5 h-5 text-success-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              ${formatCurrency(summary.totalDistributedUsd)}
            </p>
          </div>

          {/* Current NAV EUR */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                NAV (EUR)
              </span>
              <TrendingUp className="w-5 h-5 text-brand" />
            </div>
            <p className="text-2xl font-bold text-ink">
              €{formatCurrency(summary.totalNavEur)}
            </p>
          </div>

          {/* Current NAV USD */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                NAV (USD)
              </span>
              <TrendingUp className="w-5 h-5 text-success-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              ${formatCurrency(summary.totalNavUsd)}
            </p>
          </div>

          {/* Overall TVPI */}
          <div className="border-2 border-info-border bg-info/80 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Overall TVPI
              </span>
              <TrendingUp className="w-5 h-5 text-brand-secondary" />
            </div>
            <p className="text-2xl font-bold text-ink">
              {summary.overallTvpi}x
            </p>
            <p className="text-xs text-brand-secondary mt-1">
              Total Value / Paid In
            </p>
          </div>

          {/* Overall DPI */}
          <div className="border-2 border-success-border bg-success/80 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                Overall DPI
              </span>
              <TrendingUp className="w-5 h-5 text-success-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              {summary.overallDpi}x
            </p>
            <p className="text-xs text-success-fg mt-1">
              Distributions / Paid In
            </p>
          </div>

          {/* EUR TVPI */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                TVPI (EUR)
              </span>
              <BarChart3 className="w-5 h-5 text-brand" />
            </div>
            <p className="text-2xl font-bold text-ink">
              {summary.tvpiEur}x
            </p>
          </div>

          {/* USD TVPI */}
          <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-muted">
                TVPI (USD)
              </span>
              <BarChart3 className="w-5 h-5 text-success-fg" />
            </div>
            <p className="text-2xl font-bold text-ink">
              {summary.tvpiUsd}x
            </p>
          </div>
        </div>


            {/* Cash Flow & NAV Charts */}
        {/* ===== CHARTS SECTION (Tabbed) ===== */}
        {/* Cash Flow & NAV */}
        <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
          <div className="flex items-center gap-1 mb-4 bg-surface-tertiary rounded-lg p-1 w-full scroll-x whitespace-nowrap">
            {[
              { key: "cf_fund", label: "Cash Flow" },
              { key: "cf_vehicle", label: "Cash Flow by Vehicle" },
              { key: "nav_fund", label: "NAV by Fund" },
              { key: "nav_vehicle", label: "NAV by Vehicle" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setCashNavTab(t.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  cashNavTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {cashNavTab === "cf_fund" && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-ink">Cumulative Cash Flow</h3>
              {cumulativeCashFlowData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartH}>
                  <AreaChart data={cumulativeCashFlowData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="date" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }} formatter={(value) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                    <Area type="monotone" dataKey="Paid In" stroke="var(--chart-negative)" fill="var(--chart-negative)" fillOpacity={0.3} />
                    <Area type="monotone" dataKey="Distributed" stroke="var(--chart-positive)" fill="var(--chart-positive)" fillOpacity={0.3} />
                    <Area type="monotone" dataKey="Net Position" stroke="var(--chart-fill-primary)" fill="var(--chart-fill-primary)" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-ink-subtle">No data available</div>}
            </div>
          )}

          {cashNavTab === "cf_vehicle" && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-ink">Cumulative Cash Flow by Vehicle</h3>
              {vehicleCashFlowData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartH}>
                  <AreaChart data={vehicleCashFlowData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="date" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }} formatter={(value) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                    {vehicleCashFlowData.length > 0 && Object.keys(vehicleCashFlowData[0]).filter(k => k !== "date" && k !== "rawDate" && k.endsWith(" Net")).map((vKey, idx) => (
                      <Area key={vKey} type="monotone" dataKey={vKey} stackId="1" stroke={colors[idx % colors.length]} fill={colors[idx % colors.length]} fillOpacity={0.5} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-ink-subtle">No data available</div>}
            </div>
          )}

          {cashNavTab === "nav_fund" && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-ink">NAV Over Time (by Fund)</h3>
              {navOverTimeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartH}>
                  <AreaChart data={navOverTimeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="date" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }} formatter={(value) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                    {fundTableData.map((fund, idx) => {
                      const fundKey = `${fund.fund} (${fund.investor.split(" ")[0]})`;
                      return (
                        <Area key={fundKey} type="monotone" dataKey={fundKey} stackId="1" stroke={colors[idx % colors.length]} fill={colors[idx % colors.length]} fillOpacity={0.7} />
                      );
                    })}
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-ink-subtle">No data available</div>}
            </div>
          )}

          {cashNavTab === "nav_vehicle" && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-ink">NAV Over Time (by Vehicle)</h3>
              {vehicleNavOverTimeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartH}>
                  <AreaChart data={vehicleNavOverTimeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="date" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }} formatter={(value) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                    {vehicleNavOverTimeData.length > 0 && [...new Set(vehicleNavOverTimeData.flatMap(r => Object.keys(r)))].filter(k => k !== "date" && k !== "rawDate").map((vKey, idx) => (
                      <Area key={vKey} type="monotone" dataKey={vKey} stackId="1" stroke={colors[idx % colors.length]} fill={colors[idx % colors.length]} fillOpacity={0.7} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-ink-subtle">No data available</div>}
            </div>
          )}
        </div>


        {/* After-Tax Return Waterfall — pinned to Fund Summary */}
        {afterTaxWaterfallData.length > 0 && (
          <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
            <h3 className="text-lg font-semibold mb-1 text-ink">After-Tax Return Waterfall</h3>
            <p className="text-xs text-ink-muted mb-4">Portfolio-level breakdown: Gross Return → Interest Cost → Taxes → Net After-Tax Return</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={afterTaxWaterfallData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis type="number" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" stroke="var(--chart-axis)" width={120} style={{ fontSize: "0.75rem" }} />
                <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                  formatter={(v) => formatCurrency(Math.abs(v))} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {afterTaxWaterfallData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

          </div>
        )}

        {/* ── By Leverage Tab ── */}
        {perfTab === "leverage" && (() => {
          const LEVERAGE_COLORS = {
            leveraged: "var(--chart-fill-primary)",
            unleveraged: "var(--chart-positive)",
          };
          // Build leverage comparison data
          const fundsWithPerf = fundTableData
            .filter(f => (f.drawn || 0) > 0)
            .map(f => ({
              name: f.fund,
              investor: f.investor,
              tvpi: parseFloat(f.tvpi) || 0,
              dpi: parseFloat(f.dpi) || 0,
              commitment: f.commitment || 0,
              drawn: f.drawn || 0,
              nav: f.nav || 0,
              distributed: f.distributed || 0,
              isLeveraged: f.isLeveraged,
              area: f.fundArea,
              vintageYear: f.vintageYear,
            }));

          const leveraged = fundsWithPerf.filter(f => f.isLeveraged);
          const unleveraged = fundsWithPerf.filter(f => !f.isLeveraged);

          const calcGroup = (funds, label) => {
            const totalDrawn = funds.reduce((s, f) => s + f.drawn, 0);
            const totalValue = funds.reduce((s, f) => s + f.nav + f.distributed, 0);
            const avgTvpi = funds.length > 0 ? +(funds.reduce((s, f) => s + f.tvpi, 0) / funds.length).toFixed(2) : 0;
            const weightedTvpi = totalDrawn > 0 ? +(totalValue / totalDrawn).toFixed(2) : 0;
            const avgDpi = funds.length > 0 ? +(funds.reduce((s, f) => s + f.dpi, 0) / funds.length).toFixed(2) : 0;
            return { name: label, fundCount: funds.length, avgTvpi, weightedTvpi, avgDpi, totalCommitted: funds.reduce((s, f) => s + f.commitment, 0), totalDrawn, totalValue };
          };

          const compData = [calcGroup(leveraged, "Leveraged"), calcGroup(unleveraged, "Unleveraged")];

          return (
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold mb-1 text-ink">Leveraged vs Unleveraged Performance</h3>
              <p className="text-ink-muted text-xs mb-4">Compares funds with leverage structure against direct investments.</p>

              {/* Summary comparison cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {compData.map(g => (
                  <div key={g.name} className={`rounded-lg p-5 border ${g.name === "Leveraged" ? "border-brand/30 bg-brand/5" : "border-success-border bg-success/10"}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-ink">{g.name}</h4>
                      <span className="text-xs text-ink-muted">{g.fundCount} fund{g.fundCount !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <div className="text-xs text-ink-muted">Weighted TVPI</div>
                        <div className={`text-lg font-bold ${g.weightedTvpi >= 1 ? "text-success-fg" : "text-danger-fg"}`}>{g.weightedTvpi}x</div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-muted">Avg TVPI</div>
                        <div className={`text-lg font-bold ${g.avgTvpi >= 1 ? "text-success-fg" : "text-danger-fg"}`}>{g.avgTvpi}x</div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-muted">Avg DPI</div>
                        <div className="text-lg font-bold text-ink">{g.avgDpi}x</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Side-by-side bar chart */}
              <div className="bg-surface-tertiary/50 rounded-lg p-4 border border-edge-subtle mb-6">
                <h4 className="text-sm font-semibold text-ink mb-3">TVPI Comparison</h4>
                <ResponsiveContainer width="100%" height={chartHSm}>
                  <BarChart data={compData} margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="name" tick={{ fill: "var(--chart-axis)", fontSize: 11 }} />
                    <YAxis tick={{ fill: "var(--chart-axis)", fontSize: 11 }} tickFormatter={v => `${v}x`} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem", fontSize: "0.75rem" }} formatter={v => `${v}x`} />
                    <Bar dataKey="weightedTvpi" fill={LEVERAGE_COLORS.leveraged} name="Weighted TVPI" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="avgTvpi" fill={LEVERAGE_COLORS.unleveraged} name="Avg TVPI" radius={[4, 4, 0, 0]} />
                    <ReferenceLine y={1} stroke="var(--chart-axis)" strokeDasharray="3 3" />
                    <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "0.5rem" }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Individual fund scatter by leverage */}
              <div className="bg-surface-tertiary/50 rounded-lg p-4 border border-edge-subtle">
                <h4 className="text-sm font-semibold text-ink mb-1">Individual Fund Performance</h4>
                <p className="text-xs text-ink-muted mb-3">Bubble size = TVPI. Color = leverage status.</p>
                {fundsWithPerf.length > 0 ? (
                  <ResponsiveContainer width="100%" height={chartH}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="drawn" name="Drawn" tick={{ fill: "var(--chart-axis)", fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis dataKey="tvpi" name="TVPI" tick={{ fill: "var(--chart-axis)", fontSize: 11 }} tickFormatter={v => `${v}x`} domain={[0, "auto"]} />
                      <ZAxis dataKey="tvpi" range={[40, 400]} name="TVPI" />
                      <Tooltip
                        contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem", fontSize: "0.75rem" }}
                        formatter={(value, name) => {
                          if (name === "TVPI") return [`${value}x`, name];
                          if (name === "Drawn") return [`$${Number(value).toLocaleString()}`, name];
                          return [value, name];
                        }}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""}
                      />
                      <ReferenceLine y={1} stroke="var(--chart-axis)" strokeDasharray="3 3" label={{ value: "1.0x", position: "right", fill: "var(--chart-axis)", fontSize: 10 }} />
                      <Scatter name="Leveraged" data={leveraged} fill={LEVERAGE_COLORS.leveraged} opacity={0.8} />
                      <Scatter name="Unleveraged" data={unleveraged} fill={LEVERAGE_COLORS.unleveraged} opacity={0.8} />
                      <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "0.5rem" }} />
                    </ScatterChart>
                  </ResponsiveContainer>
                ) : <div className="h-[300px] flex items-center justify-center text-ink-subtle text-sm">No data</div>}
              </div>
            </div>
          );
        })()}

        {/* ── By Vehicle Tab ── */}
        {perfTab === "vehicle" && (() => {
          const VEHICLE_COLORS = [
            "var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)",
            "var(--chart-warning)", "var(--chart-danger)", "#8b5cf6", "#ec4899", "#14b8a6",
          ];

          const fundsWithPerf = fundTableData
            .filter(f => (f.drawn || 0) > 0)
            .map(f => ({
              name: f.fund,
              investor: f.investor,
              tvpi: parseFloat(f.tvpi) || 0,
              dpi: parseFloat(f.dpi) || 0,
              commitment: f.commitment || 0,
              drawn: f.drawn || 0,
              nav: f.nav || 0,
              distributed: f.distributed || 0,
            }));

          // Group by vehicle (investor)
          const byVehicle = {};
          fundsWithPerf.forEach(f => {
            if (!byVehicle[f.investor]) byVehicle[f.investor] = { funds: [], totalDrawn: 0, totalValue: 0, totalCommitted: 0 };
            byVehicle[f.investor].funds.push(f);
            byVehicle[f.investor].totalDrawn += f.drawn;
            byVehicle[f.investor].totalValue += (f.nav + f.distributed);
            byVehicle[f.investor].totalCommitted += f.commitment;
          });

          const vehicleData = Object.entries(byVehicle).map(([vehicle, g]) => ({
            name: vehicle,
            fundCount: g.funds.length,
            avgTvpi: +(g.funds.reduce((s, f) => s + f.tvpi, 0) / g.funds.length).toFixed(2),
            weightedTvpi: g.totalDrawn > 0 ? +(g.totalValue / g.totalDrawn).toFixed(2) : 0,
            avgDpi: +(g.funds.reduce((s, f) => s + f.dpi, 0) / g.funds.length).toFixed(2),
            totalCommitted: g.totalCommitted,
            totalDrawn: g.totalDrawn,
          })).sort((a, b) => b.weightedTvpi - a.weightedTvpi);

          return (
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold mb-1 text-ink">Performance by Vehicle</h3>
              <p className="text-ink-muted text-xs mb-4">Performance metrics grouped by investor entity / vehicle.</p>

              {/* Vehicle summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                {vehicleData.map((v, i) => (
                  <div key={v.name} className="rounded-lg p-4 border border-edge-subtle bg-surface-tertiary/50">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: VEHICLE_COLORS[i % VEHICLE_COLORS.length] }} />
                      <h4 className="text-sm font-semibold text-ink truncate">{v.name}</h4>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <div className="text-xs text-ink-muted">W. TVPI</div>
                        <div className={`text-base font-bold ${v.weightedTvpi >= 1 ? "text-success-fg" : "text-danger-fg"}`}>{v.weightedTvpi}x</div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-muted">Avg TVPI</div>
                        <div className={`text-base font-bold ${v.avgTvpi >= 1 ? "text-success-fg" : "text-danger-fg"}`}>{v.avgTvpi}x</div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-muted">Funds</div>
                        <div className="text-base font-bold text-ink">{v.fundCount}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bar chart comparison */}
              <div className="bg-surface-tertiary/50 rounded-lg p-4 border border-edge-subtle mb-6">
                <h4 className="text-sm font-semibold text-ink mb-3">TVPI by Vehicle</h4>
                {vehicleData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(isMobile ? 160 : 200, vehicleData.length * (isMobile ? 40 : 50))}>
                    <BarChart data={vehicleData} layout="vertical" margin={{ left: isMobile ? 10 : 60, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 11 }} tickFormatter={v => `${v}x`} />
                      <YAxis dataKey="name" type="category" tick={{ fill: "var(--chart-axis)", fontSize: isMobile ? 9 : 11 }} width={isMobile ? 80 : 120} />
                      <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem", fontSize: "0.75rem" }} formatter={v => `${v}x`} />
                      <Bar dataKey="weightedTvpi" fill="var(--chart-fill-primary)" name="Weighted TVPI" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="avgTvpi" fill="var(--chart-positive)" name="Avg TVPI" radius={[0, 4, 4, 0]} />
                      <ReferenceLine x={1} stroke="var(--chart-axis)" strokeDasharray="3 3" />
                      <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "0.5rem" }} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="h-[200px] flex items-center justify-center text-ink-subtle text-sm">No data</div>}
              </div>

              {/* Scatter: funds colored by vehicle */}
              <div className="bg-surface-tertiary/50 rounded-lg p-4 border border-edge-subtle">
                <h4 className="text-sm font-semibold text-ink mb-1">Fund Distribution by Vehicle</h4>
                <p className="text-xs text-ink-muted mb-3">Bubble size = TVPI. Color = vehicle.</p>
                {fundsWithPerf.length > 0 ? (
                  <ResponsiveContainer width="100%" height={chartH}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="drawn" name="Drawn" tick={{ fill: "var(--chart-axis)", fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis dataKey="tvpi" name="TVPI" tick={{ fill: "var(--chart-axis)", fontSize: 11 }} tickFormatter={v => `${v}x`} domain={[0, "auto"]} />
                      <ZAxis dataKey="tvpi" range={[40, 400]} name="TVPI" />
                      <Tooltip
                        contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem", fontSize: "0.75rem" }}
                        formatter={(value, name) => {
                          if (name === "TVPI") return [`${value}x`, name];
                          if (name === "Drawn") return [`$${Number(value).toLocaleString()}`, name];
                          return [value, name];
                        }}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""}
                      />
                      <ReferenceLine y={1} stroke="var(--chart-axis)" strokeDasharray="3 3" label={{ value: "1.0x", position: "right", fill: "var(--chart-axis)", fontSize: 10 }} />
                      {[...new Set(fundsWithPerf.map(f => f.investor))].map((vehicle, i) => (
                        <Scatter key={vehicle} name={vehicle} data={fundsWithPerf.filter(f => f.investor === vehicle)} fill={VEHICLE_COLORS[i % VEHICLE_COLORS.length]} opacity={0.8} />
                      ))}
                      <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "0.5rem" }} />
                    </ScatterChart>
                  </ResponsiveContainer>
                ) : <div className="h-[300px] flex items-center justify-center text-ink-subtle text-sm">No data</div>}
              </div>
            </div>
          );
        })()}

        {/* Fund Performance Summary — always visible at bottom of Performance tab */}
        <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mt-8">
          <h2 className="text-lg font-semibold mb-4 text-ink">
            Fund Performance Summary
          </h2>
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-edge">
                <th className="text-left py-3 px-4 font-semibold text-ink-muted cursor-pointer hover:text-ink">Fund</th>
                <th className="text-left py-3 px-4 font-semibold text-ink-muted cursor-pointer hover:text-ink">Vehicle</th>
                <th className="text-right py-3 px-4 font-semibold text-ink-muted">Commitment</th>
                <th className="text-right py-3 px-4 font-semibold text-ink-muted">Drawn</th>
                <th className="text-right py-3 px-4 font-semibold text-ink-muted">Distributed</th>
                <th className="text-right py-3 px-4 font-semibold text-ink-muted">NAV</th>
                <th className="text-right py-3 px-4 font-semibold text-ink-muted">TVPI</th>
                <th className="text-right py-3 px-4 font-semibold text-ink-muted">DPI</th>
                <th className="text-right py-3 px-4 font-semibold text-ink-muted">Remaining</th>
                {customColumns.map((col) => (
                  <th key={col} className="text-center py-3 px-4 font-semibold text-brand-secondary text-sm">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fundTableData.map((fund, idx) => {
                const fundKey = `${fund.fund}||${fund.investor}`;
                const pcs = pcByFundKey[fundKey] || [];
                const isExpanded = expandedFundRows[fundKey];
                return (
                  <Fragment key={fundKey}>
                    <tr className={`border-b border-edge-subtle hover:bg-surface-tertiary transition ${pcs.length > 0 ? "cursor-pointer" : ""}`}
                      onClick={() => pcs.length > 0 && setExpandedFundRows(prev => ({ ...prev, [fundKey]: !prev[fundKey] }))}>
                      <td className="py-3 px-4 font-medium text-ink">
                        <span className="flex items-center gap-1.5">
                          {pcs.length > 0 && (
                            <svg className={`w-3.5 h-3.5 text-ink-muted transition-transform ${isExpanded ? "rotate-90" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                          )}
                          {fund.fund}
                          {pcs.length > 0 && <span className="text-[10px] text-ink-subtle ml-1">({pcs.length} cos)</span>}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-ink-muted">{fund.investor}</td>
                      <td className="text-right py-3 px-4 text-ink-muted">{fund.currency}{formatCurrency(fund.commitment)}</td>
                      <td className="text-right py-3 px-4 text-ink-muted">{fund.currency}{formatCurrency(fund.drawn || 0)}</td>
                      <td className="text-right py-3 px-4 text-success-fg">{fund.currency}{formatCurrency(fund.distributed || 0)}</td>
                      <td className="text-right py-3 px-4 text-brand">{fund.currency}{formatCurrency(fund.nav || 0)}</td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${parseFloat(fund.tvpi) >= 1.0 ? "text-success-fg" : "text-danger-fg"}`}>{fund.tvpi}x</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${parseFloat(fund.dpi) >= 0.5 ? "text-success-fg" : "text-ink-muted"}`}>{fund.dpi}x</span>
                      </td>
                      <td className="text-right py-3 px-4 text-ink-muted">{fund.remainingPct}%</td>
                      {customColumns.map((col) => {
                        const val = getCustomVar(fund.fund, fund.investor, col);
                        return (
                          <td key={col} className="text-center py-3 px-4 text-brand-secondary text-sm">
                            {val !== null ? (typeof val === "number" ? (col.includes("%") || col.includes("IRR") ? val + "%" : col.includes("Multiple") || col.includes("Return") ? val + "x" : val) : val) : <span className="text-ink-subtle">&mdash;</span>}
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded && pcs.map((pc, pi) => (
                      <tr key={`${fundKey}__pc__${pi}`} className="bg-surface-tertiary/30 border-b border-edge-subtle/50">
                        <td className="py-2 px-4 pl-10 text-xs text-ink" colSpan={2}>
                          <span className="font-medium">{pc.name}</span>
                          <span className="text-ink-subtle ml-2">{pc.industry} / {pc.geography}</span>
                        </td>
                        <td className="text-right py-2 px-4 text-xs text-ink-subtle">&mdash;</td>
                        <td className="text-right py-2 px-4 text-xs text-ink-subtle">&mdash;</td>
                        <td className="text-right py-2 px-4 text-xs text-success-fg">{pc.realized ? formatCurrency(pc.realized) : "—"}</td>
                        <td className="text-right py-2 px-4 text-xs text-brand">{pc.unrealized ? formatCurrency(pc.unrealized) : "—"}</td>
                        <td className="text-right py-2 px-4 text-xs">
                          {pc.moic ? <span className={`font-medium ${pc.moic >= 1.0 ? "text-success-fg" : "text-danger-fg"}`}>{pc.moic.toFixed(2)}x</span> : "—"}
                        </td>
                        <td className="text-right py-2 px-4 text-xs text-ink-subtle">&mdash;</td>
                        <td className="text-right py-2 px-4 text-xs text-ink-subtle">&mdash;</td>
                        {customColumns.map((col) => (
                          <td key={col} className="text-center py-2 px-4 text-xs text-ink-subtle">&mdash;</td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

          </div>
        )}

        {/* ===== FX EXPOSURE TAB ===== */}
        {pageTab === "fx" && (
          <div>
            {/* FX Exposure Charts */}
        {/* FX Exposure (Tabbed) */}
        <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
          <div className="flex items-center gap-1 mb-4 bg-surface-tertiary rounded-lg p-1 w-full scroll-x whitespace-nowrap">
            {[
              { key: "fund", label: "By Fund" },
              { key: "vehicle", label: "By Vehicle" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setFxExpTab(t.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  fxExpTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {fxExpTab === "fund" && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-ink">FX Exposure by Fund</h3>
              {fxBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartH}>
                  <BarChart data={fxBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="fund" stroke="var(--chart-axis)" style={{ fontSize: "0.65rem" }} angle={-45} textAnchor="end" height={100} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }} formatter={(value) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                    <Bar dataKey="EUR" fill="var(--chart-fill-primary)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="USD" fill="var(--chart-positive)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-ink-subtle">No data available</div>}
            </div>
          )}

          {fxExpTab === "vehicle" && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-ink">FX Exposure by Vehicle</h3>
              {fxByVehicle.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartH}>
                  <BarChart data={fxByVehicle}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="vehicle" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }} formatter={(value) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                    <Bar dataKey="EUR" fill="var(--chart-fill-primary)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="USD" fill="var(--chart-positive)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-ink-subtle">No data available</div>}
            </div>
          )}
        </div>


            {/* Detailed FX Analysis */}
                {/* ===== FX ANALYSIS SECTION ===== */}
        <div className="mt-12">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-ink mb-1">FX Analysis</h2>
            <p className="text-ink-muted text-sm">
              EUR/USD impact on EUR-denominated funds. Your base currency is USD — eleven funds are
              EUR-denominated, so EUR/USD movements affect your USD returns.
            </p>
          </div>

          {/* Rate timeline + sensitivity side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* EUR/USD chart */}
            <div className="lg:col-span-2 bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold text-ink mb-1">EUR/USD Rate History</h3>
              <p className="text-ink-muted text-xs mb-4">Live data from currency MCP server · Base: EUR</p>
              {FX_DATA.fx_timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 160 : 220}>
                <LineChart data={FX_DATA.fx_timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                  <XAxis dataKey="date" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                    tickFormatter={(d) => {
                      if (d == null || typeof d !== "string") return "";
                      const p = d.split("-");
                      if (p.length < 2 || !p[0]) return "";
                      return `${p[1]}/${p[0].slice(2)}`;
                    }}
                    interval={3}
                  />
                  <YAxis domain={[0.92, 1.25]} tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                    tickFormatter={(v) => (typeof v === "number" && !Number.isNaN(v) ? v.toFixed(2) : "")}
                    width={45}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                    formatter={(v) => [(v != null && typeof v === 'number' ? v.toFixed(4) : '—'), "EUR/USD"]}
                    labelStyle={{ color: "var(--chart-axis)" }}
                  />
                  {/* Entry rate band annotations */}
                  <Line type="monotone" dataKey="eurUsd" stroke="var(--chart-fill-primary)" strokeWidth={2} dot={false} name="EUR/USD" />
                </LineChart>
              </ResponsiveContainer>
              ) : (
              <div className="flex items-center justify-center h-[220px] text-ink-subtle text-sm border border-edge-subtle rounded-lg">
                No FX rate history for this organization yet.
              </div>
              )}
              <div className="mt-3 flex items-center gap-6 text-xs text-ink-muted">
                <span>Current: <span className="text-ink font-semibold">{(Number(FX_DATA.current_rate) || 1).toFixed(4)}</span></span>
                <span>High (shown): <span className="text-success-fg font-semibold">1.2271</span> (Dec 2020)</span>
                <span>Low (shown): <span className="text-danger-fg font-semibold">0.9748</span> (Sep 2022)</span>
              </div>
            </div>

            {/* Sensitivity */}
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold text-ink mb-1">EUR Portfolio Sensitivity</h3>
              <p className="text-ink-muted text-xs mb-4">USD value of EUR NAV if EUR/USD rate moves from today</p>
              <div className="space-y-2">
                {FX_DATA.eur_sensitivity.map(s => (
                  <div key={s.scenario} className={`flex justify-between items-center text-sm px-3 py-1.5 rounded ${
                    s.pct_change === 0 ? "bg-surface-tertiary border border-edge" :
                    s.pct_change < 0 ? "bg-surface-tertiary" : "bg-surface-tertiary"
                  }`}>
                    <span className="text-ink-muted font-mono w-14">{s.scenario} EUR/USD</span>
                    <span className={`font-semibold ${s.delta_usd > 0 ? "text-success-fg" : s.delta_usd < 0 ? "text-danger-fg" : "text-ink-muted"}`}>
                      {s.delta_usd === 0 ? "—" : `$${s.delta_usd > 0 ? "+" : ""}${Math.round(s.delta_usd).toLocaleString()}`}
                    </span>
                    <span className={`text-xs ${s.delta_pct > 0 ? "text-success-fg" : s.delta_pct < 0 ? "text-danger-fg" : "text-ink-muted"}`}>
                      {s.delta_pct === 0 ? "current" : `${s.delta_pct > 0 ? "+" : ""}${s.delta_pct}%`}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-ink-subtle text-xs mt-3">
                Note: EUR↑ (rate rises) = your EUR assets are worth more in USD (tailwind)
              </p>
            </div>
          </div>

          {/* Per-fund FX impact (tabbed) */}
          {fxFundImpact.length > 0 && (
            <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-6">
              <div className="p-6 border-b border-edge-subtle">
                <h3 className="text-lg font-semibold text-ink">Per-Fund FX Impact</h3>
                <p className="text-ink-muted text-xs mt-1">
                  Compares your EUR positions at weighted-average entry rate vs current rate. Positive = EUR strengthened (tailwind for USD investor).
                </p>
                {/* Fund tabs */}
                <div className="flex flex-wrap items-center gap-1 mt-3 bg-surface-tertiary rounded-lg p-1">
                  <button
                    onClick={() => setFxFundTab("all")}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${fxFundTab === "all" ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"}`}
                  >
                    All Funds
                  </button>
                  {fxFundImpact.map((f, i) => {
                    const label = fundDisplayName(f.fund, f.investor);
                    return (
                      <button
                        key={i}
                        onClick={() => setFxFundTab(String(i))}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${fxFundTab === String(i) ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* All funds summary table */}
              {fxFundTab === "all" && (
                <div className="scroll-x">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead>
                      <tr className="text-left text-ink-muted border-b border-edge-subtle">
                        <th className="py-3 px-4 font-medium">Fund</th>
                        <th className="py-3 px-4 font-medium">Vehicle</th>
                        <th className="py-3 px-4 font-medium text-right">Drawn (EUR)</th>
                        <th className="py-3 px-4 font-medium text-right">Avg Entry Rate</th>
                        <th className="py-3 px-4 font-medium text-right">Current Rate</th>
                        <th className="py-3 px-4 font-medium text-right">Rate Δ</th>
                        <th className="py-3 px-4 font-medium text-right">USD Cost at Entry</th>
                        <th className="py-3 px-4 font-medium text-right">USD Cost Today</th>
                        <th className="py-3 px-4 font-medium text-right">FX on NAV ($)</th>
                        <th className="py-3 px-4 font-medium text-right">Net FX P&amp;L ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fxFundImpact.map((f, i) => (
                        <tr key={i} className="border-b border-edge-subtle hover:bg-surface-secondary/50 cursor-pointer" onClick={() => setFxFundTab(String(i))}>
                          <td className="py-3 px-4 text-ink font-medium">{fundDisplayName(f.fund, f.investor)}</td>
                          <td className="py-3 px-4 text-ink-muted text-xs">{vehicleDisplayName(f.investor)}</td>
                          <td className="py-3 px-4 text-right text-ink-muted">€{f.total_drawn_eur.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right text-ink-muted">{f.weighted_avg_entry_rate.toFixed(4)}</td>
                          <td className="py-3 px-4 text-right text-ink-muted">{f.current_rate.toFixed(4)}</td>
                          <td className={`py-3 px-4 text-right font-semibold ${f.rate_change_pct > 0 ? "text-success-fg" : "text-danger-fg"}`}>
                            {f.rate_change_pct > 0 ? "+" : ""}{f.rate_change_pct}%
                          </td>
                          <td className="py-3 px-4 text-right text-ink-muted">${f.usd_cost_at_entry.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right text-ink-muted">${f.usd_cost_at_current_rate.toLocaleString()}</td>
                          <td className={`py-3 px-4 text-right font-semibold ${f.fx_on_nav_usd >= 0 ? "text-success-fg" : "text-danger-fg"}`}>
                            {f.fx_on_nav_usd >= 0 ? "+" : ""}${Math.round(f.fx_on_nav_usd).toLocaleString()}
                          </td>
                          <td className={`py-3 px-4 text-right font-semibold ${f.fx_total_pl_usd >= 0 ? "text-success-fg" : "text-danger-fg"}`}>
                            {f.fx_total_pl_usd >= 0 ? "+" : ""}${Math.round(f.fx_total_pl_usd).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-edge bg-surface-tertiary/50">
                        <td className="py-3 px-4 text-ink-muted font-semibold" colSpan={2}>Total</td>
                        <td className="py-3 px-4 text-right text-ink-muted font-semibold">
                          €{fxFundImpact.reduce((s, f) => s + f.total_drawn_eur, 0).toLocaleString()}
                        </td>
                        <td colSpan={5}></td>
                        <td className={`py-3 px-4 text-right font-bold ${
                          fxFundImpact.reduce((s, f) => s + f.fx_on_nav_usd, 0) >= 0 ? "text-success-fg" : "text-danger-fg"
                        }`}>
                          {fxFundImpact.reduce((s, f) => s + f.fx_on_nav_usd, 0) >= 0 ? "+" : ""}${Math.round(fxFundImpact.reduce((s, f) => s + f.fx_on_nav_usd, 0)).toLocaleString()}
                        </td>
                        <td className={`py-3 px-4 text-right font-bold ${
                          fxFundImpact.reduce((s, f) => s + f.fx_total_pl_usd, 0) >= 0 ? "text-success-fg" : "text-danger-fg"
                        }`}>
                          {fxFundImpact.reduce((s, f) => s + f.fx_total_pl_usd, 0) >= 0 ? "+" : ""}${Math.round(fxFundImpact.reduce((s, f) => s + f.fx_total_pl_usd, 0)).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Individual fund detail view */}
              {fxFundTab !== "all" && (() => {
                const idx = parseInt(fxFundTab);
                const f = fxFundImpact[idx];
                if (!f) return null;
                const label = fundDisplayName(f.fund, f.investor);
                const navKey = Object.keys(eurNavUsdSeries.byFund).find(k => k.includes(f.fund.replace(" Leveraged", "")) && (f.fund.includes("Leveraged") ? k.includes("Leveraged") : !k.includes("Leveraged")));
                const navData = navKey ? eurNavUsdSeries.byFund[navKey] : null;
                const chartData = navData ? Object.entries(navData).sort(([a],[b]) => a.localeCompare(b)).map(([date, vals]) => ({
                  date,
                  "NAV (USD, actual rate)": vals.nav_usd,
                  "NAV (USD, entry rate)": vals.nav_usd_at_entry_rate,
                })) : [];

                return (
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-ink">{label}</h4>
                        <p className="text-ink-muted text-sm">{f.investor}</p>
                      </div>
                      <div className={`text-right px-4 py-2 rounded-lg ${f.fx_total_pl_usd >= 0 ? "bg-success/40 border border-success-border" : "bg-danger/30 border border-danger-border/50"}`}>
                        <p className="text-xs text-ink-muted">Net FX Impact</p>
                        <p className={`text-2xl font-bold ${f.fx_total_pl_usd >= 0 ? "text-success-fg" : "text-danger-fg"}`}>
                          {f.fx_total_pl_usd >= 0 ? "+" : ""}${Math.round(f.fx_total_pl_usd).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Key metrics grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-surface-tertiary rounded-lg p-3">
                        <p className="text-xs text-ink-muted">Drawn (EUR)</p>
                        <p className="text-lg font-bold text-ink">€{f.total_drawn_eur.toLocaleString()}</p>
                      </div>
                      <div className="bg-surface-tertiary rounded-lg p-3">
                        <p className="text-xs text-ink-muted">Entry Rate</p>
                        <p className="text-lg font-bold text-ink">{f.weighted_avg_entry_rate.toFixed(4)}</p>
                      </div>
                      <div className="bg-surface-tertiary rounded-lg p-3">
                        <p className="text-xs text-ink-muted">Current Rate</p>
                        <p className="text-lg font-bold text-ink">{f.current_rate.toFixed(4)}</p>
                      </div>
                      <div className="bg-surface-tertiary rounded-lg p-3">
                        <p className="text-xs text-ink-muted">Rate Change</p>
                        <p className={`text-lg font-bold ${f.rate_change_pct > 0 ? "text-success-fg" : "text-danger-fg"}`}>
                          {f.rate_change_pct > 0 ? "+" : ""}{f.rate_change_pct}%
                        </p>
                      </div>
                    </div>

                    {/* Cost comparison */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="bg-surface-tertiary rounded-lg p-3">
                        <p className="text-xs text-ink-muted">USD Cost at Entry Rate</p>
                        <p className="text-lg font-bold text-ink-muted">${f.usd_cost_at_entry.toLocaleString()}</p>
                      </div>
                      <div className="bg-surface-tertiary rounded-lg p-3">
                        <p className="text-xs text-ink-muted">USD Cost at Current Rate</p>
                        <p className="text-lg font-bold text-ink-muted">${f.usd_cost_at_current_rate.toLocaleString()}</p>
                      </div>
                      <div className="bg-surface-tertiary rounded-lg p-3">
                        <p className="text-xs text-ink-muted">FX on NAV</p>
                        <p className={`text-lg font-bold ${f.fx_on_nav_usd >= 0 ? "text-success-fg" : "text-danger-fg"}`}>
                          {f.fx_on_nav_usd >= 0 ? "+" : ""}${Math.round(f.fx_on_nav_usd).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* NAV chart */}
                    {chartData.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-ink-muted mb-2">EUR NAV in USD: Actual vs Entry-Rate</h4>
                        <p className="text-xs text-ink-subtle mb-3">Gap between lines = FX impact on your position</p>
                        <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                          <AreaChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                            <XAxis dataKey="date" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                              tickFormatter={(d) => {
                                if (d == null || typeof d !== "string") return "";
                                const p = d.split("-");
                                if (p.length < 2 || !p[0]) return "";
                                return `${p[1]}/${p[0].slice(2)}`;
                              }}
                              interval={Math.max(0, Math.floor(chartData.length / 5))}
                            />
                            <YAxis tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                              tickFormatter={(v) => (typeof v === "number" && !Number.isNaN(v) ? `$${(v / 1000).toFixed(0)}k` : "")}
                              width={55}
                            />
                            <Tooltip
                              contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                              formatter={(v, name) => [
                                v != null && typeof v === "number" && !Number.isNaN(v) ? `$${v.toLocaleString()}` : "—",
                                name,
                              ]}
                            />
                            <Legend wrapperStyle={{ fontSize: "11px" }} />
                            <Area type="monotone" dataKey="NAV (USD, entry rate)" stroke="var(--chart-slate-mid)" fill="var(--chart-slate-mid)" strokeDasharray="4 2" strokeWidth={1.5} dot={false} />
                            <Area type="monotone" dataKey="NAV (USD, actual rate)" stroke="var(--chart-fill-primary)" fill="var(--chart-fill-primary)" strokeWidth={2} dot={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Per-vehicle FX bar chart */}
          {fxVehicleSummary.length > 0 && (
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold text-ink mb-1">FX P&amp;L by Holding Vehicle (USD)</h3>
              <p className="text-ink-muted text-xs mb-4">
                Net USD impact from EUR/USD movements on EUR-denominated fund NAV
              </p>
              <ResponsiveContainer width="100%" height={isMobile ? 120 : 160}>
                <BarChart data={fxVehicleSummary} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                    tickFormatter={v => `$${v.toLocaleString()}`}
                  />
                  <YAxis type="category" dataKey="investor" width={180}
                    tick={{ fill: "var(--chart-axis)", fontSize: 11 }}
                    tickFormatter={v => v.split(" ").slice(0,2).join(" ")}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                    formatter={(v, name) => [`$${Math.round(v).toLocaleString()}`, "Net FX P&L"]}
                    labelStyle={{ color: "var(--chart-axis)" }}
                  />
                  <Bar dataKey="fx_total_pl_usd" name="Net FX P&L" radius={[0, 4, 4, 0]}>
                    {fxVehicleSummary.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fx_total_pl_usd >= 0 ? "var(--chart-positive)" : "var(--chart-negative)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* ── Currency-at-Risk Over Time ── */}
        {currencyAtRiskData.length > 0 && (
          <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8 mt-8">
            <h3 className="text-lg font-semibold mb-1 text-ink">Currency-at-Risk Over Time</h3>
            <p className="text-xs text-ink-muted mb-4">Portfolio NAV by currency over time — shows when FX swings would have maximum impact</p>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={currencyAtRiskData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis dataKey="date" stroke="var(--chart-axis)" style={{ fontSize: "0.65rem" }} />
                <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                  formatter={(v) => formatCurrency(v)} />
                <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                <Area type="monotone" dataKey="EUR" stackId="nav" stroke="var(--chart-fill-primary)" fill="var(--chart-fill-primary)" fillOpacity={0.5} />
                <Area type="monotone" dataKey="USD" stackId="nav" stroke="var(--chart-positive)" fill="var(--chart-positive)" fillOpacity={0.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

          </div>
        )}

        {/* ===== LEVERAGE ANALYSIS TAB ===== */}
        {pageTab === "leverage" && (
          <div>
        {/* ===== LEVERAGE ANALYSIS SECTION ===== */}
        {leverageData && leverageData.leveragedFunds.length > 0 && (
          <div className="mb-8">
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-brand/15 rounded-lg">
                <BarChart3 className="w-6 h-6 text-brand-secondary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-ink">Leverage Analysis</h2>
                <p className="text-ink-muted text-sm">Interest rate sensitivity on leveraged fund positions</p>
                {!customVars && (
                  <p className="text-xs text-warning-fg mt-1">Using estimated defaults ({DEFAULT_LEVERAGE_RATIO}x leverage, {DEFAULT_BASE_RATE}% rate). Load fund_variables.xlsx above for exact values.</p>
                )}
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-surface-secondary rounded-lg p-4 border border-brand/30">
                <p className="text-xs text-ink-muted mb-1">Leveraged Funds</p>
                <p className="text-2xl font-bold text-brand-secondary">{leverageData.leveragedFunds.length}</p>
                <p className="text-xs text-ink-subtle">of {leverageData.funds.length} total</p>
              </div>
              <div className="bg-surface-secondary rounded-lg p-4 border border-success-border/50">
                <p className="text-xs text-ink-muted mb-1">Participant ({Math.round(CAPITAL_STACK.participant * 100)}%)</p>
                <p className="text-2xl font-bold text-success-fg">{formatCurrency(leverageData.leveragedFunds.reduce((s, f) => s + f.participantAmt, 0), 0)}</p>
                <p className="text-xs text-ink-subtle">your capital</p>
              </div>
              <div className="bg-surface-secondary rounded-lg p-4 border border-warning-border/50">
                <p className="text-xs text-ink-muted mb-1">EQT Perf Loan ({Math.round(CAPITAL_STACK.eqtPerf * 100)}%)</p>
                <p className="text-2xl font-bold text-warning-fg">{formatCurrency(leverageData.totalEqtPerfAmt, 0)}</p>
                <p className="text-xs text-ink-subtle">{leverageData.weightedEqtPerfRate.toFixed(1)}% avg rate</p>
              </div>
              <div className="bg-surface-secondary rounded-lg p-4 border border-info-border/50">
                <p className="text-xs text-ink-muted mb-1">SEB Loan ({Math.round(CAPITAL_STACK.seb * 100)}%)</p>
                <p className="text-2xl font-bold text-brand">{formatCurrency(leverageData.totalSebAmt, 0)}</p>
                <p className="text-xs text-ink-subtle">{(leverageData.weightedSebRate + rateShift / 100).toFixed(2)}% eff. rate</p>
              </div>
              <div className="bg-surface-secondary rounded-lg p-4 border border-brand/30">
                <p className="text-xs text-ink-muted mb-1">Annual Interest</p>
                <p className={`text-xl font-bold ${leverageData.totalAnnualInterest > 0 ? "text-danger-fg" : "text-ink-muted"}`}>{formatCurrency(leverageData.totalAnnualInterest, 0)}</p>
                <p className="text-xs text-ink-subtle">
                  SEB: {formatCurrency(leverageData.totalSebAnnualInterest, 0)} | EQT: {formatCurrency(leverageData.totalEqtPerfAnnualInterest, 0)}
                </p>
              </div>
              <div className="bg-surface-secondary rounded-lg p-4 border border-brand/30">
                <p className="text-xs text-ink-muted mb-1">Period Cost (per-fund HP)</p>
                <p className={`text-xl font-bold ${leverageData.totalPeriodInterest > 0 ? "text-danger-fg" : "text-ink-muted"}`}>{formatCurrency(leverageData.totalPeriodInterest, 0)}</p>
                <p className="text-xs text-ink-subtle">total projected interest</p>
              </div>
            </div>

            {/* Interactive Sliders */}
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-6">
              <h3 className="text-lg font-semibold text-ink mb-4">Rate Scenario Controls</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Rate Shift Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm text-ink-muted">SEB Rate Shift (floating)</label>
                    <span className={`text-sm font-mono font-bold ${rateShift > 0 ? "text-danger-fg" : rateShift < 0 ? "text-success-fg" : "text-ink-muted"}`}>
                      {rateShift > 0 ? "+" : ""}{rateShift} bps
                    </span>
                  </div>
                  <input
                    type="range"
                    min={-300}
                    max={300}
                    step={25}
                    value={rateShift}
                    onChange={(e) => setRateShift(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, var(--chart-positive) 0%, var(--chart-positive) ${(rateShift + 300) / 6}%, var(--chart-gradient-mid) ${(rateShift + 300) / 6}%, var(--chart-gradient-mid) 50%, var(--chart-negative) 50%, var(--chart-negative) ${(rateShift + 300) / 6}%, var(--chart-gradient-mid) ${(rateShift + 300) / 6}%, var(--chart-gradient-mid) 100%)`}}
                  />
                  <div className="flex justify-between text-xs text-ink-subtle mt-1">
                    <span>-300bp</span>
                    <span className="text-ink-muted">Base</span>
                    <span>+300bp</span>
                  </div>
                </div>
                {/* Holding Period Adjustment Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm text-ink-muted">Holding Period Adjustment</label>
                    <span className={`text-sm font-mono font-bold ${holdingPeriodAdjust > 0 ? "text-warning-fg" : holdingPeriodAdjust < 0 ? "text-success-fg" : "text-ink-muted"}`}>
                      {holdingPeriodAdjust > 0 ? "+" : ""}{holdingPeriodAdjust} yr{Math.abs(holdingPeriodAdjust) !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={-4}
                    max={4}
                    step={1}
                    value={holdingPeriodAdjust}
                    onChange={(e) => setHoldingPeriodAdjust(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, var(--chart-positive) 0%, var(--chart-positive) ${(holdingPeriodAdjust + 4) / 8 * 100}%, var(--chart-gradient-mid) ${(holdingPeriodAdjust + 4) / 8 * 100}%, var(--chart-gradient-mid) 50%, var(--chart-negative) 50%, var(--chart-negative) ${(holdingPeriodAdjust + 4) / 8 * 100}%, var(--chart-gradient-mid) ${(holdingPeriodAdjust + 4) / 8 * 100}%, var(--chart-gradient-mid) 100%)`}}
                  />
                  <div className="flex justify-between text-xs text-ink-subtle mt-1">
                    <span>-4yr</span>
                    <span className="text-ink-muted">Base</span>
                    <span>+4yr</span>
                  </div>
                  {/* Expandable per-fund holding periods */}
                  <button
                    onClick={() => setShowHoldingPeriodDetail(!showHoldingPeriodDetail)}
                    className="mt-2 text-xs text-brand hover:text-brand-secondary transition flex items-center gap-1"
                  >
                    <span>{showHoldingPeriodDetail ? "\u25BC" : "\u25B6"}</span>
                    Per-fund holding periods
                  </button>
                  {showHoldingPeriodDetail && (
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                      {leverageData.leveragedFunds.map((lf) => {
                        const key = makeKey(lf.fund, lf.investor);
                        const v = customVars ? customVars[key] : null;
                        const basePeriod = (v && Number(v["Holding Period"]) > 0) ? Number(v["Holding Period"]) : DEFAULT_HOLDING_PERIOD;
                        const effective = holdingPeriodOverrides[key] !== undefined
                          ? holdingPeriodOverrides[key]
                          : basePeriod + holdingPeriodAdjust;
                        const isOverridden = holdingPeriodOverrides[key] !== undefined;
                        return (
                          <div key={key} className="flex items-center justify-between gap-2 text-xs">
                            <span className={`truncate ${isOverridden ? "text-warning-fg font-semibold" : "text-ink-muted"}`} title={lf.fund}>
                              {lf.fund.length > 25 ? lf.fund.slice(0, 25) + "..." : lf.fund}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-ink-subtle w-8 text-right">{basePeriod}yr</span>
                              <span className="text-ink-subtle mx-1">&rarr;</span>
                              <input
                                type="number"
                                min={1}
                                max={15}
                                value={effective}
                                onChange={(e) => {
                                  const val = Math.max(1, Math.min(15, Number(e.target.value) || 1));
                                  if (val === basePeriod + holdingPeriodAdjust) {
                                    // Matches global adjust — remove override
                                    setHoldingPeriodOverrides(prev => { const n = {...prev}; delete n[key]; return n; });
                                  } else {
                                    setHoldingPeriodOverrides(prev => ({...prev, [key]: val}));
                                  }
                                }}
                                className="w-12 px-1 py-0.5 text-center rounded border border-edge bg-surface text-ink text-xs"
                              />
                              <span className="text-ink-subtle">yr</span>
                              {isOverridden && (
                                <button
                                  onClick={() => setHoldingPeriodOverrides(prev => { const n = {...prev}; delete n[key]; return n; })}
                                  className="text-ink-subtle hover:text-danger-fg ml-1"
                                  title="Reset to base + adjustment"
                                >
                                  &times;
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {(rateShift !== 0 || holdingPeriodAdjust !== 0) && (
                <button
                  onClick={() => { setRateShift(0); setHoldingPeriodAdjust(0); setHoldingPeriodOverrides({}); }}
                  className="mt-4 text-xs text-ink-muted hover:text-ink transition px-3 py-1 rounded border border-edge hover:border-edge"
                >
                  Reset all adjustments
                </button>
              )}
            </div>

            {/* Rate Sensitivity Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                <h3 className="text-lg font-semibold text-ink mb-4">Interest Cost by Rate Scenario</h3>
                <ResponsiveContainer width="100%" height={chartH}>
                  <BarChart data={leverageData.scenarios}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="label" stroke="var(--chart-axis)" style={{ fontSize: "0.7rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                      formatter={(value, name) => [formatCurrency(value, 0), name]}
                    />
                    <Legend />
                    <Bar dataKey="annualCost" name="Annual Cost" radius={[4, 4, 0, 0]}>
                      {leverageData.scenarios.map((s, i) => (
                        <Cell key={i} fill={s.shiftBps === 0 ? "var(--chart-violet)" : s.shiftBps > 0 ? "var(--chart-negative)" : "var(--chart-positive)"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                <h3 className="text-lg font-semibold text-ink mb-4">Cumulative Interest (Per-Fund Hold)</h3>
                <ResponsiveContainer width="100%" height={chartH}>
                  <BarChart data={leverageData.scenarios}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="label" stroke="var(--chart-axis)" style={{ fontSize: "0.7rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                      formatter={(value, name) => [formatCurrency(value, 0), name]}
                    />
                    <Legend />
                    <Bar dataKey="periodCost" name="Total Period Cost" radius={[4, 4, 0, 0]}>
                      {leverageData.scenarios.map((s, i) => (
                        <Cell key={i} fill={s.shiftBps === 0 ? "var(--chart-violet)" : s.shiftBps > 0 ? "var(--chart-negative)" : "var(--chart-positive)"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Per-Fund Capital Stack & Interest Table */}
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold text-ink mb-1">Per-Fund Capital Stack & Interest</h3>
              <p className="text-xs text-ink-muted mb-4">{Math.round(CAPITAL_STACK.participant * 100)}% Participant | {Math.round(CAPITAL_STACK.eqtPerf * 100)}% EQT Perf Loan (fixed) | {Math.round(CAPITAL_STACK.seb * 100)}% SEB Loan (floating)</p>
              <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="text-left py-3 px-2 font-semibold text-ink-muted">Fund</th>
                    <th className="text-center py-3 px-2 font-semibold text-ink-muted">Hold</th>
                    <th className="text-center py-3 px-2 font-semibold text-ink-muted">1st Inv</th>
                    <th className="text-right py-3 px-2 font-semibold text-ink-muted">Total Exp.</th>
                    <th className="text-right py-3 px-2 font-semibold text-success-fg">Participant</th>
                    <th className="text-right py-3 px-2 font-semibold text-warning-fg">EQT Perf</th>
                    <th className="text-right py-3 px-2 font-semibold text-brand-secondary">SEB</th>
                    <th className="text-right py-3 px-2 font-semibold text-warning-fg">EQT Rate</th>
                    <th className="text-right py-3 px-2 font-semibold text-brand-secondary">SEB Rate</th>
                    <th className="text-right py-3 px-2 font-semibold text-warning-fg">EQT Int/yr</th>
                    <th className="text-right py-3 px-2 font-semibold text-brand-secondary">SEB Int/yr</th>
                    <th className="text-right py-3 px-2 font-semibold text-danger-fg">Total Int/yr</th>
                    <th className="text-right py-3 px-2 font-semibold text-danger-fg">Period Total</th>
                  </tr>
                </thead>
                <tbody>
                  {leverageData.funds.filter(f => f.isLeveraged).map((f) => {
                    const sym = f.currency === "EUR" ? "€" : "$";
                    return (
                      <tr key={f.fund + f.investor} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                        <td className="py-3 px-2 font-medium text-ink text-xs">{f.fund}</td>
                        <td className="text-center py-3 px-2 text-ink-muted">{f.holdingPeriod}yr</td>
                        <td className="text-center py-3 px-2 text-ink-muted">{f.firstInvestmentYear || '—'}</td>
                        <td className="text-right py-3 px-2 text-ink-muted">{sym}{formatCurrency(f.totalExposure, 0)}</td>
                        <td className="text-right py-3 px-2 text-success-fg">{sym}{formatCurrency(f.participantAmt, 0)}</td>
                        <td className="text-right py-3 px-2 text-warning-fg">{sym}{formatCurrency(f.eqtPerfAmt, 0)}</td>
                        <td className="text-right py-3 px-2 text-brand">{sym}{formatCurrency(f.sebAmt, 0)}</td>
                        <td className="text-right py-3 px-2 text-warning-fg">{f.eqtPerfRate.toFixed(1)}%</td>
                        <td className={`text-right py-3 px-2 ${rateShift !== 0 ? (rateShift > 0 ? "text-danger-fg" : "text-success-fg") : "text-brand"}`}>{f.sebEffectiveRate.toFixed(2)}%</td>
                        <td className="text-right py-3 px-2 text-warning-fg">{sym}{formatCurrency(f.eqtPerfAnnualInterest, 0)}</td>
                        <td className="text-right py-3 px-2 text-brand">{sym}{formatCurrency(f.sebAnnualInterest, 0)}</td>
                        <td className="text-right py-3 px-2 text-danger-fg">{sym}{formatCurrency(f.annualInterest, 0)}</td>
                        <td className="text-right py-3 px-2 text-danger-fg font-semibold">{sym}{formatCurrency(f.totalInterest, 0)}</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-brand/30 bg-surface-tertiary/50">
                    <td className="py-3 px-2 font-bold text-ink">Total</td>
                    <td className="text-center py-3 px-2 text-ink-subtle">—</td>
                    <td className="text-center py-3 px-2 text-ink-subtle">—</td>
                    <td className="text-right py-3 px-2 font-bold text-ink">{formatCurrency(leverageData.leveragedFunds.reduce((s, f) => s + f.totalExposure, 0), 0)}</td>
                    <td className="text-right py-3 px-2 font-bold text-success-fg">{formatCurrency(leverageData.leveragedFunds.reduce((s, f) => s + f.participantAmt, 0), 0)}</td>
                    <td className="text-right py-3 px-2 font-bold text-warning-fg">{formatCurrency(leverageData.totalEqtPerfAmt, 0)}</td>
                    <td className="text-right py-3 px-2 font-bold text-brand">{formatCurrency(leverageData.totalSebAmt, 0)}</td>
                    <td className="text-right py-3 px-2 text-warning-fg">{leverageData.weightedEqtPerfRate.toFixed(1)}%</td>
                    <td className={`text-right py-3 px-2 font-bold ${rateShift !== 0 ? (rateShift > 0 ? "text-danger-fg" : "text-success-fg") : "text-brand"}`}>{(leverageData.weightedSebRate + rateShift / 100).toFixed(2)}%</td>
                    <td className="text-right py-3 px-2 font-bold text-warning-fg">{formatCurrency(leverageData.totalEqtPerfAnnualInterest, 0)}</td>
                    <td className="text-right py-3 px-2 font-bold text-brand">{formatCurrency(leverageData.totalSebAnnualInterest, 0)}</td>
                    <td className="text-right py-3 px-2 text-danger-fg font-bold">{formatCurrency(leverageData.totalAnnualInterest, 0)}</td>
                    <td className="text-right py-3 px-2 text-danger-fg font-bold">{formatCurrency(leverageData.totalPeriodInterest, 0)}</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>

            {/* Return Waterfall Analysis */}
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mt-6">
              <h3 className="text-lg font-semibold text-ink mb-1">Return Waterfall</h3>
              <p className="text-xs text-ink-muted mb-4">Return of capital: Employee {Math.round(WATERFALL.empRocShare * 100)}%, Bank {Math.round(WATERFALL.bankRocShare * 100)}% | Capital gains: Bank {Math.round(WATERFALL.bankGainsShare * 100)}%, EQT Pref {Math.round(WATERFALL.eqtPrefGainsShare * 100)}% | Residual after repayment → Employee</p>
              <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="text-left py-3 px-2 font-semibold text-ink-muted">Fund</th>
                    <th className="text-right py-3 px-2 font-semibold text-ink-muted">Fund MOIC</th>
                    <th className="text-right py-3 px-2 font-semibold text-ink-muted">Gross Return</th>
                    <th className="text-right py-3 px-2 font-semibold text-brand-secondary">→ SEB</th>
                    <th className="text-right py-3 px-2 font-semibold text-warning-fg">→ EQT Perf</th>
                    <th className="text-right py-3 px-2 font-semibold text-success-fg">→ You</th>
                    <th className="text-right py-3 px-2 font-semibold text-success-fg">LP MOIC</th>
                  </tr>
                </thead>
                <tbody>
                  {leverageData.funds.filter(f => f.isLeveraged).map((f) => {
                    // Use cashForecastData for accurate waterfall if available
                    const ff = cashForecastData?.fundForecasts?.find(cf => cf.fund === f.fund && cf.investor === f.investor);
                    const grossReturn = f.fundMoic ? f.totalExposure * INVESTMENT_LEVEL * f.fundMoic : 0;
                    // Use pre-computed waterfall from simulation if available, otherwise estimate
                    const sebTotal = ff ? ff.sebRepaid + ff.sebGains : 0;
                    const eqtTotal = ff ? ff.eqtPerfRepaid + ff.eqtPerfGains : 0;
                    const participantTotal = ff ? ff.lpNetReturn : (f.fundMoic ? Math.max(0, grossReturn - sebTotal - eqtTotal) : 0);
                    const lpMoic = (f.fundMoic && f.participantAmt > 0) ? participantTotal / f.participantAmt : 0;
                    return (
                      <tr key={f.fund + f.investor + "wf"} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                        <td className="py-3 px-2 font-medium text-ink text-xs">{f.fund}</td>
                        <td className="text-right py-3 px-2 text-ink-muted">{f.fundMoic ? f.fundMoic.toFixed(2) + "x" : "—"}</td>
                        <td className="text-right py-3 px-2 text-ink-muted">{f.fundMoic ? formatCurrency(grossReturn, 0) : "—"}</td>
                        <td className="text-right py-3 px-2 text-brand">{f.fundMoic ? formatCurrency(sebTotal, 0) : "—"}</td>
                        <td className="text-right py-3 px-2 text-warning-fg">{f.fundMoic ? formatCurrency(eqtTotal, 0) : "—"}</td>
                        <td className={`text-right py-3 px-2 font-bold ${participantTotal > f.participantAmt ? "text-success-fg" : participantTotal > 0 ? "text-warning-fg" : "text-danger-fg"}`}>{f.fundMoic ? formatCurrency(participantTotal, 0) : "—"}</td>
                        <td className={`text-right py-3 px-2 font-bold ${lpMoic >= 1 ? "text-success-fg" : lpMoic > 0 ? "text-warning-fg" : "text-danger-fg"}`}>{f.fundMoic ? lpMoic.toFixed(2) + "x" : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {/* ── Leveraged vs Non-Leveraged Comparison ── */}
        {levVsNonLevData.length > 0 && (
          <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8 mt-8">
            <h3 className="text-lg font-semibold mb-1 text-ink">Leveraged vs. Non-Leveraged Returns</h3>
            <p className="text-xs text-ink-muted mb-4">Gross MOIC (x-axis) vs After-Tax MOIC (y-axis) — bubble size = commitment, color = leveraged/non-leveraged</p>
            <ResponsiveContainer width="100%" height={350}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis type="number" dataKey="grossMoic" name="Gross MOIC" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }}
                  tickFormatter={(v) => `${v.toFixed(1)}x`}
                  label={{ value: "Gross Fund MOIC", position: "insideBottom", offset: -5, style: { fill: "var(--chart-axis)", fontSize: "0.7rem" } }} />
                <YAxis type="number" dataKey="afterTaxMoic" name="After-Tax MOIC" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }}
                  tickFormatter={(v) => `${v.toFixed(1)}x`}
                  label={{ value: "After-Tax MOIC", angle: -90, position: "insideLeft", style: { fill: "var(--chart-axis)", fontSize: "0.7rem" } }} />
                <ZAxis type="number" dataKey="commitment" range={[60, 400]} />
                <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                  formatter={(v, name) => [name.includes("MOIC") ? `${v.toFixed(2)}x` : formatCurrency(v), name]}
                  labelFormatter={() => ""} />
                <ReferenceLine y={1} stroke="var(--chart-axis)" strokeDasharray="3 3" label={{ value: "1.0x", position: "right", style: { fontSize: "0.65rem", fill: "var(--chart-axis)" } }} />
                <Scatter data={levVsNonLevData} fill="var(--chart-fill-primary)">
                  {levVsNonLevData.map((entry, i) => (
                    <Cell key={i} fill={entry.isLeveraged ? "var(--chart-warning)" : "var(--chart-fill-primary)"} fillOpacity={0.7} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            <div className="flex gap-4 mt-3 justify-center">
              <span className="flex items-center gap-1.5 text-xs text-ink-muted"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: "var(--chart-warning)" }} />Leveraged</span>
              <span className="flex items-center gap-1.5 text-xs text-ink-muted"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: "var(--chart-fill-primary)" }} />Non-Leveraged</span>
            </div>
          </div>
        )}

          </div>
        )}

        {/* ===== CASH FORECAST TAB ===== */}
        {pageTab === "forecast" && (
          <div>
            {/* Simulation Funds */}
            <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-4">
              <div className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-ink">Simulation</h3>
                    <p className="text-xs text-ink-subtle mt-1">Add hypothetical funds to test forecast impact (local only).</p>
                  </div>
                  <button
                    onClick={addSimulatedFund}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand text-ink-onAccent text-sm font-medium hover:opacity-90 transition"
                  >
                    <Plus className="w-4 h-4" />
                    Add Fund
                  </button>
                </div>

                {simulatedFunds.length === 0 ? (
                  <div className="text-sm text-ink-muted">No simulated funds yet.</div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {simulatedFunds.map((sf) => (
                      <div key={sf.id} className="rounded-lg border border-edge bg-surface-tertiary/60 p-3 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-xs text-ink-subtle">Fund name</span>
                            <input
                              type="text"
                              value={sf.fundName}
                              onChange={(e) => updateSimulatedFund(sf.id, "fundName", e.target.value)}
                              className="mt-1 w-full rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-subtle">First drawdown year</span>
                            <select
                              value={sf.firstDrawdownYear}
                              onChange={(e) => updateSimulatedFund(sf.id, "firstDrawdownYear", e.target.value)}
                              className="mt-1 w-full rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink"
                            >
                              {forecastYears.map((yr) => (
                                <option key={yr} value={yr}>{yr}</option>
                              ))}
                            </select>
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-subtle">Commitment</span>
                            <input
                              type="number"
                              min="0"
                              step="1000"
                              value={sf.commitment}
                              onChange={(e) => updateSimulatedFund(sf.id, "commitment", e.target.value)}
                              className="mt-1 w-full rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-subtle">Participant %</span>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              step="1"
                              value={sf.participantPct}
                              onChange={(e) => updateSimulatedFund(sf.id, "participantPct", e.target.value)}
                              className="mt-1 w-full rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-subtle">EQT Perf %</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={sf.eqtPerfPct}
                              onChange={(e) => updateSimulatedFund(sf.id, "eqtPerfPct", e.target.value)}
                              className="mt-1 w-full rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-subtle">SEB %</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={sf.sebPct}
                              onChange={(e) => updateSimulatedFund(sf.id, "sebPct", e.target.value)}
                              className="mt-1 w-full rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-subtle">Total Exposure (calculated)</span>
                            <div className="mt-1 w-full rounded-md border border-edge bg-surface-tertiary px-2.5 py-1.5 text-sm text-ink-muted italic">
                              {((Number(sf.participantPct) || 15) > 0
                                ? (Number(sf.commitment) || 0) / ((Number(sf.participantPct) || 15) / 100)
                                : (Number(sf.commitment) || 0)
                              ).toLocaleString()}
                            </div>
                          </label>
                          <label className="block sm:col-span-2">
                            <span className="text-xs text-ink-subtle">Target MOIC</span>
                            <input
                              type="number"
                              min="0"
                              step="0.05"
                              value={sf.targetMoic}
                              onChange={(e) => updateSimulatedFund(sf.id, "targetMoic", e.target.value)}
                              className="mt-1 w-full rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink"
                            />
                          </label>
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={() => removeSimulatedFund(sf.id)}
                            className="text-xs text-danger-fg hover:text-danger-fg px-2 py-1 rounded bg-danger/30 hover:bg-danger/50 transition"
                          >
                            Remove fund
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SEB Loan Rate */}
            <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-4">
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-ink">SEB Loan Rate (floating)</h3>
                    <span className="text-xs text-ink-subtle">Master sets all years</span>
                  </div>
                  <button onClick={() => setExpandedSliders(p => ({ ...p, seb: !p.seb }))}
                    className="text-xs text-ink-muted hover:text-ink px-2 py-1 rounded bg-surface-tertiary hover:bg-surface-tertiary transition">
                    {expandedSliders.seb ? "Collapse" : "Per Year"}
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-bold text-success-fg w-14">{(forecastRates[forecastYears[0]] || DEFAULT_BASE_RATE).toFixed(1)}%</span>
                  <input type="range" min="0" max="12" step="0.25"
                    value={forecastRates[forecastYears[0]] || 5}
                    onChange={(e) => { const v = parseFloat(e.target.value); setForecastRates(() => { const r = {}; forecastYears.forEach(yr => r[yr] = v); return r; }); }}
                    className="flex-1 h-2 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-success" />
                  <div className="flex justify-between text-xs text-ink-subtle w-16"><span>0%</span><span>12%</span></div>
                </div>
              </div>
              {expandedSliders.seb && (
                <div className="px-5 pb-5 pt-2 border-t border-edge-subtle">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                    {forecastYears.map((yr) => (
                      <div key={yr} className="bg-surface-tertiary rounded-lg p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-ink-muted">{yr}</span>
                          <span className="text-xs font-bold text-success-fg">{(forecastRates[yr] || DEFAULT_BASE_RATE).toFixed(1)}%</span>
                        </div>
                        <input type="range" min="0" max="12" step="0.25"
                          value={forecastRates[yr] || 5}
                          onChange={(e) => setForecastRates(prev => ({ ...prev, [yr]: parseFloat(e.target.value) }))}
                          className="w-full h-1.5 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-success" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Federal Capital Gains Rate */}
            <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-4">
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-ink">Federal Capital Gains Rate</h3>
                    <span className="text-xs text-ink-subtle">Master sets all years</span>
                  </div>
                  <button onClick={() => setExpandedSliders(p => ({ ...p, fed: !p.fed }))}
                    className="text-xs text-ink-muted hover:text-ink px-2 py-1 rounded bg-surface-tertiary hover:bg-surface-tertiary transition">
                    {expandedSliders.fed ? "Collapse" : "Per Year"}
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-bold text-brand w-14">{(federalCapGainsRates[forecastYears[0]] || DEFAULT_FEDERAL_CAP_GAINS).toFixed(1)}%</span>
                  <input type="range" min="0" max="40" step="0.5"
                    value={federalCapGainsRates[forecastYears[0]] || 20}
                    onChange={(e) => { const v = parseFloat(e.target.value); setFederalCapGainsRates(() => { const r = {}; forecastYears.forEach(yr => r[yr] = v); return r; }); }}
                    className="flex-1 h-2 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-info" />
                  <div className="flex justify-between text-xs text-ink-subtle w-16"><span>0%</span><span>40%</span></div>
                </div>
              </div>
              {expandedSliders.fed && (
                <div className="px-5 pb-5 pt-2 border-t border-edge-subtle">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                    {forecastYears.map((yr) => (
                      <div key={yr} className="bg-surface-tertiary rounded-lg p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-ink-muted">{yr}</span>
                          <span className="text-xs font-bold text-brand">{(federalCapGainsRates[yr] || DEFAULT_FEDERAL_CAP_GAINS).toFixed(1)}%</span>
                        </div>
                        <input type="range" min="0" max="40" step="0.5"
                          value={federalCapGainsRates[yr] || 20}
                          onChange={(e) => setFederalCapGainsRates(prev => ({ ...prev, [yr]: parseFloat(e.target.value) }))}
                          className="w-full h-1.5 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-info" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* State Capital Gains Rate */}
            <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-4">
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-ink">State Capital Gains Rate</h3>
                    <span className="text-xs text-ink-subtle">Master sets all years</span>
                  </div>
                  <button onClick={() => setExpandedSliders(p => ({ ...p, state: !p.state }))}
                    className="text-xs text-ink-muted hover:text-ink px-2 py-1 rounded bg-surface-tertiary hover:bg-surface-tertiary transition">
                    {expandedSliders.state ? "Collapse" : "Per Year"}
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-bold text-warning-fg w-14">{(stateCapGainsRates[forecastYears[0]] || DEFAULT_STATE_CAP_GAINS).toFixed(1)}%</span>
                  <input type="range" min="0" max="15" step="0.25"
                    value={stateCapGainsRates[forecastYears[0]] || 5}
                    onChange={(e) => { const v = parseFloat(e.target.value); setStateCapGainsRates(() => { const r = {}; forecastYears.forEach(yr => r[yr] = v); return r; }); }}
                    className="flex-1 h-2 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-warning" />
                  <div className="flex justify-between text-xs text-ink-subtle w-16"><span>0%</span><span>15%</span></div>
                </div>
              </div>
              {expandedSliders.state && (
                <div className="px-5 pb-5 pt-2 border-t border-edge-subtle">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                    {forecastYears.map((yr) => (
                      <div key={yr} className="bg-surface-tertiary rounded-lg p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-ink-muted">{yr}</span>
                          <span className="text-xs font-bold text-warning-fg">{(stateCapGainsRates[yr] || DEFAULT_STATE_CAP_GAINS).toFixed(1)}%</span>
                        </div>
                        <input type="range" min="0" max="15" step="0.25"
                          value={stateCapGainsRates[yr] || 5}
                          onChange={(e) => setStateCapGainsRates(prev => ({ ...prev, [yr]: parseFloat(e.target.value) }))}
                          className="w-full h-1.5 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-warning" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* EQT Performance Loan Rate */}
            {cashForecastData && cashForecastData.fundForecasts.some(ff => ff.isLeveraged) && (
              <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-4">
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-semibold text-ink">EQT Performance Loan Rate</h3>
                      <span className="text-xs text-ink-subtle">8% default — simulate waiver</span>
                    </div>
                    <button onClick={() => setExpandedSliders(p => ({ ...p, eqt: !p.eqt }))}
                      className="text-xs text-ink-muted hover:text-ink px-2 py-1 rounded bg-surface-tertiary hover:bg-surface-tertiary transition">
                      {expandedSliders.eqt ? "Collapse" : "Per Fund"}
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm font-bold w-14 ${(() => { const levFunds = cashForecastData.fundForecasts.filter(ff => ff.isLeveraged); const avg = levFunds.length > 0 ? levFunds.reduce((s, ff) => s + (eqtPerfRates[makeKey(ff.fund, ff.investor)] !== undefined ? eqtPerfRates[makeKey(ff.fund, ff.investor)] : DEFAULT_EQT_PERF_RATE), 0) / levFunds.length : DEFAULT_EQT_PERF_RATE; return avg === 0 ? "text-success-fg" : avg < DEFAULT_EQT_PERF_RATE ? "text-warning-fg" : "text-warning-fg"; })()}`}>
                      {(() => { const levFunds = cashForecastData.fundForecasts.filter(ff => ff.isLeveraged); return levFunds.length > 0 ? (levFunds.reduce((s, ff) => s + (eqtPerfRates[makeKey(ff.fund, ff.investor)] !== undefined ? eqtPerfRates[makeKey(ff.fund, ff.investor)] : DEFAULT_EQT_PERF_RATE), 0) / levFunds.length).toFixed(1) : DEFAULT_EQT_PERF_RATE.toFixed(1); })()}%
                    </span>
                    <input type="range" min="0" max="12" step="0.5"
                      value={(() => { const levFunds = cashForecastData.fundForecasts.filter(ff => ff.isLeveraged); return levFunds.length > 0 ? levFunds.reduce((s, ff) => s + (eqtPerfRates[makeKey(ff.fund, ff.investor)] !== undefined ? eqtPerfRates[makeKey(ff.fund, ff.investor)] : DEFAULT_EQT_PERF_RATE), 0) / levFunds.length : DEFAULT_EQT_PERF_RATE; })()}
                      onChange={(e) => { const v = parseFloat(e.target.value); const newRates = {}; cashForecastData.fundForecasts.filter(ff => ff.isLeveraged).forEach(ff => { newRates[makeKey(ff.fund, ff.investor)] = v; }); setEqtPerfRates(newRates); }}
                      className="flex-1 h-2 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-brand" />
                    <div className="flex justify-between text-xs text-ink-subtle w-20"><span>0% waived</span><span>12%</span></div>
                  </div>
                </div>
                {expandedSliders.eqt && (
                  <div className="px-5 pb-5 pt-2 border-t border-edge-subtle">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {cashForecastData.fundForecasts.filter(ff => ff.isLeveraged).map((ff) => {
                        const fKey = makeKey(ff.fund, ff.investor);
                        const rate = eqtPerfRates[fKey] !== undefined ? eqtPerfRates[fKey] : DEFAULT_EQT_PERF_RATE;
                        return (
                          <div key={fKey} className="bg-surface-tertiary rounded-lg p-2.5">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-ink-muted truncate mr-2">{ff.fund.replace("EQT ", "")}</span>
                              <span className={`text-xs font-bold ${rate === 0 ? "text-success-fg" : rate < 8 ? "text-warning-fg" : "text-warning-fg"}`}>
                                {rate.toFixed(1)}%
                              </span>
                            </div>
                            <input type="range" min="0" max="12" step="0.5" value={rate}
                              onChange={(e) => setEqtPerfRates(prev => ({ ...prev, [fKey]: parseFloat(e.target.value) }))}
                              className="w-full h-1.5 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-brand" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Fund MOIC Simulation */}
            {cashForecastData && cashForecastData.fundForecasts.length > 0 && (
              <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-4">
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-semibold text-ink">Fund MOIC Simulation</h3>
                      <span className="text-xs text-ink-subtle">Adjust target MOIC per fund — base from Excel</span>
                      {Object.keys(fundMoicOverrides).length > 0 && (
                        <button onClick={() => setFundMoicOverrides({})}
                          className="text-xs text-danger-fg hover:text-danger-fg px-2 py-0.5 rounded bg-danger/30 hover:bg-danger/50 transition">
                          Reset All
                        </button>
                      )}
                    </div>
                    <button onClick={() => setExpandedSliders(p => ({ ...p, moic: !p.moic }))}
                      className="text-xs text-ink-muted hover:text-ink px-2 py-1 rounded bg-surface-tertiary hover:bg-surface-tertiary transition">
                      {expandedSliders.moic ? "Collapse" : "Per Fund"}
                    </button>
                  </div>
                </div>
                {expandedSliders.moic && (
                  <div className="px-5 pb-5 pt-2 border-t border-edge-subtle">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {cashForecastData.fundForecasts.map((ff) => {
                        const fKey = makeKey(ff.fund, ff.investor);
                        const base = ff.baseFundMoic || 0;
                        const current = fundMoicOverrides[fKey] !== undefined ? fundMoicOverrides[fKey] : (ff.fundMoic || 0);
                        const isOverridden = fundMoicOverrides[fKey] !== undefined;
                        const diff = base > 0 ? current - base : 0;
                        return (
                          <div key={fKey} className={`rounded-lg p-2.5 ${isOverridden ? "bg-info/25 border border-info-border" : "bg-surface-tertiary"}`}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-ink-muted truncate mr-2">{ff.fund.replace("EQT ", "")}</span>
                              <div className="flex items-center gap-1.5">
                                <span className={`text-xs font-bold ${current > base ? "text-success-fg" : current < base ? "text-danger-fg" : "text-brand-secondary"}`}>
                                  {current.toFixed(2)}x
                                </span>
                                {isOverridden && diff !== 0 && (
                                  <span className={`text-xs ${diff > 0 ? "text-success-fg" : "text-danger-fg"}`}>
                                    ({diff > 0 ? "+" : ""}{diff.toFixed(2)})
                                  </span>
                                )}
                              </div>
                            </div>
                            <input type="range" min="0" max="5" step="0.05"
                              value={current}
                              onChange={(e) => setFundMoicOverrides(prev => ({ ...prev, [fKey]: parseFloat(e.target.value) }))}
                              className="w-full h-1.5 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-info" />
                            <div className="flex justify-between text-xs mt-0.5">
                              <span className="text-ink-subtle">0x</span>
                              {base > 0 && <span className="text-ink-subtle">base: {base.toFixed(2)}x</span>}
                              <span className="text-ink-subtle">5x</span>
                            </div>
                            {isOverridden && (
                              <button onClick={() => setFundMoicOverrides(prev => { const n = { ...prev }; delete n[fKey]; return n; })}
                                className="text-xs text-ink-subtle hover:text-ink-muted mt-1 transition">reset</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Other Net Investment Income */}
            <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-4">
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-ink">Other Net Investment Income</h3>
                    <span className="text-xs text-ink-subtle">Dividends, interest, other gains — expands interest deduction pool</span>
                  </div>
                  <button onClick={() => setExpandedSliders(p => ({ ...p, otherInc: !p.otherInc }))}
                    className="text-xs text-ink-muted hover:text-ink px-2 py-1 rounded bg-surface-tertiary hover:bg-surface-tertiary transition">
                    {expandedSliders.otherInc ? "Collapse" : "Per Year"}
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-bold text-brand-secondary w-20">${((otherInvestmentIncome[forecastYears[0]] || 0) / 1000).toFixed(0)}k</span>
                  <input type="range" min="0" max="100000" step="1000"
                    value={otherInvestmentIncome[forecastYears[0]] || 0}
                    onChange={(e) => { const v = parseFloat(e.target.value); setOtherInvestmentIncome(() => { const r = {}; forecastYears.forEach(yr => r[yr] = v); return r; }); }}
                    className="flex-1 h-2 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-success" />
                  <div className="flex justify-between text-xs text-ink-subtle w-16"><span>$0</span><span>$100k</span></div>
                </div>
              </div>
              {expandedSliders.otherInc && (
                <div className="px-5 pb-5 pt-2 border-t border-edge-subtle">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                    {forecastYears.map((yr) => (
                      <div key={yr} className="bg-surface-tertiary rounded-lg p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-ink-muted">{yr}</span>
                          <span className="text-xs font-bold text-brand-secondary">${((otherInvestmentIncome[yr] || 0) / 1000).toFixed(0)}k</span>
                        </div>
                        <input type="range" min="0" max="100000" step="1000"
                          value={otherInvestmentIncome[yr] || 0}
                          onChange={(e) => setOtherInvestmentIncome(prev => ({ ...prev, [yr]: parseFloat(e.target.value) }))}
                          className="w-full h-1.5 bg-surface-tertiary rounded-lg appearance-none cursor-pointer accent-success" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Investment Interest Deduction Summary */}
            {cashForecastData && (cashForecastData.totalTaxSavings > 0 || cashForecastData.finalCarryforward > 0) && (
              <div className="bg-info/20 rounded-lg p-5 border border-info-border mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-base font-semibold text-info-fg">Investment Interest Deduction (IRS §163(d))</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-ink-muted mb-1">Total Interest Paid</p>
                    <p className="text-lg font-bold text-danger-fg">{formatCurrency(cashForecastData.timeline.reduce((s, t) => s + t.interest, 0), 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-muted mb-1">Deduction Used</p>
                    <p className="text-lg font-bold text-brand-secondary">{formatCurrency(cashForecastData.timeline.reduce((s, t) => s + (t.interestDeduction || 0), 0), 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-muted mb-1">Tax Savings</p>
                    <p className="text-lg font-bold text-success-fg">{formatCurrency(cashForecastData.totalTaxSavings, 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-muted mb-1">Remaining Carryforward</p>
                    <p className={`text-lg font-bold ${cashForecastData.finalCarryforward > 0 ? "text-warning-fg" : "text-ink-subtle"}`}>
                      {cashForecastData.finalCarryforward > 0 ? formatCurrency(cashForecastData.finalCarryforward, 0) : "—"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* View Toggle */}
            <div className="flex items-center gap-1 mb-6 bg-surface-tertiary rounded-lg p-1 w-full scroll-x whitespace-nowrap">
              {[
                { key: "summary", label: "Summary" },
                { key: "fund", label: "By Fund" },
                { key: "vehicle", label: "By Vehicle" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setForecastView(t.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    forecastView === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Summary View: Aggregate chart */}
            {forecastView === "summary" && cashForecastData && (
              <div className="space-y-6">
                {/* Warning banner if no targets set */}
                {cashForecastData.fundsWithoutTargets > 0 && (
                  <div className="bg-warning/40 border border-warning-border rounded-lg p-4 flex items-start gap-3">
                    <span className="text-warning-fg text-lg mt-0.5">!</span>
                    <div>
                      <div className="text-warning-fg font-medium text-sm">
                        {cashForecastData.fundsWithoutTargets} of {cashForecastData.fundForecasts.length} funds have no return targets set
                      </div>
                      <div className="text-warning-fg/70 text-xs mt-1">
                        Payout projections only appear for funds with a Target Fund MOIC or Target IRR in the Excel file. Load fund_variables.xlsx with your return assumptions to see full projections.
                      </div>
                    </div>
                  </div>
                )}

                {/* Cash Flow Chart — actual (solid) vs projected (hatched) */}
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-1">Annual Cash Flows</h3>
                  <div className="flex items-center gap-4 mb-4 text-xs text-ink-muted">
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-danger-border"></span> Actual drawdowns</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-danger-border/40"></span> Projected drawdowns</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-warning-border/40"></span> Projected interest</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-success-border"></span> Actual distributions</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-success-border/40"></span> Projected payouts</span>
                  </div>
                  <ResponsiveContainer width="100%" height={chartH}>
                    <BarChart data={cashForecastData.timeline} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="year" stroke="var(--chart-axis)" tick={{ fontSize: 12 }} />
                      <YAxis stroke="var(--chart-axis)" tickFormatter={(v) => (v >= 1000 || v <= -1000) ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                        formatter={(v, name) => [v !== 0 ? `${Math.round(v).toLocaleString()}` : "—", name]}
                        itemSorter={(item) => item.value >= 0 ? 0 : 1}
                      />
                      <Legend />
                      <ReferenceLine y={0} stroke="var(--chart-axis)" />
                      <Bar dataKey="actualDrawdowns" name="Drawdowns (actual)" fill="var(--chart-negative)" stackId="out" />
                      <Bar dataKey="projectedDrawdowns" name="Drawdowns (projected)" fill="var(--chart-negative)" fillOpacity={0.35} stackId="out" />
                      <Bar dataKey="projectedInterest" name="Interest (projected)" fill="var(--chart-orange)" fillOpacity={0.35} stackId="out" />
                      <Bar dataKey="projectedTax" name="Tax (projected)" fill="var(--chart-purple)" fillOpacity={0.35} stackId="out" />
                      <Bar dataKey="actualPayouts" name="Distributions (actual)" fill="var(--chart-positive)" stackId="in" />
                      <Bar dataKey="projectedPayouts" name="Payouts (projected)" fill="var(--chart-positive)" fillOpacity={0.35} stackId="in" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Cumulative Net Cash */}
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-1">Cumulative Net Cash Position</h3>
                  <p className="text-xs text-ink-muted mb-3">
                    Net cash balance (payouts minus drawdowns). Interest is already embedded in modeled LP payouts.
                  </p>
                  <ResponsiveContainer width="100%" height={chartH}>
                    <AreaChart data={cashForecastData.timeline} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="year" stroke="var(--chart-axis)" tick={{ fontSize: 12 }} />
                      <YAxis stroke="var(--chart-axis)" tickFormatter={(v) => (v >= 1000 || v <= -1000) ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                        formatter={(v) => [`${Math.round(v).toLocaleString()}`]}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="cumulativeNet"
                        name="Cumulative Net (pre-tax)"
                        stroke="var(--chart-fill-primary)"
                        fill="var(--chart-fill-primary)"
                        fillOpacity={0.2}
                      />
                      <Area
                        type="monotone"
                        dataKey="cumulativeNetAfterTax"
                        name="Cumulative Net (after-tax)"
                        stroke="var(--chart-purple)"
                        fill="var(--chart-purple)"
                        fillOpacity={0.15}
                        strokeDasharray="5 5"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* ── Tabbed Detail Section: Lifecycle / Cash Flows / Tax Impact ── */}
                <div className="bg-surface-secondary rounded-lg border border-edge-subtle">
                  <div className="flex items-center gap-1 p-2 bg-surface-tertiary/50 rounded-t-lg border-b border-edge-subtle">
                    {[
                      { key: "lifecycle", label: "Fund Lifecycle" },
                      { key: "cashflows", label: "Annual Cash Flows" },
                      { key: "taximpact", label: "Tax Impact" },
                    ].map((t) => (
                      <button
                        key={t.key}
                        onClick={() => setForecastDetailTab(t.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                          forecastDetailTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                {forecastDetailTab === "lifecycle" && (
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-ink mb-4">Fund Lifecycle Timeline</h3>
                  <div className="scroll-x">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-edge">
                          <th className="text-left py-3 px-4 text-ink-muted font-medium">Fund</th>
                          <th className="text-center py-3 px-4 text-ink-muted font-medium">Vintage</th>
                          <th className="text-center py-3 px-4 text-ink-muted font-medium">Invest End</th>
                          <th className="text-center py-3 px-4 text-ink-muted font-medium">Hold End</th>
                          <th className="text-center py-3 px-4 text-ink-muted font-medium">Payout End</th>
                          <th className="text-right py-3 px-4 text-ink-muted font-medium">Fund MOIC</th>
                          <th className="text-center py-3 px-4 text-ink-muted font-medium">Source</th>
                          <th className="text-right py-3 px-4 text-ink-muted font-medium">Commitment</th>
                          <th className="text-right py-3 px-4 text-ink-muted font-medium">Total Exposure</th>
                          <th className="text-right py-3 px-4 text-ink-muted font-medium">Gross Return</th>
                          <th className="text-right py-3 px-4 text-ink-muted font-medium">Interest</th>
                          <th className="text-right py-3 px-4 text-ink-muted font-medium">LP Net Return</th>
                          <th className="text-right py-3 px-4 text-ink-muted font-medium">LP MOIC</th>
                          <th className="text-right py-3 px-4 text-brand-secondary font-medium">Tax</th>
                          <th className="text-right py-3 px-4 text-brand-secondary font-medium">After-Tax</th>
                          <th className="text-right py-3 px-4 text-brand-secondary font-medium">AT MOIC</th>
                          <th className="text-center py-3 px-4 text-ink-muted font-medium">Tax Split</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashForecastData.fundForecasts.map((ff, idx) => (
                          <tr key={idx} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                            <td className="py-3 px-4 text-ink font-medium">
                              {ff.fund}
                              {ff.isLeveraged && <span className="ml-2 text-xs text-warning-fg bg-warning/30 px-1.5 py-0.5 rounded">LEV</span>}
                            </td>
                            <td className="text-center py-3 px-4 text-ink-muted">{ff.vintageYear}</td>
                            <td className="text-center py-3 px-4 text-brand">{ff.investmentEnd}</td>
                            <td className="text-center py-3 px-4 text-brand-secondary">{ff.holdingEnd}</td>
                            <td className="text-center py-3 px-4 text-success-fg">{ff.payoutEnd}</td>
                            <td className="text-right py-3 px-4 text-ink">
                              {ff.fundMoic ? `${ff.fundMoic.toFixed(2)}x` : <span className="text-ink-subtle">—</span>}
                            </td>
                            <td className="text-center py-3 px-4">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                ff.moicSource === "Excel" ? "bg-brand/20 text-brand-secondary" :
                                ff.moicSource === "From IRR" ? "bg-brand/15 text-brand-secondary" :
                                "bg-surface-tertiary text-ink-subtle"
                              }`}>{ff.moicSource}</span>
                            </td>
                            <td className="text-right py-3 px-4 text-ink-muted">{ff.myCommitment.toLocaleString()}</td>
                            <td className="text-right py-3 px-4 text-ink-muted">{ff.totalExposure.toLocaleString()}</td>
                            <td className="text-right py-3 px-4 text-ink">
                              {ff.grossFundReturn > 0 ? Math.round(ff.grossFundReturn).toLocaleString() : <span className="text-ink-subtle">—</span>}
                            </td>
                            <td className="text-right py-3 px-4 text-warning-fg">
                              {ff.projectedTotalInterest > 0 ? `-${Math.round(ff.projectedTotalInterest).toLocaleString()}` : "—"}
                            </td>
                            <td className="text-right py-3 px-4 text-success-fg">
                              {ff.lpNetReturn > 0 ? Math.round(ff.lpNetReturn).toLocaleString() : <span className="text-ink-subtle">Set target</span>}
                            </td>
                            <td className="text-right py-3 px-4 font-bold text-success-fg">
                              {ff.impliedLpMoic > 0 ? `${ff.impliedLpMoic.toFixed(2)}x` : <span className="text-ink-subtle">—</span>}
                            </td>
                            <td className="text-right py-3 px-4 text-brand-secondary">
                              {ff.totalProjectedTax > 0 ? `-${Math.round(ff.totalProjectedTax).toLocaleString()}` : "—"}
                            </td>
                            <td className="text-right py-3 px-4 font-bold text-brand-secondary">
                              {ff.afterTaxReturn > 0 ? Math.round(ff.afterTaxReturn).toLocaleString() : <span className="text-ink-subtle">—</span>}
                            </td>
                            <td className="text-right py-3 px-4 font-bold text-brand-secondary">
                              {ff.afterTaxMoic > 0 ? `${ff.afterTaxMoic.toFixed(2)}x` : <span className="text-ink-subtle">—</span>}
                            </td>
                            <td className="text-center py-3 px-4 text-xs text-ink-subtle">
                              {ff.rothPct > 0 && <span className="text-success-fg">R:{Math.round(ff.rothPct * 100)}% </span>}
                              {ff.tradIraPct > 0 && <span className="text-warning-fg">T:{Math.round(ff.tradIraPct * 100)}% </span>}
                              {ff.taxablePct > 0 && <span className="text-danger-fg">TX:{Math.round(ff.taxablePct * 100)}%</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}

                {forecastDetailTab === "cashflows" && (
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-ink mb-4">Annual Cash Flows (Actual + Projected)</h3>
                  <div className="scroll-x">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-edge">
                          <th className="text-left py-3 px-3 text-ink-muted font-medium">Year</th>
                          <th className="text-center py-3 px-3 text-ink-muted font-medium">Type</th>
                          <th className="text-right py-3 px-3 text-danger-fg font-medium">Drawdowns</th>
                          <th className="text-right py-3 px-3 text-warning-fg font-medium">Interest</th>
                          <th className="text-right py-3 px-3 text-success-fg font-medium">Distributions</th>
                          <th className="text-right py-3 px-3 text-brand-secondary font-medium">Int. Deduction</th>
                          <th className="text-right py-3 px-3 text-brand-secondary font-medium">Tax</th>
                          <th className="text-right py-3 px-3 text-warning-fg font-medium">Carryforward</th>
                          <th className="text-right py-3 px-3 text-brand font-medium">Net Cash</th>
                          <th className="text-right py-3 px-3 text-brand-secondary font-medium">Net After Tax</th>
                          <th className="text-right py-3 px-3 text-ink font-medium">Cumulative</th>
                          <th className="text-right py-3 px-3 text-brand-secondary font-medium">Cum. After Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashForecastData.timeline.map((t) => (
                          <tr key={t.year} className={`border-b border-edge-subtle ${t.year === new Date().getFullYear() ? "bg-brand/10" : ""}`}>
                            <td className="py-2 px-3 text-ink font-medium">{t.year}</td>
                            <td className="text-center py-2 px-3">
                              <span className={`text-xs px-2 py-0.5 rounded ${t.isHistorical ? "bg-surface-tertiary text-ink-muted" : "bg-brand/20 text-brand-secondary"}`}>
                                {t.isHistorical ? "Actual" : "Projected"}
                              </span>
                            </td>
                            <td className="text-right py-2 px-3 text-danger-fg">{t.drawdowns > 0 ? `-${Math.round(t.drawdowns).toLocaleString()}` : "—"}</td>
                            <td className="text-right py-2 px-3 text-warning-fg">{t.interest > 0 ? `-${Math.round(t.interest).toLocaleString()}` : "—"}</td>
                            <td className="text-right py-2 px-3 text-success-fg">{t.payouts > 0 ? `+${Math.round(t.payouts).toLocaleString()}` : "—"}</td>
                            <td className="text-right py-2 px-3 text-brand-secondary">{(t.interestDeduction || 0) > 0 ? `+${Math.round(t.interestDeduction).toLocaleString()}` : "—"}</td>
                            <td className="text-right py-2 px-3 text-brand-secondary">{t.tax > 0 ? `-${Math.round(t.tax).toLocaleString()}` : "—"}</td>
                            <td className="text-right py-2 px-3 text-warning-fg">{(t.interestCarryforward || 0) > 0 ? Math.round(t.interestCarryforward).toLocaleString() : "—"}</td>
                            <td className={`text-right py-2 px-3 font-medium ${t.netCash >= 0 ? "text-success-fg" : "text-danger-fg"}`}>{t.netCash >= 0 ? "+" : ""}{Math.round(t.netCash).toLocaleString()}</td>
                            <td className={`text-right py-2 px-3 font-medium ${t.netCashAfterTax >= 0 ? "text-brand-secondary" : "text-danger-fg"}`}>{t.netCashAfterTax >= 0 ? "+" : ""}{Math.round(t.netCashAfterTax).toLocaleString()}</td>
                            <td className={`text-right py-2 px-3 font-bold ${t.cumulativeNet >= 0 ? "text-ink" : "text-danger-fg"}`}>{Math.round(t.cumulativeNet).toLocaleString()}</td>
                            <td className={`text-right py-2 px-3 font-bold ${t.cumulativeNetAfterTax >= 0 ? "text-brand-secondary" : "text-danger-fg"}`}>{Math.round(t.cumulativeNetAfterTax).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}

                {forecastDetailTab === "taximpact" && (
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-brand-secondary mb-4">Tax Impact Summary by Fund</h3>
                  <p className="text-sm text-ink-muted mb-4">
                    Breakdown: Gross LP Return (includes returned basis) → Federal Tax → State Tax → Net After-Tax Return.
                    Roth allocations are tax-free. Traditional IRA allocations are tax-deferred (not taxed here). Only the taxable allocation gain portion is taxed at capital gains rates.
                    Portfolio totals are reconciled from timeline tax after interest-deduction carryforward, so federal/state splits are shown only at fund level.
                  </p>
                  <div className="scroll-x">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-edge">
                          <th className="text-left py-3 px-3 text-ink-muted font-medium">Fund</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">LP Net Return</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">Cost Basis</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">Total Gain</th>
                          <th className="text-center py-3 px-3 text-success-fg font-medium">Roth (tax-free)</th>
                          <th className="text-center py-3 px-3 text-warning-fg font-medium">Trad IRA (deferred)</th>
                          <th className="text-center py-3 px-3 text-danger-fg font-medium">Taxable</th>
                          <th className="text-right py-3 px-3 text-brand font-medium">Federal Tax</th>
                          <th className="text-right py-3 px-3 text-warning-fg font-medium">State Tax</th>
                          <th className="text-right py-3 px-3 text-brand-secondary font-medium">Total Tax</th>
                          <th className="text-right py-3 px-3 text-brand-secondary font-medium">After-Tax Return</th>
                          <th className="text-right py-3 px-3 text-brand-secondary font-medium">After-Tax MOIC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashForecastData.fundForecasts.map((ff, idx) => {
                          const avgFedRate = ff.hasTargets ? Object.values(federalCapGainsRates).reduce((s, r) => s + r, 0) / Object.values(federalCapGainsRates).length : 0;
                          const avgStateRate = ff.hasTargets ? Object.values(stateCapGainsRates).reduce((s, r) => s + r, 0) / Object.values(stateCapGainsRates).length : 0;
                          const taxableGain = ff.totalGain * ff.taxablePct;
                          const fedTax = taxableGain * avgFedRate / 100;
                          const stateTax = taxableGain * avgStateRate / 100;
                          return (
                            <tr key={idx} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                              <td className="py-3 px-3 text-ink font-medium text-xs">
                                {ff.fund}
                                {ff.isLeveraged && <span className="ml-1 text-xs text-warning-fg">LEV</span>}
                              </td>
                              <td className="text-right py-3 px-3 text-success-fg">{ff.lpNetReturn > 0 ? Math.round(ff.lpNetReturn).toLocaleString() : "—"}</td>
                              <td className="text-right py-3 px-3 text-ink-muted">{ff.myCommitment.toLocaleString()}</td>
                              <td className="text-right py-3 px-3 text-ink">{ff.totalGain > 0 ? Math.round(ff.totalGain).toLocaleString() : "—"}</td>
                              <td className="text-center py-3 px-3 text-success-fg">{Math.round(ff.rothPct * 100)}%</td>
                              <td className="text-center py-3 px-3 text-warning-fg">{Math.round(ff.tradIraPct * 100)}%</td>
                              <td className="text-center py-3 px-3 text-danger-fg">{Math.round(ff.taxablePct * 100)}%</td>
                              <td className="text-right py-3 px-3 text-brand">{fedTax > 0 ? `-${Math.round(fedTax).toLocaleString()}` : "—"}</td>
                              <td className="text-right py-3 px-3 text-warning-fg">{stateTax > 0 ? `-${Math.round(stateTax).toLocaleString()}` : "—"}</td>
                              <td className="text-right py-3 px-3 text-brand-secondary font-medium">{ff.totalProjectedTax > 0 ? `-${Math.round(ff.totalProjectedTax).toLocaleString()}` : "—"}</td>
                              <td className="text-right py-3 px-3 text-brand-secondary font-bold">{ff.afterTaxReturn > 0 ? Math.round(ff.afterTaxReturn).toLocaleString() : "—"}</td>
                              <td className="text-right py-3 px-3 text-brand-secondary font-bold">{ff.afterTaxMoic > 0 ? `${ff.afterTaxMoic.toFixed(2)}x` : "—"}</td>
                            </tr>
                          );
                        })}
                        <tr className="border-t-2 border-edge bg-brand/10">
                          <td className="py-3 px-3 font-bold text-ink">Portfolio Total</td>
                          <td className="text-right py-3 px-3 text-success-fg font-bold">{Math.round(cashForecastData.fundForecasts.reduce((s, f) => s + f.lpNetReturn, 0)).toLocaleString()}</td>
                          <td className="text-right py-3 px-3 text-ink-muted font-bold">{Math.round(cashForecastData.fundForecasts.reduce((s, f) => s + f.myCommitment, 0)).toLocaleString()}</td>
                          <td className="text-right py-3 px-3 text-ink font-bold">{Math.round(cashForecastData.fundForecasts.reduce((s, f) => s + f.totalGain, 0)).toLocaleString()}</td>
                          <td colSpan={3}></td>
                          <td className="text-right py-3 px-3 text-brand font-bold">—</td>
                          <td className="text-right py-3 px-3 text-warning-fg font-bold">—</td>
                          <td className="text-right py-3 px-3 text-brand-secondary font-bold">
                            {(() => {
                              const totalTax = cashForecastData.timeline.reduce((s, t) => s + (t.tax || 0), 0);
                              return totalTax > 0 ? `-${Math.round(totalTax).toLocaleString()}` : "—";
                            })()}
                          </td>
                          <td className="text-right py-3 px-3 text-brand-secondary font-bold">
                            {(() => {
                              const totalLpReturn = cashForecastData.fundForecasts.reduce((s, f) => s + f.lpNetReturn, 0);
                              const totalTax = cashForecastData.timeline.reduce((s, t) => s + (t.tax || 0), 0);
                              const totalAfterTax = totalLpReturn - totalTax;
                              return totalAfterTax > 0 ? Math.round(totalAfterTax).toLocaleString() : "—";
                            })()}
                          </td>
                          <td className="text-right py-3 px-3 text-brand-secondary font-bold">
                            {(() => {
                              const totalCost = cashForecastData.fundForecasts.reduce((s, f) => s + f.myCommitment, 0);
                              const totalLpReturn = cashForecastData.fundForecasts.reduce((s, f) => s + f.lpNetReturn, 0);
                              const totalTax = cashForecastData.timeline.reduce((s, t) => s + (t.tax || 0), 0);
                              const totalAT = totalLpReturn - totalTax;
                              return totalCost > 0 ? `${(totalAT / totalCost).toFixed(2)}x` : "—";
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 text-xs text-ink-muted">
                    Reconciliation check: after-tax gain ({Math.round(cashForecastData.totalAfterTaxGain).toLocaleString()})
                    vs final cumulative after-tax ({Math.round(cashForecastData.finalCumulativeNetAfterTax).toLocaleString()})
                    delta = {Math.round(cashForecastData.reconciliationDeltaAfterTax).toLocaleString()}.
                  </div>
                </div>
                )}
                </div>
              </div>
            )}

            {/* By Fund View */}
            {forecastView === "fund" && cashForecastData && (
              <div className="space-y-6">
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-4">Cash Flows by Fund</h3>
                  <ResponsiveContainer width="100%" height={chartH}>
                    <BarChart
                      data={cashForecastData.timeline.map(t => {
                        const entry = { year: t.year };
                        Object.entries(t.byFund).forEach(([fund, flows]) => {
                          entry[fund + " out"] = -(flows.drawdowns + flows.interest);
                          entry[fund + " in"] = flows.payouts;
                        });
                        return entry;
                      })}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="year" stroke="var(--chart-axis)" tick={{ fontSize: 12 }} />
                      <YAxis stroke="var(--chart-axis)" tickFormatter={(v) => (Math.abs(v) >= 1000) ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                        formatter={(v, name) => [`${Math.round(v).toLocaleString()}`, name]}
                      />
                      <Legend />
                      {cashForecastData.fundForecasts.map((ff, idx) => {
                        const colors = ["var(--chart-negative)", "var(--chart-orange)", "var(--chart-yellow)", "var(--chart-lime)", "var(--chart-positive)", "var(--chart-series-8)", "var(--chart-series-7)", "var(--chart-fill-primary)", "var(--chart-series-5)", "var(--chart-series-6)", "var(--chart-rose)", "var(--chart-purple)", "var(--chart-sky)", "var(--chart-positive)"];
                        const color = colors[idx % colors.length];
                        return (
                          <Bar key={ff.fund + " in"} dataKey={ff.fund + " in"} name={ff.fund + " (in)"} fill={color} stackId="in" fillOpacity={0.8} />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Per-fund detail cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {cashForecastData.fundForecasts.map((ff, idx) => {
                    const totalFutureDrawdowns = Object.values(ff.yearlyFlows).reduce((s, f) => s + f.drawdown, 0);
                    const totalFutureInterest = Object.values(ff.yearlyFlows).reduce((s, f) => s + f.interest, 0);
                    const totalFuturePayouts = Object.values(ff.yearlyFlows).reduce((s, f) => s + f.payout, 0);
                    return (
                      <div key={idx} className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
                        <div className="flex items-center gap-2 mb-3">
                          <h4 className="text-sm font-semibold text-ink">{ff.fund}</h4>
                          {ff.isLeveraged && <span className="text-xs text-warning-fg bg-warning/30 px-1.5 py-0.5 rounded">LEV</span>}
                        </div>
                        <div className="text-xs text-ink-muted mb-2">{ff.investor}</div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <div className="text-ink-subtle">Remaining</div>
                            <div className="text-warning-fg font-medium">{ff.remaining.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-ink-subtle">Fund MOIC</div>
                            <div className="text-ink font-medium">{ff.fundMoic ? `${ff.fundMoic.toFixed(2)}x` : "—"}</div>
                            <div className={`text-xs mt-0.5 ${ff.moicSource === "Excel" ? "text-brand" : ff.moicSource === "From IRR" ? "text-brand-secondary" : "text-ink-subtle"}`}>{ff.moicSource}</div>
                          </div>
                          <div>
                            <div className="text-ink-subtle">Maturity</div>
                            <div className="text-success-fg font-medium">{ff.maturityYear}</div>
                          </div>
                          <div>
                            <div className="text-ink-subtle">Future Draws</div>
                            <div className="text-danger-fg font-medium">{totalFutureDrawdowns > 0 ? `-${Math.round(totalFutureDrawdowns).toLocaleString()}` : "—"}</div>
                          </div>
                          <div>
                            <div className="text-ink-subtle">Total Interest</div>
                            <div className="text-warning-fg font-medium">{totalFutureInterest > 0 ? `-${Math.round(totalFutureInterest).toLocaleString()}` : "—"}</div>
                          </div>
                          <div>
                            <div className="text-ink-subtle">LP Net Return</div>
                            <div className="text-success-fg font-medium">{ff.lpNetReturn > 0 ? Math.round(ff.lpNetReturn).toLocaleString() : "—"}</div>
                          </div>
                          <div>
                            <div className="text-ink-subtle">LP MOIC</div>
                            <div className="text-success-fg font-bold">{ff.impliedLpMoic > 0 ? `${ff.impliedLpMoic.toFixed(2)}x` : "—"}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* By Vehicle View */}
            {forecastView === "vehicle" && cashForecastData && (
              <div className="space-y-6">
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-4">Cash Flows by Vehicle</h3>
                  {(() => {
                    // Derive unique vehicles dynamically from fund data
                    const vehicleNames = [...new Set(cashForecastData.fundForecasts.map(ff => ff.investor))];
                    const vehicleColors = ["var(--chart-negative)", "var(--chart-orange)", "var(--chart-yellow)", "var(--chart-lime)", "var(--chart-positive)", "var(--chart-fill-primary)", "var(--chart-series-8)", "var(--chart-purple)"];
                    const vehicleInColors = ["var(--chart-positive)", "var(--chart-series-8)", "var(--chart-fill-primary)", "var(--chart-lime)", "var(--chart-sky)", "var(--chart-violet)", "var(--chart-rose)", "var(--chart-indigo)"];
                    return (
                      <ResponsiveContainer width="100%" height={chartH}>
                        <BarChart
                          data={cashForecastData.timeline.map(t => {
                            const entry = { year: t.year };
                            Object.entries(t.byVehicle).forEach(([v, flows]) => {
                              const short = vehicleDisplayName(v);
                              entry[short + " draws"] = -(flows.drawdowns + flows.interest);
                              entry[short + " payouts"] = flows.payouts;
                            });
                            return entry;
                          })}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                          <XAxis dataKey="year" stroke="var(--chart-axis)" tick={{ fontSize: 12 }} />
                          <YAxis stroke="var(--chart-axis)" tickFormatter={(v) => (Math.abs(v) >= 1000) ? `${(v/1000).toFixed(0)}k` : v} />
                          <Tooltip
                            contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                            formatter={(v, name) => [`${Math.round(v).toLocaleString()}`, name]}
                          />
                          <Legend />
                          {vehicleNames.map((vName, idx) => {
                            const short = vehicleDisplayName(vName);
                            return [
                              <Bar key={short + " draws"} dataKey={short + " draws"} name={short + " (out)"} fill={vehicleColors[idx % vehicleColors.length]} stackId="out" />,
                              <Bar key={short + " payouts"} dataKey={short + " payouts"} name={short + " (in)"} fill={vehicleInColors[idx % vehicleInColors.length]} stackId="in" />,
                            ];
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>

                {/* Vehicle summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    const vehicles = {};
                    cashForecastData.fundForecasts.forEach(ff => {
                      const v = ff.investor;
                      if (!vehicles[v]) vehicles[v] = { funds: 0, totalCommitment: 0, totalRemaining: 0, lpNetReturn: 0, totalInterest: 0 };
                      vehicles[v].funds += 1;
                      vehicles[v].totalCommitment += ff.myCommitment;
                      vehicles[v].totalRemaining += ff.remaining;
                      vehicles[v].lpNetReturn += ff.lpNetReturn;
                      vehicles[v].totalInterest += ff.projectedTotalInterest;
                    });
                    return Object.entries(vehicles).map(([v, data]) => {
                      const short = vehicleDisplayName(v);
                      return (
                        <div key={v} className="bg-surface-secondary rounded-lg p-5 border border-edge-subtle">
                          <h4 className="text-sm font-semibold text-ink mb-3">{short}</h4>
                          <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                              <span className="text-ink-muted">Funds</span>
                              <span className="text-ink">{data.funds}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-ink-muted">Total Commitment</span>
                              <span className="text-ink">{data.totalCommitment.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-ink-muted">Remaining to Draw</span>
                              <span className="text-warning-fg">{data.totalRemaining.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-ink-muted">LP Net Return</span>
                              <span className="text-success-fg">{data.lpNetReturn > 0 ? Math.round(data.lpNetReturn).toLocaleString() : "—"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-ink-muted">Projected Interest</span>
                              <span className="text-warning-fg">{data.totalInterest > 0 ? `-${Math.round(data.totalInterest).toLocaleString()}` : "—"}</span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

        {/* ── Cash Flow by Strategy ── */}
        {cashFlowByStrategyData.length > 0 && (() => {
          const STRAT_COLORS = ["var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)", "var(--chart-warning)", "var(--chart-danger)", "#8b5cf6", "#ec4899", "#14b8a6"];
          return (
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8 mt-8">
              <h3 className="text-lg font-semibold mb-1 text-ink">Annual Cash Flow by Strategy</h3>
              <p className="text-xs text-ink-muted mb-4">Net cash flow (distributions minus drawdowns) by investment strategy over time</p>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={cashFlowByStrategyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                  <XAxis dataKey="year" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                  <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                    formatter={(v) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                  <ReferenceLine y={0} stroke="var(--chart-axis)" />
                  {allStrategies.map((s, i) => (
                    <Bar key={s} dataKey={s} stackId="strategy" fill={STRAT_COLORS[i % STRAT_COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* ── Drawdown Pacing: Actual vs Projected ── */}
        {drawdownPacingData.length > 0 && (
          <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-8">
            <h3 className="text-lg font-semibold mb-1 text-ink">Drawdown Pacing: Actual vs. Projected</h3>
            <p className="text-xs text-ink-muted mb-4">Cumulative drawdown % — solid line is actual capital calls, dashed line is projected future calls</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {drawdownPacingData.slice(0, 9).map(fp => (
                <div key={fp.fund} className="border border-edge-subtle rounded-lg p-3">
                  <p className="text-sm font-medium text-ink mb-2 truncate">{fp.fund}</p>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={fp.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="year" stroke="var(--chart-axis)" style={{ fontSize: "0.6rem" }} />
                      <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.6rem" }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                        formatter={(v, name) => [`${v}%`, name]} />
                      <Line type="monotone" dataKey="actual" name="Actual" stroke="var(--chart-fill-primary)" strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
                      <Line type="monotone" dataKey="projected" name="Projected" stroke="var(--chart-axis)" strokeDasharray="5 5" strokeWidth={1.5} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Tabbed: Peak Unfunded / Distribution Source ── */}
        {(peakUnfundedData.length > 0 || distSourceData.length > 0) && (
          <div className="bg-surface-secondary rounded-lg border border-edge-subtle mb-8">
            <div className="flex items-center gap-1 p-2 bg-surface-tertiary/50 rounded-t-lg border-b border-edge-subtle">
              {[
                { key: "unfunded", label: "Peak Unfunded Exposure", show: peakUnfundedData.length > 0 },
                { key: "distribution", label: "Distribution Sources", show: distSourceData.length > 0 },
              ].filter(t => t.show).map((t) => (
                <button
                  key={t.key}
                  onClick={() => setForecastChartTab(t.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    forecastChartTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {forecastChartTab === "unfunded" && peakUnfundedData.length > 0 && (
              <div className="p-4 sm:p-6">
                <p className="text-xs text-ink-muted mb-4">Outstanding unfunded commitments over time — shows when the portfolio demands the most liquidity</p>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={peakUnfundedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="year" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                      formatter={(v) => formatCurrency(v)} />
                    <Area type="monotone" dataKey="unfunded" name="Unfunded Commitments" stroke="var(--chart-warning)" fill="var(--chart-warning)" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {forecastChartTab === "distribution" && distSourceData.length > 0 && (
              <div className="p-4 sm:p-6">
                <p className="text-xs text-ink-muted mb-4">Projected distributions split into Return of Capital vs. Realized Gains by year</p>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={distSourceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="year" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                      formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                    <Area type="monotone" dataKey="returnOfCapital" name="Return of Capital" stackId="dist" stroke="var(--chart-fill-primary)" fill="var(--chart-fill-primary)" fillOpacity={0.5} />
                    <Area type="monotone" dataKey="realizedGains" name="Realized Gains" stackId="dist" stroke="var(--chart-positive)" fill="var(--chart-positive)" fillOpacity={0.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

          </div>
        )}

        {/* ===== ANALYTICS TAB ===== */}
        {pageTab === "analytics" && (
          <div className="space-y-6">
            {/* Row 1: IRR + Unfunded Coverage + Liquidity Runway */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Portfolio IRR Card */}
              <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
                <h3 className="text-sm font-medium text-ink-muted mb-2">Portfolio Returns</h3>
                <div className="flex items-baseline gap-4">
                  <div>
                    <p className={`text-3xl font-bold ${irrData.portfolioIRR !== null && irrData.portfolioIRR >= 0 ? "text-success-fg" : "text-danger-fg"}`}>
                      {irrData.portfolioIRR !== null ? `${(irrData.portfolioIRR * 100).toFixed(1)}%` : "N/A"}
                    </p>
                    <p className="text-xs text-ink-subtle mt-1">IRR (cash-flow weighted)</p>
                  </div>
                  <div className="border-l border-edge pl-4">
                    <p className={`text-2xl font-bold ${irrData.portfolioAnnTvpi !== null && irrData.portfolioAnnTvpi >= 0 ? "text-info-fg" : "text-danger-fg"}`}>
                      {irrData.portfolioAnnTvpi !== null ? `${(irrData.portfolioAnnTvpi * 100).toFixed(1)}%` : "N/A"}
                    </p>
                    <p className="text-xs text-ink-subtle mt-1">Ann. TVPI (commitment)</p>
                  </div>
                </div>
              </div>

              {/* Unfunded Coverage Ratio */}
              <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
                <h3 className="text-sm font-medium text-ink-muted mb-2">Unfunded Coverage Ratio</h3>
                <p className={`text-3xl font-bold ${unfundedCoverage.ratio >= 1 ? "text-success-fg" : unfundedCoverage.ratio >= 0.5 ? "text-warning-fg" : "text-danger-fg"}`}>
                  {unfundedCoverage.ratio.toFixed(2)}x
                </p>
                <p className="text-xs text-ink-subtle mt-1">
                  NAV {Math.round(unfundedCoverage.totalNav).toLocaleString()} / Unfunded {Math.round(unfundedCoverage.totalUnfunded).toLocaleString()}
                </p>
              </div>

              {/* Liquidity Runway */}
              <div className="bg-gradient-to-br from-surface-secondary to-surface-tertiary rounded-lg p-6 border border-edge">
                <h3 className="text-sm font-medium text-ink-muted mb-3">Projected Cash Needs</h3>
                {liquidityRunway && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {liquidityRunway.map(w => (
                      <div key={w.months} className="text-center">
                        <div className="text-xs text-ink-subtle mb-1">{w.label}</div>
                        <div className={`text-sm font-bold ${w.netNeed > 0 ? "text-danger-fg" : "text-success-fg"}`}>
                          {w.netNeed > 0 ? `-${Math.round(w.netNeed / 1000)}k` : `+${Math.round(Math.abs(w.netNeed) / 1000)}k`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* IRR by Fund Table */}
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold text-ink mb-1">Fund IRR & Performance Summary</h3>
              <p className="text-xs text-ink-muted mb-4">IRR = cash-flow-weighted return based on actual call/distribution dates &nbsp;|&nbsp; Ann. TVPI = commitment-level annualized multiple</p>
              <div className="scroll-x">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className="text-left py-3 px-3 text-ink-muted font-medium">Fund</th>
                      <th className="text-left py-3 px-3 text-ink-muted font-medium">Vehicle</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">Drawn</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">NAV</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">Distributed</th>
                      <th className="text-right py-3 px-3 text-success-fg font-medium">TVPI</th>
                      <th className="text-right py-3 px-3 text-brand font-medium">DPI</th>
                      <th className="text-right py-3 px-3 text-brand-secondary font-medium" title="Cash-flow weighted IRR using actual drawdown and distribution dates">IRR</th>
                      <th className="text-right py-3 px-3 text-info-fg font-medium" title="Annualized TVPI: TVPI^(1/years) - treats all capital as deployed at inception">Ann. TVPI</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">Years</th>
                    </tr>
                  </thead>
                  <tbody>
                    {irrData.fundIRRs.sort((a, b) => (b.irr || -999) - (a.irr || -999)).map((f, idx) => (
                      <tr key={idx} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                        <td className="py-3 px-3 text-ink font-medium text-xs">{f.fund}</td>
                        <td className="py-3 px-3 text-ink-subtle text-xs">{vehicleDisplayName(f.investor)}</td>
                        <td className="text-right py-3 px-3 text-ink-muted">{Math.round(f.drawn).toLocaleString()}</td>
                        <td className="text-right py-3 px-3 text-ink-muted">{Math.round(f.nav).toLocaleString()}</td>
                        <td className="text-right py-3 px-3 text-ink-muted">{Math.round(f.distributed).toLocaleString()}</td>
                        <td className="text-right py-3 px-3 text-success-fg font-bold">{f.tvpi.toFixed(2)}x</td>
                        <td className="text-right py-3 px-3 text-brand">{f.dpi.toFixed(2)}x</td>
                        <td className={`text-right py-3 px-3 font-bold ${f.irr !== null && f.irr >= 0 ? "text-brand-secondary" : f.irr !== null ? "text-danger-fg" : "text-ink-subtle"}`}>
                          {f.irr !== null ? `${(f.irr * 100).toFixed(1)}%` : "—"}
                        </td>
                        <td className={`text-right py-3 px-3 ${f.twirr !== null && f.twirr >= 0 ? "text-info-fg" : f.twirr !== null ? "text-danger-fg" : "text-ink-subtle"}`}>
                          {f.twirr !== null ? `${(f.twirr * 100).toFixed(1)}%` : "—"}
                        </td>
                        <td className="text-right py-3 px-3 text-ink-muted">
                          {f.years !== null ? f.years.toFixed(1) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Row 2: J-Curve + DPI vs TVPI */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* J-Curve */}
              <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                <h3 className="text-lg font-semibold text-ink mb-1">J-Curve by Fund</h3>
                <p className="text-xs text-ink-muted mb-4">Cumulative net cash flow by quarters since first drawdown</p>
                <ResponsiveContainer width="100%" height={chartH}>
                  <LineChart data={jCurveData.chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="label" stroke="var(--chart-axis)" tick={{ fontSize: 10 }} />
                    <YAxis stroke="var(--chart-axis)" tickFormatter={v => v >= 1000 || v <= -1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }} />
                    <Legend wrapperStyle={{ fontSize: "10px" }} />
                    {jCurveData.fundNames.map((name, i) => {
                      const colors = ["var(--chart-fill-primary)","var(--chart-positive)","var(--chart-series-3)","var(--chart-negative)","var(--chart-purple)","var(--chart-series-7)","var(--chart-series-6)","var(--chart-lime)","var(--chart-orange)","var(--chart-indigo)","var(--chart-series-8)","var(--chart-fuchsia)","var(--chart-amber-bright)","var(--chart-rose-soft)"];
                      return <Line key={name} type="monotone" dataKey={name} stroke={colors[i % colors.length]} dot={false} strokeWidth={2} />;
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* DPI vs TVPI Over Time */}
              <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                <h3 className="text-lg font-semibold text-ink mb-1">Portfolio DPI vs TVPI Over Time</h3>
                <p className="text-xs text-ink-muted mb-4">Tracks realization progress — DPI rising toward TVPI means paper gains becoming cash</p>
                <ResponsiveContainer width="100%" height={chartH}>
                  <AreaChart data={dpiTvpiHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="quarter" stroke="var(--chart-axis)" tick={{ fontSize: 10 }} />
                    <YAxis stroke="var(--chart-axis)" tickFormatter={v => `${v.toFixed(1)}x`} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                      formatter={(v, name) => [`${v.toFixed(3)}x`, name]} />
                    <Legend />
                    <Area type="monotone" dataKey="tvpi" name="TVPI (total value)" stroke="var(--chart-fill-primary)" fill="var(--chart-fill-primary)" fillOpacity={0.15} strokeWidth={2} />
                    <Area type="monotone" dataKey="dpi" name="DPI (cash returned)" stroke="var(--chart-positive)" fill="var(--chart-positive)" fillOpacity={0.15} strokeWidth={2} />
                    <Area type="monotone" dataKey="rvpi" name="RVPI (unrealized)" stroke="var(--chart-purple)" fill="var(--chart-purple)" fillOpacity={0.1} strokeWidth={1} strokeDasharray="4 4" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Row 3: Vintage Diversification + Distribution Waterfall */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Vintage Year Diversification */}
              <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                <h3 className="text-lg font-semibold text-ink mb-1">Vintage Year Diversification</h3>
                <p className="text-xs text-ink-muted mb-4">Capital deployed by vintage year — concentration risk if bunched in one year</p>
                <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                  <BarChart data={vintageData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="vintage" stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
                    <YAxis stroke="var(--chart-axis)" tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                      formatter={(v) => [Math.round(v).toLocaleString()]} />
                    <Legend />
                    <Bar dataKey="committed" name="Committed" fill="var(--chart-fill-primary)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="drawn" name="Drawn" fill="var(--chart-positive)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="nav" name="Current NAV" fill="var(--chart-purple)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 flex flex-wrap gap-2">
                  {vintageData.map(v => (
                    <span key={v.vintage} className="text-xs bg-surface-tertiary text-ink-muted px-2 py-1 rounded">
                      {v.vintage}: {v.funds} fund{v.funds !== 1 ? "s" : ""}
                    </span>
                  ))}
                </div>
              </div>

              {/* Distribution Waterfall by Type */}
              <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                <h3 className="text-lg font-semibold text-ink mb-1">Distribution Breakdown</h3>
                <p className="text-xs text-ink-muted mb-4">Return of capital vs realized gains — impacts tax treatment</p>
                <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                  <BarChart data={distWaterfall} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                      tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <YAxis type="category" dataKey="fund" width={140}
                      tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                      tickFormatter={v => v.replace("EQT ", "").substring(0, 20)} />
                    <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                      formatter={(v) => [Math.round(v).toLocaleString()]} />
                    <Legend />
                    <Bar dataKey="returnOfCapital" name="Return of Capital" fill="var(--chart-fill-primary)" stackId="a" />
                    <Bar dataKey="gains" name="Realized Gains" fill="var(--chart-positive)" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Row 4: Currency-Adjusted Returns */}
            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
              <h3 className="text-lg font-semibold text-ink mb-1">Currency-Adjusted Returns</h3>
              <p className="text-xs text-ink-muted mb-4">Local currency TVPI vs USD-equivalent TVPI — shows FX impact on your actual returns</p>
              <div className="scroll-x">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className="text-left py-3 px-3 text-ink-muted font-medium">Fund</th>
                      <th className="text-center py-3 px-3 text-ink-muted font-medium">CCY</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">Drawn (local)</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">NAV (local)</th>
                      <th className="text-right py-3 px-3 text-success-fg font-medium">TVPI (local)</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">Drawn (USD)</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">NAV (USD)</th>
                      <th className="text-right py-3 px-3 text-brand font-medium">TVPI (USD)</th>
                      <th className="text-right py-3 px-3 text-ink-muted font-medium">FX Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currencyAdjustedReturns.map((f, idx) => (
                      <tr key={idx} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                        <td className="py-3 px-3 text-ink font-medium text-xs">{f.fund}</td>
                        <td className="text-center py-3 px-3 text-xs text-ink-subtle">{f.currency}</td>
                        <td className="text-right py-3 px-3 text-ink-muted">{Math.round(f.drawn).toLocaleString()}</td>
                        <td className="text-right py-3 px-3 text-ink-muted">{Math.round(f.nav).toLocaleString()}</td>
                        <td className="text-right py-3 px-3 text-success-fg font-bold">{f.tvpiLocal.toFixed(2)}x</td>
                        <td className="text-right py-3 px-3 text-ink-muted">${Math.round(f.drawnUsd).toLocaleString()}</td>
                        <td className="text-right py-3 px-3 text-ink-muted">${Math.round(f.navUsd).toLocaleString()}</td>
                        <td className={`text-right py-3 px-3 font-bold ${f.tvpiUsd >= f.tvpiLocal ? "text-brand" : "text-warning-fg"}`}>
                          {f.tvpiUsd.toFixed(2)}x
                        </td>
                        <td className={`text-right py-3 px-3 text-xs ${f.fxImpact >= 0 ? "text-success-fg" : "text-danger-fg"}`}>
                          {f.currency === "EUR" ? (f.fxImpact >= 0 ? "+" : "") + Math.round(f.fxImpact).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Row 5: Sector / Geography Concentration (dual-mode: fund-level vs company-level) */}
            <div className="space-y-6">
              {/* Mode toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-ink mb-1">Portfolio Exposure</h3>
                  <p className="text-sm text-ink-muted">
                    {exposureMode === "company"
                      ? `Based on ${pcExposureData.companyCount} portfolio companies across all funds`
                      : "Based on fund-level classifications from fund_variables"}
                  </p>
                </div>
                <div className="flex items-center gap-1 bg-surface-tertiary/50 rounded-lg p-1 border border-edge-subtle">
                  {[
                    { key: "company", label: "By Company" },
                    { key: "fund", label: "By Fund" },
                  ].map((t) => (
                    <button key={t.key} onClick={() => setExposureMode(t.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                        exposureMode === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                      }`}>{t.label}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Industry / Sector Concentration */}
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-1">
                    {exposureMode === "company" ? "Industry Exposure" : "Sector / Strategy Concentration"}
                  </h3>
                  <p className="text-xs text-ink-muted mb-4">
                    {exposureMode === "company"
                      ? "Unrealized value by industry from portfolio company data"
                      : "NAV allocation by sector — set in fund_variables.xlsx"}
                  </p>
                  {exposureMode === "company" ? (
                    pcExposureData.byIndustry.length > 0 ? (
                      <ResponsiveContainer width="100%" height={Math.max(chartHSm, pcExposureData.byIndustry.length * 32)}>
                        <BarChart data={pcExposureData.byIndustry} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
                          <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                            tickFormatter={v => formatCurrency(v)} />
                          <YAxis type="category" dataKey="name" width={isMobile ? 90 : 140} tick={{ fill: "var(--chart-axis)", fontSize: isMobile ? 9 : 10 }} />
                          <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                            formatter={(v, name, props) => [formatCurrency(v) + ` (${props.payload.pct}%)`, "Value"]} />
                          <Bar dataKey="value" name="Value" fill="var(--chart-fill-primary)" radius={[0, 4, 4, 0]}>
                            {pcExposureData.byIndustry.map((entry, i) => (
                              <Cell key={i} fill={["var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)", "var(--chart-warning)", "var(--chart-danger)", "#8b5cf6", "#ec4899", "#14b8a6", "#f59e0b", "#6366f1"][i % 10]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-ink-subtle text-sm">
                        Upload Investor Data files to see company-level industry exposure
                      </div>
                    )
                  ) : (
                    concentrationData.bySector.length > 0 && concentrationData.bySector[0].name !== "Not Set" ? (
                      <ResponsiveContainer width="100%" height={chartHSm}>
                        <BarChart data={concentrationData.bySector} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
                          <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                            tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                          <YAxis type="category" dataKey="name" width={isMobile ? 80 : 120} tick={{ fill: "var(--chart-axis)", fontSize: isMobile ? 9 : 10 }} />
                          <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }} />
                          <Bar dataKey="value" name="NAV" fill="var(--chart-fill-primary)" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-ink-subtle text-sm">
                        Set "Sector / Strategy" in fund_variables.xlsx to see concentration data
                      </div>
                    )
                  )}
                </div>

                {/* Geography Concentration */}
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-1">Geography Exposure</h3>
                  <p className="text-xs text-ink-muted mb-4">
                    {exposureMode === "company"
                      ? "Unrealized value by geography from portfolio company data"
                      : "NAV allocation by geography — set in fund_variables.xlsx"}
                  </p>
                  {exposureMode === "company" ? (
                    pcExposureData.byGeography.length > 0 ? (
                      <ResponsiveContainer width="100%" height={Math.max(chartHSm, pcExposureData.byGeography.length * 32)}>
                        <BarChart data={pcExposureData.byGeography} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
                          <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                            tickFormatter={v => formatCurrency(v)} />
                          <YAxis type="category" dataKey="name" width={isMobile ? 90 : 140} tick={{ fill: "var(--chart-axis)", fontSize: isMobile ? 9 : 10 }} />
                          <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                            formatter={(v, name, props) => [formatCurrency(v) + ` (${props.payload.pct}%)`, "Value"]} />
                          <Bar dataKey="value" name="Value" fill="var(--chart-positive)" radius={[0, 4, 4, 0]}>
                            {pcExposureData.byGeography.map((entry, i) => (
                              <Cell key={i} fill={["var(--chart-positive)", "var(--chart-fill-primary)", "var(--chart-warning)", "var(--chart-indigo)", "var(--chart-danger)", "#14b8a6", "#8b5cf6", "#ec4899", "#f59e0b", "#6366f1"][i % 10]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-ink-subtle text-sm">
                        Upload Investor Data files to see company-level geography exposure
                      </div>
                    )
                  ) : (
                    concentrationData.byGeo.length > 0 && concentrationData.byGeo[0].name !== "Not Set" ? (
                      <ResponsiveContainer width="100%" height={chartHSm}>
                        <BarChart data={concentrationData.byGeo} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
                          <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                            tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                          <YAxis type="category" dataKey="name" width={isMobile ? 80 : 120} tick={{ fill: "var(--chart-axis)", fontSize: isMobile ? 9 : 10 }} />
                          <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }} />
                          <Bar dataKey="value" name="NAV" fill="var(--chart-positive)" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-ink-subtle text-sm">
                        Set "Geography" in fund_variables.xlsx to see concentration data
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Top Holdings (company-level only) */}
              {exposureMode === "company" && pcExposureData.topHoldings.length > 0 && (
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-1">Top Holdings by Unrealized Value</h3>
                  <p className="text-xs text-ink-muted mb-4">
                    Largest portfolio company positions across all funds ({pcExposureData.totalValue > 0 ? formatCurrency(pcExposureData.totalValue) + " total" : ""})
                  </p>
                  <ResponsiveContainer width="100%" height={Math.max(280, pcExposureData.topHoldings.length * 28)}>
                    <BarChart data={pcExposureData.topHoldings} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
                      <XAxis type="number" tick={{ fill: "var(--chart-axis)", fontSize: 10 }}
                        tickFormatter={v => formatCurrency(v)} />
                      <YAxis type="category" dataKey="name" width={isMobile ? 100 : 160}
                        tick={{ fill: "var(--chart-axis)", fontSize: isMobile ? 9 : 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
                        formatter={(v) => formatCurrency(v)}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            const d = payload[0].payload;
                            return `${label} — ${d.industry} / ${d.geography}${d.moic ? ` — ${d.moic.toFixed(2)}x MOIC` : ""}`;
                          }
                          return label;
                        }} />
                      <Bar dataKey="value" name="Unrealized Value" radius={[0, 4, 4, 0]}>
                        {pcExposureData.topHoldings.map((entry, i) => (
                          <Cell key={i} fill={["var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)", "var(--chart-warning)", "#8b5cf6", "#ec4899", "#14b8a6", "var(--chart-danger)", "#f59e0b", "#6366f1", "var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)", "var(--chart-warning)", "#8b5cf6"][i % 15]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Row 6: Performance vs Target */}
            {performanceTracking && performanceTracking.funds.some(f => f.hasTarget) && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-ink mb-1">Performance vs Target</h3>
                  <p className="text-sm text-ink-muted">Actual TVPI tracked against expected trajectory (IRR curve with J-curve adjustment for deployment phase)</p>
                </div>

                {/* On-Track Summary Table */}
                <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                  <h3 className="text-lg font-semibold text-ink mb-4">Fund Status Summary</h3>
                  <div className="scroll-x">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-edge">
                          <th className="text-left py-3 px-3 text-ink-muted font-medium">Fund</th>
                          <th className="text-left py-3 px-3 text-ink-muted font-medium">Vehicle</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">Age</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">Life</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">% Through</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">Target MOIC</th>
                          <th className="text-right py-3 px-3 text-brand font-medium">Expected TVPI</th>
                          <th className="text-right py-3 px-3 text-success-fg font-medium">Actual TVPI</th>
                          <th className="text-right py-3 px-3 text-ink-muted font-medium">Delta</th>
                          <th className="text-center py-3 px-3 text-ink-muted font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {performanceTracking.funds.filter(f => f.hasTarget && f.ageYears > 0).sort((a, b) => (b.deltaPct || 0) - (a.deltaPct || 0)).map((f, idx) => (
                          <tr key={idx} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                            <td className="py-3 px-3 text-ink font-medium text-xs">{f.fund}</td>
                            <td className="py-3 px-3 text-ink-subtle text-xs">{vehicleDisplayName(f.investor)}</td>
                            <td className="text-right py-3 px-3 text-ink-muted">{f.ageYears.toFixed(1)}yr</td>
                            <td className="text-right py-3 px-3 text-ink-subtle">{f.totalLife}yr</td>
                            <td className="text-right py-3 px-3">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                                  <div className="h-full bg-brand rounded-full" style={{ width: `${Math.min(100, f.pctLife * 100)}%` }}></div>
                                </div>
                                <span className="text-xs text-ink-muted">{Math.round(f.pctLife * 100)}%</span>
                              </div>
                            </td>
                            <td className="text-right py-3 px-3 text-ink-muted">{f.targetMoic.toFixed(2)}x</td>
                            <td className="text-right py-3 px-3 text-brand">{f.expectedTvpi.toFixed(2)}x</td>
                            <td className="text-right py-3 px-3 text-success-fg font-bold">{f.actualTvpi.toFixed(2)}x</td>
                            <td className={`text-right py-3 px-3 font-medium ${f.delta >= 0 ? "text-success-fg" : "text-danger-fg"}`}>
                              {f.delta >= 0 ? "+" : ""}{f.delta.toFixed(2)}x ({f.deltaPct >= 0 ? "+" : ""}{f.deltaPct.toFixed(0)}%)
                            </td>
                            <td className="text-center py-3 px-3">
                              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                                f.status === "ahead" ? "bg-success/50 text-success-fg" :
                                f.status === "behind" ? "bg-danger/40 text-danger-fg" :
                                "bg-brand/20 text-brand-secondary"
                              }`}>
                                {f.status === "ahead" ? "Ahead" : f.status === "behind" ? "Behind" : "On Track"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Performance vs Target Charts (Tabbed) */}
                {(() => {
                  const eligibleFunds = performanceTracking.funds.filter(f => f.hasTarget && f.curvePoints && f.curvePoints.length > 0);
                  if (eligibleFunds.length === 0) return null;

                  // Helper: build merged chart data for a single fund
                  const buildChartData = (f) => {
                    const chartData = f.curvePoints.map(cp => {
                      const match = f.actualPoints ? f.actualPoints.find(ap => Math.abs(ap.year - cp.year) < 0.15) : null;
                      return { ...cp, actual: match ? match.actual : undefined };
                    });
                    if (f.actualPoints) {
                      f.actualPoints.forEach(ap => {
                        if (!chartData.find(cd => Math.abs(cd.year - ap.year) < 0.15)) {
                          const expectedAtPt = f.curvePoints.reduce((closest, cp) =>
                            Math.abs(cp.year - ap.year) < Math.abs(closest.year - ap.year) ? cp : closest
                          );
                          chartData.push({ year: ap.year, expected: expectedAtPt.expected, actual: ap.actual });
                        }
                      });
                      chartData.sort((a, b) => a.year - b.year);
                    }
                    return chartData;
                  };

                  // Build combined "All Funds" chart data: normalize year axis as % of life
                  const buildAllFundsData = () => {
                    // Use year-based x-axis; merge all funds' actual points with a shared expected line
                    // Since funds have different total lives, normalize to 0-1 (% of life) then show years for the longest fund
                    const maxLife = Math.max(...eligibleFunds.map(f => f.totalLife));
                    const step = 0.25;
                    const points = [];
                    for (let t = 0; t <= maxLife; t += step) {
                      const pt = { year: t };
                      // Expected line (average across all funds that span this year)
                      let expSum = 0, expCount = 0;
                      eligibleFunds.forEach(f => {
                        if (t <= f.totalLife) {
                          const cp = f.curvePoints.reduce((closest, c) =>
                            Math.abs(c.year - t) < Math.abs(closest.year - t) ? c : closest
                          );
                          expSum += cp.expected;
                          expCount++;
                        }
                      });
                      if (expCount > 0) pt.expected = parseFloat((expSum / expCount).toFixed(3));
                      points.push(pt);
                    }
                    // Overlay each fund's actual data points
                    eligibleFunds.forEach(f => {
                      if (!f.actualPoints) return;
                      f.actualPoints.forEach(ap => {
                        let pt = points.find(p => Math.abs(p.year - ap.year) < 0.15);
                        if (!pt) {
                          pt = { year: ap.year };
                          points.push(pt);
                        }
                        const key = f.fund.replace(/[^a-zA-Z0-9]/g, '_');
                        pt[key] = ap.actual;
                      });
                    });
                    points.sort((a, b) => a.year - b.year);
                    return points;
                  };

                  const allFundsData = buildAllFundsData();
                  const fundKeys = eligibleFunds.map(f => f.fund.replace(/[^a-zA-Z0-9]/g, '_'));
                  const perfColors = ["var(--chart-positive)", "var(--chart-fill-primary)", "var(--chart-series-3)", "var(--chart-negative)", "var(--chart-series-5)", "var(--chart-series-6)", "var(--chart-series-7)", "var(--chart-series-8)", "var(--chart-orange)", "var(--chart-indigo)", "var(--chart-lime)", "var(--chart-rose)", "var(--chart-purple)", "var(--chart-sky)"];

                  // Tab options: "all" + one per fund
                  const perfTabs = [{ key: "all", label: "All Funds" }].concat(
                    eligibleFunds.map(f => ({ key: makeKey(f.fund, f.investor), label: f.fund }))
                  );

                  return (
                    <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                      <div className="flex flex-wrap items-center gap-1 mb-5 bg-surface-tertiary rounded-lg p-1">
                        {perfTabs.map((t) => (
                          <button
                            key={t.key}
                            onClick={() => setPerfTargetTab(t.key)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                              perfTargetTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                            }`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>

                      {perfTargetTab === "all" && (
                        <div>
                          <h3 className="text-lg font-semibold mb-1 text-ink">All Funds — Actual vs Expected Trajectory</h3>
                          <p className="text-xs text-ink-subtle mb-4">Dashed line = average expected TVPI across funds; colored lines = actual TVPI per fund</p>
                          <ResponsiveContainer width="100%" height={chartH}>
                            <LineChart data={allFundsData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                              <XAxis dataKey="year" stroke="var(--chart-axis)" tick={{ fontSize: 10 }}
                                tickFormatter={v => `Y${Math.round(v)}`} />
                              <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 10 }}
                                domain={[dataMin => Math.min(0.5, dataMin), 'auto']}
                                tickFormatter={v => `${v.toFixed(1)}x`} />
                              <Tooltip
                                contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px", fontSize: "11px" }}
                                formatter={(v, name) => [v ? `${v.toFixed(2)}x` : "—", name === "expected" ? "Avg Expected" : eligibleFunds.find(f => f.fund.replace(/[^a-zA-Z0-9]/g, '_') === name)?.fund || name]}
                                labelFormatter={(v) => `Year ${parseFloat(v).toFixed(1)}`}
                              />
                              <Legend
                                wrapperStyle={{ paddingTop: "0.5rem", fontSize: "11px" }}
                                formatter={(val) => val === "expected" ? "Avg Expected" : eligibleFunds.find(f => f.fund.replace(/[^a-zA-Z0-9]/g, '_') === val)?.fund || val}
                              />
                              <Line type="monotone" dataKey="expected" name="expected" stroke="var(--chart-axis)" strokeWidth={2}
                                strokeDasharray="6 3" dot={false} />
                              {eligibleFunds.map((f, idx) => {
                                const key = f.fund.replace(/[^a-zA-Z0-9]/g, '_');
                                const statusColor = f.status === "ahead" ? "var(--chart-positive)" : f.status === "behind" ? "var(--chart-negative)" : perfColors[idx % perfColors.length];
                                return (
                                  <Line key={key} type="monotone" dataKey={key} name={key} stroke={statusColor}
                                    strokeWidth={2} dot={{ fill: statusColor, r: 2.5 }} connectNulls={true} />
                                );
                              })}
                              <Line type="monotone" dataKey={() => 1} stroke="var(--chart-slate-mid)" strokeWidth={1}
                                strokeDasharray="2 4" dot={false} name="1.0x" legendType="none" />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {perfTargetTab !== "all" && (() => {
                        const selectedFund = eligibleFunds.find(f => (makeKey(f.fund, f.investor)) === perfTargetTab);
                        if (!selectedFund) return <div className="text-ink-subtle py-8 text-center">Select a fund</div>;
                        const chartData = buildChartData(selectedFund);
                        const statusColor = selectedFund.status === "ahead" ? "var(--chart-positive)" : selectedFund.status === "behind" ? "var(--chart-negative)" : "var(--chart-fill-primary)";
                        return (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-lg font-semibold text-ink">{selectedFund.fund}</h3>
                              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                                selectedFund.status === "ahead" ? "bg-success/50 text-success-fg" :
                                selectedFund.status === "behind" ? "bg-danger/40 text-danger-fg" :
                                "bg-brand/20 text-brand-secondary"
                              }`}>
                                {selectedFund.status === "ahead" ? "Ahead" : selectedFund.status === "behind" ? "Behind" : "On Track"} ({selectedFund.deltaPct >= 0 ? "+" : ""}{selectedFund.deltaPct.toFixed(0)}%)
                              </span>
                            </div>
                            <p className="text-xs text-ink-subtle mb-4">
                              {selectedFund.ageYears.toFixed(1)}yr into {selectedFund.totalLife}yr life &nbsp;|&nbsp; Target MOIC: {selectedFund.targetMoic.toFixed(2)}x &nbsp;|&nbsp; Actual: {selectedFund.actualTvpi.toFixed(2)}x vs Expected: {selectedFund.expectedTvpi.toFixed(2)}x
                            </p>
                            <ResponsiveContainer width="100%" height={chartH}>
                              <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                                <XAxis dataKey="year" stroke="var(--chart-axis)" tick={{ fontSize: 10 }}
                                  tickFormatter={v => `Y${Math.round(v)}`} />
                                <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 10 }}
                                  domain={[dataMin => Math.min(0.5, dataMin), 'auto']}
                                  tickFormatter={v => `${v.toFixed(1)}x`} />
                                <Tooltip
                                  contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px", fontSize: "12px" }}
                                  formatter={(v, name) => [v ? `${v.toFixed(2)}x` : "—", name]}
                                  labelFormatter={(v) => `Year ${parseFloat(v).toFixed(1)}`}
                                />
                                <Legend wrapperStyle={{ paddingTop: "0.5rem" }} />
                                <Line type="monotone" dataKey="expected" name="Expected Trajectory" stroke="var(--chart-axis)" strokeWidth={2}
                                  strokeDasharray="6 3" dot={false} />
                                <Line type="monotone" dataKey="actual" name="Actual TVPI" stroke={statusColor} strokeWidth={2.5}
                                  dot={{ fill: statusColor, r: 3.5 }} connectNulls={true} />
                                <Line type="monotone" dataKey={() => 1} stroke="var(--chart-slate-mid)" strokeWidth={1}
                                  strokeDasharray="2 4" dot={false} name="1.0x" legendType="none" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Tabbed: Heatmap / MOIC Sensitivity / Maturity Profile ── */}
            {(drawdownYearStrategyHeatmap.years.length > 0 || moicSensitivityData.length > 0 || maturityProfileData.length > 0) && (
              <div className="bg-surface-secondary rounded-lg border border-edge-subtle">
                <div className="flex items-center gap-1 p-2 bg-surface-tertiary/50 rounded-t-lg border-b border-edge-subtle">
                  {[
                    { key: "heatmap", label: "Drawdown × Strategy", show: drawdownYearStrategyHeatmap.years.length > 0 },
                    { key: "sensitivity", label: "MOIC Sensitivity", show: moicSensitivityData.length > 0 },
                    { key: "maturity", label: "Maturity Profile", show: maturityProfileData.length > 0 },
                  ].filter(t => t.show).map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setAnalyticsDetailTab(t.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                        analyticsDetailTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {analyticsDetailTab === "heatmap" && drawdownYearStrategyHeatmap.years.length > 0 && (
                  <div className="p-4 sm:p-6">
                    <p className="text-xs text-ink-muted mb-4">Commitment concentration by year of first drawdown and investment strategy</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-edge">
                            <th className="text-left py-2 px-3 text-ink-muted font-medium">Strategy</th>
                            {drawdownYearStrategyHeatmap.years.map(yr => (
                              <th key={yr} className="text-center py-2 px-3 text-ink-muted font-medium">{yr}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {drawdownYearStrategyHeatmap.strategies.map(strat => (
                            <tr key={strat} className="border-b border-edge-subtle">
                              <td className="py-2 px-3 font-medium text-ink">{strat}</td>
                              {drawdownYearStrategyHeatmap.years.map(yr => {
                                const val = drawdownYearStrategyHeatmap.grid[`${yr}||${strat}`] || 0;
                                const intensity = val > 0 ? Math.max(0.1, val / drawdownYearStrategyHeatmap.maxVal) : 0;
                                return (
                                  <td key={yr} className="text-center py-2 px-3">
                                    {val > 0 ? (
                                      <span className="inline-block px-2 py-1 rounded text-xs font-medium" style={{
                                        backgroundColor: `rgba(59, 130, 246, ${intensity * 0.7})`,
                                        color: intensity > 0.4 ? "white" : "var(--ink)",
                                      }}>
                                        {formatCurrency(val)}
                                      </span>
                                    ) : (
                                      <span className="text-ink-subtle">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {analyticsDetailTab === "sensitivity" && moicSensitivityData.length > 0 && (
                  <div className="p-4 sm:p-6">
                    <p className="text-xs text-ink-muted mb-4">Projected after-tax return at different MOIC assumptions (±0.5x from base)</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-edge">
                            <th className="text-left py-2 px-3 text-ink-muted font-medium">Fund</th>
                            <th className="text-center py-2 px-3 text-ink-muted font-medium">Base MOIC</th>
                            {["-0.5x", "-0.25x", "0x", "+0.25x", "+0.5x"].map(d => (
                              <th key={d} className={`text-center py-2 px-3 font-medium ${d === "0x" ? "text-brand-secondary" : "text-ink-muted"}`}>{d === "0x" ? "Base" : d}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {moicSensitivityData.map((row, i) => (
                            <tr key={i} className="border-b border-edge-subtle">
                              <td className="py-2 px-3 font-medium text-ink truncate max-w-[150px]">{row.name}</td>
                              <td className="text-center py-2 px-3 text-brand-secondary font-medium">{row.baseMoic.toFixed(2)}x</td>
                              {["-0.5x", "-0.25x", "+0x", "+0.25x", "+0.5x"].map(d => {
                                const val = row[d];
                                const baseVal = row["+0x"] || 0;
                                const diff = val - baseVal;
                                return (
                                  <td key={d} className={`text-center py-2 px-3 ${d === "+0x" ? "font-semibold text-brand-secondary" : diff > 0 ? "text-success-fg" : diff < 0 ? "text-danger-fg" : "text-ink"}`}>
                                    {formatCurrency(val)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {analyticsDetailTab === "maturity" && maturityProfileData.length > 0 && (() => {
                  const STRAT_COLORS = ["var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)", "var(--chart-warning)", "var(--chart-danger)", "#8b5cf6", "#ec4899", "#14b8a6"];
                  return (
                    <div className="p-4 sm:p-6">
                      <p className="text-xs text-ink-muted mb-4">When capital is expected to be returned, colored by strategy (based on first drawdown year + holding period)</p>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={maturityProfileData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                          <XAxis dataKey="year" stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
                          <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                          <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                            formatter={(v) => formatCurrency(v)} />
                          <Legend wrapperStyle={{ paddingTop: "1rem" }} />
                          {allStrategies.map((s, i) => (
                            <Bar key={s} dataKey={s} stackId="maturity" fill={STRAT_COLORS[i % STRAT_COLORS.length]} radius={i === allStrategies.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ===== PORTFOLIO COMPANIES TAB ===== */}
        {pageTab === "portfolio-companies" && (
          <div>
            {/* Upload + Sub-tabs header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-1 bg-surface-tertiary/50 rounded-lg p-1 border border-edge-subtle">
                {[
                  { key: "overview", label: "Company Overview" },
                  { key: "freshness", label: "Data Freshness" },
                  { key: "imports", label: "Import History" },
                ].map((t) => (
                  <button key={t.key} onClick={() => { setPcTab(t.key); setPcSelectedCompany(null); }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                      pcTab === t.key ? "bg-brand text-ink-onAccent" : "text-ink-muted hover:text-ink"
                    }`}>{t.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer px-4 py-2 bg-brand text-ink-onAccent rounded-lg text-xs font-medium hover:opacity-90 transition">
                  Upload Investor Data
                  <input type="file" accept=".xlsx,.xls" className="hidden" multiple onChange={handlePcUpload} />
                </label>
              </div>
            </div>

            {/* Upload Status Panel */}
            {pcUploadJobs.length > 0 && (
              <div className="mb-4 bg-surface-secondary rounded-lg border border-edge-subtle overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-edge-subtle">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-ink">Uploads</span>
                    {pcUploadJobs.some(j => j.status === "uploading" || j.status === "queued" || j.status === "running") && (
                      <div className="w-3 h-3 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                    )}
                  </div>
                  {pcUploadJobs.every(j => j.status === "completed" || j.status === "failed") && (
                    <button onClick={() => setPcUploadJobs([])} className="text-[10px] text-ink-muted hover:text-ink transition">Clear</button>
                  )}
                </div>
                <div className="divide-y divide-edge-subtle">
                  {pcUploadJobs.map(j => (
                    <div key={j.id} className="flex items-center gap-3 px-4 py-2">
                      {/* Status icon */}
                      <div className="flex-shrink-0">
                        {j.status === "completed" ? (
                          <svg className="w-4 h-4 text-success-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        ) : j.status === "failed" ? (
                          <svg className="w-4 h-4 text-danger-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        ) : (
                          <div className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                        )}
                      </div>
                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-ink truncate">{j.filename}</p>
                        <p className={`text-[10px] ${j.status === "failed" ? "text-danger-fg" : j.status === "completed" ? "text-success-fg" : "text-ink-muted"}`}>{j.msg}</p>
                      </div>
                      {/* Status badge */}
                      <span className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        j.status === "completed" ? "bg-success-fg/10 text-success-fg" :
                        j.status === "failed" ? "bg-danger-fg/10 text-danger-fg" :
                        j.status === "running" ? "bg-brand/10 text-brand" :
                        "bg-surface-tertiary text-ink-muted"
                      }`}>{j.status === "uploading" ? "Uploading" : j.status === "queued" ? "Queued" : j.status === "running" ? "Importing" : j.status === "completed" ? "Done" : "Failed"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pcLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                <span className="ml-3 text-ink-muted text-sm">Loading portfolio company data...</span>
              </div>
            ) : (
              <>
                {/* Company Overview */}
                {pcTab === "overview" && !pcSelectedCompany && (
                  <div className="space-y-6">
                    {pcData.companies.length === 0 ? (
                      <div className="bg-surface-secondary rounded-lg border border-edge-subtle p-8 text-center text-ink-muted">
                        <p className="text-lg mb-2">No portfolio companies yet</p>
                        <p className="text-sm">Upload an Investor Data Excel file to import portfolio company details.</p>
                      </div>
                    ) : (
                      <>
                        {/* Exposure Summary Cards */}
                        {pcExposureData.companyCount > 0 && (
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
                              <p className="text-xs text-ink-muted mb-1">Portfolio Companies</p>
                              <p className="text-2xl font-bold text-ink">{pcExposureData.companyCount}</p>
                            </div>
                            <div className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
                              <p className="text-xs text-ink-muted mb-1">Total Value</p>
                              <p className="text-2xl font-bold text-brand">{formatCurrency(pcExposureData.totalValue)}</p>
                            </div>
                            <div className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
                              <p className="text-xs text-ink-muted mb-1">Top Industry</p>
                              <p className="text-lg font-bold text-ink">{pcExposureData.byIndustry[0]?.name || "—"}</p>
                              <p className="text-xs text-ink-muted">{pcExposureData.byIndustry[0]?.pct || 0}% of value</p>
                            </div>
                            <div className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
                              <p className="text-xs text-ink-muted mb-1">Top Geography</p>
                              <p className="text-lg font-bold text-ink">{pcExposureData.byGeography[0]?.name || "—"}</p>
                              <p className="text-xs text-ink-muted">{pcExposureData.byGeography[0]?.pct || 0}% of value</p>
                            </div>
                          </div>
                        )}

                        {/* Portfolio Company Scatter/Bubble Chart */}
                        {processedBubbleData.points.length > 0 && (
                          <div className="bg-surface-secondary rounded-lg border border-edge-subtle">
                            <div className="p-4 border-b border-edge-subtle flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <h4 className="text-sm font-semibold text-ink">Holding Period vs. MOIC</h4>
                              <div className="flex flex-wrap gap-1">
                                {[
                                  { key: "all", label: "All" },
                                  { key: "fund", label: "By Fund" },
                                  { key: "area", label: "By Area" },
                                  { key: "strategy", label: "By Strategy" },
                                  { key: "type", label: "By Type" },
                                  { key: "geography", label: "By Geography" },
                                  { key: "sector", label: "By Sector" },
                                ].map(t => (
                                  <button key={t.key} onClick={() => setBubbleColorTab(t.key)}
                                    className={`px-2.5 py-1 text-xs rounded-md font-medium transition ${
                                      bubbleColorTab === t.key
                                        ? "bg-brand text-ink-onAccent"
                                        : "bg-surface-tertiary text-ink-muted hover:text-ink"
                                    }`}>{t.label}</button>
                                ))}
                              </div>
                            </div>
                            <div className="p-4">
                              {/* Legend */}
                              {bubbleColorTab !== "all" && processedBubbleData.groups.length > 0 && (
                                <div className="flex flex-wrap gap-3 mb-4">
                                  {processedBubbleData.groups.map(g => (
                                    <div key={g} className="flex items-center gap-1.5">
                                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: processedBubbleData.colorMap[g] }} />
                                      <span className="text-xs text-ink-muted">{g}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <ResponsiveContainer width="100%" height={400}>
                                <ScatterChart margin={{ top: 10, right: 30, bottom: 30, left: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-edge-subtle, #e5e7eb)" />
                                  <XAxis
                                    type="number"
                                    dataKey="holdingPeriod"
                                    name="Holding Period"
                                    unit=" yrs"
                                    tick={{ fontSize: 11, fill: "var(--color-ink-muted, #6b7280)" }}
                                    label={{ value: "Holding Period (years)", position: "bottom", offset: 10, style: { fontSize: 11, fill: "var(--color-ink-muted, #6b7280)" } }}
                                  />
                                  <YAxis
                                    type="number"
                                    dataKey="moic"
                                    name="Total MOIC"
                                    unit="x"
                                    tick={{ fontSize: 11, fill: "var(--color-ink-muted, #6b7280)" }}
                                    label={{ value: "Total MOIC", angle: -90, position: "insideLeft", offset: -5, style: { fontSize: 11, fill: "var(--color-ink-muted, #6b7280)" } }}
                                  />
                                  <ZAxis
                                    type="number"
                                    dataKey="totalValue"
                                    range={[40, 800]}
                                    name="Total Value"
                                  />
                                  <Tooltip
                                    cursor={{ strokeDasharray: "3 3" }}
                                    content={({ active, payload }) => {
                                      if (!active || !payload || !payload.length) return null;
                                      const d = payload[0].payload;
                                      return (
                                        <div className="bg-surface-primary border border-edge rounded-lg shadow-lg p-3 text-xs max-w-xs">
                                          <p className="font-semibold text-ink mb-1.5">{d.company}</p>
                                          <div className="space-y-0.5 text-ink-muted">
                                            <p>Holding: <span className="text-ink font-medium">{d.holdingPeriod} yrs</span></p>
                                            <p>MOIC: <span className={`font-medium ${d.moic >= 1 ? "text-success-fg" : "text-danger-fg"}`}>{d.moic.toFixed(2)}x</span></p>
                                            <p>Total Value: <span className="text-brand font-medium">{formatCurrency(d.totalValue)}</span></p>
                                            {bubbleColorTab !== "all" && <p>{bubbleColorTab === "fund" ? "Fund" : bubbleColorTab === "area" ? "Area" : bubbleColorTab === "strategy" ? "Strategy" : bubbleColorTab === "type" ? "Type" : bubbleColorTab === "geography" ? "Geography" : "Sector"}: <span className="text-ink font-medium">{d.group}</span></p>}
                                            {d.industry && d.industry !== "Not Classified" && <p>Industry: <span className="text-ink">{d.industry}</span></p>}
                                            {d.geography && d.geography !== "Not Classified" && <p>Geography: <span className="text-ink">{d.geography}</span></p>}
                                          </div>
                                        </div>
                                      );
                                    }}
                                  />
                                  <ReferenceLine y={1} stroke="var(--color-edge, #d1d5db)" strokeDasharray="4 4" label={{ value: "1.0x", position: "right", fontSize: 10, fill: "var(--color-ink-muted, #6b7280)" }} />
                                  {processedBubbleData.groups.map(group => (
                                    <Scatter
                                      key={group}
                                      name={group}
                                      data={processedBubbleData.points.filter(p => p.group === group)}
                                      fill={processedBubbleData.colorMap[group]}
                                      fillOpacity={0.7}
                                      stroke={processedBubbleData.colorMap[group]}
                                      strokeWidth={1}
                                    />
                                  ))}
                                </ScatterChart>
                              </ResponsiveContainer>
                              <p className="text-[10px] text-ink-muted mt-2 text-center">Bubble size represents total value. {bubbleColorTab === "all" ? "Companies appearing in multiple funds are merged." : bubbleColorTab === "fund" ? "Companies may appear multiple times if held in different funds." : "Companies appearing in multiple funds are merged."}</p>
                            </div>
                          </div>
                        )}

                        {/* Industry/Geography Exposure Visualization (treemap-style grid) */}
                        {pcExposureData.byIndustry.length > 0 && (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Industry Exposure — proportional blocks */}
                            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                              <h4 className="text-sm font-semibold text-ink mb-3">Industry Exposure</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {pcExposureData.byIndustry.map((item, i) => {
                                  const colors = ["bg-blue-500/80", "bg-emerald-500/80", "bg-violet-500/80", "bg-amber-500/80", "bg-rose-500/80", "bg-cyan-500/80", "bg-pink-500/80", "bg-teal-500/80", "bg-orange-500/80", "bg-indigo-500/80"];
                                  const minW = Math.max(60, item.pct * 2.5);
                                  return (
                                    <div key={item.name}
                                      className={`${colors[i % colors.length]} rounded-lg p-2.5 text-white transition-all hover:opacity-80 cursor-default`}
                                      style={{ flexBasis: `${Math.max(20, item.pct)}%`, flexGrow: item.pct > 15 ? 1 : 0, minWidth: `${minW}px` }}
                                      title={`${item.name}: ${formatCurrency(item.value)} (${item.pct}%)`}>
                                      <p className="text-xs font-semibold truncate">{item.name}</p>
                                      <p className="text-lg font-bold">{item.pct}%</p>
                                      <p className="text-[10px] opacity-80">{formatCurrency(item.value)}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Geography Exposure — proportional blocks */}
                            <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
                              <h4 className="text-sm font-semibold text-ink mb-3">Geography Exposure</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {pcExposureData.byGeography.map((item, i) => {
                                  const colors = ["bg-emerald-500/80", "bg-blue-500/80", "bg-amber-500/80", "bg-violet-500/80", "bg-cyan-500/80", "bg-rose-500/80", "bg-teal-500/80", "bg-pink-500/80", "bg-orange-500/80", "bg-indigo-500/80"];
                                  const minW = Math.max(60, item.pct * 2.5);
                                  return (
                                    <div key={item.name}
                                      className={`${colors[i % colors.length]} rounded-lg p-2.5 text-white transition-all hover:opacity-80 cursor-default`}
                                      style={{ flexBasis: `${Math.max(20, item.pct)}%`, flexGrow: item.pct > 15 ? 1 : 0, minWidth: `${minW}px` }}
                                      title={`${item.name}: ${formatCurrency(item.value)} (${item.pct}%)`}>
                                      <p className="text-xs font-semibold truncate">{item.name}</p>
                                      <p className="text-lg font-bold">{item.pct}%</p>
                                      <p className="text-[10px] opacity-80">{formatCurrency(item.value)}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Company Table */}
                        <div className="bg-surface-secondary rounded-lg border border-edge-subtle">
                          <div className="p-4 border-b border-edge-subtle">
                            <h4 className="text-sm font-semibold text-ink">All Portfolio Companies</h4>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-edge">
                                  <th className="text-left py-3 px-4 text-ink-muted font-medium">Company</th>
                                  <th className="text-left py-3 px-4 text-ink-muted font-medium">Geography</th>
                                  <th className="text-left py-3 px-4 text-ink-muted font-medium">Industry</th>
                                  <th className="text-right py-3 px-4 text-ink-muted font-medium">Unrealized</th>
                                  <th className="text-right py-3 px-4 text-ink-muted font-medium">MOIC</th>
                                  <th className="text-right py-3 px-4 text-ink-muted font-medium">Last Updated</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pcData.companies.map((co) => {
                                  // Look up by UUID — immune to name mismatches, includes all companies
                                  const snap = pcExposureData.byId?.[co.id];
                                  return (
                                    <tr key={co.id} className="border-b border-edge-subtle hover:bg-surface-tertiary/30 cursor-pointer transition"
                                      onClick={() => { setPcSelectedCompany(co); fetchCompanySnapshots(co.id); }}>
                                      <td className="py-3 px-4 font-medium text-ink">{co.company_name}</td>
                                      <td className="py-3 px-4 text-ink-muted">{snap?.geography || co.geography || "—"}</td>
                                      <td className="py-3 px-4 text-ink-muted">{snap?.industry || co.industry || "—"}</td>
                                      <td className="py-3 px-4 text-right text-brand">{snap?.unrealizedValue > 0 ? formatCurrency(snap.unrealizedValue) : snap?.value > 0 ? formatCurrency(snap.value) : "—"}</td>
                                      <td className="py-3 px-4 text-right">
                                        {snap?.moic ? (
                                          <span className={`font-medium ${snap.moic >= 1.0 ? "text-success-fg" : "text-danger-fg"}`}>{snap.moic.toFixed(2)}x</span>
                                        ) : "—"}
                                      </td>
                                      <td className="py-3 px-4 text-right text-ink-muted">{co.updated_at ? new Date(co.updated_at).toLocaleDateString() : "—"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Company Detail View */}
                {pcTab === "overview" && pcSelectedCompany && (
                  <div>
                    <button onClick={() => setPcSelectedCompany(null)}
                      className="flex items-center gap-1 text-sm text-brand hover:underline mb-4">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                      Back to all companies
                    </button>
                    <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-6">
                      <h3 className="text-lg font-bold text-ink mb-1">{pcSelectedCompany.company_name}</h3>
                      <div className="flex gap-4 text-sm text-ink-muted">
                        {pcSelectedCompany.geography && <span>Geography: {pcSelectedCompany.geography}</span>}
                        {pcSelectedCompany.industry && <span>Industry: {pcSelectedCompany.industry}</span>}
                      </div>
                    </div>

                    {/* Valuation History Chart */}
                    {pcData.snapshots.length > 0 && (
                      <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle mb-6">
                        <h4 className="text-sm font-semibold text-ink mb-4">Valuation History</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={pcData.snapshots.slice().reverse().map(s => ({
                            date: s.snapshot_date,
                            invested: s.invested_amount || 0,
                            unrealized: s.unrealized_value || 0,
                            realized: s.realized_proceeds || 0,
                            total: s.total_value || 0,
                            moic: s.gross_moic || 0,
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                            <XAxis dataKey="date" stroke="var(--chart-axis)" style={{ fontSize: "0.7rem" }} />
                            <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.7rem" }} tickFormatter={(v) => formatCurrency(v)} />
                            <Tooltip contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
                              formatter={(v) => formatCurrency(v)} />
                            <Legend />
                            <Area type="monotone" dataKey="invested" name="Invested" stackId="1" stroke="var(--chart-fill-primary)" fill="var(--chart-fill-primary)" fillOpacity={0.3} />
                            <Area type="monotone" dataKey="unrealized" name="Unrealized" stackId="2" stroke="var(--chart-positive)" fill="var(--chart-positive)" fillOpacity={0.3} />
                            <Area type="monotone" dataKey="realized" name="Realized" stackId="3" stroke="var(--chart-warning)" fill="var(--chart-warning)" fillOpacity={0.3} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Snapshot table */}
                    <div className="bg-surface-secondary rounded-lg border border-edge-subtle">
                      <div className="p-4 border-b border-edge-subtle">
                        <h4 className="text-sm font-semibold text-ink">Historical Snapshots</h4>
                      </div>
                      {pcData.snapshots.length === 0 ? (
                        <div className="p-6 text-center text-ink-muted text-sm">No snapshots available</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-edge">
                                <th className="text-left py-2 px-3 text-ink-muted font-medium">Date</th>
                                <th className="text-right py-2 px-3 text-ink-muted font-medium">Invested</th>
                                <th className="text-right py-2 px-3 text-ink-muted font-medium">Unrealized</th>
                                <th className="text-right py-2 px-3 text-ink-muted font-medium">Realized</th>
                                <th className="text-right py-2 px-3 text-ink-muted font-medium">Total Value</th>
                                <th className="text-right py-2 px-3 text-ink-muted font-medium">MOIC</th>
                                <th className="text-right py-2 px-3 text-ink-muted font-medium">IRR</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pcData.snapshots.map((s) => (
                                <tr key={s.id} className="border-b border-edge-subtle">
                                  <td className="py-2 px-3 text-ink">{s.snapshot_date}</td>
                                  <td className="py-2 px-3 text-right text-ink">{s.invested_amount ? formatCurrency(s.invested_amount) : "—"}</td>
                                  <td className="py-2 px-3 text-right text-ink">{s.unrealized_value ? formatCurrency(s.unrealized_value) : "—"}</td>
                                  <td className="py-2 px-3 text-right text-ink">{s.realized_proceeds ? formatCurrency(s.realized_proceeds) : "—"}</td>
                                  <td className="py-2 px-3 text-right font-medium text-ink">{s.total_value ? formatCurrency(s.total_value) : "—"}</td>
                                  <td className="py-2 px-3 text-right text-brand-secondary">{s.gross_moic ? `${s.gross_moic.toFixed(2)}x` : "—"}</td>
                                  <td className="py-2 px-3 text-right text-ink">{s.gross_irr ? `${(s.gross_irr * 100).toFixed(1)}%` : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Data Freshness */}
                {pcTab === "freshness" && (
                  <div className="bg-surface-secondary rounded-lg border border-edge-subtle">
                    {pcData.freshness.length === 0 ? (
                      <div className="p-8 text-center text-ink-muted">
                        <p className="text-lg mb-2">No freshness data</p>
                        <p className="text-sm">Import investor data files to begin tracking data freshness.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-edge">
                              <th className="text-left py-3 px-4 text-ink-muted font-medium">Fund</th>
                              <th className="text-left py-3 px-4 text-ink-muted font-medium">Investor</th>
                              <th className="text-center py-3 px-4 text-ink-muted font-medium">Status</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">Last Import</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">Total Imports</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">Real Quarters</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">Gap-Filled</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pcData.freshness.map((f, i) => (
                              <tr key={i} className="border-b border-edge-subtle">
                                <td className="py-3 px-4 font-medium text-ink">{f.fund_name || "—"}</td>
                                <td className="py-3 px-4 text-ink-muted">{f.investor_name || "—"}</td>
                                <td className="py-3 px-4 text-center">
                                  {f.is_stale ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-danger-bg text-danger-fg">
                                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
                                      Stale
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-bg text-success-fg">
                                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>
                                      Current
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-right text-ink-muted">{f.last_import_date || "Never"}</td>
                                <td className="py-3 px-4 text-right text-ink">{f.total_imports}</td>
                                <td className="py-3 px-4 text-right text-ink">{f.real_quarters}</td>
                                <td className="py-3 px-4 text-right text-ink-muted">{f.gap_filled_quarters}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Import History */}
                {pcTab === "imports" && (
                  <div className="bg-surface-secondary rounded-lg border border-edge-subtle">
                    {pcData.imports.length === 0 ? (
                      <div className="p-8 text-center text-ink-muted">
                        <p className="text-lg mb-2">No imports yet</p>
                        <p className="text-sm">Upload an Investor Data Excel file to get started.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-edge">
                              <th className="text-left py-3 px-4 text-ink-muted font-medium">Report Date</th>
                              <th className="text-left py-3 px-4 text-ink-muted font-medium">Fund</th>
                              <th className="text-left py-3 px-4 text-ink-muted font-medium">Investor</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">NAV</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">TVPI</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">IRR</th>
                              <th className="text-right py-3 px-4 text-ink-muted font-medium">Records</th>
                              <th className="text-left py-3 px-4 text-ink-muted font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pcData.imports.map((imp) => (
                              <tr key={imp.id} className="border-b border-edge-subtle">
                                <td className="py-3 px-4 font-medium text-ink">{imp.report_date}</td>
                                <td className="py-3 px-4 text-ink">{imp.fund_name || "—"}</td>
                                <td className="py-3 px-4 text-ink-muted">{imp.investor_name || "—"}</td>
                                <td className="py-3 px-4 text-right text-ink">{imp.nav ? formatCurrency(imp.nav) : "—"}</td>
                                <td className="py-3 px-4 text-right text-brand-secondary">{imp.tvpi ? `${Number(imp.tvpi).toFixed(2)}x` : "—"}</td>
                                <td className="py-3 px-4 text-right text-ink">{imp.net_irr ? `${(Number(imp.net_irr) * 100).toFixed(1)}%` : "—"}</td>
                                <td className="py-3 px-4 text-right text-ink">{imp.records_imported || 0}</td>
                                <td className="py-3 px-4">
                                  <span className={`text-xs font-medium ${imp.status === "completed" ? "text-success-fg" : imp.status === "failed" ? "text-danger-fg" : "text-ink-muted"}`}>
                                    {imp.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}


      </div>
    </div>
  );
}
